import type { DesignAxesCopy } from "@/features/design-system/source-derived/axes-copy";
import {
  EN_DESIGN_AXES_COPY,
  JA_DESIGN_AXES_COPY,
  KO_DESIGN_AXES_COPY,
  ZH_CN_DESIGN_AXES_COPY,
  ZH_TW_DESIGN_AXES_COPY,
} from "@/features/design-system/source-derived/axes-copy";
// ── lane/koentry: /studio/new + /studio/templates ──────────────────────────
import type { NewProjectCopy } from "./studio-new-project-copy";
import {
  EN_NEW_PROJECT_COPY,
  JA_NEW_PROJECT_COPY,
  KO_NEW_PROJECT_COPY,
  ZH_CN_NEW_PROJECT_COPY,
  ZH_TW_NEW_PROJECT_COPY,
} from "./studio-new-project-copy";
import type { StartersCopy } from "./studio-starters-copy";
import {
  EN_STARTERS_COPY,
  JA_STARTERS_COPY,
  KO_STARTERS_COPY,
  ZH_CN_STARTERS_COPY,
  ZH_TW_STARTERS_COPY,
} from "./studio-starters-copy";
// ── end lane/koentry imports ───────────────────────────────────────────────
import type { LocaleCode } from "@/pages/cogo-marketing/i18n/locales";
import type { PlanRightsValue } from "@/features/studio-dashboard/lib/plan-rights-view-model";
import type { StudioProbeFailure } from "@/services/studio-presentation-profile";
import type { StudioReviewUnavailableReasonCode } from "@/hooks/dashboard-data-state";

/**
 * What an Operations review screen says when a lane has no admitted
 * producer. Three branches, one shape, and the `Record` over
 * `StudioReviewUnavailableReasonCode` means a new reason code is a compile
 * error in all five tables rather than a new English sentence on a ko screen.
 */
export interface ReviewUnavailableCopy {
  summary: string;
  title: string;
  detail: string;
}

export interface StudioDictionary {
  // ── lane/koentry ─────────────────────────────────────────────────────────
  /**
   * `/studio/new` — the four-step creation wizard, and `/studio/templates`
   * — the Starters gallery. Both tables live beside nothing else
   * (`studio-new-project-copy.ts` / `studio-starters-copy.ts`) for the same
   * reason `designSystemAxes` does: this file ROUTES the active locale, it
   * does not store 1300 lines of one screen's copy five times over.
   */
  newProject: NewProjectCopy;
  starters: StartersCopy;
  // ── end lane/koentry ─────────────────────────────────────────────────────
  /**
   * Copy for the source-derived design-system axes surface. The values live
   * in axes-copy.ts (co-located with the axis pipeline and its
   * Record<AxisName, …> compile-time completeness checks); the dictionary
   * only routes the active locale's table.
   */
  designSystemAxes: DesignAxesCopy;
  language: {
    label: string;
  };
  /**
   * Copy a sighted reader never sees: landmark and region names carried on
   * `aria-label`, the loading description on each island host, and the skip
   * link that is `sr-only` until it takes focus.
   *
   * It lives in one section rather than beside each feature's copy because
   * this is the surface every English-residue sweep in this app walks past —
   * they read TEXT NODES, and none of these are one. One section gives the
   * accessibility gate a single declaration site to derive from, the way the
   * design-system axes table does for the swatch tooltips.
   *
   * Nothing here has a visible twin. An `aria-label` on a control that also
   * shows its own words must be translated in lockstep WITH those words
   * (WCAG 2.5.3 Label in Name — a voice-control user says what they can see),
   * so those belong with the feature's copy, not here.
   */
  a11y: {
    /** The `sr-only` link that jumps a keyboard user past the nav rail. */
    skipToMainContent: string;
    /** `<main>` landmark for the dashboard content area. */
    dashboardContent: string;
    /**
     * The island host's `aria-description`, e.g. "Loading Library…". Takes the
     * surface's own name so the 23 dashboard islands do not each need a
     * string — the name comes from `studioRouteLabel`, already localized.
     */
    loadingSurface: (surface: string) => string;
    /** Names for the two chrome islands, which are not routes. */
    creatorNav: string;
    header: string;
    designSystemSurfaces: string;
    designSystemInventory: string;
    importSources: string;
    librarySummary: string;
    operations: string;
    requestReviewFlow: string;
    reviewSummary: string;
    shareReadinessChecklist: string;
    /** `<iframe title>` for the generated-app preview frame. */
    appPreviewFrame: string;
    /**
     * The PRE-AUTH entry shell (`features/auth/studio-entry-shell.tsx`) —
     * sign-in, password rotation, and the three checkout outcome pages.
     *
     * These were hardcoded English literals until 2026-08-19. They were
     * outside #2324's sweep because that walk is derived from
     * `STUDIO_ROUTE_REGISTRY`, which enumerates the 20 AUTHENTICATED
     * `/studio/*` surfaces; a page rendered before sign-in is not a
     * registered route and no derivation from that table can reach it.
     * Measured live on the appliance the same day, where this shell is the
     * FIRST screen: `aria-label="Coogo home"` and
     * `aria-label="Coogo operational report"` on an `html lang="ko"` document.
     *
     * `coogo` stays Latin in every locale — it is the brand, the same
     * keep-Latin rule `appPreviewFrame` already follows.
     */
    entryBrandHome: string;
    /** The entry shell's right-hand sample operational-report panel. */
    entryReport: string;
    /**
     * Tooltip on a guidance disclosure's toggle. Takes the section's own,
     * already-localized heading — it used to be spliced as `` `Show ${title}` ``,
     * which put an English verb in front of a Korean noun on every screen that
     * carries guidance.
     */
    showSection: (name: string) => string;
    hideSection: (name: string) => string;
  };
  /**
   * The IDE shell's own chrome — everything under `/ide/*`.
   *
   * WHY IT IS NOT IN `a11y` ABOVE. That section is for copy with no visible
   * twin, and its gate proves every key reaches a DASHBOARD screen. The IDE
   * is a different shell on a different route family: `StudioDashboardShell`
   * renders `STUDIO_ROUTE_REGISTRY` routes, `StudioShell` renders the
   * authority tasks in `shell/ide-v2/studio-authority-workspaces.ts`, and no
   * walk over the first can ever mount the second. Folding IDE strings into
   * `a11y` would therefore have turned its own coverage assertion red while
   * still leaving `/ide/*` unwalked. The reach fix is a second derivation,
   * not a bigger bag of strings — see
   * `src/i18n/__tests__/ide-surface-localized.test.tsx`.
   *
   * MIXED BY DESIGN. Unlike `a11y`, this section holds both attribute-only
   * copy (`undo`, `viewportSwitcher`) and visible words (`pages.title`,
   * `reviewPreview`), because in the IDE the same string is often both: the
   * device buttons carry `d.label` on `aria-label` AND inside their tooltip,
   * so one entry has to drive both or WCAG 2.5.3 Label in Name breaks.
   */
  ide: {
    /** Icon-only history buttons in the IDE top bar. */
    undo: string;
    redo: string;
    /** `aria-label` on the preview navigator, per working view. */
    appPageList: string;
    documentList: string;
    /** `aria-label` on the floating viewport toolbar. */
    viewportSwitcher: string;
    /**
     * Device presets. The WORD is translated and the MEASUREMENT is not:
     * `375x812` is a number of pixels in every language, `Mobile` is not.
     * The dimensions are no longer written here at all — the call site
     * composes them from `BREAKPOINT_VIEWPORTS`, which is where the app
     * already reads the frame size from, so a preset resized upstream cannot
     * leave a stale number in five dictionaries.
     */
    device: {
      mobile: string;
      tablet: string;
      desktop: string;
      /** The report/document view, which is a page size rather than a device. */
      document: string;
      /** `A4` is a paper standard; only "portrait" is a word. */
      a4Portrait: string;
      /** `${label} (${width}x${height})` — one spelling for both uses. */
      framed: (label: string, width: number, height: number) => string;
      /** `${label} · ${width}x${height}px`, the size read-out beside the switcher. */
      sized: (label: string, width: number, height: number) => string;
    };
    /** The page navigator, shown while the working view is the app. */
    pages: {
      title: string;
      /** Sub-heading when the project has pages: "Preview + 3 pages". */
      summaryWithPages: (count: number) => string;
      /** Sub-heading when it has none — the runnable preview is all there is. */
      summaryPreviewOnly: string;
      runnableTitle: string;
      runnableBadge: string;
      runnableDetail: string;
      emptyTitle: string;
      emptyBody: string;
    };
    /** The document navigator, shown while the working view is the report. */
    documents: {
      title: string;
      count: (count: number) => string;
      pageCount: string;
    };
    /** The pill over the canvas that hands the surface to chat. */
    reviewPreview: string;
    /** Report actions. `DOCX` is a file format, so only this one is a word. */
    print: string;
  };
  navigation: {
    sections: {
      start: string
      build: string
      verify: string
      release: string
    }
    home: string
    create: string
    work: string
    details: string
    map: string
    code: string
    release: string
    publish: string
    launch: string
    configure: string
    settings: string
    share: string
    starters: string
    import: string
    sources: string
    library: string
    documents: string
    requests: string
    requestsAndReviews: string
    bots: string
    operations: string
    data: string
    activity: string
    impact: string
    audience: string
    analytics: string
    designSystem: string
    workspaceSettings: string
    projects: string
    templates: string
    projectSettings: string
    account: string
    signIn: string
    signOut: string
    goHome: string
    goCoogoHome: string
    coogoHomepage: string
    studioNavigation: string
    studioNavigationDescription: string
    collapseNavigation: string
    expandNavigation: string
  }
  artifacts: {
    label: string;
    app: string;
    game: string;
    design: string;
    document: string;
    guide: string;
    insights: string;
  };
  work: {
    title: string;
    subtitle: string;
    namePlaceholder: string;
    create: string;
    browseStarters: string;
    importSource: string;
    emptyTitle: string;
    emptyBody: string;
  };
  designSystem: {
    title: string;
    subtitle: string;
    validationLab: string;
    themeEditor: string;
    tabs: {
      systems: string;
      validation: string;
      vocabulary: string;
      briefings: string;
    };
    uiBlocks: {
      eyebrow: string;
      title: string;
      description: string;
      intentLabel: string;
      intentPlaceholder: string;
      shortcutsLabel: string;
      shortcuts: {
        dashboard: string;
        store: string;
        landing: string;
        auth: string;
      };
      recommendations: string;
      admittedCount: (count: number) => string;
      noMatches: string;
      preview: string;
      selectedPreview: string;
      readOnly: string;
      authorityUnavailable: string;
    };
    /**
     * The gate a SIGNED-OUT visitor gets instead of the screen —
     * `DESIGN_SYSTEM_SCREEN_CONTRACT`'s refusal reason and its recovery link
     * (`features/design-system/agentic/agentic-design-system-page.tsx`).
     *
     * These were Korean string literals inside the contract itself until
     * 2026-08-25, so the whole gate read Korean with the switcher on English.
     * They are the only words that screen has for a signed-out reader, and
     * they escaped every existing localization sweep for a structural reason
     * worth writing down: `readable-surface-localized.test.tsx` reaches this
     * feature by rendering `AgenticTabs` + `InventorySummaryCard` DIRECTLY
     * (its own comment says the route walk cannot satisfy the signed-in
     * precondition), so the blocked branch was never mounted by any sweep —
     * and those sweeps look for untranslated ENGLISH in non-English locales,
     * which cannot see Korean on an English screen in either direction.
     *
     * The contract's `check()` runs outside the component tree, so it reads
     * these through `STUDIO_DICTIONARIES[getLocale()]` — the same
     * producer-time convention as `store/project-load-error-copy.ts` and
     * `features/chat/services/chat-model-adapter.ts`, not a new mechanism.
     */
    precondition: {
      /** Why the screen will not open, when there is no session. */
      signedOutReason: string;
      /** The one control on the gate: a link to `/login`. */
      signInCta: string;
      /** The FIRST frame, while `check()` is still resolving. */
      checkingReason: string;
      /**
       * The reason shown if a verdict is unmet but carries no reason of its
       * own. Unreachable from this screen's single call site today (a report
       * that is not `ok` always has a failing verdict, and every failing
       * verdict carries a reason) — it is the default that keeps a future
       * precondition from rendering a blank gate, and it is here rather than
       * inline so that future precondition cannot re-introduce the defect.
       */
      unmetFallbackReason: string;
    };
    sourceRead: {
      /** Shown when the read failed and the failure carried no message. */
      failed: string;
      /** Wraps whatever the failure did say. `{message}` is that text. */
      failedWithMessage: string;
    };
    vocabularyPanel: {
      title: string;
      search: string;
      enumPrefix: string;
      enumAccessor: string;
      enumEvent: string;
      enumAction: string;
      colName: string;
      colMode: string;
      colSemantics: string;
      colCategory: string;
      colReturns: string;
      colDomain: string;
      colDescription: string;
      loading: string;
      errorTitle: string;
      retry: string;
      empty: string;
      unavailableTitle: string;
      unavailableDetail: string;
      noMatch: string;
      clear: string;
    };
    validatorPanel: {
      /** The spec chip above the heading. */
      specChip: string;
      /** The panel heading. */
      heading: string;
      lastRun: string;
      justNow: string;
      secondsAgo: string;
      minutesAgo: string;
      hoursAgo: string;
      rerun: string;
      validating: string;
      colRule: string;
      allChecks: string;
      noViolations: string;
      colGate: string;
      colStatus: string;
      statusPass: string;
      statusFail: string;
      flagOffTitle: string;
      flagOffBody: string;
      readyTitle: string;
      readyBody: string;
      unreadableTitle: string;
      unreadableBody: string;
      briefingReadFailed: string;
      briefingTitle: string;
      briefingIntro: string;
      briefingSignIn: string;
      briefingSignedOutDetail: string;
      briefingUnavailableTitle: string;
      briefingUnavailableDetail: string;
      briefingFieldTitle: string;
      briefingFieldBody: string;
      briefingTitlePlaceholder: string;
      briefingBodyPlaceholder: string;
      briefingReady: string;
      briefingSaving: string;
      briefingSave: string;
      briefingEmpty: string;
      repoInitTitle: string;
      repoInitBody: string;
      noBlocksTitle: string;
      noBlocksBody: string;
      errorTitle: string;
      errorBody: string;
    };
    inventory: {
      readingBadge: string;
      readingDetail: string;
      unreadableBadge: string;
      unreadableDetail: string;
      noneBadge: string;
      noneDetail: string;
      listedBadge: string;
      listedBadgeOne: string;
      listedDetail: string;
      readStamp: string;
      setAsideNote: string;
      setAsideNoteOne: string;
    };
    themeEditorPanel: { title: string; body: string };
    workbench: {
      controlsTitle: string;
      selectPlaceholder: string;
      saveToLibrary: string;
      saveDialogTitle: string;
      namePlaceholder: string;
      descriptionPlaceholder: string;
      closeLabel: string;
      cancel: string;
      save: string;
      saving: string;
      savedAs: string;
      nameRequired: string;
      saveFailed: string;
      sourceTitle: string;
      sourceEmptyBody: string;
      sourceImportCta: string;
      sourceReadPrefix: string;
      sourceVariableCount: string;
      sourceVariableCountOne: string;
      chooseAxisValue: string;
      axisValueSelectLabel: string;
    };
    projectTokens: {
      title: string;
      bodyBefore: string;
      navPath: string;
      bodyAfter: string;
    };
  };
  settings: {
    title: string;
    profile: {
      title: string;
      name: string;
      email: string;
      edit: string;
      cancel: string;
      /** Commits the in-flight display-name edit. */
      save: string;
      /** Same control while the write is in flight. */
      saving: string;
      /** Accessible name of the display-name input. */
      nameInputLabel: string;
      /** Shown only after `updateStudioDisplayName` ACCEPTS the write. */
      nameSaved: string;
      providerBlocked: string;
      signedOutBlocked: string;
    };
    /**
     * The two settings-write outcomes this screen announces. Both were inline
     * English JSX literals; the refusal in particular is the one a reader must
     * not misread, so it is stated in their language.
     */
    writeOutcome: {
      protectedRejected: string;
      accepted: string;
    };
    publishing: {
      title: string;
      exportsLabel: string;
      exportsNone: string;
      exportsCount: string;
      recordsNone: string;
      recordsCount: string;
      exportSettings: string;
      exportSettingsBlocked: string;
      reviewRecords: string;
      reviewRecordsBlocked: string;
    };
    plan: {
      title: string;
      current: string;
      manage: string;
      allowsTitle: string;
      generation: string;
      publication: string;
      packPromotion: string;
      errorLabel: string;
      lookupFailed: string;
      loading: string;
      signedOut: string;
      noSubscription: string;
      /**
       * What the three plan-rights cells can say. `buildPlanRightsViewModel`
       * returns these CODES rather than sentences — it used to return English
       * ("Not measured", "Preview only", …) straight onto a localized screen.
       * A `Record` of the code union means a new outcome is a compile error in
       * all five tables instead of a new English string on a ko surface.
       */
      rights: Record<PlanRightsValue, string>;
    };
    runtime: {
      title: string;
      note: string;
      distribution: string;
      studioSha: string;
      applianceReleaseId: string;
      observedAt: string;
      observationsTitle: string;
      available: string;
      unavailable: string;
      readFailed: string;
      notConfirmed: string;
      /**
       * Why a capability probe failed, keyed by `StudioProbeFailure['code']`.
       * `{status}` is substituted with the HTTP status where the code carries
       * one. Replaces the English sentences the probe module used to author
       * and this panel used to render verbatim.
       */
      probeFailures: Record<StudioProbeFailure['code'], string>;
      capabilities: {
        platformConnection: string;
        localAuth: string;
        googleAuth: string;
        localPublish: string;
        cloudPublish: string;
        customDomain: string;
        billing: string;
        analytics: string;
        externalIntegrations: string;
      };
    };
    domain: { title: string; defaultLabel: string; customLabel: string; connect: string; blocked: string; signedOut: string };
    github: { title: string; developer: string; connected: string; disconnect: string; disconnecting: string; invite: string; connect: string; connecting: string; confirmDisconnect: string };
    notifications: { title: string; reason: string; review: string; agentSummary: string; productUpdates: string; unavailableSuffix: string };
    danger: { title: string; deleteAccount: string; deleteBlocked: string };
    /**
     * Reasons shown on this screen when the installation sits inside a closed
     * network. Each names the *outside thing* the control needs, so the reader
     * learns why it cannot work here rather than that it is merely "off".
     */
    closedNetwork: {
      planNote: string;
      planManageBlocked: string;
      servedFromLabel: string;
      domainBlocked: string;
      githubTitle: string;
      githubBlocked: string;
      deleteBlocked: string;
    };
  };
  operations: {
    title: string;
    subtitle: string;
    reviewedOutputs: string;
    acceptanceRate: string;
    sharedArtifacts: string;
    sharedArtifactsNote: string;
    unmeasured: string;
    tiles: {
      products: string;
      orders: string;
      revenue: string;
      members: string;
      analytics: string;
    };
  };
  requests: {
    title: string;
    subtitle: string;
    openChat: string;
    howItWorks: string;
    stepsSuffix: string;
    rows: {
      inProgress: { title: string; body: string };
      needsApproval: { title: string; body: string };
      reviewTrail: { title: string; body: string };
      accessControl: { title: string; body: string };
    };
    current: { title: string; body: string };
    actions: { addSource: string; library: string; share: string; refresh: string };
    tiles: { updates: string; checks: string; needsApproval: string };
    timeline: { title: string; emptyTitle: string; emptyBody: string };
    approval: { title: string; waiting: string; empty: string };
    prepare: {
      title: string;
      appDraft: string;
      sourceImport: string;
      reviewCheck: string;
      launchSetup: string;
    };
    requestTitle: {
      appDraft: string;
      sourceAdded: string;
      reviewCheck: string;
      shareUpdate: string;
      launchSetup: string;
      requestedChange: string;
    };
    status: { ready: string; needsReview: string; inProgress: string; recorded: string };
    scope: {
      codeUpdate: string;
      designFileUpdate: string;
      publicShare: string;
      launchSetup: string;
      workspaceUpdate: string;
    };
    entry: { reuse: string; fresh: string; validation: string; approval: string };
  };
  share: {
    title: string;
    subtitle: string;
    centerTitle: string;
    centerBody: string;
    createOutput: string;
    addSource: string;
    sources: string;
    settingsDialog: {
      title: string;
      body: string;
      subdomainLabel: string;
    };
    checklist: {
      publicUrl: string;
      readinessCheck: string;
      outcomePath: string;
      updateControl: string;
      notShared: string;
      sharedCount: string;
      nothingSharedYet: string;
      readyCount: string;
      needsCheck: string;
      outcomePathValue: string;
      updateControlValue: string;
    };
    tiles: {
      sharedOutputs: string;
      readyChecks: string;
      drafts: string;
      latestUpdate: string;
      noSharedOutput: string;
    };
    health: {
      title: string;
      checked: string;
      lastRecordedCheck: string;
      needsAttention: string;
      draftsNotShared: string;
      nothingShared: string;
      noneYet: string;
      never: string;
      none: string;
      checkedOfShared: string;
      blockedCount: string;
      nothingSharedBadge: string;
      nothingSharedDetail: string;
      blockedBadge: string;
      blockedBadgeOne: string;
      blockedDetail: string;
      uncheckedBadge: string;
      uncheckedBadgeOne: string;
      uncheckedDetail: string;
      checkedBadge: string;
      checkedBadgeOne: string;
      checkedBadgeLastSuffix: string;
      checkedDetail: string;
    };
  };
  library: {
    title: string;
    subtitle: string;
    importSource: string;
    tiles: {
      savedWork: string;
      openLinks: string;
      details: string;
      launches: string;
    };
    searchPlaceholder: string;
    searchResults: string;
    savedWorkCount: string;
    previewTitle: string;
    thumbnailPreview: {
      project: string;
      template: string;
      library: string;
    };
    previewEmptyTitle: string;
    previewEmptyBody: string;
    whatYouCanDo: string;
    readyToUse: string;
    noReviewLinks: string;
    selectedDetails: {
      summary: string;
      openBeforeReviewLink: string;
    };
    empty: { title: string; body: string };
    actions: {
      open: { label: string; detail: string; noLink: string };
      edit: { label: string; detail: string; noWorkspace: string };
      launch: { label: string; detail: string; noBundle: string; busy: string };
      details: { label: string; detail: string };
      useAgain: string;
      sharePublicly: string;
      check: string;
      share: string;
      linkPr: string;
      prepareLaunch: string;
      feedback: {
        check: { progress: string; success: string; failure: string };
        sharePublicly: { progress: string; success: string; failure: string };
        prepareLaunch: { progress: string; success: string; failure: string };
      };
      noSelection: string;
    };
  };
  sources: {
    title: string;
    subtitle: string;
    addToLibrary: string;
    howItWorks: string;
    flowAriaLabel: string;
    agentIntake: {
      eyebrow: string;
      title: string;
      description: string;
      placeholder: string;
      attach: string;
      addUrl: string;
      submit: string;
      understanding: string;
      nextStep: string;
      connected: string;
      emptyConnected: string;
      advanced: string;
      advancedSummary: string;
      requestUnderstanding: string;
      urlUnderstanding: string;
      codeUnderstanding: string;
      figmaUnderstanding: string;
      documentUnderstanding: string;
      nextDescribe: string;
      nextUrl: string;
      nextCode: string;
      nextFigma: string;
      nextDocument: string;
      currentRequest: string;
      selectedFile: string;
      restoredDocument: string;
      connectedDesign: string;
      urlHint: string;
      unsupportedFile: string;
      activeDesignConflict: string;
      librarySaveMissing: string;
    };
    steps: { addSource: string; shape: string; save: string; share: string };
    form: {
      appName: string;
      audience: string;
      audienceCreator: string;
      audienceBusiness: string;
      audienceDeveloper: string;
      sourceFieldSuffix: string;
      briefPlaceholder: string;
    };
    upload: {
      title: string;
      body: string;
      button: string;
    };
    groups: {
      fromIdea: string;
      bringExisting: string;
    };
    otherWays: {
      title: string;
    };
    chatStart: {
      title: string;
      body: string;
    };
    savedWith: {
      title: string;
      openLink: string;
      savedFile: string;
      sourceSnapshot: string;
      launchCheck: string;
    };
    after: {
      title: string;
      defaultNote: string;
      figmaFileNote: string;
      documentFileNote: string;
      steps: {
        prepared: string;
        designRead: string;
        documentRead: string;
        sourceSaved: string;
        keptInBrowser: string;
        mapperOpens: string;
        structureOpens: string;
        libraryOpens: string;
      };
    };
    panel: {
      tags: string;
      librarySuffix: string;
      figmaFileNote: string;
      documentFileNote: string;
      figmaTokenLabel: string;
      figmaTokenPasteHint: string;
      forgetToken: string;
      readDesign: string;
      readDocument: string;
      localFigmaFile: string;
      localDocument: string;
    };
    help: Record<
      | 'assistant_prompt'
      | 'url_import'
      | 'ide_file'
      | 'github_pr'
      | 'figma_frame'
      | 'figma_file'
      | 'document_file',
      { title: string; detail: string; example: string }
    >;
    refLabels: Record<
      | 'assistant_prompt'
      | 'url_import'
      | 'ide_file'
      | 'github_pr'
      | 'figma_frame'
      | 'figma_file'
      | 'document_file',
      string
    >;
    cards: Record<
      | 'assistant_prompt'
      | 'url_import'
      | 'ide_file'
      | 'github_pr'
      | 'figma_frame'
      | 'figma_file'
      | 'document_file',
      { label: string; description: string; bestFor: string; output: string }
    >;
  };
  chrome: {
    breadcrumb: string;
    search: string;
    openNavigation: string;
    openChat: string;
    closeChat: string;
    notifications: string;
    lightMode: string;
    darkMode: string;
    publish: string;
    publishCurrent: string;
    openProjectToPublish: string;
    publishBlocked: string;
    conflict: string;
    saveFailed: string;
    saving: string;
    unsaved: string;
    live: string;
    saved: string;
    draft: string;
    reloadConflict: string;
    reload: string;
    reloading: string;
    exportSource: string;
    openProjectToExport: string;
    exportSuccess: string;
    toggleIntentLoop: string;
    moreControls: string;
    history: string;
  };
  proposal: {
    change: string;
    info: string;
    warning: string;
    critical: string;
    before: string;
    after: string;
    unchanged: string;
    removed: string;
    details: string;
    close: string;
    whatWillCheck: string;
    reviewChecks: string;
    reviewBeforeApply: string;
    attachedProof: string;
    check: string;
    notAttached: string;
    readyToUse: string;
    reviewFirst: string;
    anotherEditPending: string;
    compareConflict: string;
    keep: string;
    revise: string;
    reviseAria: string;
    use: string;
    reviewNeeded: string;
    keepCurrent: string;
    keepCurrentAria: string;
  };
  home: {
    title: string;
    subtitle: string;
    startWithChat: string;
    workTypes: string;
    workTypeHint: string;
    recentWork: string;
    reviewNeeded: string;
    viewAll: string;
    review: string;
    noWork: string;
    noWorkHint: string;
    firstProjectAction: string;
    continueWorking: string;
    openWork: string;
    continue: string;
    latestProjectEmpty: string;
    readiness: string;
    totalWork: string;
    productionVerified: string;
    settings: string;
    countSuffix: string;
    readinessSummary: string;
    featuredOutcome: {
      eyebrow: string;
      title: string;
      outcomes: {
        newsletterMembership: { title: string };
        reservationOperations: { title: string };
      };
      description: string;
      boundary: string;
      action: string;
      clearAction: string;
      inProgress: string;
      prompt: string;
    };
    lanes: {
      app: { title: string; prompt: string };
      document: { title: string; prompt: string };
      game: { title: string; prompt: string };
      existing: { title: string; prompt: string };
      starter: { title: string; prompt: string };
    };
  };
  chat: {
    /**
     * C1 — the WHOLE `<h1>` sentence, punctuation and word order included.
     * It used to be a fragment the component finished with `, ${name}?`, so
     * four of five locales rendered an English comma, an English question
     * mark, and English word order. A vocative goes where the language puts
     * it; only the language knows where that is.
     */
    greeting: (name: string) => string;
    placeholder: string;
    attach: string;
    attachLabel: string;
    cancel: string;
    send: string;
    checkingSession: string;
    cancelled: string;
    ontologySelectionRequired: string;
    startingNotice: string;
    appGenerationStarting: string;
    webMcpVerified: (target?: string) => string;
    readOnlyCompletionFallback: string;
    suggestions: string[];
    templateProposal: {
      heading: string;
      startFaster: string;
      accept: string;
      accepting: string;
      decline: string;
      accepted: string;
      declined: string;
      unavailable: string;
      failure: string;
    };
    /** A1 — creator-facing copy for the hero-chat inline failure alert
     * (`hero-chat-planning-error`). Raw `error.message` internals are never
     * shown to the creator persona: the closed switch in
     * features/studio-dashboard/planning-failure-copy.ts maps a recognized
     * failure to one of these strings (or to the live-planner copy authored
     * per reason in features/chat/services/live-planner-copy.ts), and
     * anything unrecognized falls back to `generic`. */
    planningFailure: {
      /** Shown when the caught error message contains
       * "Ontology admission blocked" — a fail-closed generation rejection. */
      ontologyBlocked: string;
      planNotActionable: string;
      /** Shown when the plan left part of the request uncovered — the
       * `requirement-coverage` reason. Creator-actionable on purpose: the
       * gate means "some of what you asked for is missing from the plan",
       * and shorter sentences are what actually gets it covered. */
      requirementCoverage: string;
      /** Shown for any other generic planning/generation failure. */
      generic: string;
    };
    /** Creator copy for the deterministic "update existing work" intent
     * (services/update-existing-work-intent.ts). Production 2026-08-04: this
     * request used to reach the model as free text and come back as
     * "즉시 앱을 생성할게요" — a NEW app for a request to change an old one.
     * It is now resolved on the client, and these are the only three things
     * it can say. */
    updateExistingWork: {
      /** The workspace holds no saved work. Offers creation; never starts it. */
      noExistingWork: string;
      /** Exactly one piece of work matched — `{name}` is substituted. */
      opening: string;
      /** Several distinct pieces of work — `{choices}` is substituted with the
       * de-duplicated, date-disambiguated list. */
      chooseOne: string;
    };
    /** Creator copy for a chat-dispatched tool that failed
     * (services/chat-tool-failure-copy.ts). Raw engine prose — e.g.
     * "Invalid input for Studio operation …" — is never rendered to a
     * creator; the closed switch maps a recognized failure to one of these
     * and anything unrecognized falls back to `generic`. */
    toolFailure: {
      /** The dispatched operation rejected its input against its contract. */
      invalidInput: string;
      /** The action exists but chat may not execute it (no operation contract). */
      notPermitted: string;
      /** No such Studio action/operation. */
      unknownAction: string;
      /** Any other tool failure. */
      generic: string;
    };
  };
  templateContent: {
    aiFill: string;
    briefPlaceholder: string;
    filling: string;
    aiUnavailable: string;
    fillFailed: string;
    fillBatchProgress: string;
    fillPartialNotice: string;
    fillAllFailedNotice: string;
    judgeChecking: string;
    judgeFailed: string;
    pass: string;
    caution: string;
    reasons: string;
    hide: string;
    show: string;
    general: string;
    changed: string;
    records: string;
    readOnly: string;
    invalidJson: string;
    persistError: string;
    applySuccess: string;
    applyFailed: string;
    applyFailureSummary: string;
    applying: string;
    apply: string;
    designRebindTitle: string;
    designRebindPresetLoadError: string;
    designRebindPresetPlaceholder: string;
    designRebindApply: string;
    designRebindApplying: string;
    designRebindApplySuccess: string;
    designRebindApplyFailed: string;
  };
  candidateVerification: {
    title: string;
    description: string;
    authorityNote: string;
    promotionBoundary: string;
    authorityLabel: string;
    verifiedStatus: string;
    publicationGateStatus: string;
    listLabel: string;
    refresh: string;
    refreshing: string;
    askCoogo: string;
    liveChecking: string;
    liveResult: string;
    statuses: {
      pending: string;
      checking: string;
      ready: string;
      verified: string;
      blocked: string;
      unavailable: string;
    };
    roles: {
      "sufficiency-assessment": string;
      "validator-safety-pass": string;
      "second-model-judge": string;
      "render-execution-receipt": string;
      "verification-capsule": string;
    };
    authorities: {
      "source-assessment-authority": string;
      "validator-runtime": string;
      "independent-model-judge": string;
      "renderer-runtime": string;
      "publishing-kernel": string;
    };
    causes: {
      "journal-checking": string;
      "journal-unavailable": string;
      "journal-scope-invalid": string;
      "journal-contract-invalid": string;
      "verification-pending": string;
      "producer-receipt-pending": string;
      "verification-incomplete": string;
      "source-assessment-current": string;
      "source-assessment-insufficient": string;
      "source-assessment-missing": string;
      "source-assessment-stale": string;
      "durable-journal-verified": string;
    };
  };
  report: {
    title: string;
    /** Heading label for the ontology-coverage side panel (not the removed
     * header view toggle — still rendered by
     * application-ontology-report-surface.tsx). */
    ontology: string;
    ontologySubtitle: string;
    unavailableTitle: string;
    unavailableBody: string;
    /** Developer-persona-only secondary line under `unavailableBody`; keeps the
     * prior technical wording (release-bound assessment requirement) visible
     * to developers while creators see the plain-language explanation only. */
    unavailableBodyDeveloperDetail: string;
    connectSource: string;
    axesLabel: string;
    axesSummary: string;
    axisMethod: string;
    coverage: string;
    coverageSubtitle: string;
    lowestRequiredAxis: string;
    loadingChart: string;
    currentEvidence: string;
    targetEnvelope: string;
    gap: string;
    axisDetails: string;
    gaps: string;
    priority: string;
    blocks: string;
    noTrack: string;
    askCoogo: string;
    noOpenRequirements: string;
    allDimensions: string;
    dimension: string;
    authority: string;
    resolution: string;
    current: string;
    evidence: string;
    none: string;
    status: {
      ready: string;
      warn: string;
      blocked: string;
      missing: string;
      /** Tooltip (`title`) shown on the "missing"/"Not yet" StatusPill so the
       * red pill doesn't read as an unexplained error. */
      missingTooltip: string;
    };
    /**
     * Arrival provenance for a CONVERSION project (2026-08-18 ASIS→TOBE
     * census rows #8/#9). Rendered only when `settings.asIsSnapshot` exists,
     * so a greenfield project never sees any of these strings.
     */
    conversionProvenance: {
      title: string;
      sourceLabel: string;
      capturedLabel: string;
      blocksLabel: string;
      decisionsLabel: string;
      realizationLabel: string;
      /** Shown INSTEAD of a figure when the recorded decisions contain nothing
       *  structurally checkable — coogo states the absence, never a number. */
      realizationAbsent: string;
      /** Known `sourceKind` values; an unknown kind renders its raw token. */
      sourceKindLegacyScreen: string;
    };
  };
  guide: {
    title: string;
    documents: string;
    noPageSelected: string;
    page: string;
    pageActivity: string;
    proposals: string;
    applied: string;
    exports: string;
    pages: string;
    revision: string;
    addPage: string;
    pageTitle: string;
    openingEditor: string;
    pageActions: string;
    reviewPage: string;
    proposeEdit: string;
    editContent: string;
    move: string;
    delete: string;
    draft: string;
    export: string;
    readback: string;
    output: string;
    notRequested: string;
    noExportRequested: string;
  };
  connectedDocument: {
    notConnected: string;
    connectedToTemplate: string;
    documentReference: string;
    loadDocument: string;
    /** C1 — creator-facing copy for the "Load document" failure alert
     * (`connected-document-load-error`). Raw server error codes (e.g.
     * "connection_ref_not_authorized") are never shown to the creator
     * persona; the catch path maps every code to one of these two
     * strings. The developer persona still sees the raw code. */
    loadErrorNotConnected: string;
    loadErrorGeneric: string;
    /** Panel chrome — visible to BOTH personas, so it is never persona-gated. */
    panelTitle: string;
    provider: string;
    /** Developer-persona labels. Left English until 2026-08-19, which is how
     *  `Connected document` survived a ko walk of the 가이드 tab. */
    activeSourceSnapshot: string;
    connectionReference: string;
    documentIdLabel: string;
    pageIdLabel: string;
    fetchVerifiedSnapshot: string;
    saveProjectFirst: string;
    unsavedDraftBlocker: string;
    readbackSummary: (
      blocks: number,
      unsupported: number,
      accessMode: string,
    ) => string;
    accessMode: Record<"read-only" | "read-write", string>;
    openSource: string;
    newPageParentId: string;
    publishApproval: string;
    publishAction: string;
    publishBlockedUnsupported: string;
    connectionReadOnly: string;
    proofLabel: string;
    proofStatus: Record<"pass" | "fail" | "blocked", string>;
    structure: string;
    structureReadBack: string;
    structureMissing: string;
    exportsReadBack: (count: number) => string;
    openNewCopy: string;
    publishVerified: (provider: string, versionRef: string) => string;
    publishBlockedBeforeVerified: string;
    publishProofFailed: string;
    publishGenericError: string;
  };
  behavior: {
    navigation: string;
    title: string;
    selectFlow: string;
    unsaved: string;
    target: string;
    noElement: string;
    selectInPreview: string;
    section: string;
    status: string;
    bindings: string;
    flows: string;
    empty: string;
    steps: string;
    verified: string;
    failed: string;
    notVerified: string;
    noFlows: string;
    eventBindings: string;
    connected: string;
    orphan: string;
    inspectElement: string;
    noBindings: string;
    chatActions: string;
    reviewFlow: string;
    addStep: string;
    connectEvent: string;
    testFlow: string;
    editor: string;
    unsavedFlow: string;
    noBlocks: string;
    testRun: string;
    closeFlowForm: string;
    newFlow: string;
    addBlockFirst: string;
    nodeTypes: string;
    nodes: string;
    executionModel: string;
    binding: string;
    direct: string;
    directAtomic: string;
    namedFlow: string;
    lanes: string;
    actionLane: string;
    uiLane: string;
    motionLane: string;
    order: string;
    completionBarrier: string;
    stage: string;
    saga: string;
    sagaCompensation: string;
    palette: {
      ifElse: string;
      loop: string;
      switch: string;
      parallel: string;
      tryCatch: string;
    };
    /**
     * Shown when a flow was created locally but the platform REJECTED its
     * persistence (the 409 the slice-5A walk saw behind a green "created"
     * toast). Emitted only after the persistence call is awaited — the
     * receipt follows the write, not the optimistic local mutation.
     */
    flowCreateFailed: (name: string) => string;
    /** The write reached the server and was accepted. */
    flowCreated: (name: string) => string;
    /**
     * The flow exists in this session only — the live persistence path is
     * off, so nothing left the browser. Measured on the appliance (slice 6):
     * six seconds of empty wire under an unqualified "created", and the flow
     * gone after close-and-reopen. One receipt must not cover both outcomes.
     */
    flowCreatedLocalOnly: (name: string) => string;
  };
  /**
   * Canonical `<h1>` text for routes whose page heading has no other home in
   * this dictionary — no nav-rail label to borrow (`map`, `admin`, `billing`
   * lack a `labelKey` in `studio-route-registry.ts`, so `navigation.*` never
   * carries their name) and no section of their own otherwise. Each `en`
   * value here is locked byte-identical to that route's registry
   * `pageHeading` by `__tests__/studio-route-heading-pin.test.ts`.
   */
  /**
   * Data-explorer create receipts. The create form emits these ONLY after
   * reading the project back (the Expression Lab `apply()` shape): the
   * store's `addDatasource` is an upsert — the undo/restore and import
   * paths depend on that — so the "New Datasource" affordance is the layer
   * that has to refuse a name it would otherwise destroy, and it has to say
   * which name, in which block.
   */
  /**
   * Chat approval card + the page editor's Add-UI panel chrome.
   *
   * Carryover from the wave-7 census: both are surfaces a Korean bank
   * operator reaches while doing the product's core work, and both were
   * hardcoded English in a five-locale product — the approval card being the
   * FOURTH such copy found. Element type labels ("Container", "Card") are
   * deliberately NOT here: they are the schema's own vocabulary, and
   * translating a type name is a product-language decision, not a
   * localization gap.
   */
  approval: {
    categoryFlows: string;
    categoryDatasources: string;
    categoryVariables: string;
    categoryEvents: string;
    compositeActionFallback: string;
    operationCount: (count: number) => string;
    applyAll: string;
    applySelected: (selected: number, total: number) => string;
    dismiss: string;
    applied: string;
    pendingValidation: (reason?: string) => string;
    dismissed: string;
    /**
     * The middle of three action buttons. Missed by the pass that localized
     * its two siblings (`applyAll` at one side, `dismiss` at the other) —
     * that pass asserted "the strings I localized are Korean" and so could
     * not see a sibling it never listed.
     */
    edit: string;
  };
  insertPanel: {
    noActivePage: string;
    searchPlaceholder: string;
    /**
     * A toast, not a rendered node — which is exactly why the rendered-surface
     * absence gate could not see it. The census reported it (L281); the pass
     * that fixed this panel's five JSX strings did not.
     */
    maxChildrenReached: (current: number, max: number) => string;
    fromLibrary: string;
    createManually: string;
    detailCreateOrOpenPage: string;
    detailNeedsBlockTarget: string;
    detailAppendsToBlock: string;
    detailInside: (target: string) => string;
    detailAfterSelectionIn: (target: string) => string;
    /**
     * Label above the insertion-target card. Found by the absence gate, not
     * by any key list — it sits directly above `detailInside`/`noActivePage`,
     * both of which were localized while it was not.
     */
    targetLabel: string;
    /** Primary action at the top of the panel. */
    newBlock: string;
    /** Library section, before any reusable block has been saved. */
    noLibraryBlocks: string;
    /**
     * Collapsible trigger for the library overflow. Was built by
     * concatenating a number with " more blocks", which no locale could
     * order correctly — one authored sentence per locale now.
     */
    moreBlocks: (count: number) => string;
    /** Empty state when a search matches no element. */
    noElementsFound: string;
  };
  dataExplorer: {
    datasourceCreated: (name: string) => string;
    datasourceNameTaken: (name: string, blockName: string) => string;
    datasourceCreateFailed: (name: string, blockName: string) => string;
  };
  routeHeadings: {
    map: string;
    admin: string;
    billing: string;
    /**
     * `design-system-edit`. Added with the other three's `labelKey` wiring:
     * these four screens are the whole set the route registry could not name
     * through the dictionary, so every surface that asks the registry for a
     * screen's name — breadcrumb, nav rail, command palette, and the
     * truncation `title` those render — read English in every locale.
     */
    themeEditor: string;
    newProject: string;
  };
  /**
   * Data inspector — the right-hand detail panel of the data surface
   * (`features/data-explorer/components/data-inspector.tsx`).
   *
   * Wave-7 census carryover: the panel had 50+ hardcoded English literals and
   * ZERO locale wiring, in a five-locale product. A Korean bank operator who
   * clicks any row in the data surface lands here — it is the surface on which
   * variables, datasources, events and flows are actually read and edited.
   *
   * DELIBERATELY NOT HERE, and the gate
   * (`__tests__/data-inspector-i18n.test.tsx`) asserts they stay verbatim:
   *   - variable TYPE names — string / number / boolean / array / object
   *   - variable SCOPE names — state / global / page / local
   *   - datasource TYPE option labels — REST / GraphQL / Static / Fetch /
   *     Import / API client / WebMCP
   *   - HTTP methods — GET / POST / PUT / PATCH / DELETE
   * Those are the Cogo schema's own nouns: an operator matches them against
   * what the JSON literally contains, so translating one would break the
   * match, not improve the reading. The example URL and JSON-header
   * placeholders stay verbatim in the component for the same reason.
   */
  dataInspector: {
    header: {
      title: string;
      reviewEntity: string;
    };
    fields: {
      name: string;
      block: string;
      scope: string;
      type: string;
      defaultValue: string;
      expression: string;
      path: string;
      importSource: string;
      call: string;
      method: string;
      kind: string;
      endpoint: string;
      resultTarget: string;
      headers: string;
      body: string;
      source: string;
      eventKey: string;
      component: string;
      event: string;
      flow: string;
      trigger: string;
      steps: string;
      usedBy: string;
    };
    placeholders: {
      noDefault: string;
      expression: string;
      resultTarget: string;
      body: string;
    };
    emptyStates: {
      selectEntity: string;
      entityNotFound: string;
      variableNotFound: string;
      datasourceNotFound: string;
      eventNotFound: string;
      flowNotFound: string;
    };
    actions: {
      testInExpressionLab: string;
      writeExpressionInLab: string;
      openInFlowEditor: string;
      deleteVariable: string;
      deleteDatasource: string;
      deleteEvent: string;
    };
    appManifest: {
      scopeValue: string;
      note: string;
    };
    schema: {
      request: string;
      response: string;
    };
    flowUnbound: string;
    stepCount: (count: number) => string;
    validationSummary: (errors: number, warnings: number) => string;
    referencedWarning: string;
    toasts: {
      variableDeleted: (name: string) => string;
      datasourceDeleted: (name: string) => string;
      eventRemoved: (name: string) => string;
      bindingsUpdated: (
        count: number,
        oldPath: string,
        newPath: string,
      ) => string;
      bindingsUpdateFailed: string;
    };
  };
  /**
   * Data-surface validation + destructive confirmations — the last two English
   * neighbours of the data inspector.
   *
   * CONSUMERS (all three read this table; the gate
   * `features/data-explorer/components/__tests__/data-confirm-validation-i18n.test.tsx`
   * asserts the RENDERED output, never this table):
   *   - `components/confirm-delete.tsx` — the inline destructive confirmation
   *   - `hooks/use-data-validation.ts` — every validator message the operator
   *     reads, rendered by data-inspector / entity-tree / data-canvas
   *   - `components/schema-editor/schema-field-row.tsx` — its two call sites
   *
   * WHY IT IS A SIBLING OF `dataInspector` AND NOT PART OF IT: the validator
   * messages are produced by a hook that three different components render, and
   * the confirmation is a shared component. Filing them under the inspector
   * would imply the inspector owns copy that two other surfaces also show.
   *
   * KEPT VERBATIM INSIDE THE LOCALIZED SENTENCES — the gate mutates one call
   * site each to prove it:
   *   - entity / variable / block / flow / field IDENTIFIERS. They are project
   *     payload. An operator matches them against the JSON and against what
   *     they typed; translating or case-folding one breaks the match. They are
   *     interpolated, never authored.
   *   - `#{}` — expression-language syntax, not prose.
   *   - `REST` and `URL` — the datasource type is the Cogo schema's own noun
   *     (it is one of the verbatim datasource TYPE option labels the inspector
   *     gate already pins), so the message names the same thing the type
   *     selector shows.
   */
  dataValidation: {
    /**
     * Destructive confirmations. One authored sentence per locale per variant,
     * NEVER assembled from fragments: a locale owns its own word order and the
     * position of the identifier inside it.
     *
     * `deletePrompt` and `typeChangePrompt` are separate sentences because
     * they confirm different operations. A type change that drops nested
     * fields is destructive and must still confirm — but it is NOT a delete,
     * and saying "delete" for it is a false statement on a destructive
     * control. The variant that renders each is read from a
     * `Record<ConfirmDeleteVariant, …>` in `confirm-delete.tsx`, so a new
     * variant cannot be added without a branch that reads it.
     */
    confirm: {
      /** `name` is an entity identifier — interpolated verbatim. */
      deletePrompt: (name: string) => string;
      /** `name` is a schema-field identifier — interpolated verbatim. */
      typeChangePrompt: (name: string) => string;
      /** Confirm button, delete variant. */
      deleteAction: string;
      /** Confirm button, type-change variant. Must not read as "delete". */
      typeChangeAction: string;
      cancel: string;
      /**
       * Fallback shown in place of a schema field that has no name yet. This
       * is PROSE, not an identifier — the row's own `field_name` placeholder
       * is the verbatim one — so it is translated.
       */
      unnamedField: string;
      /**
       * Nested-field warning for both schema-editor call sites.
       *
       * TRUTH CHECK (2026-08-18, this lane): `count` is
       * `field.children.length` — DIRECT children only. Both paths remove the
       * whole subtree: the delete path filters the field out of its parent's
       * `children`, and `applyTypeChange` runs `delete updated.children`. So
       * when a child is itself an object, more fields disappear than `count`
       * names. The sentence therefore says "and anything inside them" rather
       * than promising `count` is the total — an undercount stated as a total
       * would be a false receipt on a destructive control. Reported as a
       * finding; the count itself is left alone because changing it is a
       * behaviour change, not a localization.
       */
      nestedFieldWarning: (count: number) => string;
    };
    /**
     * Validator messages. Every one of these reaches the operator: the
     * field-scoped ones render under the inspector's field rows, the
     * entity-scoped ones render in the inspector body and the entity tree.
     */
    messages: {
      /** `#{}` is expression syntax — verbatim in every locale. */
      unmatchedBrackets: string;
      /** `#{}` is expression syntax — verbatim in every locale. */
      emptyExpression: string;
      duplicateVariableName: (name: string, blockName: string) => string;
      variableUnreferenced: (name: string) => string;
      flowMissing: (flowName: string) => string;
      /** `REST` is a datasource type name — verbatim in every locale. */
      restDatasourceNoEndpoint: string;
    };
  };
  /**
   * PROOF HEALTH — the status line under every project's name.
   *
   * `초안 · No proof · 1시간 전` on a Korean dashboard, measured live on the
   * appliance profile 2026-08-19. These are not edge cases: `label` and
   * `detail` render on the Home continuity rows, the Work/Share cards and the
   * readiness panel, on every project, always.
   *
   * WHY BOTH SWEEPS MISSED IT — stated here because the next person will ask.
   * #2324's walk reads the ACCESSIBILITY surface (attributes and `sr-only`
   * text); these are visible text nodes. The i18n census reads text but not
   * through the shell. And the route walk that reads text ran against an
   * EMPTY store, so no project card ever mounted — the copy is only reachable
   * with data. All three are closed by the single walk in
   * `i18n/__tests__/readable-surface-localized.test.tsx`.
   *
   * There is no `actionLabel` here on purpose. The lib used to emit one
   * (`'Open Deploy'` / `'Review evidence'`) and its ONLY consumer rewrote it
   * with `/deploy/i.test(label)` before rendering — an English-text regex that
   * a translation silently defeats. The lib now emits a machine-readable
   * `action` and the consumer picks its own words below.
   */
  proofHealth: {
    /** Status badge — a live readback passed. */
    verified: string;
    /** Status badge — a record exists but a human must look. */
    needsReview: string;
    /** Status badge — supporting proof exists, live readback never ran. */
    needsLiveCheck: string;
    /** Status badge — the check itself cannot run. */
    blocked: string;
    /** Status badge — nothing has ever been recorded. */
    none: string;
    /** Detail line. `date` is already formatted for the active locale. */
    checkedLive: (date: string) => string;
    /** Stands in for a timestamp that will not parse. */
    dateUnrecorded: string;
    liveCheckBlocked: string;
    liveCheckNeedsReview: string;
    recordBlocked: string;
    recordNeedsReview: string;
    openDeployToRun: string;
    /**
     * The two next-action words, read by `apps-page.tsx` off the health
     * object's `action` discriminator. `reviewShare` names the Share screen
     * the button actually opens — the registry's own label for `apps-live`.
     */
    reviewEvidence: string;
    reviewShare: string;
  };

  // ── lane/kodash · map · projects · apps-live · import ────────────────────
  // Four screens whose readable surface was English on 2026-08-20, harvested
  // by `__tests__/readable-surface-localized.test.tsx` (33 + 21 + 16 + 14
  // word-entries). Appended at the END of every table on purpose: two sibling
  // lanes were editing the sections above at the same time, and the tail is
  // the one place four concurrent additions cannot collide.
  //
  // EVERY key below is spelled out in all five tables. `ko`, `ja` and `zh`
  // open with `...en` and `zhTW` with `...zh`, so a key added to `en` alone
  // is INHERITED rather than missing — and because the residue walk derives
  // its keep-Latin allowlist from the translated tables, inherited English
  // allowlists its own words and the walk goes silent. Measured, not assumed:
  // mutation M5 in the register test deletes one `ko` override here and the
  // residue walk stays green. The inheritance gate is what turns it red.
  /**
   * `/studio/map` — BOTH halves. The register only ever saw the empty half:
   * the harness has no `currentProject`, so the graph, its four side panels
   * and the node-type vocabulary never mounted and never got reported. They
   * are named here anyway, because a screen whose empty state is Korean and
   * whose loaded state is English is not a translated screen.
   */
  appMap: {
    description: string;
    emptyTitle: string;
    emptyBody: string;
    createApp: string;
    importSource: string;
    openProjects: string;
    /** Header description when nothing is open — was a bare English literal. */
    noAppLoaded: string;
    /** Unit words beside the two counts; the numbers stay mono-spaced. */
    nodesUnit: string;
    edgesUnit: string;
    /** Legend / stats / node-detail share one vocabulary — `node-style.ts`. */
    nodeTypes: {
      app: string;
      page: string;
      block: string;
      atom: string;
      flow: string;
      stateVar: string;
      datasource: string;
    };
    stats: { title: string; edges: string };
    legend: { title: string; filterLabel: string; clearFilter: string };
    nodeDetail: {
      title: string;
      empty: string;
      incomingUnit: string;
      outgoingUnit: string;
    };
    sessions: { title: string; body: string };
    /**
     * The graph canvas's OWN words — React Flow's built-in accessible names
     * and keyboard descriptions, which it ships in English and exposes
     * through `<ReactFlow ariaLabelConfig>` (`Partial<AriaLabelConfig>`).
     * Keys are derived from `@xyflow/system`'s `defaultAriaLabelConfig`, not
     * typed from memory, so a library rename is a compile error rather than
     * a silently English screen reader.
     *
     * Invisible to the residue register twice over: the harness never mounts
     * a `currentProject`, and these are attributes on a third-party
     * component. Measured by seeding one — `Control Panel`, `Zoom In`,
     * `Zoom Out`, `Fit View`, `Mini Map` and two keyboard sentences.
     */
    canvas: {
      controls: string;
      zoomIn: string;
      zoomOut: string;
      fitView: string;
      interactive: string;
      miniMap: string;
      handle: string;
      nodeDescription: string;
      nodeKeyboardDisabled: string;
      nodeMoved: (direction: string, x: number, y: number) => string;
      edgeDescription: string;
    };
  };
  /**
   * The project card's chrome — `features/project/components/app-card.tsx`.
   *
   * `shareNamed` and `openActions` are the ACCESSIBLE NAMES of the two icon
   * buttons; `share` is the visible `title` on the first of them. They are one
   * key group because translating a name while its visible word stays English
   * breaks WCAG 2.5.3 Label in Name — a voice-control user says what they see.
   */
  projectCard: {
    /**
     * Status badge. Was `STATUS_LABELS[status] ?? status`, a three-entry map
     * that did not contain `active` — so a third of the seeded cards rendered
     * their raw database value as a user-facing badge. `active` is spelled
     * out here; the raw fallthrough survives for a status nobody has named.
     */
    status: {
      published: string;
      draft: string;
      archived: string;
      active: string;
    };
    share: string;
    shareNamed: (name: string) => string;
    openActions: (name: string) => string;
    rename: string;
    duplicate: string;
    settings: string;
    delete: string;
  };
  /** The Outputs list on Work and Share — `pages/apps-page.tsx`. */
  outputsSection: {
    heading: string;
    breakdown: (shared: number, drafts: number) => string;
    loading: string;
    emptyTitle: string;
    emptyBody: string;
    showMore: (count: number) => string;
  };
  /**
   * The four-step loop on `/studio/import`. Its `label`s were already read
   * from `sources.steps`; only the `detail` beneath each one stayed an
   * English literal in `LAUNCH_LOOP` — half a component translated, which is
   * the shape this lane exists to remove.
   */
  launchLoop: {
    addSource: string;
    shape: string;
    save: string;
    share: string;
  };
  // ─── lane/kobiz: the nine business screens ───────────────────────────
  /**
   * The nine business screens: Operations (Data / Activity / Impact /
   * Audience / Analytics), Requests & Reviews, the theme editor, Billing and
   * Admin.
   *
   * WHY ONE SECTION RATHER THAN NINE. Four of these screens
   * (`products` / `orders` / `revenue` / `members`) are the same screen with
   * a different noun: header, review-observation disclosure, four review
   * states, and a three-branch "not admitted" explanation. Their shared parts
   * live in `review` so a state that gains a word gains it once; the nouns
   * that differ per screen live in the per-screen tables under it.
   *
   * VISIBLE TEXT AND ACCESSIBLE NAMES ARE HERE TOGETHER, deliberately.
   * `activity.exportLabel` is what the Export button shows and
   * `activity.exportAria` is what it announces; splitting them across
   * sections is how a screen ends up with an English button and a Korean
   * accessible name, which is WCAG 2.5.3 Label in Name — a voice-control user
   * says the word they can see.
   */
  businessScreens: {
    /** The parts every Operations review screen renders identically. */
    review: {
      tryAgain: string;
      /** Buttons that name ANOTHER screen. The name is passed in from
       *  `navigation`, so the button and the nav rail cannot drift apart. */
      openSurface: (surface: string) => string;
      viewSurface: (surface: string) => string;
      tableEmpty: string;
      periodAll: string;
      observation: {
        measuredSummary: string;
        emptySummary: string;
        /** `stamp` is already formatted by `Intl.DateTimeFormat`. */
        observedAt: (stamp: string) => string;
        evidence: string;
      };
      pagination: { first: string; previous: string; next: string; last: string };
      /** `StatusBadge`'s own defaults. Screens that mean something else by a
       *  colour pass their own label — see `activity.tabs`. */
      status: {
        active: string;
        draft: string;
        archived: string;
        pending: string;
        processing: string;
        completed: string;
        refunded: string;
        shipping: string;
      };
    };
    /** `/studio/products` — Data. */
    data: {
      description: string;
      addSource: string;
      loadFailed: string;
      loading: string;
      empty: string;
      scope: string;
      summaryLabel: string;
      tiles: { sources: string; ready: string; drafts: string };
      columns: {
        name: string;
        artifact: string;
        confidence: string;
        rows: string;
        status: string;
      };
      readyStatus: string;
      filters: {
        artifact: string;
        allArtifacts: string;
        status: string;
        all: string;
        ready: string;
        draft: string;
        archived: string;
      };
      unavailable: Record<StudioReviewUnavailableReasonCode, ReviewUnavailableCopy>;
    };
    /** `/studio/orders` — Activity. */
    activity: {
      description: string;
      exportLabel: string;
      exportAria: (count: string) => string;
      exportBlockedAria: (reason: string) => string;
      blocked: {
        notLoaded: string;
        none: string;
        loading: string;
        unavailable: string;
      };
      exported: (count: number, filename: string) => string;
      loadFailed: string;
      loading: string;
      empty: string;
      scope: string;
      summaryLabel: string;
      tiles: { pending: string; applied: string; reviewed: string };
      tabs: {
        all: string;
        pending: string;
        applied: string;
        reviewed: string;
        rejected: string;
      };
      columns: {
        actor: string;
        artifact: string;
        changes: string;
        state: string;
        updated: string;
      };
      changeCount: (count: string) => string;
      unavailable: Record<StudioReviewUnavailableReasonCode, ReviewUnavailableCopy>;
      drawer: {
        title: (id: string) => string;
        close: string;
        process: string;
        refund: string;
        customer: string;
        email: string;
        app: string;
        date: string;
        items: string;
        noItems: string;
        subtotal: string;
        shipping: string;
        discount: string;
        total: string;
      };
    };
    /** `/studio/revenue` — Impact. */
    impact: {
      description: string;
      loadFailed: string;
      loading: string;
      empty: string;
      scope: string;
      columns: { artifact: string; views: string; reviews: string; cvr: string };
      seriesViews: string;
      trendTitle: string;
      byArtifact: string;
      unavailable: Record<StudioReviewUnavailableReasonCode, ReviewUnavailableCopy>;
    };
    /** `/studio/members` — Audience. */
    audience: {
      description: string;
      loadFailed: string;
      loading: string;
      empty: string;
      scope: string;
      kpis: { reviewers: string; new7d: string; returnRate: string };
      rolesTitle: string;
      recentReviewers: string;
      columns: { name: string; email: string; role: string; joined: string };
      unavailable: Record<StudioReviewUnavailableReasonCode, ReviewUnavailableCopy>;
    };
    /** `/studio/analytics`, with no deep-link context. */
    analytics: { eyebrow: string; body: string };
    /** The three-step flow inside `/studio/agent`'s "How requests work". */
    requestFlow: {
      prepared: { title: string; body: string };
      attached: { title: string; body: string };
      approval: { title: string; body: string };
    };
    /** `/studio/design-system-edit`. The preset and font NAMES it renders stay
     *  Latin — they are read off the option tables, never from here. */
    themeEditor: { badge: string; description: string; livePreviewNote: string };
    /** `/studio/billing`. The plan headline comes from `settings.plan`, which
     *  both this screen and `/studio/settings` already read. */
    billing: {
      currentPlan: string;
      currentPlanNote: string;
      cancelsAtPeriodEnd: string;
      cycle: string;
      amount: string;
      started: string;
      nextBilling: string;
      changePlan: string;
      cancelSubscription: string;
      cancelling: string;
      viewPlans: string;
      paymentHistory: string;
      paymentHistoryNote: string;
      paymentsSignedOut: string;
      paymentsFailed: string;
      paymentsEmpty: string;
      columns: {
        date: string;
        amount: string;
        status: string;
        provider: string;
        reference: string;
      };
      status: {
        active: string;
        paid: string;
        pending: string;
        cancelled: string;
        failed: string;
      };
      /** A `status` value the table above does not name — see `StatusBadge`
       *  in `billing-page.tsx` for why the token is still disclosed. */
      statusUnknown: (value: string) => string;
      cancelDialog: {
        title: string;
        body: (periodEnd: string) => string;
        keep: string;
        confirm: string;
      };
    };
    /** `/studio/admin`, denied. */
    admin: { accessDeniedTitle: string; accessDeniedBody: string };
  };
  // ─── end lane/kobiz ─────────────────────────────────────────────────
}

const en: StudioDictionary = {
  // ── lane/koentry ─────────────────────────────────────────────────────────
  newProject: EN_NEW_PROJECT_COPY,
  starters: EN_STARTERS_COPY,
  // ── end lane/koentry ─────────────────────────────────────────────────────
  designSystemAxes: EN_DESIGN_AXES_COPY,
  language: { label: "Language" },
  a11y: {
    skipToMainContent: "Skip to main content",
    dashboardContent: "Dashboard content",
    loadingSurface: (surface) => `Loading ${surface}…`,
    creatorNav: "Navigation",
    header: "Header",
    designSystemSurfaces: "Design system surfaces",
    designSystemInventory: "Design systems in this workspace",
    importSources: "Import sources",
    librarySummary: "Library summary",
    operations: "Operations",
    requestReviewFlow: "Request review flow",
    reviewSummary: "Review summary",
    shareReadinessChecklist: "Share readiness checklist",
    appPreviewFrame: "coogo app preview",
    entryBrandHome: "Coogo home",
    entryReport: "Coogo operational report",
    showSection: (name) => `Show ${name}`,
    hideSection: (name) => `Hide ${name}`,
  },
  ide: {
    undo: "Undo",
    redo: "Redo",
    appPageList: "App page list",
    documentList: "Document list",
    viewportSwitcher: "Viewport switcher",
    device: {
      mobile: "Mobile",
      tablet: "Tablet",
      desktop: "Desktop",
      document: "Document",
      a4Portrait: "A4 portrait",
      framed: (label, width, height) => `${label} (${width}x${height})`,
      sized: (label, width, height) => `${label} · ${width}x${height}px`,
    },
    pages: {
      title: "Pages",
      summaryWithPages: (count) => `Preview + ${count} pages`,
      summaryPreviewOnly: "Preview",
      runnableTitle: "Preview",
      runnableBadge: "Run",
      runnableDetail: "Run view",
      emptyTitle: "Current screen",
      emptyBody: "No page metadata yet",
    },
    documents: {
      title: "Documents",
      count: (count) => `${count} documents`,
      pageCount: "1 page",
    },
    reviewPreview: "Review preview",
    print: "Print",
  },
  navigation: {
    sections: {
      start: "Start",
      build: "Build",
      verify: "Verify",
      release: "Release",
    },
    home: 'Home',
    create: 'Create',
    work: 'Work',
    details: 'Details',
    map: 'Map',
    code: 'Code',
    release: 'Release',
    publish: 'Publish',
    launch: 'Launch',
    configure: 'Configure',
    settings: 'Settings',
    share: 'Share',
    starters: 'Starters',
    import: 'Import',
    sources: 'Sources',
    library: 'Library',
    documents: 'Documents',
    requests: 'Requests',
    requestsAndReviews: 'Requests & Reviews',
    bots: 'Bots',
    operations: 'Operations',
    data: 'Data',
    activity: 'Activity',
    impact: 'Impact',
    audience: 'Audience',
    analytics: 'Analytics',
    designSystem: 'Design System',
    workspaceSettings: 'Workspace Settings',
    projects: 'Projects',
    templates: 'Templates',
    projectSettings: 'Project settings',
    account: 'Account',
    signIn: 'Sign in',
    signOut: 'Sign out',
    goHome: 'Go to Studio home',
    goCoogoHome: 'Go to Coogo home',
    coogoHomepage: 'Coogo homepage',
    studioNavigation: 'Studio navigation',
    studioNavigationDescription: 'Navigate between Studio creation, projects, saved work, requests, and settings.',
    collapseNavigation: 'Collapse navigation',
    expandNavigation: 'Expand navigation',
  },
  artifacts: {
    label: 'Artifact mode',
    app: 'App',
    game: 'Game',
    design: 'Design',
    document: 'Document',
    guide: 'Guide',
    insights: 'Status',
  },
  work: {
    title: 'Work',
    subtitle: 'Create, manage, and review apps, documents, and reports.',
    namePlaceholder: 'New work name...',
    create: 'Create',
    browseStarters: 'Browse Starters',
    importSource: 'Import source',
    emptyTitle: 'No work yet',
    emptyBody: 'Create an app, document, or report above, or browse Starters below',
  },
  designSystem: {
    title: 'Design System',
    subtitle: 'Set the colours, type, spacing, and shape that generated apps use, and check the result against the cogo vocabulary before saving it to Library.',
    validationLab: 'Validation Lab',
    themeEditor: 'Theme editor',
    tabs: {
      systems: 'Design systems',
      validation: 'Validation Lab',
      vocabulary: 'Vocabulary',
      briefings: 'Briefings',
    },
    uiBlocks: {
      eyebrow: 'UI Block ontology',
      title: 'What are you making?',
      description: 'Describe the surface you need. Coogo finds real blocks from the admitted catalog and previews them with this Design Ontology theme.',
      intentLabel: 'Product intent',
      intentPlaceholder: 'For example, a team analytics dashboard',
      shortcutsLabel: 'Start with an intent',
      shortcuts: {
        dashboard: 'Dashboard',
        store: 'Store',
        landing: 'Landing page',
        auth: 'Sign-in flow',
      },
      recommendations: 'Recommended UI Blocks',
      admittedCount: (count) => `${count} catalog-admitted blocks`,
      noMatches: 'No admitted block matches this intent yet. Try a shorter product description.',
      preview: 'Preview with this theme',
      selectedPreview: 'Selected for the live theme preview',
      readOnly: 'Read-only preview',
      authorityUnavailable: 'Project apply and replacement stay unavailable until structural compatibility and publication authority are proven.',
    },
    precondition: {
      signedOutReason:
        'Sign in to continue. This workspace’s design systems, and the values read from your source, are shown only to a signed-in user.',
      signInCta: 'Go to sign in',
      checkingReason: 'Checking whether this screen can open…',
      unmetFallbackReason:
        'This screen cannot open yet — something it needs is not in place.',
    },
    sourceRead: {
      failed: 'This project’s design source could not be read.',
      failedWithMessage:
        'This project’s design source could not be read just now: {message}',
    },
    vocabularyPanel: {
      title: 'Vocabulary',
      search: 'Search…',
      enumPrefix: 'Prefix',
      enumAccessor: 'Accessors',
      enumEvent: 'Events',
      enumAction: 'Actions',
      colName: 'Name',
      colMode: 'Mode',
      colSemantics: 'Semantics',
      colCategory: 'Category',
      colReturns: 'Returns',
      colDomain: 'Domain',
      colDescription: 'Description',
      loading: 'Loading vocabulary…',
      errorTitle: 'Failed to load vocabulary',
      retry: 'Retry',
      empty: 'No items found in this shard.',
      unavailableTitle: 'Vocabulary unavailable',
      unavailableDetail: 'The vocabulary catalog requires the local knowledge graph.',
      noMatch: 'No matches for "{query}"',
      clear: 'Clear',
    },
    validatorPanel: {
      specChip: "§ design system v4",
      heading: "Validator",
      lastRun: 'Last:',
      justNow: 'just now',
      secondsAgo: '{n}s ago',
      minutesAgo: '{n}m ago',
      hoursAgo: '{n}h ago',
      rerun: 'Re-run',
      validating: 'Validating…',
      colRule: 'Rule',
      allChecks: 'All checks',
      noViolations: 'No rule violations to display.',
      colGate: 'Gate',
      colStatus: 'Status',
      statusPass: 'Pass',
      statusFail: 'Fail',
      flagOffTitle: 'Checks are turned off',
      flagOffBody: 'Design checks aren’t enabled in this workspace.',
      readyTitle: 'Ready to validate',
      readyBody: 'Click Re-run to validate the current project.',
      unreadableTitle: 'Could not read the check results',
      unreadableBody: 'The last validation run returned a result this panel could not parse. Click Re-run to try again.',
      briefingReadFailed: 'Briefings could not be read for this workspace.',
      briefingTitle: 'Briefings',
      briefingIntro: 'Author short briefings about this app. Briefings persist alongside blocks in the live ontology and round-trip on reload.',
      briefingSignIn: 'Sign in to author briefings.',
      briefingSignedOutDetail: 'Briefings persist per-tenant via the live ontology gateway. Sign in to load and author briefings for this app.',
      briefingUnavailableTitle: 'Briefings are unavailable in this workspace',
      briefingUnavailableDetail: 'This Studio deployment does not provide supported briefing storage. Reading and writing are disabled.',
      briefingFieldTitle: 'Title',
      briefingFieldBody: 'Body (markdown — rendered as escaped text)',
      briefingTitlePlaceholder: 'e.g. Q2 launch checklist',
      briefingBodyPlaceholder: 'Write the briefing here. Newlines preserved; HTML is escaped.',
      briefingReady: 'Ready',
      briefingSaving: 'Saving…',
      briefingSave: 'Save briefing',
      briefingEmpty: 'No briefings yet. Write the first one above and click Save.',
      repoInitTitle: 'Starting checks…',
      repoInitBody: 'Getting the checker ready — this takes a moment.',
      noBlocksTitle: 'No blocks to validate yet',
      noBlocksBody: 'Generate blocks via Edit mode chat to populate this surface.',
      errorTitle: 'Validation failed',
      errorBody: 'See error message below; click Re-run to retry.',
    },
    inventory: {
      readingBadge: 'Reading this workspace’s design systems…',
      readingDetail: 'Nothing is claimed until the list comes back.',
      unreadableBadge: 'Could not read this workspace’s design systems',
      unreadableDetail:
        'The list did not load, so this screen cannot say how many design systems this workspace has. The theme editor below still works.',
      noneBadge: 'No design system saved in this workspace{stamp}',
      noneDetail:
        'Nothing has been saved as a design system here yet. Set the axes below and save it to Library to create the first one.',
      listedBadge: '{count} design systems in this workspace{stamp}',
      listedBadgeOne: '{count} design system in this workspace{stamp}',
      listedDetail:
        'This is the list as it came back at the time above. Opening this screen lists them; it does not check anything about them.',
      readStamp: ', read {time}',
      setAsideNote:
        ' {count} more rows belong to projects created from a template and are not workspace design systems.',
      setAsideNoteOne:
        ' {count} more row belongs to projects created from a template and is not a workspace design system.',
    },
    themeEditorPanel: {
      title: 'Theme editor',
      body:
        'Change style, base colour, theme, chart palette, corner radius, and font, and watch real cogo component blocks update as you go. Save to Library when it looks right.',
    },
    workbench: {
      controlsTitle: 'Theme controls',
      selectPlaceholder: 'Select {axis}',
      saveToLibrary: 'Save to library',
      saveDialogTitle: 'Save design system',
      namePlaceholder: 'Preset name',
      descriptionPlaceholder: 'Description (optional)',
      closeLabel: 'Close',
      cancel: 'Cancel',
      save: 'Save',
      saving: 'Saving…',
      savedAs: 'Saved as “{name}”',
      nameRequired: 'Name required.',
      saveFailed: 'Save failed.',
      sourceTitle: 'Design system read from your source',
      sourceEmptyBody:
        'No design source has been imported into this project yet — every axis below is a gap until you decide it.',
      sourceImportCta: 'Import a design source (Figma)',
      sourceReadPrefix: 'Read ',
      sourceVariableCount: '· {count} variables',
      sourceVariableCountOne: '· {count} variable',
      chooseAxisValue: 'Choose',
      axisValueSelectLabel: '{axis} value',
    },
    projectTokens: {
      title: 'Tokens imported from a design file',
      bodyBefore:
        'Colours and variables read out of an imported design file belong to the project that imported them, not to the workspace. Open that project and go to ',
      navPath: 'Design → Tokens',
      bodyAfter:
        ' to review them and apply them to the project.',
    },
  },
  settings: {
    title: 'Workspace Settings',
    profile: {
      title: 'Profile',
      name: 'Name',
      email: 'Email',
      edit: 'Edit',
      cancel: 'Cancel',
      providerBlocked:
        'This sign-in provider cannot update profiles yet',
      save: 'Save',
      saving: 'Saving…',
      nameInputLabel: 'Workspace display name',
      nameSaved: 'Name saved.',
      signedOutBlocked: 'Sign in to edit your profile',
    },
    writeOutcome: {
      protectedRejected: 'This protected setting was not changed.',
      accepted: 'Project settings accepted.',
    },
    publishing: {
      title: 'Publishing',
      exportsLabel: 'Artifact exports:',
      exportsNone: 'none recorded yet',
      exportsCount: '{count} recorded',
      recordsNone:
        'No publishing records saved for this workspace yet.',
      recordsCount:
        '{count} publishing records saved for this workspace.',
      exportSettings: 'Export Settings',
      exportSettingsBlocked:
        'Not available yet — there is no settings export to download',
      reviewRecords: 'Review Records',
      reviewRecordsBlocked:
        'No records yet — publish or export something first',
    },
    plan: {
      title: 'Plan',
      current: 'Current:',
      manage: 'Manage Plan',
      allowsTitle: 'What your plan currently allows',
      generation: 'Generation',
      publication: 'Publication',
      packPromotion: 'Pack promotion',
      errorLabel: 'We couldn\'t load your plan',
      lookupFailed: 'Billing lookup failed',
      loading: 'Checking your plan…',
      signedOut: 'Sign in to see your plan',
      noSubscription: 'No active subscription',
      rights: {
        'not-measured': 'Not measured',
        'authority-readback-invalid': 'Authority readback invalid',
        'not-admitted': 'Not admitted',
        'preview-only': 'Preview only',
        'not-admitted-by-source-receipt': 'Not admitted by this source receipt',
        'preview-admitted': 'Preview admitted',
        'same-project-publication-admitted': 'Same-project publication admitted',
        'admitted': 'Admitted',
      },
    },
    runtime: {
      title: 'Runtime',
      note:
        'Release identity and availability are observations, not permissions.',
      distribution: 'Distribution',
      studioSha: 'Studio SHA',
      applianceReleaseId: 'Appliance release ID',
      observedAt: 'Observed at',
      observationsTitle: 'Capability observations',
      available: 'Available',
      unavailable: 'Unavailable',
      readFailed: 'Read failed',
      notConfirmed: 'Availability not confirmed',
      probeFailures: {
        'platform-health-status': 'Platform health read failed ({status})',
        'platform-health-malformed': 'Platform health response was malformed.',
        'platform-unreachable': 'Platform availability could not be confirmed.',
        'capability-response-rejected': 'Capability response was not admitted ({status}).',
      },
      capabilities: {
        platformConnection: 'Connected platform',
        localAuth: 'Local authentication',
        googleAuth: 'Google authentication',
        localPublish: 'Local publish',
        cloudPublish: 'Cloud publish',
        customDomain: 'Custom domain',
        billing: 'Billing',
        analytics: 'Analytics',
        externalIntegrations: 'External integrations',
      },
    },
    domain: {
      title: 'Domain',
      defaultLabel: 'Default:',
      customLabel: 'Custom:',
      connect: 'Connect Domain',
      blocked: 'Custom domains are not available yet',
      signedOut: 'Sign in to see your workspace domain',
    },
    github: {
      title: 'GitHub Integration',
      developer: 'Developer',
      connected: 'Connected',
      disconnect: 'Disconnect',
      disconnecting: 'Disconnecting...',
      invite:
        'Connect your GitHub account to export source code and set up custom CI/CD.',
      connect: 'Connect GitHub',
      connecting: 'Connecting...',
      confirmDisconnect:
        'Disconnect your GitHub account? Existing repos will not be deleted.',
    },
    notifications: {
      title: 'Notifications',
      reason:
        'Email notifications are not available yet — Studio has no service that sends them and nowhere to store the preference, so these cannot be turned on.',
      review: 'Review notifications (email)',
      agentSummary: 'Agent activity summary (daily)',
      productUpdates: 'Product updates',
      unavailableSuffix: '— not available yet',
    },
    danger: {
      title: 'Danger Zone',
      deleteAccount: 'Delete Account',
      deleteBlocked:
        'Account deletion is not available in Studio yet — write to privacy@cogo.xyz to request it',
    },
    closedNetwork: {
      planNote:
        'This installation runs on a closed network. What it may do is set by the licence installed on it, not by a subscription managed here.',
      planManageBlocked:
        'Subscriptions are managed by a hosted service this installation cannot reach',
      servedFromLabel: 'Served from:',
      domainBlocked:
        'A custom domain needs public DNS — this installation answers only on its own address',
      githubTitle: 'GitHub',
      githubBlocked:
        'Connecting an account needs github.com, which this installation cannot reach',
      deleteBlocked:
        'Accounts on a closed-network installation are held locally — ask whoever administers this appliance',
    },
  },
  operations: {
    title: 'Operations',
    subtitle:
      'Review data, activity, impact, audience, and telemetry for released outputs.',
    reviewedOutputs: 'Reviewed outputs',
    acceptanceRate: 'Acceptance rate',
    sharedArtifacts: 'Shared artifacts',
    sharedArtifactsNote: 'Apps, documents, and reports',
    unmeasured:
      'Not measured yet — Studio has no source for these numbers.',
    tiles: {
      products:
        'Source packets, extracted rows, readiness, and provenance.',
      orders:
        'Released-output activity and its recorded evidence.',
      revenue:
        'Views, conversion, accepted changes, and outcome signals.',
      members:
        'Reviewers, traffic sources, and returning visitors.',
      analytics: 'Live app telemetry after launch.',
    },
  },
  requests: {
    title: 'Requests & Reviews',
    subtitle:
        'Review requested changes, approvals, rejections, and their attached evidence.',
    openChat: 'Open chat',
    howItWorks: 'How requests work',
    stepsSuffix: ' · 3 steps',
    rows: {
      inProgress: { title: 'Requests in progress', body:
        'See app, document, and report changes cogo is preparing for you.', },
      needsApproval: { title: 'Needs approval', body:
        'Publishing, launch, and shared updates wait until you review them.', },
      reviewTrail: { title: 'Review trail', body:
        'See what changed, what was checked, and what is ready to share.', },
      accessControl: { title: 'Access control', body:
        'Keep account and publishing access limited to the current request.', },
    },
    current: { title: 'Current requests', body:
        'Use this page when cogo is preparing a change that needs review before sharing or launching.', },
    actions: {
      addSource: 'Add source',
      library: 'Library',
      share: 'Share',
      refresh: 'Refresh',
    },
    tiles: {
      updates: 'Updates',
      checks: 'Checks',
      needsApproval: 'Needs approval',
    },
    timeline: {
      title: 'Recent request timeline',
      emptyTitle: 'No active requests',
      emptyBody:
        'Start from chat or add a source. Requests that need approval will appear here.',
    },
    approval: {
      title: 'Needs your approval',
      waiting:
        'This update waits for your review before it is shared.',
      empty: 'No approvals pending.',
    },
    prepare: {
      title: 'What coogo can prepare',
      appDraft: 'App draft',
      sourceImport: 'Source import',
      reviewCheck: 'Review check',
      launchSetup: 'Launch setup',
    },
    requestTitle: {
      appDraft: 'App draft',
      sourceAdded: 'Source added',
      reviewCheck: 'Review check',
      shareUpdate: 'Share update',
      launchSetup: 'Launch setup',
      requestedChange: 'Requested change',
    },
    status: {
      ready: 'Ready',
      needsReview: 'Needs review',
      inProgress: 'In progress',
      recorded: 'Recorded',
    },
    scope: {
      codeUpdate: 'Code update',
      designFileUpdate: 'Design file update',
      publicShare: 'Public share',
      launchSetup: 'Launch setup',
      workspaceUpdate: 'Workspace update',
    },
    entry: {
      reuse: 'Reused parts',
      fresh: 'New parts',
      validation: 'Checked',
      approval: 'Approval',
    },
  },
  share: {
    title: 'Share',
    subtitle:
        'Share apps, documents, and reports while keeping source, readiness, and update signals visible.',
    centerTitle: 'Share center',
    centerBody:
        'Keep every shared output connected to its source, review state, and update path so you can keep improving it without starting over.',
    createOutput: 'Create output',
    addSource: 'Add source',
    sources: 'Sources',
    settingsDialog: {
      title: 'Share settings',
      body: 'Update the subdomain for {name}.',
      subdomainLabel: 'Subdomain',
    },
    checklist: {
      publicUrl: 'Public URL',
      readinessCheck: 'Readiness check',
      outcomePath: 'Outcome path',
      updateControl: 'Update control',
      notShared: 'Not shared',
      sharedCount: '{count} shared',
      nothingSharedYet: 'Nothing shared yet',
      readyCount: '{count} ready',
      needsCheck: 'Needs a check',
      outcomePathValue: 'Lead, booking, export, checkout, or review screen',
      updateControlValue: 'Review share settings before changes',
    },
    tiles: {
      sharedOutputs: 'Shared outputs',
      readyChecks: 'Ready checks',
      drafts: 'Drafts',
      latestUpdate: 'Latest update',
      noSharedOutput: 'No shared output',
    },
    health: {
      title: 'Share health',
      checked: 'Checked',
      lastRecordedCheck: 'Last recorded check',
      needsAttention: 'Needs attention',
      draftsNotShared: 'Drafts, not shared',
      nothingShared: 'Nothing shared',
      noneYet: 'None yet',
      never: 'Never',
      none: 'None',
      checkedOfShared: '{checked} of {shared}',
      blockedCount: '{count} blocked',
      nothingSharedBadge: 'Nothing shared yet',
      nothingSharedDetail:
        'Nothing has been shared from this workspace yet, so there is nothing here to report on. Share an output and its readiness record shows up here.',
      blockedBadge: '{blocked} of {shared} shared outputs blocked',
      blockedBadgeOne:
        '{blocked} of {shared} shared output blocked',
      blockedDetail:
        'A shared output has a blocked readiness record. Open it to see what is blocking.',
      uncheckedBadge:
        'No readiness check recorded for {shared} shared outputs',
      uncheckedBadgeOne:
        'No readiness check recorded for {shared} shared output',
      uncheckedDetail:
        'These outputs are shared, but none of them carries a readiness record. Open one and run its check to record one.',
      checkedBadge: '{checked} of {shared} shared outputs checked',
      checkedBadgeOne:
        '{checked} of {shared} shared output checked',
      checkedBadgeLastSuffix: ', last {date}',
      checkedDetail:
        'This is what was recorded the last time these outputs were checked. Opening this screen does not re-check anything.',
    },
  },
  library: {
    title: 'Library',
    subtitle:
      'Open verified reusable assets and outputs admitted for this workspace.',
    importSource: 'Import source',
    tiles: {
      savedWork: 'Saved work',
      openLinks: 'Open links',
      details: 'Details',
      launches: 'Launches',
    },
    searchPlaceholder: 'Search saved work, source, or link...',
    searchResults: 'Search results',
    savedWorkCount: '{count} saved work',
    previewTitle: 'Library preview',
    thumbnailPreview: {
      project: 'Project preview',
      template: 'Template preview',
      library: 'Library preview',
    },
    previewEmptyTitle: 'Select a Library item',
    previewEmptyBody:
      'Saved work appears here with the actions users need next: open, share, check, or prepare launch.',
    whatYouCanDo: 'What you can do',
    readyToUse: 'Ready to use',
    noReviewLinks: 'No review links yet.',
    selectedDetails: {
      summary: 'Readiness, reviews, evidence, and launch history',
      openBeforeReviewLink:
        'Open this app before creating an editable review link.',
    },
    empty: {
      title: 'No library items yet',
      body:
        'Add a brief, screen file, URL, repository change, or design frame — each saved item appears here with its visual, source, and launch actions.',
    },
    actions: {
      open: {
        label: 'Open',
        detail: 'See the app or document before sharing',
        noLink: 'This item has no open link yet',
      },
      edit: {
        label: 'Edit',
        detail: 'Return to the project workspace',
        noWorkspace: 'This item is not linked to a project workspace',
      },
      launch: {
        label: 'Launch',
        detail: 'Prepare a public package',
        noBundle: 'This item has no saved file to launch yet',
        busy: '{tool} is still running',
      },
      details: {
        label: 'Details',
        detail: 'Review source notes and next steps',
      },
      useAgain: 'Use again',
      sharePublicly: 'Share publicly',
      check: 'Check',
      share: 'Share',
      linkPr: 'Link PR',
      prepareLaunch: 'Prepare launch',
      feedback: {
        check: {
          progress: 'Checking the app…',
          success: 'App check recorded.',
          failure: 'App check failed.',
        },
        sharePublicly: {
          progress: 'Preparing public sharing…',
          success: 'Public sharing recorded.',
          failure: 'Public sharing failed.',
        },
        prepareLaunch: {
          progress: 'Preparing launch…',
          success: 'Launch preparation recorded.',
          failure: 'Launch preparation failed.',
        },
      },
      noSelection: 'Save or select an item first',
    },
  },
  sources: {
    title: 'Sources',
    subtitle: 'Add files, URLs, repositories, and design sources to this workspace.',
    addToLibrary: 'Add to Library',
    howItWorks: 'How adding a source works',
    flowAriaLabel: 'Source to workspace flow',
    agentIntake: {
      eyebrow: 'Studio agent',
      title: 'Tell Studio what you want to make',
      description: 'Write a request, choose a file, or paste a URL. Studio will prepare the next step without making you choose a source type first.',
      placeholder: 'For example: make a dashboard that shows customer support activity at a glance',
      attach: 'Attach file',
      addUrl: 'Add URL',
      submit: 'Ask Studio agent',
      understanding: 'What the agent understands',
      nextStep: 'Next step',
      connected: 'Recent and connected sources',
      emptyConnected: 'Files and URLs connected to this work will appear here.',
      advanced: 'Advanced import',
      advancedSummary: 'Choose a source type directly or set details such as Figma access, audience, and tags.',
      requestUnderstanding: 'A request for a new result',
      urlUnderstanding: 'A website to use as a reference',
      codeUnderstanding: 'An existing code or text file',
      figmaUnderstanding: 'A local Figma design file',
      documentUnderstanding: 'A document to read and inspect',
      nextDescribe: 'Studio will shape the request and prepare a Library draft.',
      nextUrl: 'Studio will connect the page as a reference and prepare a Library draft.',
      nextCode: 'Studio will read the file as a screen source and prepare a Library draft.',
      nextFigma: 'Studio will read the design so you can inspect its pages and layers.',
      nextDocument: 'Studio will read the document so you can inspect its structure.',
      currentRequest: 'Current request',
      selectedFile: 'Selected file',
      restoredDocument: 'Document kept in this browser',
      connectedDesign: 'Connected Figma design',
      urlHint: 'Paste the full URL into the same request field.',
      unsupportedFile: 'This file type cannot be read from this intake yet.',
      activeDesignConflict: 'A connected design source is preventing this new URL task from starting. Open that design source or start the URL in a new project.',
      librarySaveMissing: 'Studio finished the request, but could not confirm a saved Library draft. Your request is still here—try again.',
    },
    steps: {
      addSource: 'Add source',
      shape: 'Shape',
      save: 'Save',
      share: 'Share',
    },
    form: {
      appName: 'App name',
      audience: 'Audience',
      audienceCreator: 'Creator',
      audienceBusiness: 'Business',
      audienceDeveloper: 'Developer',
      sourceFieldSuffix: 'source',
      briefPlaceholder: 'Write the app, document, or report you want…',
    },
    upload: {
      title: 'Upload file',
      body: 'Already have an app bundle? Upload it directly and keep the file-backed record in Library.',
      button: 'Upload app bundle',
    },
    groups: {
      fromIdea: 'Start from an idea',
      bringExisting: 'Bring in something you already have',
    },
    otherWays: {
      title: 'Other ways to start',
    },
    chatStart: {
      title: 'Start with chat',
      body: 'Prefer not to choose a source? Open chat and describe what you want. The same Library and launch flow is used after creation.',
    },
    savedWith: {
      title: 'Saved with it',
      openLink: 'Open link',
      savedFile: 'Saved file',
      sourceSnapshot: 'Source snapshot',
      launchCheck: 'Launch check',
    },
    after: {
      title: 'After you add it',
      defaultNote: 'cogo saves the source so you can open, share, refine, or launch without losing where it came from.',
      figmaFileNote: 'cogo keeps the .fig evidence separate from application generation so source coverage can be reviewed first.',
      documentFileNote: 'The document stays in this browser, so it is still open after a page reload — and it is not sent anywhere.',
      steps: {
        prepared: 'Work is prepared',
        designRead: 'Design is read',
        documentRead: 'Document is read',
        sourceSaved: 'Source is saved',
        keptInBrowser: 'Kept in this browser',
        mapperOpens: 'Source mapper opens',
        structureOpens: 'Structure opens below',
        libraryOpens: 'Library opens with next actions',
      },
    },
    panel: {
      tags: 'Tags',
      librarySuffix: 'cogo saves it as a Library card with source notes and next actions.',
      figmaFileNote: 'We read your Figma file and keep the original, before anything is generated.',
      documentFileNote: 'We read your document in this browser and show its structure. Nothing is uploaded and nothing is generated from it yet.',
      figmaTokenLabel: 'Figma token',
      figmaTokenPasteHint: 'Paste a Figma token and confirm it.',
      forgetToken: 'Forget',
      readDesign: 'Read design',
      readDocument: 'Read document',
      localFigmaFile: 'Local Figma file',
      localDocument: 'Local document',
    },
    help: {
      assistant_prompt: {
        title: 'Write the request here.',
        detail: 'Include audience, offer, sections, forms, payments, and the tone you want.',
        example: 'Example: Build a booking page for a beauty creator with services, testimonials, Instagram links, and a payment-ready call to action.',
      },
      url_import: {
        title: 'Paste one public URL.',
        detail: 'cogo uses it as a visual and content reference, then keeps the source URL in Library.',
        example: 'Example: https://example.com/product-page',
      },
      ide_file: {
        title: 'Paste one component, route, or screen file.',
        detail: 'Best when you already have a screen and want to continue from it.',
        example: 'Example: src/features/checkout/checkout-page.tsx',
      },
      github_pr: {
        title: 'Paste a repo and pull request number.',
        detail: 'cogo keeps the change with review notes so it can be shared with the team.',
        example: 'Example: acme/storefront#128',
      },
      figma_frame: {
        title: 'Paste a Figma frame URL or file/node id.',
        detail: 'cogo keeps the design source so conversion quality can be reviewed later.',
        example: 'Example: https://www.figma.com/design/abc123/Checkout?node-id=12-345',
      },
      figma_file: {
        title: 'Choose a local .fig file.',
        detail: 'cogo reads your file, keeps the original, and shows what is in the design before anything is generated.',
        example: 'Example: checkout-flow.fig',
      },
      document_file: {
        title: 'Choose a .docx or .hwpx file.',
        detail: 'cogo reads the document, keeps the original, and shows its structure before anything is generated.',
        example: 'Example: account-opening-spec.docx',
      },
    },
    refLabels: {
      assistant_prompt: 'Brief',
      url_import: 'URL',
      ide_file: 'File',
      github_pr: 'GitHub',
      figma_frame: 'Figma',
      figma_file: '.fig',
      document_file: 'Document',
    },
    cards: {
      assistant_prompt: {
        label: 'Describe it',
        description: 'Write the app, document, or report you want.',
        bestFor: 'Starting from an idea',
        output: 'Draft in Library',
      },
      url_import: {
        label: 'Use a website',
        description: 'Bring in a page URL as a reference.',
        bestFor: 'Existing sites and references',
        output: 'Reference-backed draft',
      },
      ide_file: {
        label: 'Paste code',
        description: 'Bring in a component, route, or screen file.',
        bestFor: 'Existing build work',
        output: 'Screen draft',
      },
      github_pr: {
        label: 'Use a pull request',
        description: 'Attach a repository change for review.',
        bestFor: 'Team changes',
        output: 'Review card',
      },
      figma_frame: {
        label: 'Figma frame',
        description: 'Bring in a design frame for conversion review.',
        bestFor: 'Design handoff',
        output: 'Design-backed draft',
      },
      figma_file: {
        label: 'Figma .fig',
        description: 'Read a local Figma file and keep the original.',
        bestFor: 'Checking a design first',
        output: 'Design details',
      },
      document_file: {
        label: 'Word / HWPX',
        description: 'Read a .docx or .hwpx and keep the original.',
        bestFor: 'Specs, guides, manuals',
        output: 'Document details',
      },
    },
  },
  chrome: {
    breadcrumb: 'Breadcrumb',
    search: 'Search apps, documents, games, and status...',
    openNavigation: 'Open navigation',
    openChat: 'Open chat',
    closeChat: 'Close chat',
    notifications: 'Notifications',
    lightMode: 'Switch to Light Mode',
    darkMode: 'Switch to Dark Mode',
    publish: 'Publish',
    publishCurrent: 'Publish current project',
    openProjectToPublish: 'Open a server project to publish',
    publishBlocked: 'Publish blocked',
    conflict: 'Conflict',
    saveFailed: 'Save failed',
    saving: 'Saving…',
    unsaved: 'Unsaved',
    live: 'Live',
    saved: 'Saved',
    draft: 'Draft',
    reloadConflict: 'Another session modified this block — reload to get the latest',
    reload: 'Reload',
    reloading: 'Reloading…',
    exportSource: 'Export application source',
    openProjectToExport: 'Open a project before exporting',
    exportSuccess: 'Application source exported',
    toggleIntentLoop: 'Chat',
    moreControls: 'More controls',
    history: 'History',
  },
  proposal: {
    change: "Change",
    info: "Info",
    warning: "Warning",
    critical: "Critical",
    before: "Before",
    after: "After",
    unchanged: "unchanged",
    removed: "removed",
    details: "Details",
    close: "Close",
    whatWillCheck: "What coogo will check",
    reviewChecks: "Review checks",
    reviewBeforeApply: "Review before apply",
    attachedProof: "Attached proof",
    check: "Check",
    notAttached: "not attached",
    readyToUse: "ready to use",
    reviewFirst: "review first",
    anotherEditPending: "Another edit is pending.",
    compareConflict: "Compare conflicting change details",
    keep: "Keep",
    revise: "Revise",
    reviseAria: "Revise proposed change",
    use: "Use",
    reviewNeeded: "Review needed",
    keepCurrent: "Keep current",
    keepCurrentAria: "Keep current and set this proposal aside",
  },
  home: {
    title: "What would you like to make or change today?",
    subtitle:
      "Connect a source or choose a work type and coogo will help shape your first request.",
    startWithChat: "Start with chat",
    workTypes: "Work types",
    workTypeHint: "Choose a work type to prefill the composer.",
    recentWork: "Recent work",
    reviewNeeded: "Needs review",
    viewAll: "View all",
    review: "Review",
    noWork: "No work yet.",
    noWorkHint: "Start your first request in the composer above.",
    firstProjectAction: "Describe your first project",
    continueWorking: "Continue working",
    openWork: "Open work",
    continue: "Continue",
    latestProjectEmpty:
      "Your latest real project and verification status will appear here.",
    readiness: "coogo readiness",
    totalWork: "Total work",
    productionVerified: "Production verified",
    settings: "Manage settings",
    countSuffix: "",
    readinessSummary:
      "Ready to share: {ready} ready, {review} review, {blocked} blocked.",
    featuredOutcome: {
      eyebrow: "Flagship workflow · Preview",
      title: "Newsletter → Membership",
      outcomes: {
        newsletterMembership: { title: "Newsletter → Membership" },
        reservationOperations: { title: "Reservation operations" },
      },
      description:
        "Start a governed membership service preview from your subscribers and content.",
      boundary:
        "This creates a preview candidate only. Verification, publication, deployment, and the live check are each a separate step.",
      action: "Choose this workflow",
      clearAction: "Clear selected workflow",
      inProgress: "This workflow request is already in progress.",
      prompt:
        "Turn my newsletter subscribers and existing content into a paid membership service with member onboarding, protected content, and operator insights.",
    },
    lanes: {
      app: { title: "App", prompt: "I want to build an app for my work." },
      document: {
        title: "Document",
        prompt: "I want to create a document from connected sources.",
      },
      game: {
        title: "Game",
        prompt: "I want to create a game with clear rules and interactions.",
      },
      existing: {
        title: "Edit existing",
        prompt: "I want to select and update existing work.",
      },
      starter: {
        title: "From a Starter",
        prompt: "I want to choose a Starter and begin.",
      },
    },
  },
  chat: {
    greeting: (name) => `What are we making today, ${name}?`,
    placeholder: "What would you like to make or change?",
    attach: "Files · sources",
    attachLabel: "Attach a file or connect a source",
    cancel: "Cancel request",
    send: "Send request",
    checkingSession: "Checking Studio session",
    cancelled: "Planning cancelled",
    ontologySelectionRequired:
      "Choose which application package this project builds from before generating.",
    startingNotice:
      "Starting from your request. coogo will keep what you are making, the next change, and this conversation together.",
    appGenerationStarting:
      "Creating the project and preparing its screens, behavior, and data...",
    webMcpVerified: (target) =>
      target
        ? `Verified with WebMCP: ${target}.`
        : "Verified the current Studio context with WebMCP.",
    readOnlyCompletionFallback:
      "I couldn't verify the requested status from the response. No changes were made and no approval was created.",
    suggestions: [
      "Build a reservation app",
      "Turn a document into a reviewable report",
      "Update existing work",
    ],
    templateProposal: {
      heading: "A ready-made template may fit",
      startFaster: "can help you start faster.",
      accept: "Use template",
      accepting: "Creating...",
      decline: "Build from scratch",
      accepted: "Project created from the template.",
      declined:
        "Building from scratch — preparing the screens, behavior, and data.",
      unavailable: "This template suggestion is no longer available.",
      failure:
        "The template could not be used. You can still build from scratch.",
    },
    planningFailure: {
      ontologyBlocked:
        "coogo can't build this yet — the needed knowledge isn't in the library. Try a different product idea, or ask your team to add this domain.",
      planNotActionable:
        "The plan came back without the data connections this app needs, so nothing was created. Describe the information it should read and write — for example who the members are and what you charge them — and send it again.",
      requirementCoverage:
        "The plan didn't cover part of what you asked for, so nothing was created. Try splitting your request into shorter sentences and sending it again.",
      generic: "Something went wrong while planning. Please try again.",
    },
    updateExistingWork: {
      noExistingWork:
        "You don't have any saved work in this workspace yet. Tell me what you'd like to make and I'll start it.",
      opening:
        'Opening "{name}". Tell me what you\'d like to change about it.',
      chooseOne:
        "You have more than one saved project — {choices}. Which one should I open?",
    },
    toolFailure: {
      invalidInput:
        "That request didn't include what this change needs, so nothing was changed. Try describing the change in one specific sentence.",
      notPermitted:
        "That change can't be made from chat yet, so nothing was changed.",
      unknownAction:
        "coogo doesn't have an action for that yet, so nothing was changed.",
      generic: "That change couldn't be completed, so nothing was changed.",
    },
  },
  templateContent: {
    aiFill: "AI fill",
    briefPlaceholder:
      "For example: a lively neighborhood bakery with a warm tone",
    filling: "Filling...",
    aiUnavailable: "AI service is not configured yet.",
    fillFailed: "AI fill failed.",
    fillBatchProgress: "Filling batch {current} of {total}...",
    fillPartialNotice: "AI fill could not complete {count} field(s); the rest were filled.",
    fillAllFailedNotice: "AI fill failed for all {count} field(s). Nothing was filled.",
    judgeChecking: "Reviewing...",
    judgeFailed: "The review result is unavailable.",
    pass: "Pass",
    caution: "Caution",
    reasons: "Reasons",
    hide: "Hide",
    show: "Show",
    general: "General",
    changed: "Changed",
    records: "{count} records",
    readOnly:
      "Read-only in this version because its data file is not available.",
    invalidJson: "Enter valid JSON.",
    persistError: "Some changes could not be saved. Try again.",
    applySuccess: "Content applied.",
    applyFailed: "Apply failed. Review the errors below.",
    applyFailureSummary: "Apply failed for {count} item(s).",
    applying: "Applying...",
    apply: "Apply ({count})",
    designRebindTitle: "Design release",
    designRebindPresetLoadError: "Could not load presets",
    designRebindPresetPlaceholder: "Select preset",
    designRebindApply: "Apply design",
    designRebindApplying: "Applying...",
    designRebindApplySuccess: "Design applied",
    designRebindApplyFailed: "Failed to apply design",
  },
  candidateVerification: {
    title: "Candidate verification",
    description:
      "Four independent proofs and one integrity capsule are required for this generated candidate.",
    authorityNote:
      "These checks can only be marked verified by reading back the signed-in permanent record.",
    promotionBoundary:
      "Checking candidate evidence does not prove product quality, publication, deployment, or that the live site was verified.",
    authorityLabel: "Issued by {authority}",
    verifiedStatus: "Candidate evidence verified",
    publicationGateStatus: "Publication is a separate gate",
    listLabel: "Candidate verification proof roles",
    refresh: "Refresh checks",
    refreshing: "Refreshing checks",
    askCoogo: "Ask Coogo",
    liveChecking: "Refreshing candidate verification checks.",
    liveResult: "Candidate verification status: {status}.",
    statuses: {
      pending: "Pending",
      checking: "Checking",
      ready: "Ready, not verified",
      verified: "Verified",
      blocked: "Blocked",
      unavailable: "Unavailable",
    },
    roles: {
      "sufficiency-assessment": "Source sufficiency assessment",
      "validator-safety-pass": "Validator safety pass",
      "second-model-judge": "Independent model review",
      "render-execution-receipt": "Render execution receipt",
      "verification-capsule": "Verification capsule",
    },
    authorities: {
      "source-assessment-authority": "Source assessment service",
      "validator-runtime": "Safety validator",
      "independent-model-judge": "Independent model review",
      "renderer-runtime": "Render verification service",
      "publishing-kernel": "Publishing integrity service",
    },
    causes: {
      "journal-checking": "Reading the durable verification journal.",
      "journal-unavailable": "The permanent record cannot be read right now.",
      "journal-scope-invalid": "The durable journal does not match this candidate.",
      "journal-contract-invalid": "The durable journal failed contract validation.",
      "verification-pending": "No verified durable event is recorded yet.",
      "producer-receipt-pending":
        "Awaiting the named server producer receipt; this candidate is not verified.",
      "verification-incomplete": "The verified event is missing a required independent proof.",
      "source-assessment-current": "The current assessment is ready, but it is not verification.",
      "source-assessment-insufficient": "The current assessment does not meet source sufficiency requirements.",
      "source-assessment-missing":
        "A current immutable source assessment is required.",
      "source-assessment-stale": "The candidate assessment revision is no longer current.",
      "durable-journal-verified":
        "The durable journal records four independent proofs and their integrity capsule.",
    },
  },
  report: {
    title: 'Status',
    ontology: 'Ontology',
    ontologySubtitle: 'Ontology coverage · current evidence and target envelope',
    unavailableTitle: 'No status to show yet',
    unavailableBody:
      "Once your product has a verified design source, its quality status appears here. coogo never shows made-up scores.",
    unavailableBodyDeveloperDetail:
      "Select a project with the latest verified result linked to its current release. coogo does not generate placeholder scores.",
    connectSource: "Open Design sources",
    axesLabel: "Ontology coverage axes",
    axesSummary: "6 axes · minimum required score",
    axisMethod:
      "Axis values use the lowest required dimension. They visualize evidence and do not grant authority.",
    coverage: "Ontology coverage",
    coverageSubtitle: "Current evidence vs target envelope",
    lowestRequiredAxis: "lowest required axis",
    loadingChart: "Loading ontology coverage chart",
    currentEvidence: "current evidence",
    targetEnvelope: "target envelope",
    gap: "gap",
    axisDetails: "Axis details",
    gaps: "Gaps",
    priority: "priority",
    blocks: "Blocks",
    noTrack: "no track",
    askCoogo: "Ask coogo",
    noOpenRequirements:
      "No open ontology requirements in this release-bound assessment.",
    allDimensions: "All {count} application ontology dimensions",
    dimension: "Dimension",
    authority: "Authority",
    resolution: "Resolution",
    current: "Current",
    evidence: "Evidence",
    none: "none",
    status: {
      ready: "Ready",
      warn: "Warning",
      blocked: "Blocked",
      missing: "Not yet",
      missingTooltip: "No verified evidence yet",
    },
    conversionProvenance: {
      title: "Compared with the original",
      sourceLabel: "Converted from",
      capturedLabel: "Read at",
      blocksLabel: "Screen blocks read",
      decisionsLabel: "Decisions you recorded",
      realizationLabel: "Declarations carried into the app",
      realizationAbsent:
        "None of the recorded decisions can be checked structurally, so there is no realization figure. coogo does not make one up.",
      sourceKindLegacyScreen: "Pasted legacy screen",
    },
  },
  guide: {
    title: "Guide workspace",
    documents: "Guide",
    noPageSelected: "No guide page selected.",
    page: "Page",
    pageActivity: "Page activity",
    proposals: "Proposals",
    applied: "Applied",
    exports: "Exports",
    pages: "pages",
    revision: "revision",
    addPage: "Add page",
    pageTitle: "Page title",
    openingEditor: "Opening guide editor...",
    pageActions: "Page actions",
    reviewPage: "Review page",
    proposeEdit: "Propose edit",
    editContent: "Edit content",
    move: "Move",
    delete: "Delete",
    draft: "Draft",
    export: "Export",
    readback: "Verification",
    output: "Output",
    notRequested: "not requested",
    noExportRequested: "No export has been requested for this guide.",
  },
  connectedDocument: {
    notConnected: "Not connected",
    connectedToTemplate: "Connected to {provider}",
    documentReference: "Document link or name",
    loadDocument: "Load document",
    loadErrorNotConnected:
      "This workspace isn't connected yet. Ask your team to set up the document connection.",
    loadErrorGeneric: "Couldn't load the document. Please try again.",
    panelTitle: "Connected document",
    provider: "Provider",
    activeSourceSnapshot: "Active source snapshot",
    connectionReference: "Connection reference",
    documentIdLabel: "Document ID",
    pageIdLabel: "Page ID",
    fetchVerifiedSnapshot: "Fetch verified snapshot",
    saveProjectFirst:
      "Save this project before connecting a provider document.",
    unsavedDraftBlocker:
      "Finish or propose the unsaved WordGard draft before fetching a connected document snapshot.",
    readbackSummary: (blocks, unsupported, accessMode) =>
      `${blocks} blocks · ${unsupported} unsupported · ${accessMode}`,
    accessMode: { "read-only": "read-only", "read-write": "read-write" },
    openSource: "Open source",
    newPageParentId: "New page parent ID (optional)",
    publishApproval:
      "I approve creating one new provider copy. The source document will not be updated.",
    publishAction: "Create and verify new copy",
    publishBlockedUnsupported:
      "Publication is blocked until unsupported provider elements are resolved.",
    connectionReadOnly:
      "This connection is read-only. Reconnect it with provider write permission before publishing a copy.",
    proofLabel: "New-copy proof:",
    proofStatus: { pass: "pass", fail: "fail", blocked: "blocked" },
    structure: "Structure",
    structureReadBack: "read back",
    structureMissing: "missing",
    exportsReadBack: (count) => ` · ${count} exports read back`,
    openNewCopy: "Open new copy",
    publishVerified: (provider, versionRef) =>
      `New ${provider} copy verified at ${versionRef}.`,
    publishBlockedBeforeVerified:
      "Publication was blocked before a provider copy was verified.",
    publishProofFailed:
      "A new copy may have been created, but its readback proof did not pass.",
    publishGenericError: "Could not publish the connected document copy.",
  },
  behavior: {
    navigation: "ActionFlow navigation",
    title: "ActionFlow",
    selectFlow: "Select an event or flow",
    unsaved: "unsaved",
    target: "Behavior target",
    noElement: "No element selected",
    selectInPreview: "Select in Preview",
    section: "Section",
    status: "Status",
    bindings: "Bindings",
    flows: "Flows",
    empty: "empty",
    steps: "steps",
    verified: "verified",
    failed: "failed",
    notVerified: "not verified",
    noFlows: "No flows are declared for this project.",
    eventBindings: "Event bindings",
    connected: "connected",
    orphan: "orphan",
    inspectElement: "Select an app element to inspect its behavior.",
    noBindings: "No event bindings are declared for this project.",
    chatActions: "Chat actions",
    reviewFlow: "Review flow",
    addStep: "Add step",
    connectEvent: "Connect event",
    testFlow: "Test flow",
    editor: "Behavior editor",
    unsavedFlow: "unsaved flow",
    noBlocks: "No blocks yet — flows cannot be saved",
    testRun: "Test run",
    closeFlowForm: "Close flow form",
    newFlow: "New Flow",
    addBlockFirst: "Add a page block before creating a flow",
    nodeTypes: "Supported ActionFlow node types",
    nodes: "Nodes",
    executionModel: "Execution model",
    binding: "Binding",
    direct: "direct",
    directAtomic: "Direct atomic",
    namedFlow: "Named flow",
    lanes: "Lanes",
    actionLane: "Action",
    uiLane: "UI",
    motionLane: "Motion",
    order: "Order",
    completionBarrier: "Completion barrier",
    stage: "Stage",
    saga: "Saga",
    sagaCompensation: "Saga compensates in reverse order · not ACID",
    palette: {
      ifElse: "If/Else",
      loop: "Loop",
      switch: "Switch",
      parallel: "Parallel",
      tryCatch: "Try/Catch",
    },
    flowCreateFailed: (name) =>
      `Flow "${name}" was NOT created — the server rejected it and the local change has been rolled back`,
    flowCreated: (name) => `Flow "${name}" created`,
    flowCreatedLocalOnly: (name) =>
      `Flow "${name}" created in this session only — it is not saved to the server yet`,
  },
  approval: {
    categoryFlows: "Flows",
    categoryDatasources: "Datasources",
    categoryVariables: "Variables",
    categoryEvents: "Event Bindings",
    compositeActionFallback: "Composite Action",
    operationCount: (count) => `${count} operation${count !== 1 ? "s" : ""}`,
    applyAll: "Apply All",
    applySelected: (selected, total) => `Apply (${selected}/${total})`,
    dismiss: "Dismiss",
    applied: "Applied",
    pendingValidation: (reason) =>
      `Pending validation${reason ? `: ${reason}` : ""}`,
    dismissed: "Dismissed",
    edit: "Edit",
  },
  insertPanel: {
    noActivePage: "No active page",
    searchPlaceholder: "Search UI",
    maxChildrenReached: (current, max) =>
      `Maximum children reached (${current}/${max})`,
    fromLibrary: "From Library",
    createManually: "Create Manually",
    detailCreateOrOpenPage: "Create or open a page before adding UI.",
    detailNeedsBlockTarget: "New elements need a block target.",
    detailAppendsToBlock: "Adds elements at the end of this block.",
    detailInside: (target) => `Inside ${target}`,
    detailAfterSelectionIn: (target) => `After selection in ${target}`,
    targetLabel: "Target",
    newBlock: "New Block",
    noLibraryBlocks: "No reusable blocks available yet",
    moreBlocks: (count) => `${count} more blocks`,
    noElementsFound: "No elements found",
  },
  dataExplorer: {
    datasourceCreated: (name) => `Datasource "${name}" created`,
    datasourceNameTaken: (name, blockName) =>
      `A datasource named "${name}" already exists in block "${blockName}". Nothing was changed — rename this one, or edit the existing datasource.`,
    datasourceCreateFailed: (name, blockName) =>
      `Could not create "${name}" — block "${blockName}" was not found (deleted or renamed?). Nothing was changed.`,
  },
  routeHeadings: {
    map: "App Map",
    admin: "Admin",
    billing: "Billing",
    themeEditor: "Theme editor",
    newProject: "What are we making?",
  },
  dataInspector: {
    header: {
      title: "Entity Detail",
      reviewEntity: "Review entity",
    },
    fields: {
      name: "Name",
      block: "Block",
      scope: "Scope",
      type: "Type",
      defaultValue: "Default",
      expression: "Expression",
      path: "Path",
      importSource: "Import",
      call: "Call",
      method: "Method",
      kind: "Kind",
      endpoint: "Endpoint",
      resultTarget: "Result Target",
      headers: "Headers",
      body: "Body",
      source: "Source",
      eventKey: "Event Key",
      component: "Component",
      event: "Event",
      flow: "Flow",
      trigger: "Trigger",
      steps: "Steps",
      usedBy: "Used By",
    },
    placeholders: {
      noDefault: "No default",
      expression: "e.g. #{otherVar} + 1",
      resultTarget: "state variable populated by this datasource",
      body: "Request body template",
    },
    emptyStates: {
      selectEntity: "Select an entity to inspect",
      entityNotFound: "Entity not found",
      variableNotFound: "Variable not found",
      datasourceNotFound: "Datasource not found",
      eventNotFound: "Event not found",
      flowNotFound: "Flow not found",
    },
    actions: {
      testInExpressionLab: "Test in Expression Lab",
      writeExpressionInLab: "Write an expression in Expression Lab",
      openInFlowEditor: "Open in Flow Editor",
      deleteVariable: "Delete Variable",
      deleteDatasource: "Delete Datasource",
      deleteEvent: "Delete Event",
    },
    appManifest: {
      scopeValue: "App manifest",
      note: "App manifest datasources are shared project inputs. Edit block-level bindings from a block datasource or the Code surface.",
    },
    schema: {
      request: "Request Schema",
      response: "Response Schema",
    },
    flowUnbound: "-- Unbound --",
    stepCount: (count) => `${count} steps`,
    validationSummary: (errors, warnings) =>
      `${errors} error(s), ${warnings} warning(s)`,
    referencedWarning:
      "This variable is referenced elsewhere. Deleting may break expressions.",
    toasts: {
      variableDeleted: (name) => `Variable "${name}" deleted`,
      datasourceDeleted: (name) => `Datasource "${name}" deleted`,
      eventRemoved: (name) => `Event "${name}" removed`,
      bindingsUpdated: (count, oldPath, newPath) =>
        `Updated ${count} binding(s): ${oldPath} -> ${newPath}`,
      bindingsUpdateFailed:
        "Failed to update bindings. Some references to the old field name may need manual correction.",
    },
  },
  dataValidation: {
    confirm: {
      deletePrompt: (name) => `Delete "${name}"?`,
      typeChangePrompt: (name) => `Change the type of "${name}"?`,
      deleteAction: "Delete",
      typeChangeAction: "Change type",
      cancel: "Cancel",
      unnamedField: "unnamed field",
      nestedFieldWarning: (count) =>
        `This also removes ${count} nested field(s) and anything inside them.`,
    },
    messages: {
      unmatchedBrackets: "Unmatched #{} brackets",
      emptyExpression: "Empty expression #{}",
      duplicateVariableName: (name, blockName) =>
        `Duplicate variable name "${name}" in block "${blockName}"`,
      variableUnreferenced: (name) =>
        `Variable "${name}" is not referenced in any flow or expression`,
      flowMissing: (flowName) => `Flow "${flowName}" does not exist`,
      restDatasourceNoEndpoint: "REST datasource has no endpoint URL",
    },
  },
  proofHealth: {
    verified: "Verified",
    needsReview: "Needs review",
    needsLiveCheck: "Needs a live check",
    blocked: "Blocked",
    none: "No proof",
    checkedLive: (date) => `Checked live ${date}`,
    dateUnrecorded: "recorded",
    liveCheckBlocked: "The live check is blocked.",
    liveCheckNeedsReview: "The live check needs review.",
    recordBlocked: "A proof record is blocked.",
    recordNeedsReview: "A proof record needs review.",
    openDeployToRun: "Open Deploy to run the live check.",
    reviewEvidence: "Review evidence",
    reviewShare: "Review share",
  },

  // ── lane/kodash · map · projects · apps-live · import ────────────────────
  // Four screens whose readable surface was English on 2026-08-20, harvested
  // by `__tests__/readable-surface-localized.test.tsx` (33 + 21 + 16 + 14
  // word-entries). Appended at the END of every table on purpose: two sibling
  // lanes were editing the sections above at the same time, and the tail is
  // the one place four concurrent additions cannot collide.
  //
  // EVERY key below is spelled out in all five tables. `ko`, `ja` and `zh`
  // open with `...en` and `zhTW` with `...zh`, so a key added to `en` alone
  // is INHERITED rather than missing — and because the residue walk derives
  // its keep-Latin allowlist from the translated tables, inherited English
  // allowlists its own words and the walk goes silent. Measured, not assumed:
  // mutation M5 in the register test deletes one `ko` override here and the
  // residue walk stays green. The inheritance gate is what turns it red.
  appMap: {
    description:
      "Inspect the pages, blocks, actions, state, and data connections inside a cogo app.",
    emptyTitle: "Open an app to map its structure",
    emptyBody:
      "App Map becomes useful after a project, import, or generated Library item is loaded. It shows how screens, data, and ActionFlow steps connect before launch.",
    createApp: "Create app",
    importSource: "Import source",
    openProjects: "Open projects",
    noAppLoaded: "No app loaded",
    nodesUnit: "nodes",
    edgesUnit: "edges",
    nodeTypes: {
      app: "App",
      page: "Pages",
      block: "Blocks",
      atom: "DS Atoms",
      flow: "Flows",
      stateVar: "State Vars",
      datasource: "Data Sources",
    },
    stats: { title: "Stats", edges: "Edges" },
    legend: {
      title: "Legend",
      filterLabel: "Filter by node type",
      clearFilter: "Clear filter",
    },
    nodeDetail: {
      title: "Node Detail",
      empty: "Click a node to inspect",
      incomingUnit: "incoming",
      outgoingUnit: "outgoing",
    },
    sessions: {
      title: "Sessions",
      body: "Brainstorm observation timeline ships in Phase 3. This panel will replay picks / rejects / design-system tweaks for the currently opened app.",
    },
    canvas: {
      controls: "Control Panel",
      zoomIn: "Zoom In",
      zoomOut: "Zoom Out",
      fitView: "Fit View",
      interactive: "Toggle Interactivity",
      miniMap: "Mini Map",
      handle: "Handle",
      nodeDescription:
        "Press enter or space to select a node. You can then use the arrow keys to move the node around. Press delete to remove it and escape to cancel.",
      nodeKeyboardDisabled:
        "Press enter or space to select a node. Use the arrow keys to move the node around. Press delete to remove it and escape to cancel.",
      nodeMoved: (direction, x, y) => `Moved selected node ${direction}. New position, x: ${x}, y: ${y}`,
      edgeDescription:
        "Press enter or space to select an edge. You can then press delete to remove it or escape to cancel.",
    },
  },
  projectCard: {
    status: {
      published: "Live",
      draft: "Draft",
      archived: "Archived",
      // Byte-for-byte what the raw fallthrough rendered before, so the
      // English control does not move while the other four locales gain a
      // word they never had.
      active: "active",
    },
    share: "Share",
    shareNamed: (name) => `Share ${name}`,
    openActions: (name) => `Open actions for ${name}`,
    rename: "Rename",
    duplicate: "Duplicate",
    settings: "Settings",
    delete: "Delete",
  },
  outputsSection: {
    heading: "Outputs",
    breakdown: (shared, drafts) =>
      `${shared} shared · ${drafts} ${drafts === 1 ? "draft" : "drafts"}`,
    loading: "Loading outputs…",
    emptyTitle: "No outputs yet",
    emptyBody:
      "Create or import an app, document, or report to see it here — as a draft first, then as a shared output once you share it.",
    showMore: (count) => `Show ${count} more outputs`,
  },
  launchLoop: {
    addSource: "Idea, file, URL, repository, or design",
    shape: "cogo prepares the work",
    save: "Keep source and next steps together",
    share: "Open, share, or launch",
  },
  // ─── lane/kobiz: the nine business screens ───────────────────────────
  businessScreens: {
    review: {
      tryAgain: 'Try again',
      openSurface: (surface) => `Open ${surface}`,
      viewSurface: (surface) => `View ${surface}`,
      tableEmpty: 'No data available',
      periodAll: 'All',
      observation: {
        measuredSummary: 'Measured review data.',
        emptySummary: 'Review observation details',
        observedAt: (stamp) => `Observed ${stamp}`,
        evidence: 'Evidence:',
      },
      pagination: {
        first: 'First page',
        previous: 'Previous page',
        next: 'Next page',
        last: 'Last page',
      },
      status: {
        active: 'Active',
        draft: 'Draft',
        archived: 'Archived',
        pending: 'Pending',
        processing: 'Processing',
        completed: 'Completed',
        refunded: 'Refunded',
        shipping: 'Shipping',
      },
    },
    data: {
      description: 'Source packets, extracted fields, readiness, and provenance for apps, documents, and reports.',
      addSource: 'Add Source',
      loadFailed: 'We couldn\u2019t load your sources right now.',
      loading: 'Loading source review data\u2026',
      empty: 'No admitted source packets were observed for this workspace.',
      scope: 'Source packet review',
      summaryLabel: 'Product summary',
      tiles: { sources: 'Sources', ready: 'Ready', drafts: 'Drafts' },
      columns: {
        name: 'Source',
        artifact: 'Artifact',
        confidence: 'Confidence',
        rows: 'Rows',
        status: 'Status',
      },
      readyStatus: 'Ready',
      filters: {
        artifact: 'Artifact',
        allArtifacts: 'All artifacts',
        status: 'Status',
        all: 'All',
        ready: 'Ready',
        draft: 'Draft',
        archived: 'Archived',
      },
      unavailable: {
        'producer-not-configured': {
          summary: 'Source review producer connection setup is missing.',
          title: 'Configure the source review connection',
          detail: 'A workspace administrator must configure an admitted source review producer. You can continue adding source packets in the meantime.',
        },
        'metric-not-supported': {
          summary: 'The current review contract does not measure this source metric.',
          title: 'Use supported source review fields',
          detail: 'Continue with admitted source packet fields. This metric cannot be reported by the current review contract.',
        },
        'authority-not-admitted': {
          summary: 'The current source and rights authority does not admit measurement for this source data.',
          title: 'Review source authority',
          detail: 'Review the source rights and admission evidence before requesting measurement.',
        },
      },
    },
    activity: {
      description: 'Track requested changes, review state, and team actions after an artifact is shared.',
      exportLabel: 'Export',
      exportAria: (count) => `Export ${count} activity rows as CSV`,
      exportBlockedAria: (reason) => `Export \u2014 ${reason}`,
      blocked: {
        notLoaded: 'Activity could not be loaded',
        none: 'No activity to export yet',
        loading: 'Activity is loading',
        unavailable: 'Activity measurement is unavailable',
      },
      exported: (count, filename) => `Exported ${count} rows to ${filename}`,
      loadFailed: 'We couldn\u2019t load your activity right now.',
      loading: 'Loading activity review data\u2026',
      empty: 'No recorded activity was observed for this workspace.',
      scope: 'Released-output activity review',
      summaryLabel: 'Order summary',
      tiles: { pending: 'Pending', applied: 'Applied', reviewed: 'Reviewed' },
      tabs: {
        all: 'All',
        pending: 'Pending',
        applied: 'Applied',
        reviewed: 'Reviewed',
        rejected: 'Rejected',
      },
      columns: {
        actor: 'Actor',
        artifact: 'Artifact',
        changes: 'Changes',
        state: 'State',
        updated: 'Updated',
      },
      changeCount: (count) => `${count} changes`,
      unavailable: {
        'producer-not-configured': {
          summary: 'Activity review producer connection setup is missing.',
          title: 'Configure the activity review connection',
          detail: 'A workspace administrator must configure an admitted activity review producer. Sharing work remains available while setup is pending.',
        },
        'metric-not-supported': {
          summary: 'The current review contract does not measure this activity metric.',
          title: 'Use supported activity observations',
          detail: 'Continue with supported review events. This metric is outside the current activity review contract.',
        },
        'authority-not-admitted': {
          summary: 'The current source and rights authority does not admit measurement for this activity.',
          title: 'Review activity authority',
          detail: 'Review the source rights and admission evidence for the activity before requesting measurement.',
        },
      },
      drawer: {
        title: (id) => `Order #${id}`,
        close: 'Close',
        process: 'Process',
        refund: 'Refund',
        customer: 'Customer:',
        email: 'Email:',
        app: 'App:',
        date: 'Date:',
        items: 'Items',
        noItems: 'No line items recorded for this record.',
        subtotal: 'Subtotal',
        shipping: 'Shipping',
        discount: 'Discount',
        total: 'Total',
      },
    },
    impact: {
      description: 'Views, review conversion, accepted changes, and outcome signals across shared cogo artifacts.',
      loadFailed: 'We couldn\u2019t load impact review data right now.',
      loading: 'Loading impact review data\u2026',
      empty: 'The authoritative impact review found no observations for this period.',
      scope: 'Selected impact review period',
      columns: {
        artifact: 'Artifact',
        views: 'Views',
        reviews: 'Reviews',
        cvr: 'CVR',
      },
      seriesViews: 'Views',
      trendTitle: 'Impact Trend',
      byArtifact: 'Impact by artifact',
      unavailable: {
        'producer-not-configured': {
          summary: 'Impact review producer connection setup is missing.',
          title: 'Configure the impact review connection',
          detail: 'A workspace administrator must configure an admitted impact review producer. Sharing an artifact remains available while setup is pending.',
        },
        'metric-not-supported': {
          summary: 'The current review contract does not measure this impact metric.',
          title: 'Use supported impact observations',
          detail: 'Continue with supported outcome signals. This metric is outside the current impact review contract.',
        },
        'authority-not-admitted': {
          summary: 'The current source and rights authority does not admit measurement for this impact.',
          title: 'Review impact authority',
          detail: 'Review the source rights and admission evidence behind the outcome signals before requesting measurement.',
        },
      },
    },
    audience: {
      description: 'Review member growth, traffic sources, and returning visitors for published apps.',
      loadFailed: 'We couldn\u2019t load audience review data right now.',
      loading: 'Loading audience review data\u2026',
      empty: 'The authoritative audience review found no observations for this workspace.',
      scope: 'Audience review',
      kpis: { reviewers: 'Reviewers', new7d: 'New (7D)', returnRate: 'Return Rate' },
      rolesTitle: 'Audience Roles',
      recentReviewers: 'Recent reviewers',
      columns: {
        name: 'Name',
        email: 'Email',
        role: 'Role',
        joined: 'Joined',
      },
      unavailable: {
        'producer-not-configured': {
          summary: 'Audience review producer connection setup is missing.',
          title: 'Configure the audience review connection',
          detail: 'A workspace administrator must configure an admitted audience review producer. Sharing an artifact remains available while setup is pending.',
        },
        'metric-not-supported': {
          summary: 'The current review contract does not measure this audience metric.',
          title: 'Use supported audience observations',
          detail: 'Continue with supported review participation signals. This metric is outside the current audience review contract.',
        },
        'authority-not-admitted': {
          summary: 'The current source and rights authority does not admit measurement for this audience.',
          title: 'Review audience authority',
          detail: 'Review the source rights and admission evidence for audience observations before requesting measurement.',
        },
      },
    },
    analytics: { eyebrow: 'App analytics', body: 'Open a shared output to review traffic, conversion, and revenue signals. Analytics are tied to reviewed outputs so users do not land on an unrelated Home screen.' },
    requestFlow: {
      prepared: { title: 'Request is prepared', body: 'cogo prepares the app, document, or report change.' },
      attached: { title: 'Review stays attached', body: 'Source, screen, and share state stay with the request.' },
      approval: { title: 'You approve sharing', body: 'Publishing and launch updates wait until you review them.' },
    },
    themeEditor: {
      badge: 'Design System',
      description: 'Tune the Studio-ready theme axes and verify the result against real cogo component blocks before saving it to Library.',
      livePreviewNote: 'Live preview uses the same payload sent to the cogo renderer.',
    },
    billing: {
      currentPlan: 'Current Plan',
      currentPlanNote: 'Your active subscription details',
      cancelsAtPeriodEnd: 'Cancels at period end',
      cycle: 'Billing Cycle',
      amount: 'Amount',
      started: 'Started',
      nextBilling: 'Next Billing',
      changePlan: 'Change Plan',
      cancelSubscription: 'Cancel Subscription',
      cancelling: 'Cancelling...',
      viewPlans: 'View Plans',
      paymentHistory: 'Payment History',
      paymentHistoryNote: 'All your payment transactions',
      paymentsSignedOut: 'Sign in to see your payment history',
      paymentsFailed: "We couldn't load your payment history",
      paymentsEmpty: 'No payment history found',
      columns: {
        date: 'Date',
        amount: 'Amount',
        status: 'Status',
        provider: 'Provider',
        reference: 'Reference',
      },
      status: {
        active: 'active',
        paid: 'paid',
        pending: 'pending',
        cancelled: 'cancelled',
        failed: 'failed',
      },
        statusUnknown: (value) => `Unknown status (${value})`,
      cancelDialog: {
        title: 'Cancel Subscription?',
        body: (periodEnd) => `Your subscription will remain active until the end of the current billing period (${periodEnd}). After that, you will not be charged.`,
        keep: 'Keep Subscription',
        confirm: 'Yes, Cancel',
      },
    },
    admin: { accessDeniedTitle: 'Access Denied', accessDeniedBody: 'You need admin privileges to access this page.' },
  },
  // ─── end lane/kobiz ─────────────────────────────────────────────────
};

const ko: StudioDictionary = {
  ...en,
  // ── lane/koentry ─────────────────────────────────────────────────────────
  newProject: KO_NEW_PROJECT_COPY,
  starters: KO_STARTERS_COPY,
  // ── end lane/koentry ─────────────────────────────────────────────────────
  designSystemAxes: KO_DESIGN_AXES_COPY,
  language: { label: "언어" },
  a11y: {
    skipToMainContent: "본문으로 건너뛰기",
    dashboardContent: "대시보드 본문",
    loadingSurface: (surface) => `${surface} 불러오는 중…`,
    creatorNav: "탐색",
    header: "머리말",
    designSystemSurfaces: "디자인 시스템 화면",
    designSystemInventory: "이 워크스페이스의 디자인 시스템",
    importSources: "가져올 소스",
    librarySummary: "라이브러리 요약",
    operations: "운영",
    requestReviewFlow: "요청 검토 흐름",
    reviewSummary: "검토 요약",
    shareReadinessChecklist: "공유 준비 점검표",
    appPreviewFrame: "coogo 앱 미리보기",
    entryBrandHome: "coogo 홈",
    entryReport: "coogo 운영 보고",
    showSection: (name) => `${name} 펼치기`,
    hideSection: (name) => `${name} 접기`,
  },
  ide: {
    undo: "실행 취소",
    redo: "다시 실행",
    appPageList: "앱 페이지 목록",
    documentList: "문서 목록",
    viewportSwitcher: "화면 크기 전환",
    device: {
      mobile: "모바일",
      tablet: "태블릿",
      desktop: "데스크톱",
      document: "문서",
      a4Portrait: "A4 세로",
      framed: (label, width, height) => `${label} (${width}x${height})`,
      sized: (label, width, height) => `${label} · ${width}x${height}px`,
    },
    pages: {
      title: "페이지",
      summaryWithPages: (count) => `미리보기 + ${count}개 페이지`,
      summaryPreviewOnly: "미리보기",
      runnableTitle: "미리보기",
      runnableBadge: "실행",
      runnableDetail: "실행 화면",
      emptyTitle: "현재 화면",
      emptyBody: "아직 페이지 정보가 없어요",
    },
    documents: {
      title: "문서",
      count: (count) => `문서 ${count}개`,
      pageCount: "1쪽",
    },
    reviewPreview: "미리보기 검토",
    print: "인쇄",
  },
  navigation: {
    sections: {
      start: "시작",
      build: "구축",
      verify: "검증",
      release: "발행",
    },
    home: '홈',
    create: '만들기',
    work: '작업',
    details: '세부 도구',
    map: '맵',
    code: '코드',
    release: '출시',
    publish: '게시',
    launch: '출시',
    configure: '설정',
    settings: '설정',
    share: '공유',
    starters: '스타터',
    import: '가져오기',
    sources: '소스',
    library: '라이브러리',
    documents: '문서',
    requests: '요청',
    requestsAndReviews: '요청 및 검토',
    bots: '봇',
    operations: '운영',
    data: '데이터',
    activity: '활동',
    impact: '성과',
    audience: '이용자',
    analytics: '분석',
    designSystem: '디자인 시스템',
    workspaceSettings: '워크스페이스 설정',
    projects: '프로젝트',
    templates: '템플릿',
    projectSettings: '프로젝트 설정',
    account: '계정',
    signIn: '로그인',
    signOut: '로그아웃',
    goHome: 'Studio 홈으로 이동',
    goCoogoHome: 'Coogo 홈으로 이동',
    coogoHomepage: 'Coogo 홈페이지',
    studioNavigation: 'Studio 탐색',
    studioNavigationDescription: '만들기, 프로젝트, 저장된 작업, 요청, 설정 사이를 이동합니다.',
    collapseNavigation: '내비게이션 접기',
    expandNavigation: '내비게이션 펼치기',
  },
  artifacts: {
    label: '산출물 모드',
    app: '앱',
    game: '게임',
    design: '디자인',
    document: '문서',
    guide: '가이드',
    insights: '현황',
  },
  work: {
    title: '작업',
    subtitle: '앱, 문서, 리포트를 만들고 관리하고 검토합니다.',
    namePlaceholder: '새 작업 이름...',
    create: '만들기',
    browseStarters: '스타터 둘러보기',
    importSource: '소스 가져오기',
    emptyTitle: '아직 작업이 없습니다',
    emptyBody: '위에서 앱, 문서, 리포트를 만들거나 아래 스타터를 둘러보세요',
  },
  designSystem: {
    title: '디자인 시스템',
    subtitle: '생성되는 앱이 사용할 색상·타이포·간격·모양을 정하고, 라이브러리에 저장하기 전에 cogo 어휘와 맞는지 확인합니다.',
    validationLab: '검증 랩',
    themeEditor: '테마 편집기',
    tabs: {
      systems: '디자인 시스템',
      validation: '검증 랩',
      vocabulary: '어휘',
      briefings: '브리핑',
    },
    uiBlocks: {
      eyebrow: 'UI Block 온톨로지',
      title: '무엇을 만들고 있나요?',
      description: '필요한 화면을 설명하면 Coogo가 승인된 카탈로그의 실제 블록을 찾고, 현재 디자인 온톨로지 테마로 미리 보여줍니다.',
      intentLabel: '제품 의도',
      intentPlaceholder: '예: 팀 분석 대시보드',
      shortcutsLabel: '의도로 시작',
      shortcuts: {
        dashboard: '대시보드',
        store: '스토어',
        landing: '랜딩 페이지',
        auth: '로그인 흐름',
      },
      recommendations: '추천 UI Block',
      admittedCount: (count) => `카탈로그 승인 블록 ${count}개`,
      noMatches: '이 의도와 맞는 승인 블록이 아직 없습니다. 제품 설명을 더 짧게 써 보세요.',
      preview: '이 테마로 미리보기',
      selectedPreview: '라이브 테마 미리보기에 선택됨',
      readOnly: '읽기 전용 미리보기',
      authorityUnavailable: '구조 호환성과 발행 권한이 입증되기 전까지 프로젝트 적용과 교체는 제공하지 않습니다.',
    },
    precondition: {
      signedOutReason:
        '로그인이 필요합니다. 워크스페이스의 디자인시스템 목록과 소스에서 읽은 값은 로그인한 사용자에게만 표시됩니다.',
      signInCta: '로그인으로 이동',
      checkingReason: '이 화면을 열 수 있는지 확인하고 있습니다…',
      unmetFallbackReason: '이 화면을 열기 위한 조건이 아직 충족되지 않았습니다.',
    },
    sourceRead: {
      failed: '이 프로젝트의 디자인 소스를 읽지 못했습니다.',
      failedWithMessage: '지금은 이 프로젝트의 디자인 소스를 읽지 못했습니다: {message}',
    },
    vocabularyPanel: {
      title: '어휘',
      search: '검색…',
      enumPrefix: '접두사',
      enumAccessor: '접근자',
      enumEvent: '이벤트',
      enumAction: '액션',
      colName: '이름',
      colMode: '모드',
      colSemantics: '의미',
      colCategory: '분류',
      colReturns: '반환',
      colDomain: '영역',
      colDescription: '설명',
      loading: '어휘를 불러오는 중…',
      errorTitle: '어휘를 불러오지 못했습니다',
      retry: '다시 시도',
      empty: '이 조각에는 항목이 없습니다.',
      unavailableTitle: '어휘를 쓸 수 없습니다',
      unavailableDetail: '어휘 목록은 로컬 지식 그래프가 있어야 표시됩니다.',
      noMatch: '“{query}”와 맞는 항목이 없습니다',
      clear: '지우기',
    },
    validatorPanel: {
      specChip: "§ 디자인 시스템 v4",
      heading: "검증기",
      lastRun: '마지막:',
      justNow: '방금',
      secondsAgo: '{n}초 전',
      minutesAgo: '{n}분 전',
      hoursAgo: '{n}시간 전',
      rerun: '다시 실행',
      validating: '검사 중…',
      colRule: '규칙',
      allChecks: '전체 검사',
      noViolations: '표시할 규칙 위반이 없습니다.',
      colGate: '관문',
      colStatus: '상태',
      statusPass: '통과',
      statusFail: '실패',
      flagOffTitle: '검사가 꺼져 있습니다',
      flagOffBody: '이 워크스페이스에서는 디자인 검사가 켜져 있지 않습니다.',
      readyTitle: '검사할 준비가 됐습니다',
      readyBody: '다시 실행을 눌러 지금 프로젝트를 검사하세요.',
      unreadableTitle: '검사 결과를 읽지 못했습니다',
      unreadableBody: '마지막 검사가 이 화면이 해석할 수 없는 결과를 돌려줬습니다. 다시 실행을 눌러 보세요.',
      briefingReadFailed: '이 워크스페이스의 브리핑을 읽지 못했습니다.',
      briefingTitle: '브리핑',
      briefingIntro: '이 앱에 대한 짧은 브리핑을 씁니다. 브리핑은 블록과 함께 온톨로지에 남고 새로고침해도 유지됩니다.',
      briefingSignIn: '브리핑을 쓰려면 로그인하세요.',
      briefingSignedOutDetail: '브리핑은 온톨로지 게이트웨이를 통해 워크스페이스별로 보관됩니다. 이 앱의 브리핑을 읽고 쓰려면 로그인하세요.',
      briefingUnavailableTitle: '이 워크스페이스에서는 브리핑을 사용할 수 없습니다',
      briefingUnavailableDetail: '현재 Studio 배포에는 지원되는 브리핑 저장소가 없습니다. 읽기와 쓰기가 비활성화되었습니다.',
      briefingFieldTitle: '제목',
      briefingFieldBody: '본문 (마크다운 — 이스케이프된 텍스트로 표시)',
      briefingTitlePlaceholder: '예: 2분기 출시 점검표',
      briefingBodyPlaceholder: '여기에 브리핑을 적으세요. 줄바꿈은 유지되고 HTML은 이스케이프됩니다.',
      briefingReady: '준비됨',
      briefingSaving: '저장 중…',
      briefingSave: '브리핑 저장',
      briefingEmpty: '아직 브리핑이 없습니다. 위에 첫 브리핑을 쓰고 저장을 누르세요.',
      repoInitTitle: '검사를 시작하는 중…',
      repoInitBody: '검사기를 준비하고 있습니다 — 잠시 걸립니다.',
      noBlocksTitle: '아직 검사할 블록이 없습니다',
      noBlocksBody: '편집 모드 대화로 블록을 만들면 이 화면이 채워집니다.',
      errorTitle: '검사에 실패했습니다',
      errorBody: '아래 오류 메시지를 확인하고 다시 실행을 눌러 보세요.',
    },
    inventory: {
      readingBadge: '이 워크스페이스의 디자인 시스템을 읽는 중…',
      readingDetail: '목록이 돌아오기 전까지는 아무것도 단정하지 않습니다.',
      unreadableBadge: '이 워크스페이스의 디자인 시스템을 읽지 못했습니다',
      unreadableDetail:
        '목록을 불러오지 못해 이 화면은 이 워크스페이스에 디자인 시스템이 몇 개 있는지 말할 수 없습니다. 아래 테마 편집기는 그대로 쓸 수 있습니다.',
      noneBadge: '이 워크스페이스에 저장된 디자인 시스템이 없습니다{stamp}',
      noneDetail:
        '아직 디자인 시스템으로 저장된 것이 없습니다. 아래에서 축을 정하고 라이브러리에 저장하면 첫 번째가 만들어집니다.',
      listedBadge: '이 워크스페이스의 디자인 시스템 {count}개{stamp}',
      listedBadgeOne: '이 워크스페이스의 디자인 시스템 {count}개{stamp}',
      listedDetail:
        '위 시각에 돌아온 목록 그대로입니다. 이 화면을 여는 것은 목록을 불러올 뿐, 각 항목을 검사하지는 않습니다.',
      readStamp: ', {time} 읽음',
      setAsideNote:
        ' 그 밖의 {count}개 행은 템플릿으로 만든 프로젝트에 속하며, 워크스페이스 디자인 시스템이 아닙니다.',
      setAsideNoteOne:
        ' 그 밖의 {count}개 행은 템플릿으로 만든 프로젝트에 속하며, 워크스페이스 디자인 시스템이 아닙니다.',
    },
    themeEditorPanel: {
      title: '테마 편집기',
      body:
        '스타일, 기본 색상, 테마, 차트 팔레트, 모서리 둥글기, 글꼴을 바꾸면 실제 cogo 컴포넌트 블록이 그때그때 바뀝니다. 마음에 들면 라이브러리에 저장하세요.',
    },
    workbench: {
      controlsTitle: '테마 컨트롤',
      selectPlaceholder: '{axis} 선택',
      saveToLibrary: '라이브러리에 저장',
      saveDialogTitle: '디자인 시스템 저장',
      namePlaceholder: '프리셋 이름',
      descriptionPlaceholder: '설명 (선택)',
      closeLabel: '닫기',
      cancel: '취소',
      save: '저장',
      saving: '저장 중…',
      savedAs: '‘{name}’(으)로 저장했습니다',
      nameRequired: '이름을 입력해 주세요.',
      saveFailed: '저장하지 못했습니다.',
      sourceTitle: '소스에서 읽은 디자인 시스템',
      sourceEmptyBody:
        '이 프로젝트에 아직 디자인 소스를 가져오지 않았습니다 — 아래 모든 축은 직접 정하기 전까지 비어 있습니다.',
      sourceImportCta: '디자인 소스 가져오기 (Figma)',
      sourceReadPrefix: '읽은 소스: ',
      sourceVariableCount: '· 변수 {count}개',
      sourceVariableCountOne: '· 변수 {count}개',
      chooseAxisValue: '선택',
      axisValueSelectLabel: '{axis} 값',
    },
    projectTokens: {
      title: '디자인 파일에서 가져온 토큰',
      bodyBefore:
        '가져온 디자인 파일에서 읽은 색상과 변수는 워크스페이스가 아니라 그 파일을 가져온 프로젝트에 속합니다. 해당 프로젝트를 열고 ',
      navPath: '디자인 → 토큰',
      bodyAfter:
        '으로 가면 확인하고 프로젝트에 적용할 수 있습니다.',
    },
  },
  settings: {
    title: '워크스페이스 설정',
    profile: {
      title: '프로필',
      name: '이름',
      email: '이메일',
      edit: '수정',
      cancel: '취소',
      providerBlocked: '이 로그인 방식으로는 아직 프로필을 변경할 수 없습니다',
      save: '저장',
      saving: '저장 중…',
      nameInputLabel: '워크스페이스 표시 이름',
      nameSaved: '이름을 저장했습니다.',
      signedOutBlocked: '프로필을 수정하려면 로그인하세요',
    },
    writeOutcome: {
      protectedRejected: '보호된 설정이 변경되지 않았습니다.',
      accepted: '프로젝트 설정이 반영됐습니다.',
    },
    publishing: {
      title: '공개',
      exportsLabel: '산출물 내보내기:',
      exportsNone: '아직 기록 없음',
      exportsCount: '{count}건 기록됨',
      recordsNone: '이 워크스페이스에 저장된 공개 기록이 아직 없습니다.',
      recordsCount: '이 워크스페이스에 공개 기록 {count}건이 저장돼 있습니다.',
      exportSettings: '설정 내보내기',
      exportSettingsBlocked: '아직 사용할 수 없습니다 — 내려받을 설정 내보내기 파일이 없습니다',
      reviewRecords: '기록 확인',
      reviewRecordsBlocked: '아직 기록이 없습니다 — 먼저 공개하거나 내보내 주세요',
    },
    plan: {
      title: '요금제',
      current: '현재:',
      manage: '요금제 관리',
      allowsTitle: '현재 요금제로 할 수 있는 것',
      generation: '생성',
      publication: '공개',
      packPromotion: '팩 승격',
      errorLabel: '요금제 정보를 불러오지 못했습니다',
      lookupFailed: '결제 정보 조회에 실패했습니다',
      loading: '요금제를 확인하는 중…',
      signedOut: '요금제를 보려면 로그인하세요',
      noSubscription: '이용 중인 구독이 없습니다',
      rights: {
        'not-measured': '측정되지 않음',
        'authority-readback-invalid': '권한 판독값이 올바르지 않음',
        'not-admitted': '승인되지 않음',
        'preview-only': '미리보기만 가능',
        'not-admitted-by-source-receipt': '이 소스 영수증으로는 승인되지 않음',
        'preview-admitted': '미리보기 승인됨',
        'same-project-publication-admitted': '같은 프로젝트 안에서의 공개 승인됨',
        'admitted': '승인됨',
      },
    },
    runtime: {
      title: '런타임',
      note: '릴리스 식별과 가용성은 관찰된 사실이며, 권한이 아닙니다.',
      distribution: '배포 형태',
      studioSha: 'Studio 소스 SHA',
      applianceReleaseId: '어플라이언스 릴리스 ID',
      observedAt: '관찰 시각',
      observationsTitle: '가용성 관찰',
      available: '사용 가능',
      unavailable: '사용 불가',
      readFailed: '읽기 실패',
      notConfirmed: '가용성을 확인하지 못함',
      probeFailures: {
        'platform-health-status': '플랫폼 상태 조회에 실패했습니다 ({status})',
        'platform-health-malformed': '플랫폼 상태 응답의 형식이 올바르지 않습니다.',
        'platform-unreachable': '플랫폼 가용성을 확인하지 못했습니다.',
        'capability-response-rejected': '기능 응답이 승인되지 않았습니다 ({status}).',
      },
      capabilities: {
        platformConnection: '연결된 플랫폼',
        localAuth: '로컬 인증',
        googleAuth: 'Google 인증',
        localPublish: '로컬 공개',
        cloudPublish: '클라우드 공개',
        customDomain: '사용자 지정 도메인',
        billing: '결제',
        analytics: '분석',
        externalIntegrations: '외부 연동',
      },
    },
    domain: {
      title: '도메인',
      defaultLabel: '기본:',
      customLabel: '사용자 지정:',
      connect: '도메인 연결',
      blocked: '사용자 지정 도메인은 아직 사용할 수 없습니다',
      signedOut: '워크스페이스 도메인을 보려면 로그인하세요',
    },
    github: {
      title: 'GitHub 연동',
      developer: '개발자',
      connected: '연결됨',
      disconnect: '연결 해제',
      disconnecting: '연결 해제 중…',
      invite:
        'GitHub 계정을 연결하면 소스 코드를 내보내고 CI/CD를 직접 구성할 수 있습니다.',
      connect: 'GitHub 연결',
      connecting: '연결 중…',
      confirmDisconnect: 'GitHub 계정 연결을 해제할까요? 기존 저장소는 삭제되지 않습니다.',
    },
    notifications: {
      title: '알림',
      reason:
        '이메일 알림은 아직 사용할 수 없습니다 — Studio에 발송할 서비스가 없고 설정을 저장할 곳도 없어서 켤 수 없습니다.',
      review: '검토 알림 (이메일)',
      agentSummary: '에이전트 활동 요약 (매일)',
      productUpdates: '제품 소식',
      unavailableSuffix: '— 아직 사용할 수 없습니다',
    },
    danger: {
      title: '위험 구역',
      deleteAccount: '계정 삭제',
      deleteBlocked:
        'Studio에서는 아직 계정 삭제를 지원하지 않습니다 — privacy@cogo.xyz 로 요청해 주세요',
    },
    closedNetwork: {
      planNote:
        '이 설치본은 폐쇄망에서 동작합니다. 이용 범위는 여기서 관리하는 구독이 아니라 설치된 라이선스가 정합니다.',
      planManageBlocked:
        '구독 관리는 이 설치본이 닿을 수 없는 외부 서비스가 담당합니다',
      servedFromLabel: '접속 주소:',
      domainBlocked:
        '사용자 지정 도메인에는 공개 DNS가 필요합니다 — 이 설치본은 자기 주소로만 응답합니다',
      githubTitle: 'GitHub',
      githubBlocked:
        '계정 연결에는 github.com이 필요하지만 이 설치본은 그곳에 닿을 수 없습니다',
      deleteBlocked:
        '폐쇄망 설치본의 계정은 이곳에 보관됩니다 — 이 어플라이언스 관리자에게 요청하세요',
    },
  },
  operations: {
    title: '운영',
    subtitle: '공개한 결과물의 데이터·활동·성과·이용자·계측을 확인합니다.',
    reviewedOutputs: '검토한 결과물',
    acceptanceRate: '수용률',
    sharedArtifacts: '공유한 산출물',
    sharedArtifactsNote: '앱·문서·리포트',
    unmeasured: '아직 측정하지 않았습니다 — Studio에 이 수치의 출처가 없습니다.',
    tiles: {
      products: '소스 묶음, 추출한 행, 준비 상태, 출처 기록.',
      orders: '공개한 결과물의 활동과 기록된 근거.',
      revenue: '조회수, 전환, 반영된 변경, 성과 신호.',
      members: '검토자, 유입 경로, 다시 찾은 방문자.',
      analytics: '출시 후 실제 앱의 계측.',
    },
  },
  requests: {
    title: '요청·검토',
    subtitle: '요청된 변경과 승인·반려, 그리고 함께 남은 근거를 확인합니다.',
    openChat: '대화 열기',
    howItWorks: '요청은 이렇게 진행됩니다',
    stepsSuffix: ' · 3단계',
    rows: {
      inProgress: { title: '진행 중인 요청', body: 'cogo가 준비하고 있는 앱·문서·리포트 변경을 봅니다.', },
      needsApproval: { title: '승인 필요', body: '공개, 출시, 공유 업데이트는 확인하실 때까지 기다립니다.', },
      reviewTrail: { title: '검토 이력', body: '무엇이 바뀌었고, 무엇을 점검했고, 무엇이 공유 준비됐는지 봅니다.', },
      accessControl: { title: '접근 권한', body: '계정과 공개 권한을 현재 요청 범위로만 제한합니다.', },
    },
    current: { title: '현재 요청', body: '공유하거나 출시하기 전에 검토가 필요한 변경을 cogo가 준비할 때 이 화면을 사용합니다.', },
    actions: {
      addSource: '소스 추가',
      library: '라이브러리',
      share: '공유',
      refresh: '새로고침',
    },
    tiles: {
      updates: '업데이트',
      checks: '점검',
      needsApproval: '승인 필요',
    },
    timeline: {
      title: '최근 요청 이력',
      emptyTitle: '진행 중인 요청이 없습니다',
      emptyBody: '대화로 시작하거나 소스를 추가해 보세요. 승인이 필요한 요청이 여기에 나타납니다.',
    },
    approval: {
      title: '확인이 필요합니다',
      waiting: '이 업데이트는 공유되기 전에 확인을 기다리고 있습니다.',
      empty: '기다리는 승인이 없습니다.',
    },
    prepare: {
      title: 'coogo가 준비할 수 있는 것',
      appDraft: '앱 초안',
      sourceImport: '소스 가져오기',
      reviewCheck: '검토 점검',
      launchSetup: '출시 준비',
    },
    requestTitle: {
      appDraft: '앱 초안',
      sourceAdded: '소스 추가됨',
      reviewCheck: '검토 점검',
      shareUpdate: '공유 업데이트',
      launchSetup: '출시 준비',
      requestedChange: '요청된 변경',
    },
    status: {
      ready: '준비됨',
      needsReview: '검토 필요',
      inProgress: '진행 중',
      recorded: '기록됨',
    },
    scope: {
      codeUpdate: '코드 변경',
      designFileUpdate: '디자인 파일 변경',
      publicShare: '공개 공유',
      launchSetup: '출시 준비',
      workspaceUpdate: '워크스페이스 변경',
    },
    entry: {
      reuse: '재사용한 부분',
      fresh: '새로 만든 부분',
      validation: '점검됨',
      approval: '승인',
    },
  },
  share: {
    title: '공유',
    subtitle: '앱·문서·리포트를 공유하면서 소스, 점검 상태, 업데이트 경로를 계속 볼 수 있게 합니다.',
    centerTitle: '공유 센터',
    centerBody:
        '공유한 결과물마다 소스와 검토 상태, 업데이트 경로를 연결해 두어, 처음부터 다시 만들지 않고 계속 개선할 수 있게 합니다.',
    createOutput: '결과물 만들기',
    addSource: '소스 추가',
    sources: '소스',
    settingsDialog: {
      title: '공유 설정',
      body: '{name}의 서브도메인을 변경합니다.',
      subdomainLabel: '서브도메인',
    },
    checklist: {
      publicUrl: '공개 주소',
      readinessCheck: '점검 상태',
      outcomePath: '성과 경로',
      updateControl: '업데이트 관리',
      notShared: '공유하지 않음',
      sharedCount: '{count}건 공유됨',
      nothingSharedYet: '아직 공유한 것이 없습니다',
      readyCount: '{count}건 준비됨',
      needsCheck: '점검이 필요합니다',
      outcomePathValue: '문의, 예약, 내보내기, 결제, 검토 화면',
      updateControlValue: '변경 전에 공유 설정을 확인하세요',
    },
    tiles: {
      sharedOutputs: '공유한 결과물',
      readyChecks: '완료된 점검',
      drafts: '초안',
      latestUpdate: '최근 업데이트',
      noSharedOutput: '공유한 결과물 없음',
    },
    health: {
      title: '공유 상태',
      checked: '점검됨',
      lastRecordedCheck: '마지막으로 기록된 점검',
      needsAttention: '확인 필요',
      draftsNotShared: '초안(공유 안 함)',
      nothingShared: '공유한 것 없음',
      noneYet: '아직 없음',
      never: '없음',
      none: '없음',
      checkedOfShared: '{shared}건 중 {checked}건',
      blockedCount: '{count}건 막힘',
      nothingSharedBadge: '아직 공유한 것이 없습니다',
      nothingSharedDetail:
        '이 워크스페이스에서 아직 아무것도 공유하지 않아, 여기에 보고할 내용이 없습니다. 결과물을 공유하면 점검 기록이 여기에 나타납니다.',
      blockedBadge: '공유한 결과물 {shared}건 중 {blocked}건이 막혀 있습니다',
      blockedBadgeOne:
        '공유한 결과물 {shared}건 중 {blocked}건이 막혀 있습니다',
      blockedDetail: '공유한 결과물 중 점검 기록이 막힌 것이 있습니다. 열어서 무엇이 막고 있는지 확인하세요.',
      uncheckedBadge: '공유한 결과물 {shared}건에 점검 기록이 없습니다',
      uncheckedBadgeOne:
        '공유한 결과물 {shared}건에 점검 기록이 없습니다',
      uncheckedDetail:
        '이 결과물들은 공유되어 있지만 점검 기록이 하나도 없습니다. 하나를 열어 점검을 실행하면 기록이 남습니다.',
      checkedBadge: '공유한 결과물 {shared}건 중 {checked}건 점검됨',
      checkedBadgeOne:
        '공유한 결과물 {shared}건 중 {checked}건 점검됨',
      checkedBadgeLastSuffix: ', 마지막 {date}',
      checkedDetail:
        '이 결과물들을 마지막으로 점검했을 때 기록된 내용입니다. 이 화면을 여는 것만으로는 다시 점검하지 않습니다.',
    },
  },
  library: {
    title: '라이브러리',
    subtitle:
      '이 워크스페이스에서 확인된, 다시 쓸 수 있는 자료와 결과물을 엽니다.',
    importSource: '소스 가져오기',
    tiles: {
      savedWork: '저장한 작업',
      openLinks: '열 수 있는 링크',
      details: '세부 내용',
      launches: '출시',
    },
    searchPlaceholder: '저장한 작업, 소스, 링크 검색…',
    searchResults: '검색 결과',
    savedWorkCount: '저장한 작업 {count}건',
    previewTitle: '라이브러리 미리보기',
    thumbnailPreview: {
      project: '프로젝트 미리보기',
      template: '스타터 미리보기',
      library: '라이브러리 미리보기',
    },
    previewEmptyTitle: '라이브러리 항목을 선택하세요',
    previewEmptyBody:
      '저장한 작업이 여기에 나타나고, 다음에 필요한 동작 — 열기, 공유, 확인, 출시 준비 — 이 함께 표시됩니다.',
    whatYouCanDo: '여기서 할 수 있는 것',
    readyToUse: '사용할 준비',
    noReviewLinks: '아직 검토 링크가 없습니다.',
    selectedDetails: {
      summary: '준비 상태, 검토, 근거, 출시 이력',
      openBeforeReviewLink:
        '편집 가능한 검토 링크를 만들려면 먼저 이 앱을 열어 주세요.',
    },
    empty: {
      title: '아직 라이브러리에 항목이 없습니다',
      body:
        '기획서, 화면 파일, URL, 저장소 변경, 디자인 프레임을 추가해 보세요 — 저장한 항목마다 미리보기와 소스, 출시 동작이 여기에 함께 나타납니다.',
    },
    actions: {
      open: {
        label: '열기',
        detail: '공유하기 전에 앱이나 문서를 확인합니다',
        noLink: '이 항목에는 아직 열 수 있는 링크가 없습니다',
      },
      edit: {
        label: '편집',
        detail: '프로젝트 작업 공간으로 돌아갑니다',
        noWorkspace: '이 항목은 프로젝트 작업 공간과 연결되어 있지 않습니다',
      },
      launch: {
        label: '출시',
        detail: '공개용 패키지를 준비합니다',
        noBundle: '이 항목에는 아직 출시할 저장 파일이 없습니다',
        busy: '{tool} 이(가) 아직 실행 중입니다',
      },
      details: {
        label: '세부 내용',
        detail: '소스 메모와 다음 단계를 확인합니다',
      },
      useAgain: '다시 사용',
      sharePublicly: '공개 공유',
      check: '점검',
      share: '공유',
      linkPr: 'PR 연결',
      prepareLaunch: '출시 준비',
      feedback: {
        check: {
          progress: '앱을 점검하는 중입니다…',
          success: '앱 점검을 기록했습니다.',
          failure: '앱 점검에 실패했습니다.',
        },
        sharePublicly: {
          progress: '공개 공유를 준비하는 중입니다…',
          success: '공개 공유를 기록했습니다.',
          failure: '공개 공유에 실패했습니다.',
        },
        prepareLaunch: {
          progress: '출시를 준비하는 중입니다…',
          success: '출시 준비를 기록했습니다.',
          failure: '출시 준비에 실패했습니다.',
        },
      },
      noSelection: '먼저 항목을 저장하거나 선택해 주세요',
    },
  },
  sources: {
    title: '소스',
    subtitle: '파일, URL, 저장소, 디자인 소스를 이 워크스페이스에 추가합니다.',
    addToLibrary: '라이브러리에 추가',
    howItWorks: '소스를 추가하면 어떻게 되나요',
    flowAriaLabel: '소스에서 워크스페이스까지의 흐름',
    agentIntake: {
      eyebrow: 'Studio 에이전트',
      title: '원하는 결과를 말해 주세요',
      description: '요청을 쓰거나 파일 또는 URL을 주세요. 종류를 먼저 고르지 않아도 Studio가 다음 단계를 준비합니다.',
      placeholder: '예: 고객 지원 현황을 한눈에 보는 대시보드를 만들어 줘',
      attach: '파일 첨부',
      addUrl: 'URL 입력',
      submit: '에이전트에게 맡기기',
      understanding: '에이전트가 이해한 내용',
      nextStep: '다음 단계',
      connected: '최근 및 연결된 소스',
      emptyConnected: '파일이나 URL을 추가하면 이 작업에 연결된 소스가 여기에 표시됩니다.',
      advanced: '고급 가져오기',
      advancedSummary: '소스 유형을 직접 선택하거나 Figma 토큰, 대상, 태그 같은 세부 옵션을 설정합니다.',
      requestUnderstanding: '새 결과를 만들기 위한 요청',
      urlUnderstanding: '참고할 웹 URL',
      codeUnderstanding: '기존 코드 또는 텍스트 파일',
      figmaUnderstanding: '로컬 Figma 디자인 파일',
      documentUnderstanding: '읽고 구조를 확인할 문서',
      nextDescribe: 'Studio가 요청을 정리하고 Library 초안을 준비합니다.',
      nextUrl: '페이지를 참고 소스로 연결하고 Library 초안을 준비합니다.',
      nextCode: '파일 내용을 화면 소스로 읽고 Library 초안을 준비합니다.',
      nextFigma: '디자인을 읽어 페이지와 레이어를 확인합니다.',
      nextDocument: '문서를 읽어 구조와 내용을 확인합니다.',
      currentRequest: '현재 요청',
      selectedFile: '선택한 파일',
      restoredDocument: '브라우저에 보관된 문서',
      connectedDesign: '연결된 Figma 디자인',
      urlHint: '같은 입력창에 전체 URL을 붙여 넣으세요.',
      unsupportedFile: '이 파일 형식은 아직 이 입력창에서 읽을 수 없습니다.',
      activeDesignConflict: '현재 연결된 디자인 소스 때문에 새 URL 작업을 시작할 수 없습니다. 해당 디자인 소스를 열거나 새 프로젝트에서 URL로 시작하세요.',
      librarySaveMissing: 'Library에 저장된 초안을 확인하지 못했습니다. 요청은 이 화면에 그대로 있으니 다시 시도해 주세요.',
    },
    steps: {
      addSource: '소스 추가',
      shape: '다듬기',
      save: '저장',
      share: '공유',
    },
    form: {
      appName: '앱 이름',
      audience: '대상',
      audienceCreator: '크리에이터',
      audienceBusiness: '비즈니스',
      audienceDeveloper: '개발자',
      sourceFieldSuffix: '소스',
      briefPlaceholder: '만들고 싶은 앱·문서·리포트를 여기에 적어 주세요. 대상, 제공할 것, 화면 구성, 입력 양식, 결제, 원하는 말투를 함께 적으면 좋습니다.',
    },
    upload: {
      title: '파일 업로드',
      body: '이미 앱 번들이 있나요? 바로 업로드하면 파일 기반 기록이 라이브러리에 남습니다.',
      button: '앱 번들 업로드',
    },
    groups: {
      fromIdea: '아이디어에서 시작하기',
      bringExisting: '이미 가진 것 가져오기',
    },
    otherWays: {
      title: '다른 방법으로 시작하기',
    },
    chatStart: {
      title: '대화로 시작하기',
      body: '소스를 고르지 않아도 됩니다. 대화를 열고 원하는 것을 설명하세요. 만든 뒤에는 동일한 라이브러리·출시 흐름을 사용합니다.',
    },
    savedWith: {
      title: '함께 저장되는 것',
      openLink: '열 수 있는 링크',
      savedFile: '저장된 파일',
      sourceSnapshot: '소스 스냅샷',
      launchCheck: '출시 점검',
    },
    after: {
      title: '추가하고 나면',
      defaultNote: 'cogo가 소스를 보관해, 어디서 온 것인지 잃지 않고 열고 공유하고 다듬고 출시할 수 있습니다.',
      figmaFileNote: 'cogo는 .fig 증거를 애플리케이션 생성과 분리해 보관해, 소스 커버리지를 먼저 검토할 수 있게 합니다.',
      documentFileNote: '문서는 이 브라우저 안에 남아 새로고침해도 그대로 열려 있으며, 어디로도 전송되지 않습니다.',
      steps: {
        prepared: '작업이 준비됩니다',
        designRead: '디자인을 읽습니다',
        documentRead: '문서를 읽습니다',
        sourceSaved: '소스가 저장됩니다',
        keptInBrowser: '이 브라우저에 보관됩니다',
        mapperOpens: '소스 매퍼가 열립니다',
        structureOpens: '아래에 구조가 열립니다',
        libraryOpens: '다음 할 일과 함께 라이브러리가 열립니다',
      },
    },
    panel: {
      tags: '태그',
      librarySuffix: 'cogo가 소스 메모와 다음 할 일을 붙여 라이브러리 카드로 저장합니다.',
      figmaFileNote: '생성하기 전에 Figma 파일을 읽고 원본은 그대로 보관합니다.',
      documentFileNote: '문서를 이 브라우저 안에서 읽어 구조를 보여 줍니다. 아무것도 업로드되지 않고, 아직 이 문서로 생성하지도 않습니다.',
      figmaTokenLabel: 'Figma 토큰',
      figmaTokenPasteHint: 'Figma 토큰을 붙여넣고 확인하세요.',
      forgetToken: '제거',
      readDesign: '디자인 읽기',
      readDocument: '문서 읽기',
      localFigmaFile: '로컬 Figma 파일',
      localDocument: '로컬 문서',
    },
    help: {
      assistant_prompt: {
        title: '여기에 요청을 적어 주세요.',
        detail: '대상, 제공할 것, 화면 구성, 입력 양식, 결제, 원하는 말투를 함께 적어 주세요.',
        example: '예: 뷰티 크리에이터를 위한 예약 페이지를 만들어 주세요. 서비스 목록, 후기, 인스타그램 링크, 바로 결제로 이어지는 버튼까지 넣어 주세요.',
      },
      url_import: {
        title: '공개된 URL 하나를 붙여넣으세요.',
        detail: 'cogo가 화면과 내용의 참고 자료로 사용하고, 출처 URL은 라이브러리에 남깁니다.',
        example: '예: https://example.com/product-page',
      },
      ide_file: {
        title: '컴포넌트·라우트·화면 파일 하나를 붙여넣으세요.',
        detail: '이미 만든 화면에서 이어서 작업할 때 가장 좋습니다.',
        example: '예: src/features/checkout/checkout-page.tsx',
      },
      github_pr: {
        title: '저장소와 풀 리퀘스트 번호를 붙여넣으세요.',
        detail: 'cogo가 변경 사항과 검토 메모를 함께 보관해 팀과 공유할 수 있게 합니다.',
        example: '예: acme/storefront#128',
      },
      figma_frame: {
        title: 'Figma 프레임 URL 또는 파일/노드 ID를 붙여넣으세요.',
        detail: 'cogo가 디자인 출처를 보관해 변환 품질을 나중에 검토할 수 있습니다.',
        example: '예: https://www.figma.com/design/abc123/Checkout?node-id=12-345',
      },
      figma_file: {
        title: '로컬 .fig 파일을 선택하세요.',
        detail: 'cogo가 파일을 읽고 원본을 그대로 보관하며, 생성 전에 디자인에 무엇이 있는지 보여 줍니다.',
        example: '예: checkout-flow.fig',
      },
      document_file: {
        title: '.docx 또는 .hwpx 파일을 선택하세요.',
        detail: 'cogo가 문서를 읽고 원본을 그대로 보관하며, 생성 전에 문서 구조를 보여 줍니다.',
        example: '예: account-opening-spec.docx',
      },
    },
    refLabels: {
      assistant_prompt: '요약',
      url_import: 'URL',
      ide_file: '파일',
      github_pr: 'GitHub',
      figma_frame: 'Figma',
      figma_file: '.fig',
      document_file: '문서',
    },
    cards: {
      assistant_prompt: {
        label: '말로 설명하기',
        description: '만들고 싶은 앱·문서·리포트를 글로 적습니다.',
        bestFor: '아이디어에서 시작할 때',
        output: '라이브러리 초안',
      },
      url_import: {
        label: '웹사이트 사용',
        description: '페이지 URL을 참고 자료로 가져옵니다.',
        bestFor: '기존 사이트와 참고 자료',
        output: '참고 기반 초안',
      },
      ide_file: {
        label: '코드 붙여넣기',
        description: '컴포넌트·라우트·화면 파일을 가져옵니다.',
        bestFor: '이미 만든 코드가 있을 때',
        output: '화면 초안',
      },
      github_pr: {
        label: '풀 리퀘스트 사용',
        description: '검토할 저장소 변경을 첨부합니다.',
        bestFor: '팀 변경 사항',
        output: '검토 카드',
      },
      figma_frame: {
        label: 'Figma 프레임',
        description: '변환 검토를 위해 디자인 프레임을 가져옵니다.',
        bestFor: '디자인 핸드오프',
        output: '디자인 기반 초안',
      },
      figma_file: {
        label: 'Figma .fig',
        description: '로컬 Figma 파일을 읽고 원본을 그대로 보관합니다.',
        bestFor: '디자인을 먼저 확인할 때',
        output: '디자인 상세',
      },
      document_file: {
        label: 'Word / HWPX',
        description: '기존 .docx 또는 .hwpx를 읽고 원본을 그대로 보관합니다.',
        bestFor: '사양서·가이드·매뉴얼',
        output: '문서 상세',
      },
    },
  },
  chrome: {
    breadcrumb: '현재 위치',
    search: '앱, 문서, 게임, 현황 검색...',
    openNavigation: '탐색 열기',
    openChat: '채팅 열기',
    closeChat: '채팅 닫기',
    notifications: '알림',
    lightMode: '라이트 모드로 전환',
    darkMode: '다크 모드로 전환',
    publish: '게시',
    publishCurrent: '현재 프로젝트 게시',
    openProjectToPublish: '게시할 서버 프로젝트를 여세요',
    publishBlocked: '게시 차단',
    conflict: '충돌',
    saveFailed: '저장 실패',
    saving: '저장 중…',
    unsaved: '저장 안 됨',
    live: '운영 중',
    saved: '저장됨',
    draft: '초안',
    reloadConflict: '다른 세션에서 이 블록을 수정했습니다 — 최신 상태를 다시 불러오세요',
    reload: '다시 불러오기',
    reloading: '불러오는 중…',
    exportSource: '애플리케이션 소스 내보내기',
    openProjectToExport: '내보낼 프로젝트를 여세요',
    exportSuccess: '애플리케이션 소스를 내보냈습니다',
    toggleIntentLoop: '채팅',
    moreControls: '더 보기',
    history: '기록',
  },
  proposal: {
    change: "변경 제안",
    info: "정보",
    warning: "주의",
    critical: "중요",
    before: "변경 전",
    after: "변경 후",
    unchanged: "변경 없음",
    removed: "제거",
    details: "세부 정보",
    close: "닫기",
    whatWillCheck: "coogo가 확인할 항목",
    reviewChecks: "검토 항목",
    reviewBeforeApply: "적용 전 검토",
    attachedProof: "첨부 근거",
    check: "근거",
    notAttached: "첨부 없음",
    readyToUse: "적용 가능",
    reviewFirst: "검토 필요",
    anotherEditPending: "다른 편집이 대기 중입니다.",
    compareConflict: "충돌한 변경 세부 정보 비교",
    keep: "유지",
    revise: "수정",
    reviseAria: "제안한 변경 수정",
    use: "적용",
    reviewNeeded: "검토 필요",
    keepCurrent: "현재안 유지",
    keepCurrentAria: "현재안을 유지하고 이 제안을 종료",
  },
  home: {
    title: "오늘 무엇을 만들거나 바꿀까요?",
    subtitle:
      "소스를 연결하거나 작업 유형을 고르면 coogo가 첫 요청을 함께 정리합니다.",
    startWithChat: "채팅으로 시작",
    workTypes: "작업 유형",
    workTypeHint: "작업 유형을 고르면 컴포저에 시작 문장이 채워집니다.",
    recentWork: "최근 작업",
    reviewNeeded: "확인 필요",
    viewAll: "모두 보기",
    review: "확인하기",
    noWork: "아직 작업이 없습니다.",
    noWorkHint: "위 컴포저에서 첫 작업을 시작하세요.",
    firstProjectAction: "첫 프로젝트 설명하기",
    continueWorking: "이어서 작업하기",
    openWork: "작업 열기",
    continue: "이어가기",
    latestProjectEmpty:
      "실제 프로젝트가 생기면 가장 최근 작업과 검증 상태가 여기에 표시됩니다.",
    readiness: "coogo 준비 상태",
    totalWork: "전체 작업",
    productionVerified: "운영 검증",
    settings: "설정 관리",
    countSuffix: "개",
    readinessSummary:
      "공유 준비 상태: 준비 {ready}개, 확인 {review}개, 차단 {blocked}개.",
    featuredOutcome: {
      eyebrow: "대표 워크플로 · 프리뷰",
      title: "뉴스레터 → 멤버십",
      outcomes: {
        newsletterMembership: { title: "뉴스레터 → 멤버십" },
        reservationOperations: { title: "예약 운영" },
      },
      description:
        "구독자와 콘텐츠를 바탕으로 관리되는 멤버십 서비스 프리뷰를 시작합니다.",
      boundary:
        "프리뷰 후보만 만듭니다. 검증, 발행, 배포, 실제 운영 확인은 각각 별도 단계입니다.",
      action: "이 워크플로 선택",
      clearAction: "선택한 워크플로 해제",
      inProgress: "이 워크플로 요청이 이미 진행 중입니다.",
      prompt:
        '뉴스레터 구독자와 기존 콘텐츠를 회원 온보딩, 보호 콘텐츠, 운영 현황을 갖춘 유료 멤버십 서비스로 전환하고 싶어요.',
    },
    lanes: {
      app: { title: "앱", prompt: "업무에 사용할 앱을 만들고 싶어요." },
      document: {
        title: "문서",
        prompt: "연결한 소스를 바탕으로 문서를 만들고 싶어요.",
      },
      game: {
        title: "게임",
        prompt: "규칙과 상호작용이 분명한 게임을 만들고 싶어요.",
      },
      existing: {
        title: "기존 작업 수정",
        prompt: "기존 작업을 선택해 수정하고 싶어요.",
      },
      starter: {
        title: "Starter에서",
        prompt: "Starter를 골라서 시작하고 싶어요.",
      },
    },
  },
  chat: {
    greeting: (name) => `${name}님, 오늘 무엇을 만들까요?`,
    placeholder: "무엇을 만들거나 바꾸고 싶나요?",
    attach: "파일 · 연결",
    attachLabel: "파일 또는 소스 연결",
    cancel: "요청 취소",
    send: "요청 보내기",
    checkingSession: "Studio 세션 확인 중",
    cancelled: "계획 취소됨",
    ontologySelectionRequired:
      "생성하기 전에 이 프로젝트가 사용할 애플리케이션 패키지를 선택해 주세요.",
    startingNotice:
      "요청에서 시작합니다. coogo가 만들고 있는 것, 다음 변경, 대화를 함께 이어갑니다.",
    appGenerationStarting:
      "프로젝트를 만들고 화면, 동작, 데이터를 준비하는 중입니다...",
    webMcpVerified: (target) =>
      target
        ? `WebMCP로 확인했습니다: ${target}.`
        : "WebMCP로 현재 Studio 컨텍스트를 확인했습니다.",
    readOnlyCompletionFallback:
      "요청한 상태를 응답에서 확인하지 못했습니다. 변경은 수행하지 않았고 승인 대기도 만들지 않았습니다.",
    suggestions: [
      "예약 앱 만들어줘",
      "문서를 검토 가능한 리포트로 바꿔줘",
      "기존 작업을 수정하고 싶어",
    ],
    templateProposal: {
      heading: "이미 준비된 템플릿과 비슷해요",
      startFaster: "템플릿으로 빠르게 시작할 수 있어요.",
      accept: "템플릿으로 만들기",
      accepting: "만드는 중...",
      decline: "자유 생성",
      accepted: "템플릿으로 프로젝트를 만들었어요.",
      declined:
        "자유 생성으로 진행할게요 — 화면, 동작, 데이터를 준비하는 중입니다.",
      unavailable: "이 템플릿 제안은 더 이상 사용할 수 없어요.",
      failure:
        "템플릿으로 만들지 못했어요 — 자유 생성으로 다시 시도할 수 있어요.",
    },
    planningFailure: {
      ontologyBlocked:
        "아직 이 요청을 만들 수 없어요 — 필요한 지식이 라이브러리에 없습니다. 다른 제품 아이디어를 시도하거나, 팀에 이 도메인 추가를 요청해 주세요.",
      planNotActionable:
        "이 앱에 필요한 데이터 연결이 계획에 담기지 않아서 아무것도 만들지 않았어요. 어떤 정보를 읽고 저장해야 하는지 — 예를 들어 멤버가 누구인지, 무엇을 청구하는지 — 함께 적어 다시 보내 주세요.",
      requirementCoverage:
        "요청하신 내용 중 일부가 계획에 담기지 않아서 아무것도 만들지 않았어요. 요청을 짧은 문장으로 나눠서 다시 보내 주세요.",
      generic: "계획 중 문제가 발생했어요. 다시 시도해 주세요.",
    },
    updateExistingWork: {
      noExistingWork:
        "이 워크스페이스에는 아직 저장된 작업이 없어요. 무엇을 만들지 알려주시면 시작할게요.",
      opening: '"{name}"을(를) 여는 중이에요. 무엇을 바꿀지 알려주세요.',
      chooseOne:
        "저장된 프로젝트가 여러 개예요 — {choices}. 어떤 것을 열까요?",
    },
    toolFailure: {
      invalidInput:
        "이 변경에 필요한 내용이 요청에 없어서 아무것도 바꾸지 않았어요. 바꾸고 싶은 내용을 한 문장으로 구체적으로 알려주세요.",
      notPermitted:
        "이 변경은 아직 채팅에서 실행할 수 없어서 아무것도 바꾸지 않았어요.",
      unknownAction:
        "coogo에 아직 그 동작이 없어서 아무것도 바꾸지 않았어요.",
      generic: "변경을 완료하지 못해서 아무것도 바꾸지 않았어요.",
    },
  },
  templateContent: {
    aiFill: "AI로 채우기",
    briefPlaceholder: "예: 활기찬 동네 빵집, 따뜻한 톤",
    filling: "채우는 중...",
    aiUnavailable: "AI 서비스가 아직 구성되지 않았어요.",
    fillFailed: "채우기에 실패했어요.",
    fillBatchProgress: "배치 {current}/{total} 채우는 중...",
    fillPartialNotice: "AI가 {count}개 항목을 채우지 못했어요. 나머지는 채워졌어요.",
    fillAllFailedNotice: "AI가 {count}개 항목을 모두 채우지 못했어요. 아무것도 채워지지 않았어요.",
    judgeChecking: "검토 중...",
    judgeFailed: "검토 결과를 가져오지 못했어요.",
    pass: "통과",
    caution: "주의",
    reasons: "이유",
    hide: "숨기기",
    show: "보기",
    general: "일반",
    changed: "변경됨",
    records: "{count}개 레코드",
    readOnly: "이 버전에서는 수정할 수 없어요 (데이터 파일 미보유).",
    invalidJson: "올바른 JSON이 아니에요.",
    persistError: "일부 변경이 저장되지 못했어요 — 다시 시도해 주세요.",
    applySuccess: "콘텐츠를 적용했습니다.",
    applyFailed: "적용에 실패했어요. 아래 오류를 확인해 주세요.",
    applyFailureSummary: "적용에 실패했어요 ({count}건).",
    applying: "적용 중...",
    apply: "적용 ({count})",
    designRebindTitle: "디자인 릴리스",
    designRebindPresetLoadError: "프리셋을 불러오지 못했어요",
    designRebindPresetPlaceholder: "프리셋 선택",
    designRebindApply: "디자인 적용",
    designRebindApplying: "적용 중...",
    designRebindApplySuccess: "디자인을 적용했습니다",
    designRebindApplyFailed: "디자인 적용에 실패했어요",
  },
  candidateVerification: {
    title: "후보 검증",
    description:
      "이 생성 후보에는 독립 근거 4개와 무결성 캡슐 1개가 필요합니다.",
    authorityNote:
      "인증된 영속 저널의 읽기 결과만 검증 완료 상태를 부여할 수 있습니다.",
    promotionBoundary:
      "후보 근거 검증은 제품 품질, 발행, 배포 또는 실제 운영 확인을 증명하지 않습니다.",
    authorityLabel: "발급 주체: {authority}",
    verifiedStatus: "후보 근거 검증됨",
    publicationGateStatus: "발행은 별도 게이트",
    listLabel: "후보 검증 근거 역할",
    refresh: "검사 새로고침",
    refreshing: "검사 새로고침 중",
    askCoogo: "Coogo에게 묻기",
    liveChecking: "후보 검증 검사를 새로고침하고 있습니다.",
    liveResult: "후보 검증 상태: {status}.",
    statuses: {
      pending: "대기 중",
      checking: "확인 중",
      ready: "준비됨, 미검증",
      verified: "검증됨",
      blocked: "차단됨",
      unavailable: "확인 불가",
    },
    roles: {
      "sufficiency-assessment": "소스 충분성 평가",
      "validator-safety-pass": "Validator 안전성 통과",
      "second-model-judge": "독립 모델 검토",
      "render-execution-receipt": "렌더 실행 영수증",
      "verification-capsule": "검증 캡슐",
    },
    authorities: {
      "source-assessment-authority": "소스 평가 서비스",
      "validator-runtime": "안전성 검증 서비스",
      "independent-model-judge": "독립 모델 검토 서비스",
      "renderer-runtime": "렌더 실행 검증 서비스",
      "publishing-kernel": "발행 무결성 서비스",
    },
    causes: {
      "journal-checking": "영속 검증 저널을 읽고 있습니다.",
      "journal-unavailable": "영속 저널 읽기 결과를 사용할 수 없습니다.",
      "journal-scope-invalid": "영속 저널이 이 후보와 일치하지 않습니다.",
      "journal-contract-invalid": "영속 저널이 계약 검증을 통과하지 못했습니다.",
      "verification-pending": "아직 검증된 영속 이벤트가 없습니다.",
      "producer-receipt-pending":
        "해당 서버 발급 주체의 검증 기록을 기다리고 있으며 아직 검증되지 않았습니다.",
      "verification-incomplete": "검증 이벤트에 필요한 독립 근거가 빠져 있습니다.",
      "source-assessment-current": "현재 평가는 준비되었지만 검증 완료를 뜻하지 않습니다.",
      "source-assessment-insufficient": "현재 평가가 소스 충분성 요구 사항을 충족하지 못합니다.",
      "source-assessment-missing": "현재의 변경 불가 소스 평가가 필요합니다.",
      "source-assessment-stale": "후보의 평가 리비전이 더 이상 최신이 아닙니다.",
      "durable-journal-verified":
        "영속 저널에 독립 근거 4개와 이를 묶는 무결성 캡슐이 기록되었습니다.",
    },
  },
  report: {
    ...en.report,
    title: '현황',
    ontologySubtitle: '온톨로지 커버리지 · 현재 근거와 목표 범위',
    unavailableTitle: '아직 보여줄 현황이 없어요',
    unavailableBody:
      "제품에 검증된 디자인 소스가 연결되면 품질 현황이 여기에 표시됩니다. coogo는 지어낸 점수를 보여주지 않아요.",
    unavailableBodyDeveloperDetail:
      "현재 릴리스에 연결된 최신 검증 결과가 있는 프로젝트를 선택하세요. coogo는 임시 점수를 만들지 않습니다.",
    connectSource: "디자인 소스 열기",
    axesLabel: "온톨로지 커버리지 축",
    axesSummary: "6개 축 · 필수 최저 점수",
    axisMethod:
      "축 값은 필수 차원의 최저 점수를 사용합니다. 근거 시각화이며 권한을 부여하지 않습니다.",
    coverage: "온톨로지 커버리지",
    coverageSubtitle: "현재 근거와 목표 범위",
    lowestRequiredAxis: "필수 축 최저값",
    loadingChart: "온톨로지 커버리지 차트 불러오는 중",
    currentEvidence: "현재 근거",
    targetEnvelope: "목표 범위",
    gap: "격차",
    axisDetails: "축 상세",
    gaps: "격차",
    priority: "우선순위",
    blocks: "차단 항목",
    noTrack: "차단 없음",
    askCoogo: "coogo에게 묻기",
    noOpenRequirements:
      "이 릴리스 연결 assessment에는 열린 온톨로지 요구사항이 없습니다.",
    allDimensions: "application ontology 전체 {count}개 차원",
    dimension: "차원",
    authority: "권한",
    resolution: "해결",
    current: "현재",
    evidence: "근거",
    none: "없음",
    status: {
      ready: "준비됨",
      warn: "주의",
      blocked: "차단됨",
      missing: "아직 없음",
      missingTooltip: "검증된 증거가 아직 없어요",
    },
    conversionProvenance: {
      title: "원본 대비",
      sourceLabel: "전환한 원본",
      capturedLabel: "읽어들인 시각",
      blocksLabel: "읽어낸 화면 블록",
      decisionsLabel: "기록된 전환 결정",
      realizationLabel: "앱에 반영된 선언",
      realizationAbsent:
        "기록된 결정 중 구조적으로 확인할 수 있는 것이 없어 이행 수치가 없습니다. coogo는 수치를 지어내지 않아요.",
      sourceKindLegacyScreen: "붙여넣은 레거시 화면",
    },
  },
  guide: {
    title: "가이드 작업 공간",
    documents: "가이드",
    noPageSelected: "선택한 가이드 페이지가 없습니다.",
    page: "페이지",
    pageActivity: "페이지 활동",
    proposals: "제안",
    applied: "적용",
    exports: "내보내기",
    pages: "페이지",
    revision: "리비전",
    addPage: "페이지 추가",
    pageTitle: "페이지 제목",
    openingEditor: "가이드 편집기 여는 중...",
    pageActions: "페이지 작업",
    reviewPage: "페이지 검토",
    proposeEdit: "수정 제안",
    editContent: "내용 편집",
    move: "이동",
    delete: "삭제",
    draft: "초안",
    export: "내보내기",
    readback: "검증 결과",
    output: "출력",
    notRequested: "요청 전",
    noExportRequested: "현재 가이드에 요청된 내보내기가 없습니다.",
  },
  connectedDocument: {
    notConnected: "연결 안 됨",
    connectedToTemplate: "{provider}에 연결됨",
    documentReference: "문서 링크 또는 이름",
    loadDocument: "문서 불러오기",
    loadErrorNotConnected:
      "아직 문서 연결이 설정되지 않았어요. 팀에 문서 연결 설정을 요청해 주세요.",
    loadErrorGeneric: "문서를 불러오지 못했어요. 다시 시도해 주세요.",
    panelTitle: "연결된 문서",
    provider: "제공자",
    activeSourceSnapshot: "활성 소스 스냅샷",
    connectionReference: "연결 참조",
    documentIdLabel: "문서 ID",
    pageIdLabel: "페이지 ID",
    fetchVerifiedSnapshot: "검증된 스냅샷 가져오기",
    saveProjectFirst:
      "제공자 문서를 연결하기 전에 이 프로젝트를 먼저 저장하세요.",
    unsavedDraftBlocker:
      "연결된 문서 스냅샷을 가져오기 전에 저장하지 않은 WordGard 초안을 마무리하거나 제안하세요.",
    readbackSummary: (blocks, unsupported, accessMode) =>
      `블록 ${blocks}개 · 미지원 ${unsupported}개 · ${accessMode}`,
    accessMode: { "read-only": "읽기 전용", "read-write": "읽기·쓰기" },
    openSource: "원본 열기",
    newPageParentId: "새 페이지의 상위 ID (선택)",
    publishApproval:
      "제공자 쪽에 새 사본 하나를 만드는 데 동의합니다. 원본 문서는 변경되지 않습니다.",
    publishAction: "새 사본 만들고 검증",
    publishBlockedUnsupported:
      "지원되지 않는 제공자 요소가 해결될 때까지 게시가 차단됩니다.",
    connectionReadOnly:
      "이 연결은 읽기 전용입니다. 사본을 게시하려면 제공자 쓰기 권한으로 다시 연결하세요.",
    proofLabel: "새 사본 증적:",
    proofStatus: { pass: "통과", fail: "실패", blocked: "차단됨" },
    structure: "구조",
    structureReadBack: "다시 읽음",
    structureMissing: "없음",
    exportsReadBack: (count) => ` · 내보내기 ${count}건 다시 읽음`,
    openNewCopy: "새 사본 열기",
    publishVerified: (provider, versionRef) =>
      `새 ${provider} 사본을 ${versionRef}에서 확인했습니다.`,
    publishBlockedBeforeVerified:
      "제공자 사본이 검증되기 전에 게시가 차단되었습니다.",
    publishProofFailed:
      "새 사본이 만들어졌을 수 있으나, 다시 읽기 증적이 통과하지 못했습니다.",
    publishGenericError: "연결된 문서 사본을 게시하지 못했습니다.",
  },
  behavior: {
    ...en.behavior,
    navigation: "ActionFlow 탐색",
    title: "액션플로우",
    selectFlow: "이벤트 또는 플로우 선택",
    unsaved: "저장 안 됨",
    target: "동작 대상",
    noElement: "선택한 요소 없음",
    selectInPreview: "미리보기에서 선택",
    section: "섹션",
    status: "상태",
    bindings: "연결",
    flows: "플로우",
    empty: "비어 있음",
    steps: "단계",
    verified: "검증됨",
    failed: "실패",
    notVerified: "미검증",
    noFlows: "이 프로젝트에 선언된 플로우가 없습니다.",
    eventBindings: "이벤트 연결",
    connected: "연결됨",
    orphan: "고립",
    inspectElement: "동작을 확인할 앱 요소를 선택하세요.",
    noBindings: "이 프로젝트에 선언된 이벤트 연결이 없습니다.",
    chatActions: "채팅 작업",
    reviewFlow: "플로우 검토",
    addStep: "단계 추가",
    connectEvent: "이벤트 연결",
    testFlow: "플로우 테스트",
    editor: "동작 편집기",
    unsavedFlow: "저장 안 된 플로우",
    noBlocks: "아직 블록이 없어 flow를 저장할 수 없습니다",
    testRun: "테스트 실행",
    closeFlowForm: "플로우 양식 닫기",
    newFlow: "새 플로우",
    addBlockFirst: "플로우를 만들기 전에 페이지 블록을 추가하세요",
    nodeTypes: "지원하는 ActionFlow 노드 유형",
    nodes: "노드",
    executionModel: "실행 모델",
    binding: "연결",
    direct: "직접",
    directAtomic: "직접 atomic",
    namedFlow: "이름 있는 플로우",
    lanes: "영역",
    actionLane: "Action",
    uiLane: "UI",
    motionLane: "Motion",
    order: "순서",
    completionBarrier: "완료 대기",
    stage: "Stage",
    saga: "Saga",
    sagaCompensation: "Saga는 역순 보상 · ACID 아님",
    palette: {
      ifElse: "조건 분기",
      loop: "루프",
      switch: "스위치",
      parallel: "병렬",
      tryCatch: "예외 처리",
    },
    flowCreateFailed: (name) =>
      `플로우 "${name}"이(가) 만들어지지 않았습니다 — 서버가 거부해서 로컬 변경을 되돌렸습니다`,
    flowCreated: (name) => `플로우 "${name}"을(를) 만들었습니다`,
    flowCreatedLocalOnly: (name) =>
      `플로우 "${name}"을(를) 이 세션에만 만들었습니다 — 아직 서버에 저장되지 않았습니다`,
  },
  approval: {
    categoryFlows: "플로우",
    categoryDatasources: "데이터소스",
    categoryVariables: "변수",
    categoryEvents: "이벤트 연결",
    compositeActionFallback: "묶음 작업",
    operationCount: (count) => `작업 ${count}개`,
    applyAll: "모두 적용",
    applySelected: (selected, total) => `적용 (${selected}/${total})`,
    dismiss: "취소",
    applied: "적용됨",
    pendingValidation: (reason) =>
      `검증 대기 중${reason ? `: ${reason}` : ""}`,
    dismissed: "취소됨",
    edit: "편집",
  },
  insertPanel: {
    noActivePage: "열린 페이지가 없습니다",
    searchPlaceholder: "UI 검색",
    maxChildrenReached: (current, max) =>
      `자식 요소 최대치에 도달했습니다 (${current}/${max})`,
    fromLibrary: "라이브러리에서",
    createManually: "직접 만들기",
    detailCreateOrOpenPage: "UI를 추가하기 전에 페이지를 만들거나 열어 주세요.",
    detailNeedsBlockTarget: "새 요소를 넣을 블록이 필요합니다.",
    detailAppendsToBlock: "이 블록의 끝에 요소를 추가합니다.",
    detailInside: (target) => `${target} 안에`,
    detailAfterSelectionIn: (target) => `${target}에서 선택한 요소 뒤에`,
    targetLabel: "대상",
    newBlock: "새 블록",
    noLibraryBlocks: "아직 재사용할 수 있는 블록이 없습니다",
    moreBlocks: (count) => `블록 ${count}개 더 보기`,
    noElementsFound: "요소를 찾을 수 없습니다",
  },
  dataExplorer: {
    datasourceCreated: (name) => `데이터소스 "${name}"을(를) 만들었습니다`,
    datasourceNameTaken: (name, blockName) =>
      `"${blockName}" 블록에 이미 "${name}" 데이터소스가 있습니다. 아무것도 바뀌지 않았습니다 — 다른 이름을 쓰거나 기존 데이터소스를 편집하세요.`,
    datasourceCreateFailed: (name, blockName) =>
      `"${name}"을(를) 만들지 못했습니다 — "${blockName}" 블록을 찾을 수 없습니다(삭제되었거나 이름이 바뀌었나요?). 아무것도 바뀌지 않았습니다.`,
  },
  routeHeadings: {
    map: "앱 맵",
    admin: "관리자",
    billing: "결제",
    themeEditor: "테마 편집기",
    newProject: "무엇을 만들까요?",
  },
  dataInspector: {
    header: {
      title: "항목 상세",
      reviewEntity: "항목 검토",
    },
    fields: {
      name: "이름",
      block: "블록",
      scope: "범위",
      type: "유형",
      defaultValue: "기본값",
      expression: "표현식",
      path: "경로",
      importSource: "가져온 위치",
      call: "호출",
      method: "메서드",
      kind: "종류",
      endpoint: "엔드포인트",
      resultTarget: "결과 대상",
      headers: "헤더",
      body: "본문",
      source: "원본",
      eventKey: "이벤트 키",
      component: "컴포넌트",
      event: "이벤트",
      flow: "플로우",
      trigger: "트리거",
      steps: "단계",
      usedBy: "참조",
    },
    placeholders: {
      noDefault: "기본값 없음",
      expression: "예: #{otherVar} + 1",
      resultTarget: "이 데이터소스가 채우는 상태 변수",
      body: "요청 본문 템플릿",
    },
    emptyStates: {
      selectEntity: "살펴볼 항목을 선택하세요",
      entityNotFound: "항목을 찾을 수 없습니다",
      variableNotFound: "변수를 찾을 수 없습니다",
      datasourceNotFound: "데이터소스를 찾을 수 없습니다",
      eventNotFound: "이벤트를 찾을 수 없습니다",
      flowNotFound: "플로우를 찾을 수 없습니다",
    },
    actions: {
      testInExpressionLab: "표현식 랩에서 테스트",
      writeExpressionInLab: "표현식 랩에서 표현식 작성",
      openInFlowEditor: "플로우 편집기에서 열기",
      deleteVariable: "변수 삭제",
      deleteDatasource: "데이터소스 삭제",
      deleteEvent: "이벤트 삭제",
    },
    appManifest: {
      scopeValue: "앱 매니페스트",
      note: "앱 매니페스트 데이터소스는 프로젝트 전체가 공유하는 입력입니다. 블록 단위 바인딩은 블록 데이터소스나 코드 화면에서 편집하세요.",
    },
    schema: {
      request: "요청 스키마",
      response: "응답 스키마",
    },
    flowUnbound: "-- 연결 안 됨 --",
    stepCount: (count) => `${count}단계`,
    validationSummary: (errors, warnings) =>
      `오류 ${errors}건, 경고 ${warnings}건`,
    referencedWarning:
      "이 변수는 다른 곳에서 참조하고 있습니다. 삭제하면 표현식이 깨질 수 있습니다.",
    toasts: {
      variableDeleted: (name) => `변수 "${name}"을(를) 삭제했습니다`,
      datasourceDeleted: (name) => `데이터소스 "${name}"을(를) 삭제했습니다`,
      eventRemoved: (name) => `이벤트 "${name}"을(를) 제거했습니다`,
      bindingsUpdated: (count, oldPath, newPath) =>
        `바인딩 ${count}건을 업데이트했습니다: ${oldPath} -> ${newPath}`,
      bindingsUpdateFailed:
        "바인딩 업데이트에 실패했습니다. 이전 필드 이름을 참조하는 일부 항목은 직접 고쳐야 할 수 있습니다.",
    },
  },
  dataValidation: {
    confirm: {
      deletePrompt: (name) => `"${name}"을(를) 삭제할까요?`,
      typeChangePrompt: (name) => `"${name}"의 유형을 변경할까요?`,
      deleteAction: "삭제",
      typeChangeAction: "유형 변경",
      cancel: "취소",
      unnamedField: "이름 없는 필드",
      nestedFieldWarning: (count) =>
        `이 필드에 속한 하위 필드 ${count}개와 그 안의 내용도 함께 삭제됩니다.`,
    },
    messages: {
      unmatchedBrackets: "#{} 괄호의 짝이 맞지 않습니다",
      emptyExpression: "표현식 #{}가 비어 있습니다",
      duplicateVariableName: (name, blockName) =>
        `블록 "${blockName}"에 변수 이름 "${name}"이(가) 중복됩니다`,
      variableUnreferenced: (name) =>
        `변수 "${name}"을(를) 참조하는 플로우나 표현식이 없습니다`,
      flowMissing: (flowName) => `플로우 "${flowName}"이(가) 존재하지 않습니다`,
      restDatasourceNoEndpoint: "REST 데이터소스에 엔드포인트 URL이 없습니다",
    },
  },
  proofHealth: {
    verified: "검증됨",
    needsReview: "확인 필요",
    needsLiveCheck: "라이브 점검 필요",
    blocked: "차단됨",
    none: "증거 없음",
    checkedLive: (date) => `${date} 라이브 확인`,
    dateUnrecorded: "기록됨",
    liveCheckBlocked: "라이브 점검이 차단되었습니다.",
    liveCheckNeedsReview: "라이브 점검을 확인해야 합니다.",
    recordBlocked: "증거 기록이 차단되었습니다.",
    recordNeedsReview: "증거 기록을 확인해야 합니다.",
    openDeployToRun: "배포에서 라이브 점검을 실행하세요.",
    reviewEvidence: "증거 확인",
    reviewShare: "공유 확인",
  },

  // ── lane/kodash · map · projects · apps-live · import ────────────────────
  // Four screens whose readable surface was English on 2026-08-20, harvested
  // by `__tests__/readable-surface-localized.test.tsx` (33 + 21 + 16 + 14
  // word-entries). Appended at the END of every table on purpose: two sibling
  // lanes were editing the sections above at the same time, and the tail is
  // the one place four concurrent additions cannot collide.
  //
  // EVERY key below is spelled out in all five tables. `ko`, `ja` and `zh`
  // open with `...en` and `zhTW` with `...zh`, so a key added to `en` alone
  // is INHERITED rather than missing — and because the residue walk derives
  // its keep-Latin allowlist from the translated tables, inherited English
  // allowlists its own words and the walk goes silent. Measured, not assumed:
  // mutation M5 in the register test deletes one `ko` override here and the
  // residue walk stays green. The inheritance gate is what turns it red.
  appMap: {
    description: "cogo 앱 안의 화면, 블록, 액션, 상태, 데이터 연결을 살펴봅니다.",
    emptyTitle: "앱을 열면 구조를 지도로 볼 수 있습니다",
    emptyBody:
      "앱 맵은 프로젝트나 가져온 소스, 생성된 라이브러리 항목을 불러온 뒤부터 쓸모가 있습니다. 출시 전에 화면과 데이터, 액션플로우 단계가 어떻게 이어지는지 보여 줍니다.",
    createApp: "앱 만들기",
    importSource: "소스 가져오기",
    openProjects: "작업 열기",
    noAppLoaded: "불러온 앱 없음",
    nodesUnit: "노드",
    edgesUnit: "연결",
    nodeTypes: {
      app: "앱",
      page: "화면",
      block: "블록",
      atom: "디자인 요소",
      flow: "플로",
      stateVar: "상태 변수",
      datasource: "데이터 소스",
    },
    stats: { title: "통계", edges: "연결" },
    legend: {
      title: "범례",
      filterLabel: "노드 종류로 거르기",
      clearFilter: "필터 지우기",
    },
    nodeDetail: {
      title: "노드 상세",
      empty: "노드를 누르면 자세히 볼 수 있습니다",
      incomingUnit: "들어옴",
      outgoingUnit: "나감",
    },
    sessions: {
      title: "세션",
      body: "브레인스토밍 관찰 기록은 3단계에서 제공됩니다. 이 패널은 지금 열려 있는 앱의 선택·보류·디자인 시스템 수정 내역을 다시 보여 주게 됩니다.",
    },
    canvas: {
      controls: "보기 조절",
      zoomIn: "확대",
      zoomOut: "축소",
      fitView: "화면에 맞추기",
      interactive: "조작 잠금 전환",
      miniMap: "미니 지도",
      handle: "연결점",
      nodeDescription:
        "엔터나 스페이스를 누르면 노드를 선택합니다. 그다음 방향키로 노드를 옮길 수 있습니다. 지우려면 delete, 취소하려면 esc를 누르세요.",
      nodeKeyboardDisabled:
        "엔터나 스페이스를 누르면 노드를 선택합니다. 방향키로 노드를 옮길 수 있습니다. 지우려면 delete, 취소하려면 esc를 누르세요.",
      nodeMoved: (direction, x, y) => `선택한 노드를 ${direction} 방향으로 옮겼습니다. 새 위치 x: ${x}, y: ${y}`,
      edgeDescription:
        "엔터나 스페이스를 누르면 연결선을 선택합니다. 그다음 지우려면 delete, 취소하려면 esc를 누르세요.",
    },
  },
  projectCard: {
    status: {
      published: "공유 중",
      draft: "초안",
      archived: "보관됨",
      active: "진행 중",
    },
    share: "공유",
    shareNamed: (name) => `${name} 공유`,
    openActions: (name) => `${name} 작업 메뉴 열기`,
    rename: "이름 바꾸기",
    duplicate: "복제",
    settings: "설정",
    delete: "삭제",
  },
  outputsSection: {
    heading: "결과물",
    breakdown: (shared, drafts) => `공유 ${shared}건 · 초안 ${drafts}건`,
    loading: "결과물 불러오는 중…",
    emptyTitle: "아직 결과물이 없습니다",
    emptyBody:
      "앱, 문서, 리포트를 만들거나 가져오면 여기에 표시됩니다. 처음에는 초안으로, 공유하면 공유한 결과물로 바뀝니다.",
    showMore: (count) => `결과물 ${count}건 더 보기`,
  },
  launchLoop: {
    addSource: "아이디어, 파일, URL, 저장소, 디자인",
    shape: "cogo가 작업을 준비합니다",
    save: "소스와 다음 단계를 함께 보관합니다",
    share: "열기, 공유, 출시",
  },
  // ─── lane/kobiz: the nine business screens ───────────────────────────
  businessScreens: {
    review: {
      tryAgain: '다시 시도',
      openSurface: (surface) => `${surface} 열기`,
      viewSurface: (surface) => `${surface} 보기`,
      tableEmpty: '표시할 데이터가 없습니다',
      periodAll: '전체',
      observation: {
        measuredSummary: '측정된 검토 데이터입니다.',
        emptySummary: '검토 관측 상세',
        observedAt: (stamp) => `관측 시각 ${stamp}`,
        evidence: '근거:',
      },
      pagination: {
        first: '첫 페이지',
        previous: '이전 페이지',
        next: '다음 페이지',
        last: '마지막 페이지',
      },
      status: {
        active: '사용 중',
        draft: '초안',
        archived: '보관됨',
        pending: '대기',
        processing: '처리 중',
        completed: '완료',
        refunded: '환불됨',
        shipping: '배송 중',
      },
    },
    data: {
      description: '앱·문서·리포트의 소스 묶음, 추출한 필드, 준비 상태, 출처 기록.',
      addSource: '소스 추가',
      loadFailed: '지금은 소스를 불러오지 못했습니다.',
      loading: '소스 검토 데이터를 불러오는 중…',
      empty: '이 워크스페이스에서 승인된 소스 묶음이 관측되지 않았습니다.',
      scope: '소스 묶음 검토',
      summaryLabel: '소스 요약',
      tiles: { sources: '소스', ready: '준비됨', drafts: '초안' },
      columns: {
        name: '소스',
        artifact: '산출물',
        confidence: '신뢰도',
        rows: '행 수',
        status: '상태',
      },
      readyStatus: '준비됨',
      filters: {
        artifact: '산출물',
        allArtifacts: '전체 산출물',
        status: '상태',
        all: '전체',
        ready: '준비됨',
        draft: '초안',
        archived: '보관됨',
      },
      unavailable: {
        'producer-not-configured': {
          summary: '소스 검토 데이터 생산자 연결이 설정되지 않았습니다.',
          title: '소스 검토 연결 설정하기',
          detail: '워크스페이스 관리자가 승인된 소스 검토 데이터 생산자를 연결해야 합니다. 그동안에도 소스 묶음은 계속 추가할 수 있습니다.',
        },
        'metric-not-supported': {
          summary: '현재 검토 계약은 이 소스 지표를 측정하지 않습니다.',
          title: '지원되는 소스 검토 항목 사용하기',
          detail: '승인된 소스 묶음 항목으로 계속 진행하세요. 이 지표는 현재 검토 계약으로는 보고할 수 없습니다.',
        },
        'authority-not-admitted': {
          summary: '현재 소스·권리 권한이 이 소스 데이터의 측정을 승인하지 않습니다.',
          title: '소스 권한 검토하기',
          detail: '측정을 요청하기 전에 소스 권리와 승인 근거를 확인하세요.',
        },
      },
    },
    activity: {
      description: '산출물을 공유한 뒤의 요청된 변경, 검토 상태, 팀 활동을 추적합니다.',
      exportLabel: '내보내기',
      exportAria: (count) => `활동 ${count}건을 CSV로 내보내기`,
      exportBlockedAria: (reason) => `내보내기 — ${reason}`,
      blocked: {
        notLoaded: '활동 내역을 불러오지 못했습니다',
        none: '아직 내보낼 활동이 없습니다',
        loading: '활동 내역을 불러오는 중입니다',
        unavailable: '활동 측정을 사용할 수 없습니다',
      },
      exported: (count, filename) => `${count}건을 ${filename} 파일로 내보냈습니다`,
      loadFailed: '지금은 활동 내역을 불러오지 못했습니다.',
      loading: '활동 검토 데이터를 불러오는 중…',
      empty: '이 워크스페이스에서 기록된 활동이 관측되지 않았습니다.',
      scope: '공개한 결과물의 활동 검토',
      summaryLabel: '활동 요약',
      tiles: { pending: '대기', applied: '반영됨', reviewed: '검토됨' },
      tabs: {
        all: '전체',
        pending: '대기',
        applied: '반영됨',
        reviewed: '검토됨',
        rejected: '반려됨',
      },
      columns: {
        actor: '수행자',
        artifact: '산출물',
        changes: '변경',
        state: '상태',
        updated: '갱신',
      },
      changeCount: (count) => `변경 ${count}건`,
      unavailable: {
        'producer-not-configured': {
          summary: '활동 검토 데이터 생산자 연결이 설정되지 않았습니다.',
          title: '활동 검토 연결 설정하기',
          detail: '워크스페이스 관리자가 승인된 활동 검토 데이터 생산자를 연결해야 합니다. 설정하는 동안에도 작업 공유는 계속할 수 있습니다.',
        },
        'metric-not-supported': {
          summary: '현재 검토 계약은 이 활동 지표를 측정하지 않습니다.',
          title: '지원되는 활동 관측 사용하기',
          detail: '지원되는 검토 이벤트로 계속 진행하세요. 이 지표는 현재 활동 검토 계약의 범위 밖입니다.',
        },
        'authority-not-admitted': {
          summary: '현재 소스·권리 권한이 이 활동의 측정을 승인하지 않습니다.',
          title: '활동 권한 검토하기',
          detail: '측정을 요청하기 전에 이 활동에 대한 소스 권리와 승인 근거를 확인하세요.',
        },
      },
      drawer: {
        title: (id) => `활동 #${id}`,
        close: '닫기',
        process: '처리',
        refund: '되돌리기',
        customer: '수행자:',
        email: '이메일:',
        app: '앱:',
        date: '일시:',
        items: '항목',
        noItems: '이 기록에는 항목이 없습니다.',
        subtotal: '소계',
        shipping: '배송',
        discount: '할인',
        total: '합계',
      },
    },
    impact: {
      description: '공유한 cogo 산출물 전체의 조회수, 검토 전환, 반영된 변경, 성과 신호.',
      loadFailed: '지금은 성과 검토 데이터를 불러오지 못했습니다.',
      loading: '성과 검토 데이터를 불러오는 중…',
      empty: '이 기간에 대해 공식 성과 검토에서 관측된 내용이 없습니다.',
      scope: '선택한 성과 검토 기간',
      columns: {
        artifact: '산출물',
        views: '조회수',
        reviews: '검토',
        cvr: '전환율',
      },
      seriesViews: '조회수',
      trendTitle: '성과 추이',
      byArtifact: '산출물별 성과',
      unavailable: {
        'producer-not-configured': {
          summary: '성과 검토 데이터 생산자 연결이 설정되지 않았습니다.',
          title: '성과 검토 연결 설정하기',
          detail: '워크스페이스 관리자가 승인된 성과 검토 데이터 생산자를 연결해야 합니다. 설정하는 동안에도 산출물 공유는 계속할 수 있습니다.',
        },
        'metric-not-supported': {
          summary: '현재 검토 계약은 이 성과 지표를 측정하지 않습니다.',
          title: '지원되는 성과 관측 사용하기',
          detail: '지원되는 성과 신호로 계속 진행하세요. 이 지표는 현재 성과 검토 계약의 범위 밖입니다.',
        },
        'authority-not-admitted': {
          summary: '현재 소스·권리 권한이 이 성과의 측정을 승인하지 않습니다.',
          title: '성과 권한 검토하기',
          detail: '측정을 요청하기 전에 성과 신호 뒤에 있는 소스 권리와 승인 근거를 확인하세요.',
        },
      },
    },
    audience: {
      description: '공개한 앱의 이용자 증가, 유입 경로, 재방문자를 확인합니다.',
      loadFailed: '지금은 이용자 검토 데이터를 불러오지 못했습니다.',
      loading: '이용자 검토 데이터를 불러오는 중…',
      empty: '이 워크스페이스에 대해 공식 이용자 검토에서 관측된 내용이 없습니다.',
      scope: '이용자 검토',
      kpis: { reviewers: '검토자', new7d: '신규 (7일)', returnRate: '재방문율' },
      rolesTitle: '이용자 역할',
      recentReviewers: '최근 검토자',
      columns: {
        name: '이름',
        email: '이메일',
        role: '역할',
        joined: '참여일',
      },
      unavailable: {
        'producer-not-configured': {
          summary: '이용자 검토 데이터 생산자 연결이 설정되지 않았습니다.',
          title: '이용자 검토 연결 설정하기',
          detail: '워크스페이스 관리자가 승인된 이용자 검토 데이터 생산자를 연결해야 합니다. 설정하는 동안에도 산출물 공유는 계속할 수 있습니다.',
        },
        'metric-not-supported': {
          summary: '현재 검토 계약은 이 이용자 지표를 측정하지 않습니다.',
          title: '지원되는 이용자 관측 사용하기',
          detail: '지원되는 검토 참여 신호로 계속 진행하세요. 이 지표는 현재 이용자 검토 계약의 범위 밖입니다.',
        },
        'authority-not-admitted': {
          summary: '현재 소스·권리 권한이 이 이용자 정보의 측정을 승인하지 않습니다.',
          title: '이용자 권한 검토하기',
          detail: '측정을 요청하기 전에 이용자 관측에 대한 소스 권리와 승인 근거를 확인하세요.',
        },
      },
    },
    analytics: { eyebrow: '앱 계측', body: '공유한 결과물을 열어 유입, 전환, 성과 신호를 확인하세요. 계측은 검토된 결과물에 연결되어 있어, 관계 없는 홈 화면으로 이동하지 않습니다.' },
    requestFlow: {
      prepared: { title: '요청이 준비됩니다', body: 'cogo가 앱·문서·리포트의 변경을 준비합니다.' },
      attached: { title: '검토 근거가 함께 남습니다', body: '소스·화면·공유 상태가 요청과 함께 남습니다.' },
      approval: { title: '공유는 승인 후에', body: '공개와 출시 업데이트는 검토를 마칠 때까지 기다립니다.' },
    },
    themeEditor: {
      badge: '디자인 시스템',
      description: 'Studio에서 바로 쓸 수 있는 테마 축을 조정하고, 실제 cogo 컴포넌트 블록으로 결과를 확인한 뒤 라이브러리에 저장하세요.',
      livePreviewNote: '실시간 미리보기는 cogo 렌더러에 전달되는 것과 같은 페이로드를 사용합니다.',
    },
    billing: {
      currentPlan: '현재 요금제',
      currentPlanNote: '이용 중인 구독의 상세 내용',
      cancelsAtPeriodEnd: '현재 주기 종료 시 해지',
      cycle: '결제 주기',
      amount: '금액',
      started: '시작일',
      nextBilling: '다음 결제',
      changePlan: '요금제 변경',
      cancelSubscription: '구독 해지',
      cancelling: '해지하는 중…',
      viewPlans: '요금제 보기',
      paymentHistory: '결제 내역',
      paymentHistoryNote: '전체 결제 내역',
      paymentsSignedOut: '결제 내역을 보려면 로그인하세요',
      paymentsFailed: '결제 내역을 불러오지 못했습니다',
      paymentsEmpty: '결제 내역이 없습니다',
      columns: {
        date: '일자',
        amount: '금액',
        status: '상태',
        provider: '결제 수단',
        reference: '참조',
      },
      status: {
        active: '이용 중',
        paid: '결제됨',
        pending: '대기',
        cancelled: '해지됨',
        failed: '실패',
      },
        statusUnknown: (value) => `알 수 없는 상태 (${value})`,
      cancelDialog: {
        title: '구독을 해지할까요?',
        body: (periodEnd) => `현재 결제 주기가 끝나는 ${periodEnd}까지는 구독이 유지됩니다. 그 이후에는 청구되지 않습니다.`,
        keep: '구독 유지',
        confirm: '해지하기',
      },
    },
    admin: { accessDeniedTitle: '접근 권한 없음', accessDeniedBody: '이 페이지에 접근하려면 관리자 권한이 필요합니다.' },
  },
  // ─── end lane/kobiz ─────────────────────────────────────────────────
};

const ja: StudioDictionary = {
  ...en,
  // ── lane/koentry ─────────────────────────────────────────────────────────
  newProject: JA_NEW_PROJECT_COPY,
  starters: JA_STARTERS_COPY,
  // ── end lane/koentry ─────────────────────────────────────────────────────
  designSystemAxes: JA_DESIGN_AXES_COPY,
  language: { label: "言語" },
  a11y: {
    skipToMainContent: "本文へスキップ",
    dashboardContent: "ダッシュボード本文",
    loadingSurface: (surface) => `${surface}を読み込み中…`,
    creatorNav: "ナビゲーション",
    header: "ヘッダー",
    designSystemSurfaces: "デザインシステムの画面",
    designSystemInventory: "このワークスペースのデザインシステム",
    importSources: "取り込み元",
    librarySummary: "ライブラリの概要",
    operations: "運用",
    requestReviewFlow: "依頼レビューの流れ",
    reviewSummary: "レビュー概要",
    shareReadinessChecklist: "共有準備チェックリスト",
    appPreviewFrame: "coogo アプリのプレビュー",
    entryBrandHome: "coogo ホーム",
    entryReport: "coogo 運用レポート",
    showSection: (name) => `${name}を表示`,
    hideSection: (name) => `${name}を非表示`,
  },
  ide: {
    undo: "元に戻す",
    redo: "やり直す",
    appPageList: "アプリのページ一覧",
    documentList: "ドキュメント一覧",
    viewportSwitcher: "表示サイズの切り替え",
    device: {
      mobile: "モバイル",
      tablet: "タブレット",
      desktop: "デスクトップ",
      document: "ドキュメント",
      a4Portrait: "A4 縦",
      framed: (label, width, height) => `${label} (${width}x${height})`,
      sized: (label, width, height) => `${label} · ${width}x${height}px`,
    },
    pages: {
      title: "ページ",
      summaryWithPages: (count) => `プレビュー + ${count}ページ`,
      summaryPreviewOnly: "プレビュー",
      runnableTitle: "プレビュー",
      runnableBadge: "実行",
      runnableDetail: "実行画面",
      emptyTitle: "現在の画面",
      emptyBody: "ページ情報はまだありません",
    },
    documents: {
      title: "ドキュメント",
      count: (count) => `ドキュメント ${count}件`,
      pageCount: "1ページ",
    },
    reviewPreview: "プレビューを確認",
    print: "印刷",
  },
  navigation: {
    sections: {
      start: "開始",
      build: "構築",
      verify: "検証",
      release: "公開",
    },
    home: 'ホーム',
    create: '作成',
    work: '作業',
    details: '詳細ツール',
    map: 'マップ',
    code: 'コード',
    release: 'リリース',
    publish: '公開',
    launch: 'リリース',
    configure: '設定',
    settings: '設定',
    share: '共有',
    starters: 'スターター',
    import: 'インポート',
    sources: 'ソース',
    library: 'ライブラリ',
    documents: 'ドキュメント',
    requests: '依頼',
    requestsAndReviews: '依頼とレビュー',
    bots: 'ボット',
    operations: '運用',
    data: 'データ',
    activity: 'アクティビティ',
    impact: '成果',
    audience: '利用者',
    analytics: '分析',
    designSystem: 'デザインシステム',
    workspaceSettings: 'ワークスペース設定',
    projects: 'プロジェクト',
    templates: 'テンプレート',
    projectSettings: 'プロジェクト設定',
    account: 'アカウント',
    signIn: 'ログイン',
    signOut: 'ログアウト',
    goHome: 'Studioホームへ移動',
    goCoogoHome: 'Coogoホームへ移動',
    coogoHomepage: 'Coogoホームページ',
    studioNavigation: 'Studioナビゲーション',
    studioNavigationDescription: '作成、プロジェクト、保存済み作業、依頼、設定へ移動します。',
    collapseNavigation: 'ナビゲーションを折りたたむ',
    expandNavigation: 'ナビゲーションを展開する',
  },
  artifacts: {
    label: '成果物モード',
    app: 'アプリ',
    game: 'ゲーム',
    design: 'デザイン',
    document: 'ドキュメント',
    guide: 'ガイド',
    insights: '状況',
  },
  work: {
    title: '作業',
    subtitle: 'アプリ、ドキュメント、レポートを作成・管理・レビューします。',
    namePlaceholder: '新しい作業の名前...',
    create: '作成',
    browseStarters: 'スターターを見る',
    importSource: 'ソースを取り込む',
    emptyTitle: 'まだ作業がありません',
    emptyBody: '上でアプリ・ドキュメント・レポートを作成するか、下のスターターをご覧ください',
  },
  designSystem: {
    title: 'デザインシステム',
    subtitle: '生成されるアプリが使う色・タイポグラフィ・余白・形を決め、ライブラリに保存する前に cogo の語彙と合っているか確認します。',
    validationLab: '検証ラボ',
    themeEditor: 'テーマエディタ',
    tabs: {
      systems: 'デザインシステム',
      validation: '検証ラボ',
      vocabulary: '語彙',
      briefings: 'ブリーフィング',
    },
    uiBlocks: {
      eyebrow: 'UI Block オントロジー',
      title: '何を作りますか？',
      description: '必要な画面を説明すると、Coogo が承認済みカタログから実際のブロックを探し、現在のデザインオントロジーテーマでプレビューします。',
      intentLabel: 'プロダクトの意図',
      intentPlaceholder: '例: チーム分析ダッシュボード',
      shortcutsLabel: '意図から始める',
      shortcuts: { dashboard: 'ダッシュボード', store: 'ストア', landing: 'ランディングページ', auth: 'サインインフロー' },
      recommendations: 'おすすめ UI Block',
      admittedCount: (count) => `カタログ承認済み ${count} ブロック`,
      noMatches: 'この意図に合う承認済みブロックはまだありません。より短い説明を試してください。',
      preview: 'このテーマでプレビュー',
      selectedPreview: 'ライブテーマプレビューに選択済み',
      readOnly: '読み取り専用プレビュー',
      authorityUnavailable: '構造の互換性と公開権限が証明されるまで、プロジェクトへの適用と置換は利用できません。',
    },
    precondition: {
      signedOutReason:
        'サインインが必要です。このワークスペースのデザインシステム一覧と、ソースから読み取った値は、サインインしたユーザーにのみ表示されます。',
      signInCta: 'サインインへ移動',
      checkingReason: 'この画面を開けるか確認しています…',
      unmetFallbackReason: 'この画面を開くための条件が、まだ満たされていません。',
    },
    sourceRead: {
      failed: 'このプロジェクトのデザインソースを読み取れませんでした。',
      failedWithMessage: '今はこのプロジェクトのデザインソースを読み取れませんでした: {message}',
    },
    vocabularyPanel: {
      title: '語彙',
      search: '検索…',
      enumPrefix: '接頭辞',
      enumAccessor: 'アクセサ',
      enumEvent: 'イベント',
      enumAction: 'アクション',
      colName: '名前',
      colMode: 'モード',
      colSemantics: '意味',
      colCategory: '分類',
      colReturns: '戻り値',
      colDomain: '領域',
      colDescription: '説明',
      loading: '語彙を読み込み中…',
      errorTitle: '語彙を読み込めませんでした',
      retry: '再試行',
      empty: 'このシャードに項目がありません。',
      unavailableTitle: '語彙を利用できません',
      unavailableDetail: '語彙カタログの表示にはローカルのナレッジグラフが必要です。',
      noMatch: '「{query}」に一致する項目がありません',
      clear: 'クリア',
    },
    validatorPanel: {
      specChip: "§ デザインシステム v4",
      heading: "検証ツール",
      lastRun: '最終:',
      justNow: 'たった今',
      secondsAgo: '{n}秒前',
      minutesAgo: '{n}分前',
      hoursAgo: '{n}時間前',
      rerun: '再実行',
      validating: '検査中…',
      colRule: 'ルール',
      allChecks: 'すべての検査',
      noViolations: '表示するルール違反はありません。',
      colGate: 'ゲート',
      colStatus: '状態',
      statusPass: '合格',
      statusFail: '不合格',
      flagOffTitle: '検査が無効です',
      flagOffBody: 'このワークスペースではデザイン検査が有効になっていません。',
      readyTitle: '検査の準備ができました',
      readyBody: '再実行を押して現在のプロジェクトを検査してください。',
      unreadableTitle: '検査結果を読み取れませんでした',
      unreadableBody: '前回の検査が、この画面では解釈できない結果を返しました。再実行してください。',
      briefingReadFailed: 'このワークスペースのブリーフィングを読み取れませんでした。',
      briefingTitle: 'ブリーフィング',
      briefingIntro: 'このアプリについての短いブリーフィングを書きます。ブリーフィングはブロックとともにオントロジーに残り、再読み込みしても保持されます。',
      briefingSignIn: 'ブリーフィングを書くにはサインインしてください。',
      briefingSignedOutDetail: 'ブリーフィングはオントロジーゲートウェイ経由でワークスペースごとに保存されます。このアプリのブリーフィングを読み書きするにはサインインしてください。',
      briefingUnavailableTitle: 'このワークスペースではブリーフィングを利用できません',
      briefingUnavailableDetail: '現在の Studio 配備には対応するブリーフィング保存先がありません。読み取りと書き込みは無効です。',
      briefingFieldTitle: 'タイトル',
      briefingFieldBody: '本文（マークダウン — エスケープされたテキストとして表示）',
      briefingTitlePlaceholder: '例: 第2四半期リリース確認リスト',
      briefingBodyPlaceholder: 'ここにブリーフィングを書いてください。改行は保持され、HTML はエスケープされます。',
      briefingReady: '準備完了',
      briefingSaving: '保存中…',
      briefingSave: 'ブリーフィングを保存',
      briefingEmpty: 'ブリーフィングはまだありません。上に最初の一件を書いて保存を押してください。',
      repoInitTitle: '検査を開始しています…',
      repoInitBody: 'チェッカーを準備しています — 少し時間がかかります。',
      noBlocksTitle: '検査するブロックがまだありません',
      noBlocksBody: '編集モードのチャットでブロックを作るとこの画面が埋まります。',
      errorTitle: '検査に失敗しました',
      errorBody: '下のエラーメッセージを確認し、再実行を押してください。',
    },
    inventory: {
      readingBadge: 'このワークスペースのデザインシステムを読み込み中…',
      readingDetail: '一覧が返ってくるまで、何も断定しません。',
      unreadableBadge: 'このワークスペースのデザインシステムを読み込めませんでした',
      unreadableDetail:
        '一覧を読み込めなかったため、この画面はこのワークスペースにデザインシステムがいくつあるかを言えません。下のテーマエディターはそのまま使えます。',
      noneBadge: 'このワークスペースに保存されたデザインシステムはありません{stamp}',
      noneDetail:
        'ここにデザインシステムとして保存されたものはまだありません。下で軸を決めてライブラリに保存すると、最初の1つができます。',
      listedBadge: 'このワークスペースのデザインシステム {count} 件{stamp}',
      listedBadgeOne: 'このワークスペースのデザインシステム {count} 件{stamp}',
      listedDetail:
        '上の時刻に返ってきた一覧そのままです。この画面を開くと一覧が読み込まれるだけで、各項目を検査するわけではありません。',
      readStamp: '、{time} 読み込み',
      setAsideNote:
        ' ほかの {count} 行はテンプレートから作られたプロジェクトのもので、ワークスペースのデザインシステムではありません。',
      setAsideNoteOne:
        ' ほかの {count} 行はテンプレートから作られたプロジェクトのもので、ワークスペースのデザインシステムではありません。',
    },
    themeEditorPanel: {
      title: 'テーマエディター',
      body:
        'スタイル、ベースカラー、テーマ、チャートパレット、角丸、フォントを変えると、実際の cogo コンポーネントブロックがその場で変わります。よければライブラリに保存してください。',
    },
    workbench: {
      controlsTitle: 'テーマコントロール',
      selectPlaceholder: '{axis} を選択',
      saveToLibrary: 'ライブラリに保存',
      saveDialogTitle: 'デザインシステムを保存',
      namePlaceholder: 'プリセット名',
      descriptionPlaceholder: '説明（任意）',
      closeLabel: '閉じる',
      cancel: 'キャンセル',
      save: '保存',
      saving: '保存中…',
      savedAs: '「{name}」として保存しました',
      nameRequired: '名前を入力してください。',
      saveFailed: '保存できませんでした。',
      sourceTitle: 'ソースから読み取ったデザインシステム',
      sourceEmptyBody: 'このプロジェクトにはまだデザインソースが取り込まれていません — 下のすべての軸は、自分で決めるまで空のままです。',
      sourceImportCta: 'デザインソースを取り込む（Figma）',
      sourceReadPrefix: '読み取り元: ',
      sourceVariableCount: '· 変数 {count} 件',
      sourceVariableCountOne: '· 変数 {count} 件',
      chooseAxisValue: '決定',
      axisValueSelectLabel: '{axis} の値',
    },
    projectTokens: {
      title: 'デザインファイルから取り込んだトークン',
      bodyBefore:
        '取り込んだデザインファイルから読んだ色や変数は、ワークスペースではなく、そのファイルを取り込んだプロジェクトのものです。そのプロジェクトを開いて ',
      navPath: 'デザイン → トークン',
      bodyAfter:
        ' に進むと、確認してプロジェクトに適用できます。',
    },
  },
  settings: {
    title: 'ワークスペース設定',
    profile: {
      title: 'プロフィール',
      name: '名前',
      email: 'メール',
      edit: '編集',
      cancel: 'キャンセル',
      providerBlocked: 'このサインイン方法ではまだプロフィールを変更できません',
      save: '保存',
      saving: '保存中…',
      nameInputLabel: 'ワークスペースの表示名',
      nameSaved: '名前を保存しました。',
      signedOutBlocked: 'プロフィールを編集するにはサインインしてください',
    },
    writeOutcome: {
      protectedRejected: '保護された設定は変更されませんでした。',
      accepted: 'プロジェクト設定が反映されました。',
    },
    publishing: {
      title: '公開',
      exportsLabel: '成果物のエクスポート:',
      exportsNone: 'まだ記録がありません',
      exportsCount: '{count} 件を記録',
      recordsNone: 'このワークスペースに保存された公開記録はまだありません。',
      recordsCount: 'このワークスペースに公開記録が {count} 件保存されています。',
      exportSettings: '設定をエクスポート',
      exportSettingsBlocked: 'まだ利用できません — ダウンロードできる設定のエクスポートがありません',
      reviewRecords: '記録を確認',
      reviewRecordsBlocked: 'まだ記録がありません — 先に公開するかエクスポートしてください',
    },
    plan: {
      title: 'プラン',
      current: '現在:',
      manage: 'プランを管理',
      allowsTitle: '現在のプランでできること',
      generation: '生成',
      publication: '公開',
      packPromotion: 'パックの昇格',
      errorLabel: 'プラン情報を読み込めませんでした',
      lookupFailed: '課金情報の照会に失敗しました',
      loading: 'プランを確認しています…',
      signedOut: 'プランを見るにはサインインしてください',
      noSubscription: '有効なサブスクリプションがありません',
      rights: {
        'not-measured': '未計測',
        'authority-readback-invalid': '権限の読み戻しが不正です',
        'not-admitted': '未承認',
        'preview-only': 'プレビューのみ',
        'not-admitted-by-source-receipt': 'このソース受領証では未承認',
        'preview-admitted': 'プレビューが承認済み',
        'same-project-publication-admitted': '同一プロジェクト内の公開が承認済み',
        'admitted': '承認済み',
      },
    },
    runtime: {
      title: 'ランタイム',
      note: 'リリースの識別と可用性は観測された事実であり、権限ではありません。',
      distribution: '配布形態',
      studioSha: 'Studio ソース SHA',
      applianceReleaseId: 'アプライアンスのリリース ID',
      observedAt: '観測時刻',
      observationsTitle: '可用性の観測',
      available: '利用可能',
      unavailable: '利用不可',
      readFailed: '読み取り失敗',
      notConfirmed: '可用性を確認できません',
      probeFailures: {
        'platform-health-status': 'プラットフォームのヘルス取得に失敗しました（{status}）',
        'platform-health-malformed': 'プラットフォームのヘルス応答の形式が不正です。',
        'platform-unreachable': 'プラットフォームの可用性を確認できませんでした。',
        'capability-response-rejected': '機能の応答が受け付けられませんでした（{status}）。',
      },
      capabilities: {
        platformConnection: '接続中のプラットフォーム',
        localAuth: 'ローカル認証',
        googleAuth: 'Google 認証',
        localPublish: 'ローカル公開',
        cloudPublish: 'クラウド公開',
        customDomain: 'カスタムドメイン',
        billing: '課金',
        analytics: '分析',
        externalIntegrations: '外部連携',
      },
    },
    domain: {
      title: 'ドメイン',
      defaultLabel: '既定:',
      customLabel: 'カスタム:',
      connect: 'ドメインを接続',
      blocked: 'カスタムドメインはまだ利用できません',
      signedOut: 'ワークスペースのドメインを見るにはサインインしてください',
    },
    github: {
      title: 'GitHub 連携',
      developer: '開発者',
      connected: '接続済み',
      disconnect: '接続を解除',
      disconnecting: '接続を解除しています…',
      invite:
        'GitHub アカウントを接続すると、ソースコードの書き出しと CI/CD の設定ができます。',
      connect: 'GitHub を接続',
      connecting: '接続しています…',
      confirmDisconnect: 'GitHub アカウントの接続を解除しますか？既存のリポジトリは削除されません。',
    },
    notifications: {
      title: '通知',
      reason:
        'メール通知はまだ利用できません — Studio に送信するサービスがなく、設定を保存する場所もないため、オンにできません。',
      review: 'レビュー通知（メール）',
      agentSummary: 'エージェント活動の要約（毎日）',
      productUpdates: '製品のお知らせ',
      unavailableSuffix: '— まだ利用できません',
    },
    danger: {
      title: '危険な操作',
      deleteAccount: 'アカウントを削除',
      deleteBlocked:
        'Studio ではまだアカウント削除に対応していません — privacy@cogo.xyz までご依頼ください',
    },
    closedNetwork: {
      planNote:
        'このインストールは閉域ネットワークで動作します。利用範囲はここで管理する購読ではなく、インストールされたライセンスが決めます。',
      planManageBlocked:
        '購読の管理は、このインストールから到達できない外部サービスが担当します',
      servedFromLabel: '接続先:',
      domainBlocked:
        'カスタムドメインには公開 DNS が必要です — このインストールは自身のアドレスでのみ応答します',
      githubTitle: 'GitHub',
      githubBlocked:
        'アカウント連携には github.com が必要ですが、このインストールからは到達できません',
      deleteBlocked:
        '閉域インストールのアカウントはこの機器内にあります — このアプライアンスの管理者にご依頼ください',
    },
  },
  operations: {
    title: '運用',
    subtitle: '公開した成果物のデータ・アクティビティ・成果・利用者・計測を確認します。',
    reviewedOutputs: 'レビューした成果物',
    acceptanceRate: '受理率',
    sharedArtifacts: '共有した成果物',
    sharedArtifactsNote: 'アプリ・文書・レポート',
    unmeasured: 'まだ計測していません — Studio にこれらの数値の出どころがありません。',
    tiles: {
      products: 'ソースの束、抽出した行、準備状況、来歴。',
      orders: '公開した成果物のアクティビティと記録された根拠。',
      revenue: '表示数、コンバージョン、反映された変更、成果のシグナル。',
      members: 'レビュアー、流入元、再訪問者。',
      analytics: '公開後の実アプリの計測。',
    },
  },
  requests: {
    title: 'リクエストとレビュー',
    subtitle: '依頼された変更、承認・却下、そして添えられた根拠を確認します。',
    openChat: 'チャットを開く',
    howItWorks: 'リクエストの進み方',
    stepsSuffix: '・3ステップ',
    rows: {
      inProgress: { title: '進行中のリクエスト', body: 'cogo が準備しているアプリ・文書・レポートの変更を見ます。', },
      needsApproval: { title: '承認が必要', body: '公開、リリース、共有の更新は、確認いただくまで待機します。', },
      reviewTrail: { title: 'レビュー履歴', body: '何が変わり、何を点検し、何が共有できる状態かを見ます。', },
      accessControl: { title: 'アクセス権限', body: 'アカウントと公開の権限を、現在のリクエストの範囲に限定します。', },
    },
    current: { title: '現在のリクエスト', body: '共有や公開の前にレビューが必要な変更を cogo が準備しているとき、この画面を使います。', },
    actions: {
      addSource: 'ソースを追加',
      library: 'ライブラリ',
      share: '共有',
      refresh: '再読み込み',
    },
    tiles: {
      updates: '更新',
      checks: '点検',
      needsApproval: '承認が必要',
    },
    timeline: {
      title: '最近のリクエスト履歴',
      emptyTitle: '進行中のリクエストはありません',
      emptyBody: 'チャットから始めるか、ソースを追加してください。承認が必要なリクエストがここに表示されます。',
    },
    approval: {
      title: '確認が必要です',
      waiting: 'この更新は、共有される前にあなたの確認を待っています。',
      empty: '保留中の承認はありません。',
    },
    prepare: {
      title: 'coogo が準備できること',
      appDraft: 'アプリの下書き',
      sourceImport: 'ソースの取り込み',
      reviewCheck: 'レビュー点検',
      launchSetup: '公開の準備',
    },
    requestTitle: {
      appDraft: 'アプリの下書き',
      sourceAdded: 'ソースを追加',
      reviewCheck: 'レビュー点検',
      shareUpdate: '共有の更新',
      launchSetup: '公開の準備',
      requestedChange: '依頼された変更',
    },
    status: {
      ready: '準備完了',
      needsReview: '要レビュー',
      inProgress: '進行中',
      recorded: '記録済み',
    },
    scope: {
      codeUpdate: 'コードの変更',
      designFileUpdate: 'デザインファイルの変更',
      publicShare: '一般公開',
      launchSetup: '公開の準備',
      workspaceUpdate: 'ワークスペースの変更',
    },
    entry: {
      reuse: '再利用した部分',
      fresh: '新しく作った部分',
      validation: '点検済み',
      approval: '承認',
    },
  },
  share: {
    title: '共有',
    subtitle: 'アプリ・文書・レポートを共有しながら、ソース、点検状況、更新の経路を見えるままにします。',
    centerTitle: '共有センター',
    centerBody:
        '共有した成果物ごとにソースとレビュー状況、更新の経路をつないでおき、最初から作り直さずに改善を続けられるようにします。',
    createOutput: '成果物を作る',
    addSource: 'ソースを追加',
    sources: 'ソース',
    settingsDialog: {
      title: '共有設定',
      body: '{name} のサブドメインを更新します。',
      subdomainLabel: 'サブドメイン',
    },
    checklist: {
      publicUrl: '公開URL',
      readinessCheck: '点検状況',
      outcomePath: '成果の導線',
      updateControl: '更新の管理',
      notShared: '未共有',
      sharedCount: '{count} 件を共有中',
      nothingSharedYet: 'まだ共有していません',
      readyCount: '{count} 件が準備済み',
      needsCheck: '点検が必要です',
      outcomePathValue: '問い合わせ、予約、書き出し、決済、レビュー画面',
      updateControlValue: '変更の前に共有設定を確認してください',
    },
    tiles: {
      sharedOutputs: '共有した成果物',
      readyChecks: '完了した点検',
      drafts: '下書き',
      latestUpdate: '最終更新',
      noSharedOutput: '共有した成果物なし',
    },
    health: {
      title: '共有の状況',
      checked: '点検済み',
      lastRecordedCheck: '最後に記録された点検',
      needsAttention: '要確認',
      draftsNotShared: '下書き（未共有）',
      nothingShared: '共有なし',
      noneYet: 'まだありません',
      never: 'なし',
      none: 'なし',
      checkedOfShared: '{shared} 件中 {checked} 件',
      blockedCount: '{count} 件が停止',
      nothingSharedBadge: 'まだ共有していません',
      nothingSharedDetail:
        'このワークスペースからはまだ何も共有されていないため、ここに報告する内容はありません。成果物を共有すると、その点検記録がここに表示されます。',
      blockedBadge: '共有した成果物 {shared} 件のうち {blocked} 件が停止しています',
      blockedBadgeOne:
        '共有した成果物 {shared} 件のうち {blocked} 件が停止しています',
      blockedDetail: '共有した成果物の中に、点検記録が停止しているものがあります。開いて原因を確認してください。',
      uncheckedBadge: '共有した成果物 {shared} 件に点検記録がありません',
      uncheckedBadgeOne:
        '共有した成果物 {shared} 件に点検記録がありません',
      uncheckedDetail:
        'これらの成果物は共有されていますが、点検記録が一つもありません。ひとつ開いて点検を実行すると記録されます。',
      checkedBadge: '共有した成果物 {shared} 件のうち {checked} 件を点検済み',
      checkedBadgeOne:
        '共有した成果物 {shared} 件のうち {checked} 件を点検済み',
      checkedBadgeLastSuffix: '、最終 {date}',
      checkedDetail: 'これらの成果物を最後に点検したときに記録された内容です。この画面を開くだけでは再点検しません。',
    },
  },
  library: {
    title: 'ライブラリ',
    subtitle:
      'このワークスペースで確認済みの、再利用できる素材と成果物を開きます。',
    importSource: 'ソースを取り込む',
    tiles: {
      savedWork: '保存した作業',
      openLinks: '開けるリンク',
      details: '詳細',
      launches: '公開',
    },
    searchPlaceholder: '保存した作業・ソース・リンクを検索…',
    searchResults: '検索結果',
    savedWorkCount: '保存した作業 {count} 件',
    previewTitle: 'ライブラリのプレビュー',
    thumbnailPreview: {
      project: 'プロジェクトのプレビュー',
      template: 'スターターのプレビュー',
      library: 'ライブラリのプレビュー',
    },
    previewEmptyTitle: 'ライブラリの項目を選んでください',
    previewEmptyBody:
      '保存した作業がここに表示され、次に必要な操作 — 開く、共有、確認、公開の準備 — が一緒に並びます。',
    whatYouCanDo: 'ここでできること',
    readyToUse: '使う準備',
    noReviewLinks: 'レビューリンクはまだありません。',
    selectedDetails: {
      summary: '準備状況、レビュー、根拠、公開履歴',
      openBeforeReviewLink:
        '編集可能なレビューリンクを作成する前に、このアプリを開いてください。',
    },
    empty: {
      title: 'ライブラリにはまだ項目がありません',
      body:
        '企画書、画面ファイル、URL、リポジトリの変更、デザインフレームを追加してください — 保存した項目ごとに、プレビューとソース、公開の操作がここに並びます。',
    },
    actions: {
      open: {
        label: '開く',
        detail: '共有する前にアプリや文書を確認します',
        noLink: 'この項目にはまだ開けるリンクがありません',
      },
      edit: {
        label: '編集',
        detail: 'プロジェクトの作業スペースに戻ります',
        noWorkspace: 'この項目はプロジェクトの作業スペースに紐づいていません',
      },
      launch: {
        label: '公開',
        detail: '公開用パッケージを準備します',
        noBundle: 'この項目にはまだ公開できる保存ファイルがありません',
        busy: '{tool} はまだ実行中です',
      },
      details: {
        label: '詳細',
        detail: 'ソースのメモと次の手順を確認します',
      },
      useAgain: 'もう一度使う',
      sharePublicly: '公開共有',
      check: '確認',
      share: '共有',
      linkPr: 'PR をリンク',
      prepareLaunch: '公開準備',
      feedback: {
        check: {
          progress: 'アプリを点検しています…',
          success: 'アプリの点検を記録しました。',
          failure: 'アプリの点検に失敗しました。',
        },
        sharePublicly: {
          progress: '公開共有を準備しています…',
          success: '公開共有を記録しました。',
          failure: '公開共有に失敗しました。',
        },
        prepareLaunch: {
          progress: '公開を準備しています…',
          success: '公開準備を記録しました。',
          failure: '公開準備に失敗しました。',
        },
      },
      noSelection: '先に項目を保存するか選択してください',
    },
  },
  sources: {
    title: 'ソース',
    subtitle: 'ファイル、URL、リポジトリ、デザインソースをこのワークスペースに追加します。',
    addToLibrary: 'ライブラリに追加',
    howItWorks: 'ソースを追加するとどうなるか',
    flowAriaLabel: 'ソースからワークスペースへの流れ',
    agentIntake: {
      eyebrow: 'Studio エージェント',
      title: '作りたいものを Studio に伝えてください',
      description: '依頼を書くか、ファイルまたは URL を追加してください。先にソースの種類を選ばなくても、Studio が次のステップを準備します。',
      placeholder: '例：カスタマーサポートの状況を一覧できるダッシュボードを作って',
      attach: 'ファイルを添付',
      addUrl: 'URL を追加',
      submit: 'Studio エージェントに依頼',
      understanding: 'エージェントが理解した内容',
      nextStep: '次のステップ',
      connected: '最近使ったソースと接続済みソース',
      emptyConnected: 'この作業に接続したファイルや URL がここに表示されます。',
      advanced: '詳細なインポート',
      advancedSummary: 'ソースの種類を直接選ぶか、Figma アクセス、対象、タグなどの詳細を設定します。',
      requestUnderstanding: '新しい成果物を作るための依頼',
      urlUnderstanding: '参考にする Web サイト',
      codeUnderstanding: '既存のコードまたはテキストファイル',
      figmaUnderstanding: 'ローカルの Figma デザインファイル',
      documentUnderstanding: '読み取り、構造を確認する文書',
      nextDescribe: 'Studio が依頼を整理し、Library の下書きを準備します。',
      nextUrl: 'ページを参考ソースとして接続し、Library の下書きを準備します。',
      nextCode: 'ファイルを画面ソースとして読み取り、Library の下書きを準備します。',
      nextFigma: 'デザインを読み取り、ページとレイヤーを確認できるようにします。',
      nextDocument: '文書を読み取り、構造を確認できるようにします。',
      currentRequest: '現在の依頼',
      selectedFile: '選択したファイル',
      restoredDocument: 'このブラウザーに保存された文書',
      connectedDesign: '接続済みの Figma デザイン',
      urlHint: '同じ依頼欄に完全な URL を貼り付けてください。',
      unsupportedFile: 'このファイル形式は、まだこの入力欄では読み取れません。',
      activeDesignConflict: '接続中のデザインソースがあるため、新しい URL 作業を開始できません。そのデザインを開くか、新しいプロジェクトで URL から開始してください。',
      librarySaveMissing: 'Library に保存された下書きを確認できませんでした。依頼はこの画面に残っています。もう一度お試しください。',
    },
    steps: {
      addSource: 'ソース追加',
      shape: '整える',
      save: '保存',
      share: '共有',
    },
    form: {
      appName: 'アプリ名',
      audience: '対象',
      audienceCreator: 'クリエイター',
      audienceBusiness: 'ビジネス',
      audienceDeveloper: '開発者',
      sourceFieldSuffix: 'ソース',
      briefPlaceholder: '作りたいアプリ・ドキュメント・レポートをここに書いてください。対象、提供内容、画面構成、フォーム、決済、望むトーンも添えてください。',
    },
    upload: {
      title: 'ファイルをアップロード',
      body: 'すでにアプリバンドルがありますか？そのままアップロードすると、ファイルに基づく記録がライブラリに残ります。',
      button: 'アプリバンドルをアップロード',
    },
    groups: {
      fromIdea: 'アイデアから始める',
      bringExisting: 'すでにあるものを取り込む',
    },
    otherWays: {
      title: '別の方法で始める',
    },
    chatStart: {
      title: 'チャットから始める',
      body: 'ソースを選ばなくても大丈夫です。チャットを開いて作りたいものを説明してください。作成後は同じライブラリと公開の流れを使います。',
    },
    savedWith: {
      title: '一緒に保存されるもの',
      openLink: '開けるリンク',
      savedFile: '保存されたファイル',
      sourceSnapshot: 'ソースのスナップショット',
      launchCheck: '公開チェック',
    },
    after: {
      title: '追加したあと',
      defaultNote: 'cogo がソースを保管するので、出どころを見失わずに開く・共有する・整える・公開ができます。',
      figmaFileNote: 'cogo は .fig の証跡をアプリ生成と分けて保管し、ソースの網羅性を先に確認できるようにします。',
      documentFileNote: 'ドキュメントはこのブラウザ内に残り、再読み込み後も開いたままで、どこにも送信されません。',
      steps: {
        prepared: '作業が準備されます',
        designRead: 'デザインを読み取ります',
        documentRead: 'ドキュメントを読み取ります',
        sourceSaved: 'ソースが保存されます',
        keptInBrowser: 'このブラウザに保管されます',
        mapperOpens: 'ソースマッパーが開きます',
        structureOpens: '下に構造が開きます',
        libraryOpens: '次のアクションとともにライブラリが開きます',
      },
    },
    panel: {
      tags: 'タグ',
      librarySuffix: 'cogo がソースのメモと次のアクションを添えてライブラリカードとして保存します。',
      figmaFileNote: '生成する前に Figma ファイルを読み、原本はそのまま保持します。',
      documentFileNote: 'ドキュメントをこのブラウザ内で読み、構造を表示します。アップロードはされず、まだ生成も行いません。',
      figmaTokenLabel: 'Figma トークン',
      figmaTokenPasteHint: 'Figma トークンを貼り付けて確認してください。',
      forgetToken: '削除',
      readDesign: 'デザインを読み取る',
      readDocument: 'ドキュメントを読み取る',
      localFigmaFile: 'ローカルの Figma ファイル',
      localDocument: 'ローカルのドキュメント',
    },
    help: {
      assistant_prompt: {
        title: 'ここにリクエストを書いてください。',
        detail: '対象、提供内容、画面構成、フォーム、決済、望むトーンも書き添えてください。',
        example: '例: ビューティークリエイター向けの予約ページを作ってください。サービス一覧、お客様の声、Instagram リンク、そのまま決済に進めるボタンまで入れてください。',
      },
      url_import: {
        title: '公開URLを1つ貼り付けてください。',
        detail: 'cogo が画面と内容の参考として使い、出典URLをライブラリに残します。',
        example: '例: https://example.com/product-page',
      },
      ide_file: {
        title: 'コンポーネント・ルート・画面ファイルを1つ貼り付けてください。',
        detail: 'すでに画面があり、そこから続けたいときに最適です。',
        example: '例: src/features/checkout/checkout-page.tsx',
      },
      github_pr: {
        title: 'リポジトリとプルリクエスト番号を貼り付けてください。',
        detail: 'cogo が変更とレビューメモを保持し、チームと共有できるようにします。',
        example: '例: acme/storefront#128',
      },
      figma_frame: {
        title: 'FigmaフレームのURLまたはファイル/ノードIDを貼り付けてください。',
        detail: 'cogo がデザインの出典を保持し、変換品質を後で確認できるようにします。',
        example: '例: https://www.figma.com/design/abc123/Checkout?node-id=12-345',
      },
      figma_file: {
        title: 'ローカルの .fig ファイルを選んでください。',
        detail: 'cogo がファイルを読み、原本を保持し、生成前にデザインの中身を表示します。',
        example: '例: checkout-flow.fig',
      },
      document_file: {
        title: '.docx または .hwpx ファイルを選んでください。',
        detail: 'cogo が文書を読み、原本を保持し、生成前に文書の構造を表示します。',
        example: '例: account-opening-spec.docx',
      },
    },
    refLabels: {
      assistant_prompt: '概要',
      url_import: 'URL',
      ide_file: 'ファイル',
      github_pr: 'GitHub',
      figma_frame: 'Figma',
      figma_file: '.fig',
      document_file: 'ドキュメント',
    },
    cards: {
      assistant_prompt: {
        label: '言葉で説明',
        description: '作りたいアプリ・ドキュメント・レポートを書きます。',
        bestFor: 'アイデアから始めるとき',
        output: 'ライブラリの下書き',
      },
      url_import: {
        label: 'ウェブサイトを使う',
        description: 'ページURLを参考として取り込みます。',
        bestFor: '既存サイトや参考資料',
        output: '参考に基づく下書き',
      },
      ide_file: {
        label: 'コードを貼り付け',
        description: 'コンポーネント・ルート・画面ファイルを取り込みます。',
        bestFor: '既存の実装があるとき',
        output: '画面の下書き',
      },
      github_pr: {
        label: 'プルリクエストを使う',
        description: 'レビュー対象のリポジトリ変更を添付します。',
        bestFor: 'チームの変更',
        output: 'レビューカード',
      },
      figma_frame: {
        label: 'Figmaフレーム',
        description: '変換レビュー用にデザインフレームを取り込みます。',
        bestFor: 'デザインの受け渡し',
        output: 'デザインに基づく下書き',
      },
      figma_file: {
        label: 'Figma .fig',
        description: 'ローカルのFigmaファイルを読み、原本を保持します。',
        bestFor: '先にデザインを確認するとき',
        output: 'デザイン詳細',
      },
      document_file: {
        label: 'Word / HWPX',
        description: '.docx または .hwpx を読み、原本を保持します。',
        bestFor: '仕様書・ガイド・マニュアル',
        output: 'ドキュメント詳細',
      },
    },
  },
  chrome: {
    breadcrumb: '現在位置',
    search: 'アプリ、文書、ゲーム、状況を検索...',
    openNavigation: 'ナビゲーションを開く',
    openChat: 'チャットを開く',
    closeChat: 'チャットを閉じる',
    notifications: '通知',
    lightMode: 'ライトモードに切り替え',
    darkMode: 'ダークモードに切り替え',
    publish: '公開',
    publishCurrent: '現在のプロジェクトを公開',
    openProjectToPublish: '公開するサーバープロジェクトを開いてください',
    publishBlocked: '公開がブロックされました',
    conflict: '競合',
    saveFailed: '保存失敗',
    saving: '保存中…',
    unsaved: '未保存',
    live: '公開中',
    saved: '保存済み',
    draft: '下書き',
    reloadConflict: '別のセッションがこのブロックを変更しました — 最新状態を再読み込みしてください',
    reload: '再読み込み',
    reloading: '再読み込み中…',
    exportSource: 'アプリケーションソースをエクスポート',
    openProjectToExport: 'エクスポートするプロジェクトを開いてください',
    exportSuccess: 'アプリケーションソースをエクスポートしました',
    toggleIntentLoop: 'チャット',
    moreControls: 'その他の操作',
    history: '履歴',
  },
  proposal: {
    change: "変更案",
    info: "情報",
    warning: "注意",
    critical: "重要",
    before: "変更前",
    after: "変更後",
    unchanged: "変更なし",
    removed: "削除",
    details: "詳細",
    close: "閉じる",
    whatWillCheck: "coogoが確認する項目",
    reviewChecks: "確認項目",
    reviewBeforeApply: "適用前に確認",
    attachedProof: "添付された根拠",
    check: "根拠",
    notAttached: "添付なし",
    readyToUse: "適用可能",
    reviewFirst: "要確認",
    anotherEditPending: "別の編集が保留中です。",
    compareConflict: "競合する変更の詳細を比較",
    keep: "維持",
    revise: "修正",
    reviseAria: "提案された変更を修正",
    use: "適用",
    reviewNeeded: "要確認",
    keepCurrent: "現在のまま",
    keepCurrentAria: "現在の状態を維持してこの提案を終了",
  },
  home: {
    ...en.home,
    title: "今日は何を作成・変更しますか？",
    subtitle:
      "ソースを接続するか作業タイプを選ぶと、coogoが最初の依頼を整理します。",
    startWithChat: "チャットで開始",
    workTypes: "作業タイプ",
    workTypeHint: "作業タイプを選ぶと、入力欄に開始文が入ります。",
    recentWork: "最近の作業",
    reviewNeeded: "要確認",
    viewAll: "すべて表示",
    review: "確認",
    noWork: "作業はまだありません。",
    noWorkHint: "上の入力欄から最初の作業を開始してください。",
    firstProjectAction: "最初のプロジェクトを説明する",
    continueWorking: "作業を続ける",
    openWork: "作業を開く",
    continue: "続ける",
    latestProjectEmpty: "実際のプロジェクトと検証状態がここに表示されます。",
    readiness: "coogo 準備状況",
    totalWork: "全作業",
    productionVerified: "本番検証済み",
    settings: "設定を管理",
    countSuffix: "件",
    readinessSummary:
      "共有準備: 準備完了 {ready}件、確認 {review}件、ブロック {blocked}件。",
    featuredOutcome: {
      eyebrow: "代表ワークフロー · プレビュー",
      title: "ニュースレター → メンバーシップ",
      outcomes: {
        newsletterMembership: { title: "ニュースレター → メンバーシップ" },
        reservationOperations: { title: "予約運用" },
      },
      description:
        "購読者とコンテンツから、管理されたメンバーシップサービスのプレビューを開始します。",
      boundary:
        "プレビュー候補のみを作成します。検証、公開、デプロイ、本番環境の読み取りはそれぞれ別のゲートです。",
      action: "このワークフローを選択",
      clearAction: "選択したワークフローを解除",
      inProgress: "このワークフローのリクエストはすでに進行中です。",
      prompt:
        "ニュースレター購読者と既存コンテンツを、会員オンボーディング、限定コンテンツ、運営インサイトを備えた有料メンバーシップサービスに変換したいです。",
    },
    lanes: {
      app: { title: "アプリ", prompt: "業務用アプリを作成したいです。" },
      document: {
        title: "文書",
        prompt: "接続したソースから文書を作成したいです。",
      },
      game: {
        title: "ゲーム",
        prompt: "明確なルールと操作を持つゲームを作成したいです。",
      },
      existing: {
        title: "既存作業を編集",
        prompt: "既存の作業を選んで更新したいです。",
      },
      starter: {
        title: "Starterから",
        prompt: "Starterを選んで開始したいです。",
      },
    },
  },
  chat: {
    ...en.chat,
    greeting: (name) => `${name}さん、今日は何を作りますか？`,
    placeholder: "何を作成・変更しますか？",
    attach: "ファイル・ソース",
    attachLabel: "ファイル添付またはソース接続",
    cancel: "依頼をキャンセル",
    send: "依頼を送信",
    checkingSession: "Studioセッションを確認中",
    cancelled: "計画をキャンセルしました",
    ontologySelectionRequired:
      "生成する前に、このプロジェクトが使用するアプリケーションパッケージを選択してください。",
    startingNotice:
      "依頼から開始します。coogoが制作物、次の変更、会話を一緒に管理します。",
    appGenerationStarting:
      "プロジェクトを作成し、画面・動作・データを準備しています...",
    webMcpVerified: (target) =>
      target
        ? `WebMCPで確認しました: ${target}.`
        : "WebMCPで現在のStudioコンテキストを確認しました。",
    readOnlyCompletionFallback:
      "応答から依頼された状況を確認できませんでした。変更は行わず、承認待ちも作成していません。",
    suggestions: [
      "予約アプリを作成",
      "文書をレビュー可能なレポートに変換",
      "既存の作業を更新",
    ],
    templateProposal: {
      heading: "既存のテンプレートが適合する可能性があります",
      startFaster: "を使うと、すばやく開始できます。",
      accept: "テンプレートを使用",
      accepting: "作成中...",
      decline: "ゼロから作成",
      accepted: "テンプレートからプロジェクトを作成しました。",
      declined: "ゼロから作成し、画面・動作・データを準備しています。",
      unavailable: "このテンプレート候補は利用できなくなりました。",
      failure:
        "テンプレートを使用できませんでした。ゼロから作成できます。",
    },
    planningFailure: {
      ontologyBlocked:
        "まだこのリクエストを作成できません — 必要な知識がライブラリにありません。別の製品アイデアを試すか、チームにこのドメインの追加を依頼してください。",
      planNotActionable:
        "このアプリに必要なデータ接続が計画に含まれていなかったため、何も作成していません。読み書きすべき情報（例：メンバーは誰か、何を請求するか）を書き添えて、もう一度送ってください。",
      requirementCoverage:
        "ご依頼の一部が計画に含まれなかったため、何も作成していません。リクエストを短い文に分けて、もう一度お送りください。",
      generic: "計画中に問題が発生しました。もう一度お試しください。",
    },
    updateExistingWork: {
      noExistingWork:
        "このワークスペースにはまだ保存された作業がありません。何を作りたいか教えていただければ始めます。",
      opening: "「{name}」を開いています。何を変更したいか教えてください。",
      chooseOne:
        "保存されたプロジェクトが複数あります — {choices}。どれを開きますか？",
    },
    toolFailure: {
      invalidInput:
        "この変更に必要な情報がリクエストに含まれていなかったため、何も変更していません。変更したい内容を具体的な一文で教えてください。",
      notPermitted:
        "この変更はまだチャットから実行できないため、何も変更していません。",
      unknownAction:
        "coogo にはまだその操作がないため、何も変更していません。",
      generic: "変更を完了できなかったため、何も変更していません。",
    },
  },
  templateContent: {
    aiFill: "AIで入力",
    briefPlaceholder: "例：活気ある街のベーカリー、温かいトーン",
    filling: "入力中...",
    aiUnavailable: "AIサービスはまだ設定されていません。",
    fillFailed: "AI入力に失敗しました。",
    fillBatchProgress: "バッチ {current}/{total} を入力中...",
    fillPartialNotice: "AIが{count}件の項目を入力できませんでした。残りは入力されています。",
    fillAllFailedNotice: "AIが{count}件の項目をすべて入力できませんでした。何も入力されていません。",
    judgeChecking: "確認中...",
    judgeFailed: "レビュー結果を取得できません。",
    pass: "合格",
    caution: "注意",
    reasons: "理由",
    hide: "非表示",
    show: "表示",
    general: "一般",
    changed: "変更済み",
    records: "{count}件のレコード",
    readOnly:
      "データファイルがないため、このバージョンでは読み取り専用です。",
    invalidJson: "有効なJSONを入力してください。",
    persistError: "一部の変更を保存できませんでした。再試行してください。",
    applySuccess: "コンテンツを適用しました。",
    applyFailed: "適用に失敗しました。以下のエラーを確認してください。",
    applyFailureSummary: "{count}件の適用に失敗しました。",
    applying: "適用中...",
    apply: "適用 ({count})",
    designRebindTitle: "デザインリリース",
    designRebindPresetLoadError: "プリセットを読み込めませんでした",
    designRebindPresetPlaceholder: "プリセットを選択",
    designRebindApply: "デザインを適用",
    designRebindApplying: "適用中...",
    designRebindApplySuccess: "デザインを適用しました",
    designRebindApplyFailed: "デザインの適用に失敗しました",
  },
  candidateVerification: {
    title: "候補の検証",
    description:
      "この生成候補には4つの独立証拠と1つの完全性カプセルが必要です。",
    authorityNote:
      "認証済み永続ジャーナルの読み取りだけが検証済み状態を付与できます。",
    promotionBoundary:
      "候補証拠の検証は、製品品質、公開、デプロイ、本番環境の読み取りを証明しません。",
    authorityLabel: "発行元: {authority}",
    verifiedStatus: "候補証拠を検証済み",
    publicationGateStatus: "公開は別のゲート",
    listLabel: "候補検証の証拠ロール",
    refresh: "チェックを更新",
    refreshing: "チェックを更新中",
    askCoogo: "Coogoに質問",
    liveChecking: "候補検証チェックを更新しています。",
    liveResult: "候補検証の状態: {status}。",
    statuses: {
      pending: "保留中",
      checking: "確認中",
      ready: "準備完了、未検証",
      verified: "検証済み",
      blocked: "ブロック",
      unavailable: "利用不可",
    },
    roles: {
      "sufficiency-assessment": "ソース十分性評価",
      "validator-safety-pass": "Validator安全性合格",
      "second-model-judge": "独立モデルレビュー",
      "render-execution-receipt": "レンダー実行レシート",
      "verification-capsule": "検証カプセル",
    },
    authorities: {
      "source-assessment-authority": "ソース評価サービス",
      "validator-runtime": "安全性検証サービス",
      "independent-model-judge": "独立モデルレビュー",
      "renderer-runtime": "レンダー検証サービス",
      "publishing-kernel": "公開整合性サービス",
    },
    causes: {
      "journal-checking": "永続検証ジャーナルを読み取っています。",
      "journal-unavailable": "永続ジャーナルの読み取りを利用できません。",
      "journal-scope-invalid": "永続ジャーナルがこの候補と一致しません。",
      "journal-contract-invalid": "永続ジャーナルが契約検証に失敗しました。",
      "verification-pending": "検証済み永続イベントはまだありません。",
      "producer-receipt-pending":
        "指定されたサーバー生成元のレシートを待機中で、候補は未検証です。",
      "verification-incomplete": "検証イベントに必要な独立証拠が不足しています。",
      "source-assessment-current": "現在の評価は準備済みですが、検証済みではありません。",
      "source-assessment-insufficient": "現在の評価はソース十分性要件を満たしていません。",
      "source-assessment-missing": "現在の不変ソース評価が必要です。",
      "source-assessment-stale": "候補の評価リビジョンは最新ではありません。",
      "durable-journal-verified":
        "永続ジャーナルに4つの独立証拠と完全性カプセルが記録されています。",
    },
  },
  report: {
    ...en.report,
    title: '状況',
    ontology: 'オントロジー',
    ontologySubtitle: 'オントロジーカバレッジ · 現在の根拠と目標範囲',
    unavailableTitle: 'まだ表示できる状況がありません',
    connectSource: 'デザインソースを開く',
    unavailableBody:
      "製品に検証済みのデザインソースが接続されると、品質状況がここに表示されます。coogoは作り物のスコアを表示しません。",
    unavailableBodyDeveloperDetail:
      "現在のリリースに紐づく最新の検証結果があるプロジェクトを選択してください。coogoは仮のスコアを生成しません。",
    axesLabel: "オントロジーカバレッジ軸",
    axesSummary: "6軸 · 必須最低スコア",
    axisMethod:
      "軸の値には必須ディメンションの最低スコアを使用します。根拠の可視化であり、権限を付与しません。",
    coverage: "オントロジーカバレッジ",
    coverageSubtitle: "現在の根拠と目標範囲",
    lowestRequiredAxis: "必須軸の最低値",
    loadingChart: "オントロジーカバレッジチャートを読み込み中",
    currentEvidence: "現在の根拠",
    targetEnvelope: "目標範囲",
    gap: "ギャップ",
    axisDetails: "軸の詳細",
    gaps: "ギャップ",
    priority: "優先度",
    blocks: "ブロック項目",
    noTrack: "ブロックなし",
    askCoogo: "coogoに相談",
    noOpenRequirements:
      "このリリース連携assessmentに未解決のオントロジー要件はありません。",
    allDimensions: "application ontology 全{count}ディメンション",
    dimension: "ディメンション",
    authority: "権限",
    resolution: "解決",
    current: "現在",
    evidence: "根拠",
    none: "なし",
    status: {
      ready: "準備完了",
      warn: "注意",
      blocked: "ブロック中",
      missing: "未取得",
      missingTooltip: "検証済みの根拠がまだありません",
    },
    conversionProvenance: {
      title: "元の画面との比較",
      sourceLabel: "変換した元",
      capturedLabel: "読み取った時刻",
      blocksLabel: "読み取った画面ブロック",
      decisionsLabel: "記録された変換の決定",
      realizationLabel: "アプリに反映された宣言",
      realizationAbsent:
        "記録された決定のうち構造的に確認できるものがないため、反映率はありません。coogo は数値を作り出しません。",
      sourceKindLegacyScreen: "貼り付けたレガシー画面",
    },
  },
  guide: {
    title: "ガイドワークスペース",
    documents: "ガイド",
    noPageSelected: "ガイドページが選択されていません。",
    page: "ページ",
    pageActivity: "ページのアクティビティ",
    proposals: "提案",
    applied: "適用済み",
    exports: "エクスポート",
    pages: "ページ",
    revision: "リビジョン",
    addPage: "ページを追加",
    pageTitle: "ページタイトル",
    openingEditor: "ガイドエディターを開いています...",
    pageActions: "ページ操作",
    reviewPage: "ページを確認",
    proposeEdit: "修正を提案",
    editContent: "内容を編集",
    move: "移動",
    delete: "削除",
    draft: "下書き",
    export: "エクスポート",
    readback: "検証結果",
    output: "出力",
    notRequested: "未依頼",
    noExportRequested: "このガイドにはエクスポート依頼がありません。",
  },
  connectedDocument: {
    notConnected: "未接続",
    connectedToTemplate: "{provider}に接続済み",
    documentReference: "ドキュメントのリンクまたは名前",
    loadDocument: "ドキュメントを読み込む",
    loadErrorNotConnected:
      "このワークスペースはまだ接続されていません。文書接続の設定をチームに依頼してください。",
    loadErrorGeneric: "ドキュメントを読み込めませんでした。再試行してください。",
    panelTitle: "接続されたドキュメント",
    provider: "プロバイダ",
    activeSourceSnapshot: "有効なソーススナップショット",
    connectionReference: "接続リファレンス",
    documentIdLabel: "ドキュメント ID",
    pageIdLabel: "ページ ID",
    fetchVerifiedSnapshot: "検証済みスナップショットを取得",
    saveProjectFirst:
      "プロバイダのドキュメントを接続する前に、このプロジェクトを保存してください。",
    unsavedDraftBlocker:
      "接続されたドキュメントのスナップショットを取得する前に、未保存の WordGard 下書きを完了または提案してください。",
    readbackSummary: (blocks, unsupported, accessMode) =>
      `ブロック ${blocks} 件 · 未対応 ${unsupported} 件 · ${accessMode}`,
    accessMode: { "read-only": "読み取り専用", "read-write": "読み書き" },
    openSource: "ソースを開く",
    newPageParentId: "新規ページの親 ID（任意）",
    publishApproval:
      "プロバイダ側に新しいコピーを 1 つ作成することを承認します。元のドキュメントは更新されません。",
    publishAction: "新しいコピーを作成して検証",
    publishBlockedUnsupported:
      "未対応のプロバイダ要素が解決されるまで公開はブロックされます。",
    connectionReadOnly:
      "この接続は読み取り専用です。コピーを公開するには、書き込み権限で接続し直してください。",
    proofLabel: "新規コピーの証跡:",
    proofStatus: { pass: "合格", fail: "不合格", blocked: "ブロック" },
    structure: "構造",
    structureReadBack: "読み戻し済み",
    structureMissing: "なし",
    exportsReadBack: (count) => ` · エクスポート ${count} 件を読み戻し`,
    openNewCopy: "新しいコピーを開く",
    publishVerified: (provider, versionRef) =>
      `新しい ${provider} のコピーを ${versionRef} で確認しました。`,
    publishBlockedBeforeVerified:
      "プロバイダのコピーが検証される前に公開がブロックされました。",
    publishProofFailed:
      "新しいコピーが作成された可能性はありますが、読み戻しの証跡は合格しませんでした。",
    publishGenericError:
      "接続されたドキュメントのコピーを公開できませんでした。",
  },
  behavior: {
    ...en.behavior,
    navigation: "ActionFlowナビゲーション",
    title: "アクションフロー",
    selectFlow: "イベントまたはフローを選択",
    unsaved: "未保存",
    target: "動作対象",
    noElement: "要素が選択されていません",
    selectInPreview: "プレビューで選択",
    section: "セクション",
    status: "状態",
    bindings: "接続",
    flows: "フロー",
    empty: "空",
    steps: "ステップ",
    verified: "検証済み",
    failed: "失敗",
    notVerified: "未検証",
    noFlows: "このプロジェクトに宣言されたフローはありません。",
    eventBindings: "イベント接続",
    connected: "接続済み",
    orphan: "未接続",
    inspectElement: "動作を確認するアプリ要素を選択してください。",
    noBindings: "このプロジェクトに宣言されたイベント接続はありません。",
    chatActions: "チャット操作",
    reviewFlow: "フローを確認",
    addStep: "ステップを追加",
    connectEvent: "イベントを接続",
    testFlow: "フローをテスト",
    editor: "動作エディター",
    unsavedFlow: "未保存のフロー",
    noBlocks: "ブロックがないためフローを保存できません",
    testRun: "テスト実行",
    closeFlowForm: "フローフォームを閉じる",
    newFlow: "新規フロー",
    addBlockFirst: "フローを作成する前にページブロックを追加してください",
    nodeTypes: "対応するActionFlowノード種別",
    nodes: "ノード",
    executionModel: "実行モデル",
    binding: "接続",
    direct: "直接",
    directAtomic: "直接 atomic",
    namedFlow: "名前付きフロー",
    lanes: "領域",
    actionLane: "Action",
    uiLane: "UI",
    motionLane: "Motion",
    order: "順序",
    completionBarrier: "完了待機",
    stage: "Stage",
    saga: "Saga",
    sagaCompensation: "Sagaは逆順に補償 · ACIDではありません",
    palette: {
      ifElse: "条件分岐",
      loop: "ループ",
      switch: "スイッチ",
      parallel: "並列",
      tryCatch: "例外処理",
    },
    flowCreateFailed: (name) =>
      `Flow「${name}」は作成されませんでした — サーバーが拒否したため、ローカルの変更は元に戻しました`,
    flowCreated: (name) => `Flow "${name}" を作成しました`,
    flowCreatedLocalOnly: (name) =>
      `Flow "${name}" はこのセッション内だけに作成されました — サーバーにはまだ保存されていません`,
  },
  approval: {
    categoryFlows: "フロー",
    categoryDatasources: "データソース",
    categoryVariables: "変数",
    categoryEvents: "イベント接続",
    compositeActionFallback: "まとめて実行",
    operationCount: (count) => `操作 ${count} 件`,
    applyAll: "すべて適用",
    applySelected: (selected, total) => `適用 (${selected}/${total})`,
    dismiss: "取り消す",
    applied: "適用しました",
    pendingValidation: (reason) =>
      `検証待ち${reason ? `: ${reason}` : ""}`,
    dismissed: "取り消しました",
    edit: "編集",
  },
  insertPanel: {
    noActivePage: "開いているページがありません",
    searchPlaceholder: "UI を検索",
    maxChildrenReached: (current, max) =>
      `子要素の上限に達しました (${current}/${max})`,
    fromLibrary: "ライブラリから",
    createManually: "自分で作る",
    detailCreateOrOpenPage: "UI を追加する前に、ページを作るか開いてください。",
    detailNeedsBlockTarget: "新しい要素を入れるブロックが必要です。",
    detailAppendsToBlock: "このブロックの最後に要素を追加します。",
    detailInside: (target) => `${target} の中に`,
    detailAfterSelectionIn: (target) => `${target} で選んだ要素の後に`,
    targetLabel: "対象",
    newBlock: "新しいブロック",
    noLibraryBlocks: "再利用できるブロックがまだありません",
    moreBlocks: (count) => `ほかに ${count} 個のブロックを表示`,
    noElementsFound: "要素が見つかりません",
  },
  dataExplorer: {
    datasourceCreated: (name) => `データソース「${name}」を作成しました`,
    datasourceNameTaken: (name, blockName) =>
      `ブロック「${blockName}」には既にデータソース「${name}」があります。何も変更していません — 別の名前を付けるか、既存のデータソースを編集してください。`,
    datasourceCreateFailed: (name, blockName) =>
      `「${name}」を作成できませんでした — ブロック「${blockName}」が見つかりません（削除または名称変更されましたか？）。何も変更していません。`,
  },
  routeHeadings: {
    map: "アプリマップ",
    admin: "管理者",
    billing: "課金",
    themeEditor: "テーマエディター",
    newProject: "何を作りますか？",
  },
  dataInspector: {
    header: {
      title: "エンティティ詳細",
      reviewEntity: "エンティティをレビュー",
    },
    fields: {
      name: "名前",
      block: "ブロック",
      scope: "スコープ",
      type: "型",
      defaultValue: "既定値",
      expression: "式",
      path: "パス",
      importSource: "インポート元",
      call: "呼び出し",
      method: "メソッド",
      kind: "種別",
      endpoint: "エンドポイント",
      resultTarget: "結果の格納先",
      headers: "ヘッダー",
      body: "ボディ",
      source: "ソース",
      eventKey: "イベントキー",
      component: "コンポーネント",
      event: "イベント",
      flow: "フロー",
      trigger: "トリガー",
      steps: "ステップ",
      usedBy: "参照",
    },
    placeholders: {
      noDefault: "既定値なし",
      expression: "例: #{otherVar} + 1",
      resultTarget: "このデータソースが書き込む状態変数",
      body: "リクエストボディのテンプレート",
    },
    emptyStates: {
      selectEntity: "詳細を見るエンティティを選択してください",
      entityNotFound: "エンティティが見つかりません",
      variableNotFound: "変数が見つかりません",
      datasourceNotFound: "データソースが見つかりません",
      eventNotFound: "イベントが見つかりません",
      flowNotFound: "フローが見つかりません",
    },
    actions: {
      testInExpressionLab: "式ラボでテスト",
      writeExpressionInLab: "式ラボで式を作成",
      openInFlowEditor: "フローエディタで開く",
      deleteVariable: "変数を削除",
      deleteDatasource: "データソースを削除",
      deleteEvent: "イベントを削除",
    },
    appManifest: {
      scopeValue: "アプリマニフェスト",
      note: "アプリマニフェストのデータソースはプロジェクト共通の入力です。ブロック単位のバインディングはブロックのデータソースまたはコード画面で編集してください。",
    },
    schema: {
      request: "リクエストスキーマ",
      response: "レスポンススキーマ",
    },
    flowUnbound: "-- 未接続 --",
    stepCount: (count) => `${count} ステップ`,
    validationSummary: (errors, warnings) =>
      `エラー ${errors} 件、警告 ${warnings} 件`,
    referencedWarning:
      "この変数は他の場所から参照されています。削除すると式が壊れる可能性があります。",
    toasts: {
      variableDeleted: (name) => `変数「${name}」を削除しました`,
      datasourceDeleted: (name) => `データソース「${name}」を削除しました`,
      eventRemoved: (name) => `イベント「${name}」を削除しました`,
      bindingsUpdated: (count, oldPath, newPath) =>
        `${count} 件のバインディングを更新しました: ${oldPath} -> ${newPath}`,
      bindingsUpdateFailed:
        "バインディングの更新に失敗しました。古いフィールド名への参照は手動で修正が必要な場合があります。",
    },
  },
  dataValidation: {
    confirm: {
      deletePrompt: (name) => `「${name}」を削除しますか?`,
      typeChangePrompt: (name) => `「${name}」の型を変更しますか?`,
      deleteAction: "削除",
      typeChangeAction: "型を変更",
      cancel: "キャンセル",
      unnamedField: "名前のないフィールド",
      nestedFieldWarning: (count) =>
        `このフィールドの下にある ${count} 個のフィールドと、その中身もすべて削除されます。`,
    },
    messages: {
      unmatchedBrackets: "#{} の括弧が対応していません",
      emptyExpression: "式 #{} が空です",
      duplicateVariableName: (name, blockName) =>
        `ブロック「${blockName}」で変数名「${name}」が重複しています`,
      variableUnreferenced: (name) =>
        `変数「${name}」はどのフローや式からも参照されていません`,
      flowMissing: (flowName) => `フロー「${flowName}」は存在しません`,
      restDatasourceNoEndpoint:
        "REST データソースにエンドポイント URL がありません",
    },
  },
  proofHealth: {
    verified: "検証済み",
    needsReview: "要確認",
    needsLiveCheck: "ライブ点検が必要",
    blocked: "ブロック中",
    none: "証跡なし",
    checkedLive: (date) => `${date} にライブ確認`,
    dateUnrecorded: "記録済み",
    liveCheckBlocked: "ライブ点検がブロックされています。",
    liveCheckNeedsReview: "ライブ点検の確認が必要です。",
    recordBlocked: "証跡レコードがブロックされています。",
    recordNeedsReview: "証跡レコードの確認が必要です。",
    openDeployToRun: "デプロイを開いてライブ点検を実行してください。",
    reviewEvidence: "証跡を確認",
    reviewShare: "共有を確認",
  },

  // ── lane/kodash · map · projects · apps-live · import ────────────────────
  // Four screens whose readable surface was English on 2026-08-20, harvested
  // by `__tests__/readable-surface-localized.test.tsx` (33 + 21 + 16 + 14
  // word-entries). Appended at the END of every table on purpose: two sibling
  // lanes were editing the sections above at the same time, and the tail is
  // the one place four concurrent additions cannot collide.
  //
  // EVERY key below is spelled out in all five tables. `ko`, `ja` and `zh`
  // open with `...en` and `zhTW` with `...zh`, so a key added to `en` alone
  // is INHERITED rather than missing — and because the residue walk derives
  // its keep-Latin allowlist from the translated tables, inherited English
  // allowlists its own words and the walk goes silent. Measured, not assumed:
  // mutation M5 in the register test deletes one `ko` override here and the
  // residue walk stays green. The inheritance gate is what turns it red.
  appMap: {
    description:
      "cogo アプリ内の画面、ブロック、アクション、状態、データ接続を確認します。",
    emptyTitle: "アプリを開くと構造をマップで確認できます",
    emptyBody:
      "アプリマップは、プロジェクトや取り込んだソース、生成されたライブラリ項目を読み込んでから役に立ちます。公開前に画面・データ・アクションフローのステップがどうつながるかを示します。",
    createApp: "アプリを作成",
    importSource: "ソースを取り込む",
    openProjects: "作業を開く",
    noAppLoaded: "読み込まれたアプリなし",
    nodesUnit: "ノード",
    edgesUnit: "エッジ",
    nodeTypes: {
      app: "アプリ",
      page: "画面",
      block: "ブロック",
      atom: "デザイン要素",
      flow: "フロー",
      stateVar: "状態変数",
      datasource: "データソース",
    },
    stats: { title: "統計", edges: "エッジ" },
    legend: {
      title: "凡例",
      filterLabel: "ノード種別で絞り込む",
      clearFilter: "絞り込みを解除",
    },
    nodeDetail: {
      title: "ノード詳細",
      empty: "ノードをクリックすると詳細を確認できます",
      incomingUnit: "入力",
      outgoingUnit: "出力",
    },
    sessions: {
      title: "セッション",
      body: "ブレインストーミングの観察タイムラインはフェーズ 3 で提供予定です。このパネルでは、現在開いているアプリの採用・見送り・デザインシステムの調整を振り返れるようになります。",
    },
    canvas: {
      controls: "表示コントロール",
      zoomIn: "拡大",
      zoomOut: "縮小",
      fitView: "画面に合わせる",
      interactive: "操作ロックの切り替え",
      miniMap: "ミニマップ",
      handle: "接続ポイント",
      nodeDescription:
        "Enter またはスペースでノードを選択します。その後、矢印キーでノードを移動できます。削除するには delete、取り消すには esc を押してください。",
      nodeKeyboardDisabled:
        "Enter またはスペースでノードを選択します。矢印キーでノードを移動できます。削除するには delete、取り消すには esc を押してください。",
      nodeMoved: (direction, x, y) => `選択したノードを ${direction} に移動しました。新しい位置 x: ${x}、y: ${y}`,
      edgeDescription:
        "Enter またはスペースでエッジを選択します。その後、削除するには delete、取り消すには esc を押してください。",
    },
  },
  projectCard: {
    status: {
      published: "公開中",
      draft: "下書き",
      archived: "アーカイブ済み",
      active: "進行中",
    },
    share: "共有",
    shareNamed: (name) => `${name} を共有`,
    openActions: (name) => `${name} の操作メニューを開く`,
    rename: "名前を変更",
    duplicate: "複製",
    settings: "設定",
    delete: "削除",
  },
  outputsSection: {
    heading: "成果物",
    breakdown: (shared, drafts) => `共有 ${shared}件 · 下書き ${drafts}件`,
    loading: "成果物を読み込み中…",
    emptyTitle: "まだ成果物がありません",
    emptyBody:
      "アプリ・ドキュメント・レポートを作成するか取り込むと、ここに表示されます。最初は下書きとして、共有すると共有した成果物として並びます。",
    showMore: (count) => `成果物をさらに ${count}件 表示`,
  },
  launchLoop: {
    addSource: "アイデア、ファイル、URL、リポジトリ、デザイン",
    shape: "cogo が作業を整えます",
    save: "ソースと次のステップをまとめて保存します",
    share: "開く・共有する・公開する",
  },
  // ─── lane/kobiz: the nine business screens ───────────────────────────
  businessScreens: {
    review: {
      tryAgain: 'もう一度試す',
      openSurface: (surface) => `${surface}を開く`,
      viewSurface: (surface) => `${surface}を見る`,
      tableEmpty: '表示できるデータがありません',
      periodAll: 'すべて',
      observation: {
        measuredSummary: '計測されたレビューデータです。',
        emptySummary: 'レビュー観測の詳細',
        observedAt: (stamp) => `観測 ${stamp}`,
        evidence: '根拠:',
      },
      pagination: {
        first: '最初のページ',
        previous: '前のページ',
        next: '次のページ',
        last: '最後のページ',
      },
      status: {
        active: '有効',
        draft: '下書き',
        archived: 'アーカイブ済み',
        pending: '保留',
        processing: '処理中',
        completed: '完了',
        refunded: '返金済み',
        shipping: '出荷中',
      },
    },
    data: {
      description: 'アプリ・文書・レポートのソースの束、抽出したフィールド、準備状況、来歴。',
      addSource: 'ソースを追加',
      loadFailed: '現在、ソースを読み込めませんでした。',
      loading: 'ソースのレビューデータを読み込んでいます…',
      empty: 'このワークスペースで受理されたソースの束は観測されませんでした。',
      scope: 'ソースの束のレビュー',
      summaryLabel: 'ソースの概要',
      tiles: { sources: 'ソース', ready: '準備完了', drafts: '下書き' },
      columns: {
        name: 'ソース',
        artifact: '成果物',
        confidence: '確度',
        rows: '行数',
        status: '状態',
      },
      readyStatus: '準備完了',
      filters: {
        artifact: '成果物',
        allArtifacts: 'すべての成果物',
        status: '状態',
        all: 'すべて',
        ready: '準備完了',
        draft: '下書き',
        archived: 'アーカイブ済み',
      },
      unavailable: {
        'producer-not-configured': {
          summary: 'ソースレビューのデータ提供元の接続が設定されていません。',
          title: 'ソースレビューの接続を設定する',
          detail: 'ワークスペース管理者が受理済みのソースレビュー提供元を設定する必要があります。その間もソースの束は追加できます。',
        },
        'metric-not-supported': {
          summary: '現在のレビュー契約はこのソース指標を計測しません。',
          title: '対応しているソースレビュー項目を使う',
          detail: '受理済みのソース束の項目で続けてください。この指標は現在のレビュー契約では報告できません。',
        },
        'authority-not-admitted': {
          summary: '現在のソースと権利の権限は、このソースデータの計測を認めていません。',
          title: 'ソースの権限を確認する',
          detail: '計測を依頼する前に、ソースの権利と受理の根拠を確認してください。',
        },
      },
    },
    activity: {
      description: '成果物を共有した後の、依頼された変更・レビュー状態・チームの操作を追跡します。',
      exportLabel: 'エクスポート',
      exportAria: (count) => `アクティビティ ${count} 件を CSV でエクスポート`,
      exportBlockedAria: (reason) => `エクスポート — ${reason}`,
      blocked: {
        notLoaded: 'アクティビティを読み込めませんでした',
        none: 'エクスポートできるアクティビティがまだありません',
        loading: 'アクティビティを読み込んでいます',
        unavailable: 'アクティビティの計測を利用できません',
      },
      exported: (count, filename) => `${count} 件を ${filename} に書き出しました`,
      loadFailed: '現在、アクティビティを読み込めませんでした。',
      loading: 'アクティビティのレビューデータを読み込んでいます…',
      empty: 'このワークスペースで記録されたアクティビティは観測されませんでした。',
      scope: '公開した成果物のアクティビティレビュー',
      summaryLabel: 'アクティビティの概要',
      tiles: { pending: '保留', applied: '反映済み', reviewed: 'レビュー済み' },
      tabs: {
        all: 'すべて',
        pending: '保留',
        applied: '反映済み',
        reviewed: 'レビュー済み',
        rejected: '却下',
      },
      columns: {
        actor: '実行者',
        artifact: '成果物',
        changes: '変更',
        state: '状態',
        updated: '更新',
      },
      changeCount: (count) => `変更 ${count} 件`,
      unavailable: {
        'producer-not-configured': {
          summary: 'アクティビティレビューのデータ提供元の接続が設定されていません。',
          title: 'アクティビティレビューの接続を設定する',
          detail: 'ワークスペース管理者が受理済みのアクティビティレビュー提供元を設定する必要があります。設定中も作業の共有は続けられます。',
        },
        'metric-not-supported': {
          summary: '現在のレビュー契約はこのアクティビティ指標を計測しません。',
          title: '対応しているアクティビティ観測を使う',
          detail: '対応しているレビューイベントで続けてください。この指標は現在のアクティビティレビュー契約の範囲外です。',
        },
        'authority-not-admitted': {
          summary: '現在のソースと権利の権限は、このアクティビティの計測を認めていません。',
          title: 'アクティビティの権限を確認する',
          detail: '計測を依頼する前に、このアクティビティのソース権利と受理の根拠を確認してください。',
        },
      },
      drawer: {
        title: (id) => `アクティビティ #${id}`,
        close: '閉じる',
        process: '処理',
        refund: '差し戻し',
        customer: '実行者:',
        email: 'メール:',
        app: 'アプリ:',
        date: '日時:',
        items: '項目',
        noItems: 'この記録に項目はありません。',
        subtotal: '小計',
        shipping: '送料',
        discount: '割引',
        total: '合計',
      },
    },
    impact: {
      description: '共有した cogo 成果物全体の表示数、レビュー転換、反映された変更、成果のシグナル。',
      loadFailed: '現在、成果のレビューデータを読み込めませんでした。',
      loading: '成果のレビューデータを読み込んでいます…',
      empty: 'この期間について、正式な成果レビューでは観測がありませんでした。',
      scope: '選択した成果レビュー期間',
      columns: {
        artifact: '成果物',
        views: '表示数',
        reviews: 'レビュー',
        cvr: '転換率',
      },
      seriesViews: '表示数',
      trendTitle: '成果の推移',
      byArtifact: '成果物ごとの成果',
      unavailable: {
        'producer-not-configured': {
          summary: '成果レビューのデータ提供元の接続が設定されていません。',
          title: '成果レビューの接続を設定する',
          detail: 'ワークスペース管理者が受理済みの成果レビュー提供元を設定する必要があります。設定中も成果物の共有は続けられます。',
        },
        'metric-not-supported': {
          summary: '現在のレビュー契約はこの成果指標を計測しません。',
          title: '対応している成果観測を使う',
          detail: '対応している成果シグナルで続けてください。この指標は現在の成果レビュー契約の範囲外です。',
        },
        'authority-not-admitted': {
          summary: '現在のソースと権利の権限は、この成果の計測を認めていません。',
          title: '成果の権限を確認する',
          detail: '計測を依頼する前に、成果シグナルの背後にあるソース権利と受理の根拠を確認してください。',
        },
      },
    },
    audience: {
      description: '公開したアプリの利用者の増加、流入元、再訪問者を確認します。',
      loadFailed: '現在、利用者のレビューデータを読み込めませんでした。',
      loading: '利用者のレビューデータを読み込んでいます…',
      empty: 'このワークスペースについて、正式な利用者レビューでは観測がありませんでした。',
      scope: '利用者のレビュー',
      kpis: { reviewers: 'レビュアー', new7d: '新規 (7日)', returnRate: '再訪率' },
      rolesTitle: '利用者の役割',
      recentReviewers: '最近のレビュアー',
      columns: {
        name: '名前',
        email: 'メール',
        role: '役割',
        joined: '参加日',
      },
      unavailable: {
        'producer-not-configured': {
          summary: '利用者レビューのデータ提供元の接続が設定されていません。',
          title: '利用者レビューの接続を設定する',
          detail: 'ワークスペース管理者が受理済みの利用者レビュー提供元を設定する必要があります。設定中も成果物の共有は続けられます。',
        },
        'metric-not-supported': {
          summary: '現在のレビュー契約はこの利用者指標を計測しません。',
          title: '対応している利用者観測を使う',
          detail: '対応しているレビュー参加のシグナルで続けてください。この指標は現在の利用者レビュー契約の範囲外です。',
        },
        'authority-not-admitted': {
          summary: '現在のソースと権利の権限は、この利用者情報の計測を認めていません。',
          title: '利用者の権限を確認する',
          detail: '計測を依頼する前に、利用者観測のソース権利と受理の根拠を確認してください。',
        },
      },
    },
    analytics: { eyebrow: 'アプリの計測', body: '共有した成果物を開いて、流入・転換・成果のシグナルを確認してください。計測はレビュー済みの成果物に結び付いているため、関係のないホーム画面に移動することはありません。' },
    requestFlow: {
      prepared: { title: 'リクエストが準備されます', body: 'cogo がアプリ・文書・レポートの変更を準備します。' },
      attached: { title: 'レビューの根拠が残ります', body: 'ソース・画面・共有の状態がリクエストとともに残ります。' },
      approval: { title: '共有はあなたの承認後', body: '公開と提供開始の更新は、あなたがレビューするまで待機します。' },
    },
    themeEditor: {
      badge: 'デザインシステム',
      description: 'Studio でそのまま使えるテーマ軸を調整し、実際の cogo コンポーネントブロックで結果を確認してからライブラリに保存します。',
      livePreviewNote: 'ライブプレビューは cogo レンダラーに渡すものと同じペイロードを使います。',
    },
    billing: {
      currentPlan: '現在のプラン',
      currentPlanNote: 'ご利用中のサブスクリプションの詳細',
      cancelsAtPeriodEnd: '現在の期間の終了時に解約',
      cycle: '請求サイクル',
      amount: '金額',
      started: '開始日',
      nextBilling: '次回請求',
      changePlan: 'プラン変更',
      cancelSubscription: 'サブスクリプションを解約',
      cancelling: '解約しています…',
      viewPlans: 'プランを見る',
      paymentHistory: '支払い履歴',
      paymentHistoryNote: 'すべての支払い履歴',
      paymentsSignedOut: '支払い履歴を見るにはログインしてください',
      paymentsFailed: '支払い履歴を読み込めませんでした',
      paymentsEmpty: '支払い履歴はありません',
      columns: {
        date: '日付',
        amount: '金額',
        status: '状態',
        provider: '決済手段',
        reference: '参照',
      },
      status: {
        active: '有効',
        paid: '支払い済み',
        pending: '保留',
        cancelled: '解約済み',
        failed: '失敗',
      },
        statusUnknown: (value) => `不明な状態 (${value})`,
      cancelDialog: {
        title: 'サブスクリプションを解約しますか？',
        body: (periodEnd) => `現在の請求期間が終わる ${periodEnd} までは有効です。それ以降は請求されません。`,
        keep: 'サブスクリプションを継続',
        confirm: 'はい、解約します',
      },
    },
    admin: { accessDeniedTitle: 'アクセス権限がありません', accessDeniedBody: 'このページにアクセスするには管理者権限が必要です。' },
  },
  // ─── end lane/kobiz ─────────────────────────────────────────────────
};

const zh: StudioDictionary = {
  ...en,
  // ── lane/koentry ─────────────────────────────────────────────────────────
  newProject: ZH_CN_NEW_PROJECT_COPY,
  starters: ZH_CN_STARTERS_COPY,
  // ── end lane/koentry ─────────────────────────────────────────────────────
  designSystemAxes: ZH_CN_DESIGN_AXES_COPY,
  language: { label: "语言" },
  a11y: {
    skipToMainContent: "跳到主要内容",
    dashboardContent: "仪表板内容",
    loadingSurface: (surface) => `正在加载${surface}…`,
    creatorNav: "导航",
    header: "页眉",
    designSystemSurfaces: "设计系统页面",
    designSystemInventory: "此工作区的设计系统",
    importSources: "导入来源",
    librarySummary: "库概览",
    operations: "运营",
    requestReviewFlow: "请求审核流程",
    reviewSummary: "审核摘要",
    shareReadinessChecklist: "共享就绪清单",
    appPreviewFrame: "coogo 应用预览",
    entryBrandHome: "coogo 首页",
    entryReport: "coogo 运营报告",
    showSection: (name) => `展开${name}`,
    hideSection: (name) => `收起${name}`,
  },
  ide: {
    undo: "撤销",
    redo: "重做",
    appPageList: "应用页面列表",
    documentList: "文档列表",
    viewportSwitcher: "视口切换",
    device: {
      mobile: "手机",
      tablet: "平板",
      desktop: "桌面",
      document: "文档",
      a4Portrait: "A4 纵向",
      framed: (label, width, height) => `${label} (${width}x${height})`,
      sized: (label, width, height) => `${label} · ${width}x${height}px`,
    },
    pages: {
      title: "页面",
      summaryWithPages: (count) => `预览 + ${count} 个页面`,
      summaryPreviewOnly: "预览",
      runnableTitle: "预览",
      runnableBadge: "运行",
      runnableDetail: "运行视图",
      emptyTitle: "当前屏幕",
      emptyBody: "暂无页面信息",
    },
    documents: {
      title: "文档",
      count: (count) => `${count} 个文档`,
      pageCount: "1 页",
    },
    reviewPreview: "检查预览",
    print: "打印",
  },
  navigation: {
    sections: {
      start: "开始",
      build: "构建",
      verify: "验证",
      release: "发布",
    },
    home: '首页',
    create: '创建',
    work: '工作',
    details: '详细工具',
    map: '地图',
    code: '代码',
    release: '发布',
    publish: '发布',
    launch: '上线',
    configure: '设置',
    settings: '设置',
    share: '分享',
    starters: '入门模板',
    import: '导入',
    sources: '来源',
    library: '资料库',
    documents: '文档',
    requests: '请求',
    requestsAndReviews: '请求与审核',
    bots: '机器人',
    operations: '运营',
    data: '数据',
    activity: '活动',
    impact: '成效',
    audience: '受众',
    analytics: '分析',
    designSystem: '设计系统',
    workspaceSettings: '工作区设置',
    projects: '项目',
    templates: '模板',
    projectSettings: '项目设置',
    account: '账户',
    signIn: '登录',
    signOut: '退出登录',
    goHome: '前往 Studio 首页',
    goCoogoHome: '前往 Coogo 首页',
    coogoHomepage: 'Coogo 主页',
    studioNavigation: 'Studio 导航',
    studioNavigationDescription: '在创建、项目、已保存工作、请求和设置之间导航。',
    collapseNavigation: '收起导航栏',
    expandNavigation: '展开导航栏',
  },
  artifacts: {
    label: '产物模式',
    app: '应用',
    game: '游戏',
    design: '设计',
    document: '文档',
    guide: '指南',
    insights: '现状',
  },
  work: {
    title: '工作',
    subtitle: '创建、管理并审阅应用、文档与报告。',
    namePlaceholder: '新工作名称...',
    create: '创建',
    browseStarters: '浏览模板',
    importSource: '导入来源',
    emptyTitle: '暂无工作',
    emptyBody: '在上方创建应用、文档或报告，或浏览下方模板',
  },
  designSystem: {
    title: '设计系统',
    subtitle: '设定生成应用所使用的颜色、字体、间距与形状，并在保存到库之前对照 cogo 词汇进行检查。',
    validationLab: '验证实验室',
    themeEditor: '主题编辑器',
    tabs: {
      systems: '设计系统',
      validation: '验证实验室',
      vocabulary: '词汇',
      briefings: '简报',
    },
    uiBlocks: {
      eyebrow: 'UI Block 本体',
      title: '你正在制作什么？',
      description: '描述所需界面，Coogo 会从已批准目录中查找真实区块，并使用当前设计本体主题预览。',
      intentLabel: '产品意图',
      intentPlaceholder: '例如：团队分析仪表板',
      shortcutsLabel: '从意图开始',
      shortcuts: { dashboard: '仪表板', store: '商店', landing: '着陆页', auth: '登录流程' },
      recommendations: '推荐 UI Block',
      admittedCount: (count) => `${count} 个目录已批准区块`,
      noMatches: '暂无与此意图匹配的已批准区块。请尝试更简短的产品描述。',
      preview: '使用此主题预览',
      selectedPreview: '已选用于实时主题预览',
      readOnly: '只读预览',
      authorityUnavailable: '在结构兼容性和发布权限得到证明之前，项目应用与替换不可用。',
    },
    precondition: {
      signedOutReason:
        '需要登录。此工作区的设计系统列表，以及从来源读取的值，仅向已登录的用户显示。',
      signInCta: '前往登录',
      checkingReason: '正在检查此页面能否打开…',
      unmetFallbackReason: '打开此页面所需的条件尚未满足。',
    },
    sourceRead: {
      failed: '无法读取此项目的设计来源。',
      failedWithMessage: '目前无法读取此项目的设计来源：{message}',
    },
    vocabularyPanel: {
      title: '词汇',
      search: '搜索…',
      enumPrefix: '前缀',
      enumAccessor: '访问器',
      enumEvent: '事件',
      enumAction: '动作',
      colName: '名称',
      colMode: '模式',
      colSemantics: '语义',
      colCategory: '分类',
      colReturns: '返回',
      colDomain: '领域',
      colDescription: '说明',
      loading: '正在加载词汇…',
      errorTitle: '无法加载词汇',
      retry: '重试',
      empty: '此分片中没有条目。',
      unavailableTitle: '词汇不可用',
      unavailableDetail: '词汇目录需要本地知识图谱才能显示。',
      noMatch: '没有与“{query}”匹配的条目',
      clear: '清除',
    },
    validatorPanel: {
      specChip: "§ 设计系统 v4",
      heading: "验证器",
      lastRun: '上次：',
      justNow: '刚刚',
      secondsAgo: '{n}秒前',
      minutesAgo: '{n}分钟前',
      hoursAgo: '{n}小时前',
      rerun: '重新运行',
      validating: '正在检查…',
      colRule: '规则',
      allChecks: '全部检查',
      noViolations: '没有可显示的规则违规。',
      colGate: '关卡',
      colStatus: '状态',
      statusPass: '通过',
      statusFail: '未通过',
      flagOffTitle: '检查已关闭',
      flagOffBody: '此工作区未启用设计检查。',
      readyTitle: '可以开始检查',
      readyBody: '点击“重新运行”以检查当前项目。',
      unreadableTitle: '无法读取检查结果',
      unreadableBody: '上次检查返回了此页面无法解析的结果。请点击“重新运行”重试。',
      briefingReadFailed: '无法读取此工作区的简报。',
      briefingTitle: '简报',
      briefingIntro: '为这个应用撰写简短的简报。简报会与区块一同保存在本体中，重新加载后仍然保留。',
      briefingSignIn: '请登录后撰写简报。',
      briefingSignedOutDetail: '简报通过本体网关按工作区保存。请登录以读取和撰写此应用的简报。',
      briefingUnavailableTitle: '此工作区暂时无法使用简报',
      briefingUnavailableDetail: '当前 Studio 部署未提供受支持的简报存储。读取和写入已停用。',
      briefingFieldTitle: '标题',
      briefingFieldBody: '正文（Markdown — 以转义文本呈现）',
      briefingTitlePlaceholder: '例如：第二季度发布检查清单',
      briefingBodyPlaceholder: '在此撰写简报。换行会保留，HTML 会被转义。',
      briefingReady: '就绪',
      briefingSaving: '正在保存…',
      briefingSave: '保存简报',
      briefingEmpty: '还没有简报。在上方写下第一条并点击保存。',
      repoInitTitle: '正在启动检查…',
      repoInitBody: '正在准备检查器 — 需要一点时间。',
      noBlocksTitle: '暂无可检查的区块',
      noBlocksBody: '在编辑模式聊天中生成区块后，此页面就会有内容。',
      errorTitle: '检查失败',
      errorBody: '请查看下方错误信息，然后点击“重新运行”。',
    },
    inventory: {
      readingBadge: '正在读取此工作区的设计系统…',
      readingDetail: '在列表返回之前，不做任何断言。',
      unreadableBadge: '无法读取此工作区的设计系统',
      unreadableDetail:
        '列表没有加载成功，所以此页面无法说明该工作区有多少个设计系统。下方的主题编辑器仍可使用。',
      noneBadge: '此工作区没有保存的设计系统{stamp}',
      noneDetail:
        '这里还没有任何内容被保存为设计系统。在下方设定各个维度并保存到库，即可创建第一个。',
      listedBadge: '此工作区的设计系统 {count} 个{stamp}',
      listedBadgeOne: '此工作区的设计系统 {count} 个{stamp}',
      listedDetail:
        '这是上面时间点返回的列表原样。打开此页面只会列出它们，并不会对它们做任何检查。',
      readStamp: '，{time} 读取',
      setAsideNote:
        ' 另有 {count} 行属于以模板创建的项目，不是工作区设计系统。',
      setAsideNoteOne:
        ' 另有 {count} 行属于以模板创建的项目，不是工作区设计系统。',
    },
    themeEditorPanel: {
      title: '主题编辑器',
      body:
        '调整样式、基础色、主题、图表配色、圆角和字体，真实的 cogo 组件区块会随之更新。满意后保存到库。',
    },
    workbench: {
      controlsTitle: '主题控件',
      selectPlaceholder: '选择{axis}',
      saveToLibrary: '保存到库',
      saveDialogTitle: '保存设计系统',
      namePlaceholder: '预设名称',
      descriptionPlaceholder: '说明（可选）',
      closeLabel: '关闭',
      cancel: '取消',
      save: '保存',
      saving: '正在保存…',
      savedAs: '已保存为“{name}”',
      nameRequired: '请输入名称。',
      saveFailed: '保存失败。',
      sourceTitle: '从来源读取的设计系统',
      sourceEmptyBody: '此项目尚未导入任何设计来源 — 在你决定之前，下方每个维度都是空缺。',
      sourceImportCta: '导入设计来源（Figma）',
      sourceReadPrefix: '读取自 ',
      sourceVariableCount: '· {count} 个变量',
      sourceVariableCountOne: '· {count} 个变量',
      chooseAxisValue: '选定',
      axisValueSelectLabel: '{axis} 值',
    },
    projectTokens: {
      title: '从设计文件导入的令牌',
      bodyBefore:
        '从导入的设计文件中读到的颜色和变量属于导入它的项目，而不属于工作区。打开那个项目并进入 ',
      navPath: '设计 → 令牌',
      bodyAfter:
        ' 即可查看并应用到该项目。',
    },
  },
  settings: {
    title: '工作区设置',
    profile: {
      title: '个人资料',
      name: '名称',
      email: '邮箱',
      edit: '编辑',
      cancel: '取消',
      providerBlocked: '当前登录方式还无法修改个人资料',
      save: '保存',
      saving: '保存中…',
      nameInputLabel: '工作区显示名称',
      nameSaved: '名称已保存。',
      signedOutBlocked: '登录后可编辑个人资料',
    },
    writeOutcome: {
      protectedRejected: '此受保护的设置未被更改。',
      accepted: '项目设置已接受。',
    },
    publishing: {
      title: '发布',
      exportsLabel: '成果导出：',
      exportsNone: '尚无记录',
      exportsCount: '已记录 {count} 项',
      recordsNone: '此工作区还没有保存任何发布记录。',
      recordsCount: '此工作区已保存 {count} 条发布记录。',
      exportSettings: '导出设置',
      exportSettingsBlocked: '尚不可用 — 没有可下载的设置导出',
      reviewRecords: '查看记录',
      reviewRecordsBlocked: '尚无记录 — 请先发布或导出',
    },
    plan: {
      title: '套餐',
      current: '当前：',
      manage: '管理套餐',
      allowsTitle: '当前套餐可以做什么',
      generation: '生成',
      publication: '发布',
      packPromotion: '包提升',
      errorLabel: '无法加载你的套餐',
      lookupFailed: '账单查询失败',
      loading: '正在检查你的套餐…',
      signedOut: '登录后可查看你的套餐',
      noSubscription: '没有正在生效的订阅',
      rights: {
        'not-measured': '未测量',
        'authority-readback-invalid': '权限回读无效',
        'not-admitted': '未准入',
        'preview-only': '仅限预览',
        'not-admitted-by-source-receipt': '此来源凭据未准入',
        'preview-admitted': '预览已准入',
        'same-project-publication-admitted': '同项目内发布已准入',
        'admitted': '已准入',
      },
    },
    runtime: {
      title: '运行时',
      note: '发布标识与可用性是观测到的事实，而不是权限。',
      distribution: '分发形态',
      studioSha: 'Studio 源码 SHA',
      applianceReleaseId: '设备发布 ID',
      observedAt: '观测时间',
      observationsTitle: '可用性观测',
      available: '可用',
      unavailable: '不可用',
      readFailed: '读取失败',
      notConfirmed: '无法确认可用性',
      probeFailures: {
        'platform-health-status': '平台健康检查读取失败（{status}）',
        'platform-health-malformed': '平台健康检查的响应格式不正确。',
        'platform-unreachable': '无法确认平台的可用性。',
        'capability-response-rejected': '能力响应未被接受（{status}）。',
      },
      capabilities: {
        platformConnection: '已连接的平台',
        localAuth: '本地认证',
        googleAuth: 'Google 认证',
        localPublish: '本地发布',
        cloudPublish: '云端发布',
        customDomain: '自定义域名',
        billing: '账单',
        analytics: '分析',
        externalIntegrations: '外部集成',
      },
    },
    domain: {
      title: '域名',
      defaultLabel: '默认：',
      customLabel: '自定义：',
      connect: '连接域名',
      blocked: '自定义域名尚不可用',
      signedOut: '登录后可查看你的工作区域名',
    },
    github: {
      title: 'GitHub 集成',
      developer: '开发者',
      connected: '已连接',
      disconnect: '断开连接',
      disconnecting: '正在断开…',
      invite: '连接 GitHub 账号后，可导出源代码并自行配置 CI/CD。',
      connect: '连接 GitHub',
      connecting: '正在连接…',
      confirmDisconnect: '要断开 GitHub 账号吗？已有的仓库不会被删除。',
    },
    notifications: {
      title: '通知',
      reason: '邮件通知尚不可用 — Studio 没有发送服务，也没有存储偏好的地方，因此无法开启。',
      review: '审阅通知（邮件）',
      agentSummary: '智能体活动摘要（每日）',
      productUpdates: '产品动态',
      unavailableSuffix: '— 尚不可用',
    },
    danger: {
      title: '危险区域',
      deleteAccount: '删除账号',
      deleteBlocked: 'Studio 目前还不支持删除账号 — 请写信到 privacy@cogo.xyz 提出申请',
    },
    closedNetwork: {
      planNote:
        '此安装运行在封闭网络中。可用范围由安装的许可证决定，而不是由这里管理的订阅决定。',
      planManageBlocked: '订阅由此安装无法访问的外部服务管理',
      servedFromLabel: '访问地址:',
      domainBlocked: '自定义域名需要公共 DNS — 此安装只在自己的地址上应答',
      githubTitle: 'GitHub',
      githubBlocked: '连接账号需要 github.com，此安装无法访问',
      deleteBlocked: '封闭网络安装的账号保存在本机 — 请联系此设备的管理员',
    },
  },
  operations: {
    title: '运营',
    subtitle: '查看已发布成果的数据、活动、成效、受众与遥测。',
    reviewedOutputs: '已审阅的成果',
    acceptanceRate: '接受率',
    sharedArtifacts: '已共享的产物',
    sharedArtifactsNote: '应用、文档与报告',
    unmeasured: '尚未测量 — Studio 没有这些数字的来源。',
    tiles: {
      products: '来源数据包、抽取的行、就绪状态与来历。',
      orders: '已发布成果的活动及其记录的证据。',
      revenue: '浏览量、转化、已采纳的变更与成效信号。',
      members: '审阅者、流量来源与回访者。',
      analytics: '上线后真实应用的遥测。',
    },
  },
  requests: {
    title: '请求与审阅',
    subtitle: '查看请求的变更、批准与驳回，以及随附的证据。',
    openChat: '打开对话',
    howItWorks: '请求是这样进行的',
    stepsSuffix: ' · 3 步',
    rows: {
      inProgress: { title: '进行中的请求', body: '查看 cogo 正在为你准备的应用、文档与报告变更。', },
      needsApproval: { title: '需要批准', body: '发布、上线与共享更新都会等你审阅之后再进行。', },
      reviewTrail: { title: '审阅记录', body: '查看改了什么、检查了什么、以及什么已可共享。', },
      accessControl: { title: '访问权限', body: '把账号与发布权限限制在当前请求的范围内。', },
    },
    current: { title: '当前请求', body: '当 cogo 正在准备需要在共享或上线前审阅的变更时，使用此页面。', },
    actions: {
      addSource: '添加来源',
      library: '资源库',
      share: '共享',
      refresh: '刷新',
    },
    tiles: {
      updates: '更新',
      checks: '检查',
      needsApproval: '需要批准',
    },
    timeline: {
      title: '最近的请求记录',
      emptyTitle: '没有进行中的请求',
      emptyBody: '从对话开始，或添加一个来源。需要批准的请求会出现在这里。',
    },
    approval: {
      title: '需要你的批准',
      waiting: '此更新在共享前正等待你的审阅。',
      empty: '没有待处理的批准。',
    },
    prepare: {
      title: 'coogo 可以准备的内容',
      appDraft: '应用草稿',
      sourceImport: '导入来源',
      reviewCheck: '审阅检查',
      launchSetup: '上线准备',
    },
    requestTitle: {
      appDraft: '应用草稿',
      sourceAdded: '已添加来源',
      reviewCheck: '审阅检查',
      shareUpdate: '共享更新',
      launchSetup: '上线准备',
      requestedChange: '请求的变更',
    },
    status: {
      ready: '已就绪',
      needsReview: '需要审阅',
      inProgress: '进行中',
      recorded: '已记录',
    },
    scope: {
      codeUpdate: '代码变更',
      designFileUpdate: '设计文件变更',
      publicShare: '公开共享',
      launchSetup: '上线准备',
      workspaceUpdate: '工作区变更',
    },
    entry: {
      reuse: '复用的部分',
      fresh: '新建的部分',
      validation: '已检查',
      approval: '批准',
    },
  },
  share: {
    title: '共享',
    subtitle: '共享应用、文档和报告，同时让来源、检查状态与更新路径保持可见。',
    centerTitle: '共享中心',
    centerBody: '让每个共享的成果都连着它的来源、审阅状态和更新路径，这样你无需从头再来即可持续改进。',
    createOutput: '创建成果',
    addSource: '添加来源',
    sources: '来源',
    settingsDialog: {
      title: '共享设置',
      body: '更新 {name} 的子域名。',
      subdomainLabel: '子域名',
    },
    checklist: {
      publicUrl: '公开网址',
      readinessCheck: '检查状态',
      outcomePath: '成果路径',
      updateControl: '更新管理',
      notShared: '未共享',
      sharedCount: '已共享 {count} 项',
      nothingSharedYet: '还没有共享任何内容',
      readyCount: '{count} 项已就绪',
      needsCheck: '需要检查',
      outcomePathValue: '咨询、预约、导出、结账或审阅页面',
      updateControlValue: '更改前请查看共享设置',
    },
    tiles: {
      sharedOutputs: '共享的成果',
      readyChecks: '已完成的检查',
      drafts: '草稿',
      latestUpdate: '最近更新',
      noSharedOutput: '没有共享的成果',
    },
    health: {
      title: '共享状况',
      checked: '已检查',
      lastRecordedCheck: '最近记录的检查',
      needsAttention: '需要注意',
      draftsNotShared: '草稿（未共享）',
      nothingShared: '没有共享',
      noneYet: '暂无',
      never: '从未',
      none: '无',
      checkedOfShared: '{shared} 项中 {checked} 项',
      blockedCount: '{count} 项受阻',
      nothingSharedBadge: '还没有共享任何内容',
      nothingSharedDetail: '此工作区尚未共享任何内容，因此这里没有可报告的信息。共享一个成果后，它的检查记录就会出现在这里。',
      blockedBadge: '共享的 {shared} 项成果中有 {blocked} 项受阻',
      blockedBadgeOne:
        '共享的 {shared} 项成果中有 {blocked} 项受阻',
      blockedDetail: '有共享成果的检查记录处于受阻状态。打开它查看是什么在阻塞。',
      uncheckedBadge: '共享的 {shared} 项成果没有任何检查记录',
      uncheckedBadgeOne:
        '共享的 {shared} 项成果没有任何检查记录',
      uncheckedDetail: '这些成果已共享，但都没有检查记录。打开其中一个并运行检查即可记录。',
      checkedBadge: '共享的 {shared} 项成果中已检查 {checked} 项',
      checkedBadgeOne:
        '共享的 {shared} 项成果中已检查 {checked} 项',
      checkedBadgeLastSuffix: '，最近 {date}',
      checkedDetail: '这是上次检查这些成果时记录的内容。打开此页面并不会重新检查。',
    },
  },
  library: {
    title: '资源库',
    subtitle:
      '打开此工作区中已核验、可复用的素材与成果。',
    importSource: '导入来源',
    tiles: {
      savedWork: '已保存的工作',
      openLinks: '可打开的链接',
      details: '详情',
      launches: '发布',
    },
    searchPlaceholder: '搜索已保存的工作、来源或链接…',
    searchResults: '搜索结果',
    savedWorkCount: '已保存的工作 {count} 项',
    previewTitle: '资源库预览',
    thumbnailPreview: {
      project: '项目预览',
      template: '模板预览',
      library: '资源库预览',
    },
    previewEmptyTitle: '请选择一个资源库条目',
    previewEmptyBody:
      '已保存的工作会显示在这里，并附带接下来需要的操作：打开、共享、检查或准备发布。',
    whatYouCanDo: '可以做什么',
    readyToUse: '可以使用',
    noReviewLinks: '还没有审阅链接。',
    selectedDetails: {
      summary: '就绪状态、审阅、证据和发布记录',
      openBeforeReviewLink:
        '请先打开此应用，再创建可编辑的审阅链接。',
    },
    empty: {
      title: '资源库中还没有条目',
      body:
        '添加需求说明、页面文件、URL、仓库变更或设计画板 — 每个保存的条目都会连同预览、来源和发布操作出现在这里。',
    },
    actions: {
      open: {
        label: '打开',
        detail: '在共享前查看应用或文档',
        noLink: '此条目还没有可打开的链接',
      },
      edit: {
        label: '编辑',
        detail: '返回项目工作区',
        noWorkspace: '此条目未关联到任何项目工作区',
      },
      launch: {
        label: '发布',
        detail: '准备公开发布包',
        noBundle: '此条目还没有可发布的已保存文件',
        busy: '{tool} 仍在运行',
      },
      details: {
        label: '详情',
        detail: '查看来源备注与后续步骤',
      },
      useAgain: '再次使用',
      sharePublicly: '公开分享',
      check: '检查',
      share: '分享',
      linkPr: '关联 PR',
      prepareLaunch: '准备发布',
      feedback: {
        check: {
          progress: '正在检查应用…',
          success: '已记录应用检查。',
          failure: '应用检查失败。',
        },
        sharePublicly: {
          progress: '正在准备公开分享…',
          success: '已记录公开分享。',
          failure: '公开分享失败。',
        },
        prepareLaunch: {
          progress: '正在准备发布…',
          success: '已记录发布准备。',
          failure: '发布准备失败。',
        },
      },
      noSelection: '请先保存或选择一个条目',
    },
  },
  sources: {
    title: '来源',
    subtitle: '将文件、URL、代码仓库和设计来源添加到此工作区。',
    addToLibrary: '添加到库',
    howItWorks: '添加来源后会发生什么',
    flowAriaLabel: '从来源到工作区的流程',
    agentIntake: {
      eyebrow: 'Studio 智能体',
      title: '告诉 Studio 你想创建什么',
      description: '输入需求、选择文件或粘贴 URL。无需先选择来源类型，Studio 会准备下一步。',
      placeholder: '例如：创建一个能一览客户支持动态的仪表板',
      attach: '添加文件',
      addUrl: '添加 URL',
      submit: '交给 Studio 智能体',
      understanding: '智能体理解的内容',
      nextStep: '下一步',
      connected: '最近和已连接的来源',
      emptyConnected: '与此工作连接的文件和 URL 将显示在这里。',
      advanced: '高级导入',
      advancedSummary: '直接选择来源类型，或设置 Figma 访问、受众和标签等详细信息。',
      requestUnderstanding: '创建新成果的需求',
      urlUnderstanding: '用作参考的网站',
      codeUnderstanding: '现有代码或文本文件',
      figmaUnderstanding: '本地 Figma 设计文件',
      documentUnderstanding: '要读取并检查结构的文档',
      nextDescribe: 'Studio 将整理需求并准备 Library 草稿。',
      nextUrl: 'Studio 将页面连接为参考来源并准备 Library 草稿。',
      nextCode: 'Studio 将文件作为界面来源读取并准备 Library 草稿。',
      nextFigma: 'Studio 将读取设计，以便检查页面和图层。',
      nextDocument: 'Studio 将读取文档，以便检查其结构。',
      currentRequest: '当前需求',
      selectedFile: '已选文件',
      restoredDocument: '保存在此浏览器中的文档',
      connectedDesign: '已连接的 Figma 设计',
      urlHint: '请在同一个需求输入框中粘贴完整 URL。',
      unsupportedFile: '此输入区域暂时无法读取这种文件格式。',
      activeDesignConflict: '当前连接的设计来源阻止了新的 URL 任务。请打开该设计来源，或在新项目中从 URL 开始。',
      librarySaveMissing: '未能确认 Library 中已保存的草稿。你的需求仍保留在此页面，请重试。',
    },
    steps: {
      addSource: '添加来源',
      shape: '整理',
      save: '保存',
      share: '分享',
    },
    form: {
      appName: '应用名称',
      audience: '受众',
      audienceCreator: '创作者',
      audienceBusiness: '企业',
      audienceDeveloper: '开发者',
      sourceFieldSuffix: '来源',
      briefPlaceholder: '在此写下你想要的应用、文档或报告。请一并说明受众、提供内容、页面结构、表单、支付与期望语气。',
    },
    upload: {
      title: '上传文件',
      body: '已经有应用包了吗？直接上传，库中会保留基于文件的记录。',
      button: '上传应用包',
    },
    groups: {
      fromIdea: '从想法开始',
      bringExisting: '导入已有的内容',
    },
    otherWays: {
      title: '其他开始方式',
    },
    chatStart: {
      title: '从对话开始',
      body: '不想选择来源？打开对话并描述你想要的内容。创建之后使用相同的库与发布流程。',
    },
    savedWith: {
      title: '一并保存的内容',
      openLink: '可打开的链接',
      savedFile: '已保存的文件',
      sourceSnapshot: '来源快照',
      launchCheck: '发布检查',
    },
    after: {
      title: '添加之后',
      defaultNote: 'cogo 会保存来源，因此你可以打开、分享、优化或发布，而不会丢失它的出处。',
      figmaFileNote: 'cogo 将 .fig 证据与应用生成分开保存，以便先审阅来源覆盖情况。',
      documentFileNote: '文档保留在此浏览器中，刷新后依然打开，且不会被发送到任何地方。',
      steps: {
        prepared: '工作已准备',
        designRead: '已读取设计',
        documentRead: '已读取文档',
        sourceSaved: '来源已保存',
        keptInBrowser: '保留在此浏览器',
        mapperOpens: '打开来源映射器',
        structureOpens: '在下方展开结构',
        libraryOpens: '打开库并显示后续操作',
      },
    },
    panel: {
      tags: '标签',
      librarySuffix: 'cogo 会附上来源备注与后续操作，保存为库卡片。',
      figmaFileNote: '在生成任何内容之前，我们会读取你的 Figma 文件并保留原件。',
      documentFileNote: '我们在此浏览器内读取文档并展示其结构。不会上传，也尚未据此生成任何内容。',
      figmaTokenLabel: 'Figma 令牌',
      figmaTokenPasteHint: '请粘贴 Figma 令牌并确认。',
      forgetToken: '移除',
      readDesign: '读取设计',
      readDocument: '读取文档',
      localFigmaFile: '本地 Figma 文件',
      localDocument: '本地文档',
    },
    help: {
      assistant_prompt: {
        title: '在此写下你的需求。',
        detail: '请一并写明受众、提供内容、页面结构、表单、支付与期望语气。',
        example: '例如：为美妆创作者做一个预约页面，包含服务列表、用户评价、Instagram 链接，以及可直接付款的行动按钮。',
      },
      url_import: {
        title: '粘贴一个公开 URL。',
        detail: 'cogo 将其作为视觉与内容参考，并把来源 URL 保留在库中。',
        example: '例如：https://example.com/product-page',
      },
      ide_file: {
        title: '粘贴一个组件、路由或页面文件。',
        detail: '当你已有页面并希望在其基础上继续时最合适。',
        example: '例如：src/features/checkout/checkout-page.tsx',
      },
      github_pr: {
        title: '粘贴仓库与拉取请求编号。',
        detail: 'cogo 会保留变更与评审说明，便于与团队共享。',
        example: '例如：acme/storefront#128',
      },
      figma_frame: {
        title: '粘贴 Figma 画板 URL 或文件/节点 ID。',
        detail: 'cogo 会保留设计来源，便于日后评审转换质量。',
        example: '例如：https://www.figma.com/design/abc123/Checkout?node-id=12-345',
      },
      figma_file: {
        title: '选择本地 .fig 文件。',
        detail: 'cogo 会读取文件、保留原件，并在生成前展示设计中的内容。',
        example: '例如：checkout-flow.fig',
      },
      document_file: {
        title: '选择 .docx 或 .hwpx 文件。',
        detail: 'cogo 会读取文档、保留原件，并在生成前展示其结构。',
        example: '例如：account-opening-spec.docx',
      },
    },
    refLabels: {
      assistant_prompt: '简述',
      url_import: 'URL',
      ide_file: '文件',
      github_pr: 'GitHub',
      figma_frame: 'Figma',
      figma_file: '.fig',
      document_file: '文档',
    },
    cards: {
      assistant_prompt: {
        label: '用文字描述',
        description: '写下你想要的应用、文档或报告。',
        bestFor: '从一个想法开始',
        output: '库中的草稿',
      },
      url_import: {
        label: '使用网站',
        description: '将页面 URL 作为参考引入。',
        bestFor: '已有站点与参考资料',
        output: '基于参考的草稿',
      },
      ide_file: {
        label: '粘贴代码',
        description: '引入组件、路由或页面文件。',
        bestFor: '已有实现时',
        output: '页面草稿',
      },
      github_pr: {
        label: '使用拉取请求',
        description: '附加需要评审的仓库变更。',
        bestFor: '团队变更',
        output: '评审卡片',
      },
      figma_frame: {
        label: 'Figma 画板',
        description: '引入设计画板以进行转换评审。',
        bestFor: '设计交接',
        output: '基于设计的草稿',
      },
      figma_file: {
        label: 'Figma .fig',
        description: '读取本地 Figma 文件并保留原件。',
        bestFor: '先确认设计时',
        output: '设计详情',
      },
      document_file: {
        label: 'Word / HWPX',
        description: '读取 .docx 或 .hwpx 并保留原件。',
        bestFor: '规格、指南、手册',
        output: '文档详情',
      },
    },
  },
  chrome: {
    breadcrumb: '当前位置',
    search: '搜索应用、文档、游戏和现状...',
    openNavigation: '打开导航',
    openChat: '打开聊天',
    closeChat: '关闭聊天',
    notifications: '通知',
    lightMode: '切换到浅色模式',
    darkMode: '切换到深色模式',
    publish: '发布',
    publishCurrent: '发布当前项目',
    openProjectToPublish: '请打开要发布的服务器项目',
    publishBlocked: '发布已阻止',
    conflict: '冲突',
    saveFailed: '保存失败',
    saving: '正在保存…',
    unsaved: '未保存',
    live: '已上线',
    saved: '已保存',
    draft: '草稿',
    reloadConflict: '另一会话修改了此区块 — 请重新加载最新状态',
    reload: '重新加载',
    reloading: '正在重新加载…',
    exportSource: '导出应用源文件',
    openProjectToExport: '请打开要导出的项目',
    exportSuccess: '应用源文件已导出',
    toggleIntentLoop: '聊天',
    moreControls: '更多控制',
    history: '历史',
  },
  proposal: {
    change: "更改建议",
    info: "信息",
    warning: "注意",
    critical: "重要",
    before: "更改前",
    after: "更改后",
    unchanged: "无更改",
    removed: "已移除",
    details: "详细信息",
    close: "关闭",
    whatWillCheck: "coogo 将检查的项目",
    reviewChecks: "检查项目",
    reviewBeforeApply: "应用前检查",
    attachedProof: "附加依据",
    check: "依据",
    notAttached: "未附加",
    readyToUse: "可以应用",
    reviewFirst: "需要检查",
    anotherEditPending: "另一个编辑正在等待。",
    compareConflict: "比较冲突更改的详细信息",
    keep: "保留",
    revise: "修改",
    reviseAria: "修改建议的更改",
    use: "应用",
    reviewNeeded: "需要检查",
    keepCurrent: "保留当前版本",
    keepCurrentAria: "保留当前版本并结束此建议",
  },
  home: {
    ...en.home,
    title: "今天想创建或修改什么？",
    subtitle: "连接来源或选择工作类型，coogo 会协助整理首个请求。",
    startWithChat: "从聊天开始",
    workTypes: "工作类型",
    workTypeHint: "选择工作类型后，输入框会填入起始语句。",
    recentWork: "最近工作",
    reviewNeeded: "需要确认",
    viewAll: "查看全部",
    review: "确认",
    noWork: "还没有工作。",
    noWorkHint: "请从上方输入框开始第一个任务。",
    firstProjectAction: "描述第一个项目",
    continueWorking: "继续工作",
    openWork: "打开工作",
    continue: "继续",
    latestProjectEmpty: "真实项目和验证状态会显示在这里。",
    readiness: "coogo 就绪状态",
    totalWork: "全部工作",
    productionVerified: "生产验证",
    settings: "管理设置",
    countSuffix: "个",
    readinessSummary:
      "可分享状态：就绪 {ready} 个，待确认 {review} 个，受阻 {blocked} 个。",
    featuredOutcome: {
      eyebrow: "旗舰工作流 · 预览",
      title: "通讯订阅 → 会员服务",
      outcomes: {
        newsletterMembership: { title: "通讯订阅 → 会员服务" },
        reservationOperations: { title: "预约运营" },
      },
      description: "基于订阅者和内容启动受控的会员服务预览。",
      boundary:
        "仅创建预览候选项。验证、发布、部署和生产环境实时回读均为独立关卡。",
      action: "选择此工作流",
      clearAction: "清除所选工作流",
      inProgress: "此工作流请求已在进行中。",
      prompt:
        "将我的通讯订阅者和现有内容转换为包含会员引导、受保护内容和运营洞察的付费会员服务。",
    },
    lanes: {
      app: { title: "应用", prompt: "我想创建一个工作应用。" },
      document: { title: "文档", prompt: "我想根据已连接的来源创建文档。" },
      game: { title: "游戏", prompt: "我想创建一个规则和互动清晰的游戏。" },
      existing: { title: "修改现有工作", prompt: "我想选择并更新现有工作。" },
      starter: {
        title: "从 Starter 开始",
        prompt: "我想选择 Starter 并开始。",
      },
    },
  },
  chat: {
    ...en.chat,
    greeting: (name) => `${name}，今天要创建什么？`,
    placeholder: "想创建或修改什么？",
    attach: "文件 · 来源",
    attachLabel: "附加文件或连接来源",
    cancel: "取消请求",
    send: "发送请求",
    checkingSession: "正在检查 Studio 会话",
    cancelled: "计划已取消",
    ontologySelectionRequired:
      "生成前请先选择本项目要使用的应用程序包。",
    startingNotice:
      "从你的请求开始。coogo 会将制作内容、下一步修改和对话保持在一起。",
    appGenerationStarting: "正在创建项目并准备页面、交互和数据...",
    webMcpVerified: (target) =>
      target
        ? `已通过 WebMCP 确认：${target}。`
        : "已通过 WebMCP 确认当前 Studio 上下文。",
    readOnlyCompletionFallback:
      "无法从响应中确认你询问的状态。未执行任何更改，也未创建待审批项。",
    suggestions: [
      "创建预约应用",
      "将文档转换为可审阅的报告",
      "更新现有工作",
    ],
    templateProposal: {
      heading: "现有模板可能适合",
      startFaster: "可帮助你更快开始。",
      accept: "使用模板",
      accepting: "正在创建...",
      decline: "从头创建",
      accepted: "已使用模板创建项目。",
      declined: "正在从头创建并准备页面、交互和数据。",
      unavailable: "此模板建议已不可用。",
      failure: "无法使用该模板。你仍可从头创建。",
    },
    planningFailure: {
      ontologyBlocked:
        "coogo 目前还无法构建此内容 — 所需的知识尚未收录在资料库中。请尝试其他产品创意，或请团队添加此领域的知识。",
      planNotActionable:
        "计划中没有包含这个应用所需的数据连接，因此没有创建任何内容。请补充它需要读取和写入的信息——例如成员是谁、向他们收取什么——然后重新发送。",
      requirementCoverage:
        "规划没有涵盖你请求中的一部分内容，因此未创建任何内容。请把请求拆成更短的句子后重新发送。",
      generic: "规划过程中出现问题，请重试。",
    },
    updateExistingWork: {
      noExistingWork:
        "此工作区还没有已保存的作品。告诉我你想做什么，我来开始。",
      opening: "正在打开“{name}”。告诉我你想修改什么。",
      chooseOne: "你有多个已保存的项目 — {choices}。要打开哪一个？",
    },
    toolFailure: {
      invalidInput:
        "请求中缺少此更改所需的内容，因此未做任何更改。请用一句具体的话说明你想修改什么。",
      notPermitted: "此更改暂时无法从聊天中执行，因此未做任何更改。",
      unknownAction: "coogo 目前还没有该操作，因此未做任何更改。",
      generic: "无法完成此更改，因此未做任何更改。",
    },
  },
  templateContent: {
    aiFill: "AI 填充",
    briefPlaceholder: "例如：充满活力的社区面包店，温暖的色调",
    filling: "正在填充...",
    aiUnavailable: "AI 服务尚未配置。",
    fillFailed: "AI 填充失败。",
    fillBatchProgress: "正在填充批次 {current}/{total}...",
    fillPartialNotice: "AI 未能填充 {count} 个字段；其余字段已填充。",
    fillAllFailedNotice: "AI 未能填充全部 {count} 个字段，没有任何内容被填充。",
    judgeChecking: "正在审阅...",
    judgeFailed: "无法获取审阅结果。",
    pass: "通过",
    caution: "注意",
    reasons: "原因",
    hide: "隐藏",
    show: "显示",
    general: "常规",
    changed: "已更改",
    records: "{count} 条记录",
    readOnly: "由于数据文件不可用，此版本为只读。",
    invalidJson: "请输入有效的 JSON。",
    persistError: "部分更改无法保存，请重试。",
    applySuccess: "内容已应用。",
    applyFailed: "应用失败，请检查下面的错误。",
    applyFailureSummary: "{count} 项应用失败。",
    applying: "正在应用...",
    apply: "应用 ({count})",
    designRebindTitle: "设计发布",
    designRebindPresetLoadError: "无法加载预设",
    designRebindPresetPlaceholder: "选择预设",
    designRebindApply: "应用设计",
    designRebindApplying: "正在应用...",
    designRebindApplySuccess: "已应用设计",
    designRebindApplyFailed: "设计应用失败",
  },
  candidateVerification: {
    title: "候选项验证",
    description: "此生成候选项需要四项独立依据和一项完整性胶囊。",
    authorityNote:
      "只有经过身份验证的持久日志回读才能将这些检查标记为已验证。",
    promotionBoundary:
      "候选项依据验证不证明产品质量、发布、部署或生产环境实时回读。",
    authorityLabel: "签发方：{authority}",
    verifiedStatus: "候选项依据已验证",
    publicationGateStatus: "发布是独立关卡",
    listLabel: "候选项验证依据角色",
    refresh: "刷新检查",
    refreshing: "正在刷新检查",
    askCoogo: "询问 Coogo",
    liveChecking: "正在刷新候选项验证检查。",
    liveResult: "候选项验证状态：{status}。",
    statuses: {
      pending: "待处理",
      checking: "检查中",
      ready: "已就绪，未验证",
      verified: "已验证",
      blocked: "已阻止",
      unavailable: "不可用",
    },
    roles: {
      "sufficiency-assessment": "来源充分性评估",
      "validator-safety-pass": "Validator 安全检查通过",
      "second-model-judge": "独立模型审查",
      "render-execution-receipt": "渲染执行回执",
      "verification-capsule": "验证胶囊",
    },
    authorities: {
      "source-assessment-authority": "来源评估服务",
      "validator-runtime": "安全验证服务",
      "independent-model-judge": "独立模型评审服务",
      "renderer-runtime": "渲染验证服务",
      "publishing-kernel": "发布完整性服务",
    },
    causes: {
      "journal-checking": "正在读取持久验证日志。",
      "journal-unavailable": "持久日志回读当前不可用。",
      "journal-scope-invalid": "持久日志与此候选项不匹配。",
      "journal-contract-invalid": "持久日志未通过契约验证。",
      "verification-pending": "尚未记录已验证的持久事件。",
      "producer-receipt-pending":
        "正在等待指定服务器生成方的回执；此候选项尚未验证。",
      "verification-incomplete": "验证事件缺少必需的独立依据。",
      "source-assessment-current": "当前评估已就绪，但不代表已验证。",
      "source-assessment-insufficient": "当前评估不满足来源充分性要求。",
      "source-assessment-missing": "需要当前且不可变的来源评估。",
      "source-assessment-stale": "候选项的评估版本已不是最新版本。",
      "durable-journal-verified": "持久日志已记录四项独立依据及其完整性胶囊。",
    },
  },
  report: {
    ...en.report,
    title: '现状',
    ontology: '本体',
    ontologySubtitle: '本体覆盖率 · 当前证据与目标范围',
    connectSource: '打开设计来源',
    unavailableTitle: '暂无可显示的现状',
    unavailableBody:
      "产品连接经过验证的设计来源后，品质现状将显示在这里。coogo 不会显示编造的分数。",
    unavailableBodyDeveloperDetail:
      "请选择具有与当前发布版本关联的最新验证结果的项目。coogo 不会生成占位分数。",
    axesLabel: "本体覆盖率轴",
    axesSummary: "6 个轴 · 必需维度最低分",
    axisMethod: "轴值取必需维度中的最低分。它用于呈现证据，不授予权限。",
    coverage: "本体覆盖率",
    coverageSubtitle: "当前证据与目标范围",
    lowestRequiredAxis: "必需轴最低值",
    loadingChart: "正在加载本体覆盖率图表",
    currentEvidence: "当前证据",
    targetEnvelope: "目标范围",
    gap: "差距",
    axisDetails: "轴详情",
    gaps: "差距",
    priority: "优先级",
    blocks: "阻塞项",
    noTrack: "无阻塞",
    askCoogo: "询问 coogo",
    noOpenRequirements: "此发布版本绑定的 assessment 中没有未解决的本体要求。",
    allDimensions: "全部 {count} 个 application ontology 维度",
    dimension: "维度",
    authority: "权限",
    resolution: "解决方式",
    current: "当前",
    evidence: "证据",
    none: "无",
    status: {
      ready: "已就绪",
      warn: "警告",
      blocked: "已阻止",
      missing: "尚无",
      missingTooltip: "尚无经过验证的证据",
    },
    conversionProvenance: {
      title: "与原始画面对比",
      sourceLabel: "转换自",
      capturedLabel: "读取时间",
      blocksLabel: "已读取的画面块",
      decisionsLabel: "已记录的转换决定",
      realizationLabel: "已落入应用的声明",
      realizationAbsent:
        "已记录的决定中没有可结构化核对的项，因此没有落实率。coogo 不会编造数值。",
      sourceKindLegacyScreen: "粘贴的遗留画面",
    },
  },
  guide: {
    title: "指南工作区",
    documents: "指南",
    noPageSelected: "尚未选择指南页面。",
    page: "页面",
    pageActivity: "页面活动",
    proposals: "建议",
    applied: "已应用",
    exports: "导出",
    pages: "页",
    revision: "修订",
    addPage: "添加页面",
    pageTitle: "页面标题",
    openingEditor: "正在打开指南编辑器...",
    pageActions: "页面操作",
    reviewPage: "检查页面",
    proposeEdit: "建议修改",
    editContent: "编辑内容",
    move: "移动",
    delete: "删除",
    draft: "草稿",
    export: "导出",
    readback: "验证结果",
    output: "输出",
    notRequested: "尚未请求",
    noExportRequested: "当前指南没有导出请求。",
  },
  connectedDocument: {
    notConnected: "未连接",
    connectedToTemplate: "已连接到{provider}",
    documentReference: "文档链接或名称",
    loadDocument: "加载文档",
    loadErrorNotConnected: "此工作区尚未连接。请联系团队设置文档连接。",
    loadErrorGeneric: "无法加载文档，请重试。",
    panelTitle: "已连接的文档",
    provider: "提供方",
    activeSourceSnapshot: "当前来源快照",
    connectionReference: "连接引用",
    documentIdLabel: "文档 ID",
    pageIdLabel: "页面 ID",
    fetchVerifiedSnapshot: "获取已验证快照",
    saveProjectFirst: "连接提供方文档前，请先保存此项目。",
    unsavedDraftBlocker:
      "获取已连接文档快照前，请先完成或提交未保存的 WordGard 草稿。",
    readbackSummary: (blocks, unsupported, accessMode) =>
      `${blocks} 个区块 · ${unsupported} 个不支持 · ${accessMode}`,
    accessMode: { "read-only": "只读", "read-write": "读写" },
    openSource: "打开来源",
    newPageParentId: "新页面的父级 ID（可选）",
    publishApproval:
      "我同意在提供方创建一份新副本。源文档不会被更新。",
    publishAction: "创建并验证新副本",
    publishBlockedUnsupported: "在解决不支持的提供方元素之前，发布将被阻止。",
    connectionReadOnly:
      "此连接为只读。请使用具有写入权限的方式重新连接后再发布副本。",
    proofLabel: "新副本证据:",
    proofStatus: { pass: "通过", fail: "失败", blocked: "已阻止" },
    structure: "结构",
    structureReadBack: "已回读",
    structureMissing: "缺失",
    exportsReadBack: (count) => ` · 已回读 ${count} 项导出`,
    openNewCopy: "打开新副本",
    publishVerified: (provider, versionRef) =>
      `已在 ${versionRef} 验证新的 ${provider} 副本。`,
    publishBlockedBeforeVerified: "在验证提供方副本之前，发布已被阻止。",
    publishProofFailed: "新副本可能已创建，但其回读证据未通过。",
    publishGenericError: "无法发布已连接文档的副本。",
  },
  behavior: {
    ...en.behavior,
    navigation: "ActionFlow 导航",
    title: "动作流程",
    selectFlow: "选择事件或流程",
    unsaved: "未保存",
    target: "行为目标",
    noElement: "未选择元素",
    selectInPreview: "在预览中选择",
    section: "区段",
    status: "状态",
    bindings: "绑定",
    flows: "流程",
    empty: "空",
    steps: "步骤",
    verified: "已验证",
    failed: "失败",
    notVerified: "未验证",
    noFlows: "此项目未声明流程。",
    eventBindings: "事件绑定",
    connected: "已连接",
    orphan: "孤立",
    inspectElement: "请选择要检查行为的应用元素。",
    noBindings: "此项目未声明事件绑定。",
    chatActions: "聊天操作",
    reviewFlow: "审核流程",
    addStep: "添加步骤",
    connectEvent: "连接事件",
    testFlow: "测试流程",
    editor: "行为编辑器",
    unsavedFlow: "未保存流程",
    noBlocks: "尚无区块，无法保存流程",
    testRun: "运行测试",
    closeFlowForm: "关闭流程表单",
    newFlow: "新建流程",
    addBlockFirst: "创建流程前请先添加页面区块",
    nodeTypes: "支持的 ActionFlow 节点类型",
    nodes: "节点",
    executionModel: "执行模型",
    binding: "绑定",
    direct: "直接",
    directAtomic: "直接 atomic",
    namedFlow: "命名流程",
    lanes: "领域",
    actionLane: "Action",
    uiLane: "UI",
    motionLane: "Motion",
    order: "顺序",
    completionBarrier: "完成屏障",
    stage: "Stage",
    saga: "Saga",
    sagaCompensation: "Saga 按逆序补偿 · 非 ACID",
    palette: {
      ifElse: "条件分支",
      loop: "循环",
      switch: "分支",
      parallel: "并行",
      tryCatch: "异常处理",
    },
    flowCreateFailed: (name) =>
      `Flow“${name}”未创建 — 服务器拒绝了该请求，本地更改已回滚`,
    flowCreated: (name) => `已创建 Flow "${name}"`,
    flowCreatedLocalOnly: (name) =>
      `Flow "${name}" 仅在本次会话中创建 — 尚未保存到服务器`,
  },
  approval: {
    categoryFlows: "流程",
    categoryDatasources: "数据源",
    categoryVariables: "变量",
    categoryEvents: "事件绑定",
    compositeActionFallback: "组合操作",
    operationCount: (count) => `${count} 项操作`,
    applyAll: "全部应用",
    applySelected: (selected, total) => `应用 (${selected}/${total})`,
    dismiss: "取消",
    applied: "已应用",
    pendingValidation: (reason) => `等待验证${reason ? `：${reason}` : ""}`,
    dismissed: "已取消",
    edit: "编辑",
  },
  insertPanel: {
    noActivePage: "没有打开的页面",
    searchPlaceholder: "搜索 UI",
    maxChildrenReached: (current, max) =>
      `已达到子元素上限 (${current}/${max})`,
    fromLibrary: "来自组件库",
    createManually: "手动创建",
    detailCreateOrOpenPage: "添加 UI 之前，请先创建或打开一个页面。",
    detailNeedsBlockTarget: "新元素需要一个块作为目标。",
    detailAppendsToBlock: "在此块的末尾添加元素。",
    detailInside: (target) => `在 ${target} 内`,
    detailAfterSelectionIn: (target) => `在 ${target} 中所选元素之后`,
    targetLabel: "目标",
    newBlock: "新建块",
    noLibraryBlocks: "尚无可重复使用的块",
    moreBlocks: (count) => `再显示 ${count} 个块`,
    noElementsFound: "找不到元素",
  },
  dataExplorer: {
    datasourceCreated: (name) => `已创建数据源“${name}”`,
    datasourceNameTaken: (name, blockName) =>
      `块“${blockName}”中已存在名为“${name}”的数据源。未做任何更改 — 请换一个名称，或编辑现有的数据源。`,
    datasourceCreateFailed: (name, blockName) =>
      `无法创建“${name}” — 找不到块“${blockName}”（是否已删除或重命名？）。未做任何更改。`,
  },
  routeHeadings: {
    map: "应用地图",
    admin: "管理员",
    billing: "账单",
    themeEditor: "主题编辑器",
    newProject: "我们要创建什么？",
  },
  dataInspector: {
    header: {
      title: "实体详情",
      reviewEntity: "审阅实体",
    },
    fields: {
      name: "名称",
      block: "区块",
      scope: "作用域",
      type: "类型",
      defaultValue: "默认值",
      expression: "表达式",
      path: "路径",
      importSource: "导入来源",
      call: "调用",
      method: "方法",
      kind: "种类",
      endpoint: "端点",
      resultTarget: "结果目标",
      headers: "请求头",
      body: "请求体",
      source: "来源",
      eventKey: "事件键",
      component: "组件",
      event: "事件",
      flow: "流程",
      trigger: "触发器",
      steps: "步骤",
      usedBy: "引用",
    },
    placeholders: {
      noDefault: "无默认值",
      expression: "例如 #{otherVar} + 1",
      resultTarget: "此数据源写入的状态变量",
      body: "请求体模板",
    },
    emptyStates: {
      selectEntity: "请选择要查看的实体",
      entityNotFound: "找不到实体",
      variableNotFound: "找不到变量",
      datasourceNotFound: "找不到数据源",
      eventNotFound: "找不到事件",
      flowNotFound: "找不到流程",
    },
    actions: {
      testInExpressionLab: "在表达式实验室中测试",
      writeExpressionInLab: "在表达式实验室中编写表达式",
      openInFlowEditor: "在流程编辑器中打开",
      deleteVariable: "删除变量",
      deleteDatasource: "删除数据源",
      deleteEvent: "删除事件",
    },
    appManifest: {
      scopeValue: "应用清单",
      note: "应用清单中的数据源是整个项目共享的输入。区块级绑定请在区块数据源或代码界面中编辑。",
    },
    schema: {
      request: "请求结构",
      response: "响应结构",
    },
    flowUnbound: "-- 未绑定 --",
    stepCount: (count) => `${count} 个步骤`,
    validationSummary: (errors, warnings) =>
      `${errors} 个错误，${warnings} 个警告`,
    referencedWarning:
      "此变量在其他位置被引用。删除后可能导致表达式失效。",
    toasts: {
      variableDeleted: (name) => `已删除变量“${name}”`,
      datasourceDeleted: (name) => `已删除数据源“${name}”`,
      eventRemoved: (name) => `已移除事件“${name}”`,
      bindingsUpdated: (count, oldPath, newPath) =>
        `已更新 ${count} 个绑定：${oldPath} -> ${newPath}`,
      bindingsUpdateFailed:
        "更新绑定失败。部分引用旧字段名的位置可能需要手动修正。",
    },
  },
  dataValidation: {
    confirm: {
      deletePrompt: (name) => `确定删除“${name}”吗？`,
      typeChangePrompt: (name) => `确定更改“${name}”的类型吗？`,
      deleteAction: "删除",
      typeChangeAction: "更改类型",
      cancel: "取消",
      unnamedField: "未命名字段",
      nestedFieldWarning: (count) =>
        `这会同时删除该字段下的 ${count} 个嵌套字段及其内部的所有内容。`,
    },
    messages: {
      unmatchedBrackets: "#{} 括号不匹配",
      emptyExpression: "表达式 #{} 为空",
      duplicateVariableName: (name, blockName) =>
        `区块“${blockName}”中的变量名“${name}”重复`,
      variableUnreferenced: (name) => `变量“${name}”未被任何流程或表达式引用`,
      flowMissing: (flowName) => `流程“${flowName}”不存在`,
      restDatasourceNoEndpoint: "REST 数据源没有端点 URL",
    },
  },
  proofHealth: {
    verified: "已验证",
    needsReview: "需要复核",
    needsLiveCheck: "需要线上检查",
    blocked: "已阻断",
    none: "无凭证",
    checkedLive: (date) => `${date} 线上已检查`,
    dateUnrecorded: "已记录",
    liveCheckBlocked: "线上检查已被阻断。",
    liveCheckNeedsReview: "线上检查需要复核。",
    recordBlocked: "凭证记录已被阻断。",
    recordNeedsReview: "凭证记录需要复核。",
    openDeployToRun: "打开发布页运行线上检查。",
    reviewEvidence: "查看凭证",
    reviewShare: "查看分享",
  },

  // ── lane/kodash · map · projects · apps-live · import ────────────────────
  // Four screens whose readable surface was English on 2026-08-20, harvested
  // by `__tests__/readable-surface-localized.test.tsx` (33 + 21 + 16 + 14
  // word-entries). Appended at the END of every table on purpose: two sibling
  // lanes were editing the sections above at the same time, and the tail is
  // the one place four concurrent additions cannot collide.
  //
  // EVERY key below is spelled out in all five tables. `ko`, `ja` and `zh`
  // open with `...en` and `zhTW` with `...zh`, so a key added to `en` alone
  // is INHERITED rather than missing — and because the residue walk derives
  // its keep-Latin allowlist from the translated tables, inherited English
  // allowlists its own words and the walk goes silent. Measured, not assumed:
  // mutation M5 in the register test deletes one `ko` override here and the
  // residue walk stays green. The inheritance gate is what turns it red.
  appMap: {
    description: "查看 cogo 应用内部的页面、区块、动作、状态与数据连接。",
    emptyTitle: "打开一个应用即可查看其结构地图",
    emptyBody:
      "载入项目、导入的来源或生成的库项目后，应用地图才会派上用场。它会在发布前展示页面、数据与动作流程步骤之间的连接。",
    createApp: "创建应用",
    importSource: "导入来源",
    openProjects: "打开工作",
    noAppLoaded: "未载入应用",
    nodesUnit: "个节点",
    edgesUnit: "条连接",
    nodeTypes: {
      app: "应用",
      page: "页面",
      block: "区块",
      atom: "设计元素",
      flow: "流程",
      stateVar: "状态变量",
      datasource: "数据源",
    },
    stats: { title: "统计", edges: "连接" },
    legend: {
      title: "图例",
      filterLabel: "按节点类型筛选",
      clearFilter: "清除筛选",
    },
    nodeDetail: {
      title: "节点详情",
      empty: "点击节点查看详情",
      incomingUnit: "入边",
      outgoingUnit: "出边",
    },
    sessions: {
      title: "会话",
      body: "头脑风暴观察时间线将在第 3 阶段推出。此面板将回放当前打开应用的采纳、否决与设计系统调整记录。",
    },
    canvas: {
      controls: "视图控制",
      zoomIn: "放大",
      zoomOut: "缩小",
      fitView: "适应窗口",
      interactive: "切换交互锁定",
      miniMap: "缩略图",
      handle: "连接点",
      nodeDescription:
        "按回车或空格选中节点，然后可用方向键移动它。按 delete 删除，按 esc 取消。",
      nodeKeyboardDisabled:
        "按回车或空格选中节点。用方向键移动它。按 delete 删除，按 esc 取消。",
      nodeMoved: (direction, x, y) => `已将选中节点向 ${direction} 移动。新位置 x：${x}，y：${y}`,
      edgeDescription:
        "按回车或空格选中连接线，然后按 delete 删除，按 esc 取消。",
    },
  },
  projectCard: {
    status: {
      published: "已上线",
      draft: "草稿",
      archived: "已归档",
      active: "进行中",
    },
    share: "分享",
    shareNamed: (name) => `分享 ${name}`,
    openActions: (name) => `打开 ${name} 的操作菜单`,
    rename: "重命名",
    duplicate: "复制",
    settings: "设置",
    delete: "删除",
  },
  outputsSection: {
    heading: "成果",
    breakdown: (shared, drafts) => `已共享 ${shared} 个 · 草稿 ${drafts} 个`,
    loading: "正在载入成果…",
    emptyTitle: "暂无成果",
    emptyBody:
      "创建或导入应用、文档或报告后，就会显示在这里——先是草稿，共享后成为共享的成果。",
    showMore: (count) => `再显示 ${count} 个成果`,
  },
  launchLoop: {
    addSource: "想法、文件、URL、代码仓库或设计稿",
    shape: "cogo 会准备好这份工作",
    save: "把来源与后续步骤保存在一起",
    share: "打开、共享或发布",
  },
  // ─── lane/kobiz: the nine business screens ───────────────────────────
  businessScreens: {
    review: {
      tryAgain: '重试',
      openSurface: (surface) => `打开${surface}`,
      viewSurface: (surface) => `查看${surface}`,
      tableEmpty: '暂无可显示的数据',
      periodAll: '全部',
      observation: {
        measuredSummary: '已测量的审阅数据。',
        emptySummary: '审阅观测详情',
        observedAt: (stamp) => `观测于 ${stamp}`,
        evidence: '证据：',
      },
      pagination: {
        first: '首页',
        previous: '上一页',
        next: '下一页',
        last: '末页',
      },
      status: {
        active: '启用',
        draft: '草稿',
        archived: '已归档',
        pending: '待处理',
        processing: '处理中',
        completed: '已完成',
        refunded: '已退款',
        shipping: '配送中',
      },
    },
    data: {
      description: '应用、文档与报告的来源数据包、抽取的字段、就绪状态与来历。',
      addSource: '添加来源',
      loadFailed: '当前无法加载你的来源。',
      loading: '正在加载来源审阅数据…',
      empty: '未在此工作区观测到已受理的来源数据包。',
      scope: '来源数据包审阅',
      summaryLabel: '来源摘要',
      tiles: { sources: '来源', ready: '就绪', drafts: '草稿' },
      columns: {
        name: '来源',
        artifact: '产物',
        confidence: '置信度',
        rows: '行数',
        status: '状态',
      },
      readyStatus: '就绪',
      filters: {
        artifact: '产物',
        allArtifacts: '全部产物',
        status: '状态',
        all: '全部',
        ready: '就绪',
        draft: '草稿',
        archived: '已归档',
      },
      unavailable: {
        'producer-not-configured': {
          summary: '来源审阅数据生产方的连接尚未配置。',
          title: '配置来源审阅连接',
          detail: '需由工作区管理员配置已受理的来源审阅数据生产方。在此期间仍可继续添加来源数据包。',
        },
        'metric-not-supported': {
          summary: '当前审阅约定不测量该来源指标。',
          title: '使用受支持的来源审阅字段',
          detail: '请继续使用已受理的来源数据包字段。当前审阅约定无法报告该指标。',
        },
        'authority-not-admitted': {
          summary: '当前来源与权利授权不允许测量该来源数据。',
          title: '检查来源授权',
          detail: '在申请测量之前，请先检查来源权利与受理证据。',
        },
      },
    },
    activity: {
      description: '追踪产物共享后请求的变更、审阅状态与团队操作。',
      exportLabel: '导出',
      exportAria: (count) => `将 ${count} 条活动导出为 CSV`,
      exportBlockedAria: (reason) => `导出 — ${reason}`,
      blocked: {
        notLoaded: '无法加载活动',
        none: '暂无可导出的活动',
        loading: '正在加载活动',
        unavailable: '活动测量不可用',
      },
      exported: (count, filename) => `已将 ${count} 条导出到 ${filename}`,
      loadFailed: '当前无法加载你的活动。',
      loading: '正在加载活动审阅数据…',
      empty: '未在此工作区观测到已记录的活动。',
      scope: '已发布成果的活动审阅',
      summaryLabel: '活动摘要',
      tiles: { pending: '待处理', applied: '已采纳', reviewed: '已审阅' },
      tabs: {
        all: '全部',
        pending: '待处理',
        applied: '已采纳',
        reviewed: '已审阅',
        rejected: '已驳回',
      },
      columns: {
        actor: '操作者',
        artifact: '产物',
        changes: '变更',
        state: '状态',
        updated: '更新',
      },
      changeCount: (count) => `${count} 项变更`,
      unavailable: {
        'producer-not-configured': {
          summary: '活动审阅数据生产方的连接尚未配置。',
          title: '配置活动审阅连接',
          detail: '需由工作区管理员配置已受理的活动审阅数据生产方。配置期间仍可继续共享工作。',
        },
        'metric-not-supported': {
          summary: '当前审阅约定不测量该活动指标。',
          title: '使用受支持的活动观测',
          detail: '请继续使用受支持的审阅事件。该指标不在当前活动审阅约定范围内。',
        },
        'authority-not-admitted': {
          summary: '当前来源与权利授权不允许测量该活动。',
          title: '检查活动授权',
          detail: '在申请测量之前，请先检查该活动的来源权利与受理证据。',
        },
      },
      drawer: {
        title: (id) => `活动 #${id}`,
        close: '关闭',
        process: '处理',
        refund: '退回',
        customer: '操作者：',
        email: '邮箱：',
        app: '应用：',
        date: '时间：',
        items: '条目',
        noItems: '此记录没有条目。',
        subtotal: '小计',
        shipping: '运费',
        discount: '折扣',
        total: '合计',
      },
    },
    impact: {
      description: '已共享 cogo 产物的浏览量、审阅转化、已采纳的变更与成效信号。',
      loadFailed: '当前无法加载成效审阅数据。',
      loading: '正在加载成效审阅数据…',
      empty: '在该时间段内，权威成效审阅没有任何观测。',
      scope: '所选成效审阅时间段',
      columns: {
        artifact: '产物',
        views: '浏览量',
        reviews: '审阅',
        cvr: '转化率',
      },
      seriesViews: '浏览量',
      trendTitle: '成效趋势',
      byArtifact: '按产物划分的成效',
      unavailable: {
        'producer-not-configured': {
          summary: '成效审阅数据生产方的连接尚未配置。',
          title: '配置成效审阅连接',
          detail: '需由工作区管理员配置已受理的成效审阅数据生产方。配置期间仍可继续共享产物。',
        },
        'metric-not-supported': {
          summary: '当前审阅约定不测量该成效指标。',
          title: '使用受支持的成效观测',
          detail: '请继续使用受支持的成效信号。该指标不在当前成效审阅约定范围内。',
        },
        'authority-not-admitted': {
          summary: '当前来源与权利授权不允许测量该成效。',
          title: '检查成效授权',
          detail: '在申请测量之前，请先检查成效信号背后的来源权利与受理证据。',
        },
      },
    },
    audience: {
      description: '查看已发布应用的成员增长、流量来源与回访者。',
      loadFailed: '当前无法加载受众审阅数据。',
      loading: '正在加载受众审阅数据…',
      empty: '对于此工作区，权威受众审阅没有任何观测。',
      scope: '受众审阅',
      kpis: { reviewers: '审阅者', new7d: '新增（7 天）', returnRate: '回访率' },
      rolesTitle: '受众角色',
      recentReviewers: '最近的审阅者',
      columns: {
        name: '姓名',
        email: '邮箱',
        role: '角色',
        joined: '加入时间',
      },
      unavailable: {
        'producer-not-configured': {
          summary: '受众审阅数据生产方的连接尚未配置。',
          title: '配置受众审阅连接',
          detail: '需由工作区管理员配置已受理的受众审阅数据生产方。配置期间仍可继续共享产物。',
        },
        'metric-not-supported': {
          summary: '当前审阅约定不测量该受众指标。',
          title: '使用受支持的受众观测',
          detail: '请继续使用受支持的审阅参与信号。该指标不在当前受众审阅约定范围内。',
        },
        'authority-not-admitted': {
          summary: '当前来源与权利授权不允许测量该受众。',
          title: '检查受众授权',
          detail: '在申请测量之前，请先检查受众观测的来源权利与受理证据。',
        },
      },
    },
    analytics: { eyebrow: '应用遥测', body: '打开一个已共享的成果，查看流量、转化与成效信号。遥测与已审阅的成果绑定，因此不会跳到无关的主页。' },
    requestFlow: {
      prepared: { title: '请求已准备', body: 'cogo 正在准备应用、文档或报告的变更。' },
      attached: { title: '审阅证据随行', body: '来源、界面与共享状态都会随请求一起保留。' },
      approval: { title: '由你批准共享', body: '发布与上线更新会等到你完成审阅。' },
    },
    themeEditor: {
      badge: '设计系统',
      description: '调整可直接用于 Studio 的主题维度，用真实的 cogo 组件区块核对结果，然后保存到资源库。',
      livePreviewNote: '实时预览使用与发送给 cogo 渲染器完全相同的载荷。',
    },
    billing: {
      currentPlan: '当前套餐',
      currentPlanNote: '你正在使用的订阅详情',
      cancelsAtPeriodEnd: '将在本周期结束时取消',
      cycle: '计费周期',
      amount: '金额',
      started: '开始日期',
      nextBilling: '下次计费',
      changePlan: '更换套餐',
      cancelSubscription: '取消订阅',
      cancelling: '正在取消…',
      viewPlans: '查看套餐',
      paymentHistory: '付款记录',
      paymentHistoryNote: '你的全部付款记录',
      paymentsSignedOut: '登录后可查看你的付款记录',
      paymentsFailed: '无法加载你的付款记录',
      paymentsEmpty: '没有付款记录',
      columns: {
        date: '日期',
        amount: '金额',
        status: '状态',
        provider: '支付方式',
        reference: '参考号',
      },
      status: {
        active: '生效中',
        paid: '已支付',
        pending: '待处理',
        cancelled: '已取消',
        failed: '失败',
      },
        statusUnknown: (value) => `未知状态（${value}）`,
      cancelDialog: {
        title: '要取消订阅吗？',
        body: (periodEnd) => `订阅将保留到本计费周期结束的 ${periodEnd}。此后不再计费。`,
        keep: '保留订阅',
        confirm: '是，取消',
      },
    },
    admin: { accessDeniedTitle: '拒绝访问', accessDeniedBody: '你需要管理员权限才能访问此页面。' },
  },
  // ─── end lane/kobiz ─────────────────────────────────────────────────
};

const zhTW: StudioDictionary = {
  ...zh,
  // ── lane/koentry ─────────────────────────────────────────────────────────
  newProject: ZH_TW_NEW_PROJECT_COPY,
  starters: ZH_TW_STARTERS_COPY,
  // ── end lane/koentry ─────────────────────────────────────────────────────
  designSystemAxes: ZH_TW_DESIGN_AXES_COPY,
  language: { label: "語言" },
  a11y: {
    skipToMainContent: "跳至主要內容",
    dashboardContent: "儀表板內容",
    loadingSurface: (surface) => `正在載入${surface}…`,
    creatorNav: "導覽",
    header: "頁首",
    designSystemSurfaces: "設計系統頁面",
    designSystemInventory: "此工作區的設計系統",
    importSources: "匯入來源",
    librarySummary: "程式庫概覽",
    operations: "營運",
    requestReviewFlow: "請求審核流程",
    reviewSummary: "審核摘要",
    shareReadinessChecklist: "共用就緒清單",
    appPreviewFrame: "coogo 應用程式預覽",
    entryBrandHome: "coogo 首頁",
    entryReport: "coogo 營運報告",
    showSection: (name) => `展開${name}`,
    hideSection: (name) => `收合${name}`,
  },
  ide: {
    undo: "復原",
    redo: "重做",
    appPageList: "應用程式頁面清單",
    documentList: "文件清單",
    viewportSwitcher: "檢視區切換",
    device: {
      mobile: "手機",
      tablet: "平板",
      desktop: "桌機",
      document: "文件",
      a4Portrait: "A4 直向",
      framed: (label, width, height) => `${label} (${width}x${height})`,
      sized: (label, width, height) => `${label} · ${width}x${height}px`,
    },
    pages: {
      title: "頁面",
      summaryWithPages: (count) => `預覽 + ${count} 個頁面`,
      summaryPreviewOnly: "預覽",
      runnableTitle: "預覽",
      runnableBadge: "執行",
      runnableDetail: "執行檢視",
      emptyTitle: "目前畫面",
      emptyBody: "尚無頁面資訊",
    },
    documents: {
      title: "文件",
      count: (count) => `${count} 份文件`,
      pageCount: "1 頁",
    },
    reviewPreview: "檢視預覽",
    print: "列印",
  },
  navigation: {
    sections: {
      start: "開始",
      build: "建置",
      verify: "驗證",
      release: "發佈",
    },
    home: '首頁',
    create: '建立',
    work: '工作',
    details: '詳細工具',
    map: '地圖',
    code: '程式碼',
    release: '發布',
    publish: '發布',
    launch: '上線',
    configure: '設定',
    settings: '設定',
    share: '分享',
    starters: '入門範本',
    import: '匯入',
    sources: '來源',
    library: '資料庫',
    documents: '文件',
    requests: '請求',
    requestsAndReviews: '請求與審核',
    bots: '機器人',
    operations: '營運',
    data: '資料',
    activity: '活動',
    impact: '成效',
    audience: '受眾',
    analytics: '分析',
    designSystem: '設計系統',
    workspaceSettings: '工作區設定',
    projects: '專案',
    templates: '範本',
    projectSettings: '專案設定',
    account: '帳戶',
    signIn: '登入',
    signOut: '登出',
    goHome: '前往 Studio 首頁',
    goCoogoHome: '前往 Coogo 首頁',
    coogoHomepage: 'Coogo 首頁',
    studioNavigation: 'Studio 導覽',
    studioNavigationDescription: '在建立、專案、已儲存工作、請求和設定之間導覽。',
    collapseNavigation: '收合導覽列',
    expandNavigation: '展開導覽列',
  },
  artifacts: {
    label: '產出模式',
    app: '應用',
    game: '遊戲',
    design: '設計',
    document: '文件',
    guide: '指南',
    insights: '現況',
  },
  work: {
    title: '工作',
    subtitle: '建立、管理並審閱應用、文件與報告。',
    namePlaceholder: '新工作名稱...',
    create: '建立',
    browseStarters: '瀏覽範本',
    importSource: '匯入來源',
    emptyTitle: '尚無工作',
    emptyBody: '在上方建立應用、文件或報告，或瀏覽下方範本',
  },
  designSystem: {
    title: '設計系統',
    subtitle: '設定產生應用所使用的色彩、字體、間距與形狀，並在儲存到資料庫之前對照 cogo 詞彙進行檢查。',
    validationLab: '驗證實驗室',
    themeEditor: '主題編輯器',
    tabs: {
      systems: '設計系統',
      validation: '驗證實驗室',
      vocabulary: '詞彙',
      briefings: '簡報',
    },
    uiBlocks: {
      eyebrow: 'UI Block 本體',
      title: '你正在製作什麼？',
      description: '描述所需畫面，Coogo 會從已批准目錄中找到真實區塊，並以目前的設計本體主題預覽。',
      intentLabel: '產品意圖',
      intentPlaceholder: '例如：團隊分析儀表板',
      shortcutsLabel: '從意圖開始',
      shortcuts: { dashboard: '儀表板', store: '商店', landing: '登陸頁', auth: '登入流程' },
      recommendations: '推薦 UI Block',
      admittedCount: (count) => `${count} 個目錄已批准區塊`,
      noMatches: '目前沒有與此意圖相符的已批准區塊。請嘗試更簡短的產品描述。',
      preview: '以此主題預覽',
      selectedPreview: '已選用於即時主題預覽',
      readOnly: '唯讀預覽',
      authorityUnavailable: '在結構相容性與發佈權限得到證明前，不提供專案套用與替換。',
    },
    precondition: {
      signedOutReason:
        '需要登入。此工作區的設計系統清單，以及從來源讀取的值，僅向已登入的使用者顯示。',
      signInCta: '前往登入',
      checkingReason: '正在檢查此頁面是否可以開啟…',
      unmetFallbackReason: '開啟此頁面所需的條件尚未滿足。',
    },
    sourceRead: {
      failed: '無法讀取此專案的設計來源。',
      failedWithMessage: '目前無法讀取此專案的設計來源：{message}',
    },
    vocabularyPanel: {
      title: '詞彙',
      search: '搜尋…',
      enumPrefix: '前綴',
      enumAccessor: '存取器',
      enumEvent: '事件',
      enumAction: '動作',
      colName: '名稱',
      colMode: '模式',
      colSemantics: '語意',
      colCategory: '分類',
      colReturns: '回傳',
      colDomain: '領域',
      colDescription: '說明',
      loading: '正在載入詞彙…',
      errorTitle: '無法載入詞彙',
      retry: '重試',
      empty: '此分片中沒有項目。',
      unavailableTitle: '詞彙無法使用',
      unavailableDetail: '詞彙清單需要本機知識圖譜才能顯示。',
      noMatch: '沒有與「{query}」相符的項目',
      clear: '清除',
    },
    validatorPanel: {
      specChip: "§ 設計系統 v4",
      heading: "驗證器",
      lastRun: '上次：',
      justNow: '剛剛',
      secondsAgo: '{n}秒前',
      minutesAgo: '{n}分鐘前',
      hoursAgo: '{n}小時前',
      rerun: '重新執行',
      validating: '正在檢查…',
      colRule: '規則',
      allChecks: '全部檢查',
      noViolations: '沒有可顯示的規則違規。',
      colGate: '關卡',
      colStatus: '狀態',
      statusPass: '通過',
      statusFail: '未通過',
      flagOffTitle: '檢查已關閉',
      flagOffBody: '此工作區未啟用設計檢查。',
      readyTitle: '可以開始檢查',
      readyBody: '點選「重新執行」以檢查目前的專案。',
      unreadableTitle: '無法讀取檢查結果',
      unreadableBody: '上次檢查回傳了此頁面無法解析的結果。請點選「重新執行」再試一次。',
      briefingReadFailed: '無法讀取此工作區的簡報。',
      briefingTitle: '簡報',
      briefingIntro: '為這個應用撰寫簡短的簡報。簡報會與區塊一同保存在本體中，重新載入後仍會保留。',
      briefingSignIn: '請登入後撰寫簡報。',
      briefingSignedOutDetail: '簡報透過本體閘道依工作區保存。請登入以讀取和撰寫此應用的簡報。',
      briefingUnavailableTitle: '此工作區暫時無法使用簡報',
      briefingUnavailableDetail: '目前的 Studio 部署未提供受支援的簡報儲存。讀取與寫入已停用。',
      briefingFieldTitle: '標題',
      briefingFieldBody: '內文（Markdown — 以逸出文字呈現）',
      briefingTitlePlaceholder: '例如：第二季發布檢查清單',
      briefingBodyPlaceholder: '在此撰寫簡報。換行會保留，HTML 會被逸出。',
      briefingReady: '就緒',
      briefingSaving: '正在儲存…',
      briefingSave: '儲存簡報',
      briefingEmpty: '還沒有簡報。在上方寫下第一則並點選儲存。',
      repoInitTitle: '正在啟動檢查…',
      repoInitBody: '正在準備檢查器 — 需要一點時間。',
      noBlocksTitle: '尚無可檢查的區塊',
      noBlocksBody: '在編輯模式聊天中產生區塊後，此頁面就會有內容。',
      errorTitle: '檢查失敗',
      errorBody: '請查看下方的錯誤訊息，然後點選「重新執行」。',
    },
    inventory: {
      readingBadge: '正在讀取此工作區的設計系統…',
      readingDetail: '在清單回傳之前，不做任何斷言。',
      unreadableBadge: '無法讀取此工作區的設計系統',
      unreadableDetail:
        '清單沒有載入成功，所以此頁面無法說明該工作區有多少個設計系統。下方的主題編輯器仍可使用。',
      noneBadge: '此工作區沒有已儲存的設計系統{stamp}',
      noneDetail:
        '這裡還沒有任何內容被儲存為設計系統。在下方設定各個維度並儲存到資料庫，即可建立第一個。',
      listedBadge: '此工作區的設計系統 {count} 個{stamp}',
      listedBadgeOne: '此工作區的設計系統 {count} 個{stamp}',
      listedDetail:
        '這是上面時間點回傳的清單原樣。開啟此頁面只會列出它們，並不會對它們做任何檢查。',
      readStamp: '，{time} 讀取',
      setAsideNote:
        ' 另有 {count} 列屬於以範本建立的專案，不是工作區設計系統。',
      setAsideNoteOne:
        ' 另有 {count} 列屬於以範本建立的專案，不是工作區設計系統。',
    },
    themeEditorPanel: {
      title: '主題編輯器',
      body:
        '調整樣式、基礎色、主題、圖表配色、圓角與字型，真實的 cogo 元件區塊會隨之更新。滿意後儲存到資料庫。',
    },
    workbench: {
      controlsTitle: '主題控制項',
      selectPlaceholder: '選擇{axis}',
      saveToLibrary: '儲存到資料庫',
      saveDialogTitle: '儲存設計系統',
      namePlaceholder: '預設名稱',
      descriptionPlaceholder: '說明（選填）',
      closeLabel: '關閉',
      cancel: '取消',
      save: '儲存',
      saving: '正在儲存…',
      savedAs: '已儲存為「{name}」',
      nameRequired: '請輸入名稱。',
      saveFailed: '儲存失敗。',
      sourceTitle: '從來源讀取的設計系統',
      sourceEmptyBody: '此專案尚未匯入任何設計來源 — 在你決定之前，下方每個維度都是空缺。',
      sourceImportCta: '匯入設計來源（Figma）',
      sourceReadPrefix: '讀取自 ',
      sourceVariableCount: '· {count} 個變數',
      sourceVariableCountOne: '· {count} 個變數',
      chooseAxisValue: '選定',
      axisValueSelectLabel: '{axis} 值',
    },
    projectTokens: {
      title: '從設計檔匯入的權杖',
      bodyBefore:
        '從匯入的設計檔中讀到的顏色與變數屬於匯入它的專案，而不屬於工作區。開啟該專案並前往 ',
      navPath: '設計 → 權杖',
      bodyAfter:
        ' 即可檢視並套用到該專案。',
    },
  },
  settings: {
    title: '工作區設定',
    profile: {
      title: '個人資料',
      name: '名稱',
      email: '電子郵件',
      edit: '編輯',
      cancel: '取消',
      providerBlocked: '目前的登入方式還無法修改個人資料',
      save: '儲存',
      saving: '儲存中…',
      nameInputLabel: '工作區顯示名稱',
      nameSaved: '名稱已儲存。',
      signedOutBlocked: '登入後可編輯個人資料',
    },
    writeOutcome: {
      protectedRejected: '此受保護的設定未被變更。',
      accepted: '專案設定已接受。',
    },
    publishing: {
      title: '發布',
      exportsLabel: '成果匯出：',
      exportsNone: '尚無紀錄',
      exportsCount: '已記錄 {count} 項',
      recordsNone: '此工作區還沒有儲存任何發布紀錄。',
      recordsCount: '此工作區已儲存 {count} 筆發布紀錄。',
      exportSettings: '匯出設定',
      exportSettingsBlocked: '尚不可用 — 沒有可下載的設定匯出',
      reviewRecords: '檢視紀錄',
      reviewRecordsBlocked: '尚無紀錄 — 請先發布或匯出',
    },
    plan: {
      title: '方案',
      current: '目前：',
      manage: '管理方案',
      allowsTitle: '目前方案可以做什麼',
      generation: '生成',
      publication: '發布',
      packPromotion: '套件提升',
      errorLabel: '無法載入你的方案',
      lookupFailed: '帳單查詢失敗',
      loading: '正在檢查你的方案…',
      signedOut: '登入後可檢視你的方案',
      noSubscription: '沒有生效中的訂閱',
      rights: {
        'not-measured': '未量測',
        'authority-readback-invalid': '權限回讀無效',
        'not-admitted': '未准入',
        'preview-only': '僅限預覽',
        'not-admitted-by-source-receipt': '此來源憑據未准入',
        'preview-admitted': '預覽已准入',
        'same-project-publication-admitted': '同專案內發布已准入',
        'admitted': '已准入',
      },
    },
    runtime: {
      title: '執行環境',
      note: '發布識別與可用性是觀測到的事實，而不是權限。',
      distribution: '散布形態',
      studioSha: 'Studio 原始碼 SHA',
      applianceReleaseId: '設備發布 ID',
      observedAt: '觀測時間',
      observationsTitle: '可用性觀測',
      available: '可用',
      unavailable: '不可用',
      readFailed: '讀取失敗',
      notConfirmed: '無法確認可用性',
      probeFailures: {
        'platform-health-status': '平台健康檢查讀取失敗（{status}）',
        'platform-health-malformed': '平台健康檢查的回應格式不正確。',
        'platform-unreachable': '無法確認平台的可用性。',
        'capability-response-rejected': '能力回應未被接受（{status}）。',
      },
      capabilities: {
        platformConnection: '已連線的平台',
        localAuth: '本機認證',
        googleAuth: 'Google 認證',
        localPublish: '本機發布',
        cloudPublish: '雲端發布',
        customDomain: '自訂網域',
        billing: '帳單',
        analytics: '分析',
        externalIntegrations: '外部整合',
      },
    },
    domain: {
      title: '網域',
      defaultLabel: '預設：',
      customLabel: '自訂：',
      connect: '連接網域',
      blocked: '自訂網域尚不可用',
      signedOut: '登入後可檢視你的工作區網域',
    },
    github: {
      title: 'GitHub 整合',
      developer: '開發者',
      connected: '已連線',
      disconnect: '中斷連線',
      disconnecting: '正在中斷…',
      invite: '連接 GitHub 帳號後，可匯出原始碼並自行設定 CI/CD。',
      connect: '連接 GitHub',
      connecting: '正在連線…',
      confirmDisconnect: '要中斷 GitHub 帳號連線嗎？既有的儲存庫不會被刪除。',
    },
    notifications: {
      title: '通知',
      reason: '電子郵件通知尚不可用 — Studio 沒有寄送服務，也沒有儲存偏好的地方，因此無法開啟。',
      review: '審閱通知（電子郵件）',
      agentSummary: '代理活動摘要（每日）',
      productUpdates: '產品消息',
      unavailableSuffix: '— 尚不可用',
    },
    danger: {
      title: '危險區域',
      deleteAccount: '刪除帳號',
      deleteBlocked: 'Studio 目前還不支援刪除帳號 — 請寫信到 privacy@cogo.xyz 提出申請',
    },
    closedNetwork: {
      planNote:
        '此安裝運行在封閉網路中。可用範圍由安裝的授權決定，而不是由這裡管理的訂閱決定。',
      planManageBlocked: '訂閱由此安裝無法連線的外部服務管理',
      servedFromLabel: '連線位址:',
      domainBlocked: '自訂網域需要公開 DNS — 此安裝只在自己的位址上回應',
      githubTitle: 'GitHub',
      githubBlocked: '連結帳號需要 github.com，此安裝無法連線',
      deleteBlocked: '封閉網路安裝的帳號保存在本機 — 請聯繫此設備的管理員',
    },
  },
  operations: {
    title: '營運',
    subtitle: '檢視已發布成果的資料、活動、成效、受眾與遙測。',
    reviewedOutputs: '已審閱的成果',
    acceptanceRate: '接受率',
    sharedArtifacts: '已分享的產物',
    sharedArtifactsNote: '應用、文件與報告',
    unmeasured: '尚未測量 — Studio 沒有這些數字的來源。',
    tiles: {
      products: '來源資料包、擷取的列、就緒狀態與來歷。',
      orders: '已發布成果的活動及其記錄的證據。',
      revenue: '瀏覽量、轉換、已採納的變更與成效訊號。',
      members: '審閱者、流量來源與回訪者。',
      analytics: '上線後真實應用的遙測。',
    },
  },
  requests: {
    title: '請求與審閱',
    subtitle: '檢視請求的變更、核准與退回，以及隨附的證據。',
    openChat: '開啟對話',
    howItWorks: '請求是這樣進行的',
    stepsSuffix: ' · 3 步',
    rows: {
      inProgress: { title: '進行中的請求', body: '檢視 cogo 正在為你準備的應用、文件與報告變更。', },
      needsApproval: { title: '需要核准', body: '發布、上線與分享更新都會等你審閱之後再進行。', },
      reviewTrail: { title: '審閱紀錄', body: '檢視改了什麼、檢查了什麼，以及什麼已可分享。', },
      accessControl: { title: '存取權限', body: '把帳號與發布權限限制在目前請求的範圍內。', },
    },
    current: { title: '目前請求', body: '當 cogo 正在準備需要在分享或上線前審閱的變更時，使用此頁面。', },
    actions: {
      addSource: '新增來源',
      library: '資料庫',
      share: '分享',
      refresh: '重新整理',
    },
    tiles: {
      updates: '更新',
      checks: '檢查',
      needsApproval: '需要核准',
    },
    timeline: {
      title: '最近的請求紀錄',
      emptyTitle: '沒有進行中的請求',
      emptyBody: '從對話開始，或新增一個來源。需要核准的請求會出現在這裡。',
    },
    approval: {
      title: '需要你的核准',
      waiting: '此更新在分享前正等待你的審閱。',
      empty: '沒有待處理的核准。',
    },
    prepare: {
      title: 'coogo 可以準備的內容',
      appDraft: '應用草稿',
      sourceImport: '匯入來源',
      reviewCheck: '審閱檢查',
      launchSetup: '上線準備',
    },
    requestTitle: {
      appDraft: '應用草稿',
      sourceAdded: '已新增來源',
      reviewCheck: '審閱檢查',
      shareUpdate: '分享更新',
      launchSetup: '上線準備',
      requestedChange: '請求的變更',
    },
    status: {
      ready: '已就緒',
      needsReview: '需要審閱',
      inProgress: '進行中',
      recorded: '已記錄',
    },
    scope: {
      codeUpdate: '程式碼變更',
      designFileUpdate: '設計檔變更',
      publicShare: '公開分享',
      launchSetup: '上線準備',
      workspaceUpdate: '工作區變更',
    },
    entry: {
      reuse: '重複使用的部分',
      fresh: '新建立的部分',
      validation: '已檢查',
      approval: '核准',
    },
  },
  share: {
    title: '分享',
    subtitle: '分享應用、文件與報告，同時讓來源、檢查狀態與更新路徑保持可見。',
    centerTitle: '分享中心',
    centerBody: '讓每個分享的成果都連著它的來源、審閱狀態與更新路徑，這樣你無需從頭再來即可持續改進。',
    createOutput: '建立成果',
    addSource: '新增來源',
    sources: '來源',
    settingsDialog: {
      title: '分享設定',
      body: '更新 {name} 的子網域。',
      subdomainLabel: '子網域',
    },
    checklist: {
      publicUrl: '公開網址',
      readinessCheck: '檢查狀態',
      outcomePath: '成果路徑',
      updateControl: '更新管理',
      notShared: '未分享',
      sharedCount: '已分享 {count} 項',
      nothingSharedYet: '還沒有分享任何內容',
      readyCount: '{count} 項已就緒',
      needsCheck: '需要檢查',
      outcomePathValue: '諮詢、預約、匯出、結帳或審閱頁面',
      updateControlValue: '變更前請檢視分享設定',
    },
    tiles: {
      sharedOutputs: '分享的成果',
      readyChecks: '已完成的檢查',
      drafts: '草稿',
      latestUpdate: '最近更新',
      noSharedOutput: '沒有分享的成果',
    },
    health: {
      title: '分享狀況',
      checked: '已檢查',
      lastRecordedCheck: '最近記錄的檢查',
      needsAttention: '需要注意',
      draftsNotShared: '草稿（未分享）',
      nothingShared: '沒有分享',
      noneYet: '暫無',
      never: '從未',
      none: '無',
      checkedOfShared: '{shared} 項中 {checked} 項',
      blockedCount: '{count} 項受阻',
      nothingSharedBadge: '還沒有分享任何內容',
      nothingSharedDetail: '此工作區尚未分享任何內容，因此這裡沒有可回報的資訊。分享一個成果後，它的檢查紀錄就會出現在這裡。',
      blockedBadge: '分享的 {shared} 項成果中有 {blocked} 項受阻',
      blockedBadgeOne:
        '分享的 {shared} 項成果中有 {blocked} 項受阻',
      blockedDetail: '有分享成果的檢查紀錄處於受阻狀態。開啟它查看是什麼在阻擋。',
      uncheckedBadge: '分享的 {shared} 項成果沒有任何檢查紀錄',
      uncheckedBadgeOne:
        '分享的 {shared} 項成果沒有任何檢查紀錄',
      uncheckedDetail: '這些成果已分享，但都沒有檢查紀錄。開啟其中一個並執行檢查即可記錄。',
      checkedBadge: '分享的 {shared} 項成果中已檢查 {checked} 項',
      checkedBadgeOne:
        '分享的 {shared} 項成果中已檢查 {checked} 項',
      checkedBadgeLastSuffix: '，最近 {date}',
      checkedDetail: '這是上次檢查這些成果時記錄的內容。開啟此頁面並不會重新檢查。',
    },
  },
  library: {
    title: '資料庫',
    subtitle:
      '開啟此工作區中已核驗、可重複使用的素材與成果。',
    importSource: '匯入來源',
    tiles: {
      savedWork: '已儲存的工作',
      openLinks: '可開啟的連結',
      details: '詳細資料',
      launches: '發布',
    },
    searchPlaceholder: '搜尋已儲存的工作、來源或連結…',
    searchResults: '搜尋結果',
    savedWorkCount: '已儲存的工作 {count} 項',
    previewTitle: '資料庫預覽',
    thumbnailPreview: {
      project: '專案預覽',
      template: '範本預覽',
      library: '資料庫預覽',
    },
    previewEmptyTitle: '請選擇一個資料庫項目',
    previewEmptyBody:
      '已儲存的工作會顯示在這裡，並附上接下來需要的操作：開啟、分享、檢查或準備發布。',
    whatYouCanDo: '可以做什麼',
    readyToUse: '可以使用',
    noReviewLinks: '還沒有審閱連結。',
    selectedDetails: {
      summary: '就緒狀態、審閱、證據與發布記錄',
      openBeforeReviewLink:
        '請先開啟此應用，再建立可編輯的審閱連結。',
    },
    empty: {
      title: '資料庫中還沒有項目',
      body:
        '新增需求說明、頁面檔案、URL、儲存庫變更或設計畫板 — 每個已儲存的項目都會連同預覽、來源與發布操作出現在這裡。',
    },
    actions: {
      open: {
        label: '開啟',
        detail: '在分享前查看應用或文件',
        noLink: '此項目還沒有可開啟的連結',
      },
      edit: {
        label: '編輯',
        detail: '返回專案工作區',
        noWorkspace: '此項目未連結到任何專案工作區',
      },
      launch: {
        label: '發布',
        detail: '準備公開發布包',
        noBundle: '此項目還沒有可發布的已儲存檔案',
        busy: '{tool} 仍在執行',
      },
      details: {
        label: '詳細資料',
        detail: '檢視來源備註與後續步驟',
      },
      useAgain: '再次使用',
      sharePublicly: '公開分享',
      check: '檢查',
      share: '分享',
      linkPr: '連結 PR',
      prepareLaunch: '準備發布',
      feedback: {
        check: {
          progress: '正在檢查應用…',
          success: '已記錄應用檢查。',
          failure: '應用檢查失敗。',
        },
        sharePublicly: {
          progress: '正在準備公開分享…',
          success: '已記錄公開分享。',
          failure: '公開分享失敗。',
        },
        prepareLaunch: {
          progress: '正在準備發布…',
          success: '已記錄發布準備。',
          failure: '發布準備失敗。',
        },
      },
      noSelection: '請先儲存或選擇一個項目',
    },
  },
  sources: {
    title: '來源',
    subtitle: '將檔案、URL、程式碼儲存庫與設計來源加入此工作區。',
    addToLibrary: '加入資料庫',
    howItWorks: '加入來源後會發生什麼',
    flowAriaLabel: '從來源到工作區的流程',
    agentIntake: {
      eyebrow: 'Studio 智慧代理',
      title: '告訴 Studio 你想建立什麼',
      description: '輸入需求、選擇檔案或貼上 URL。無須先選擇來源類型，Studio 會準備下一步。',
      placeholder: '例如：建立一個能一覽客戶支援動態的儀表板',
      attach: '加入檔案',
      addUrl: '加入 URL',
      submit: '交給 Studio 智慧代理',
      understanding: '智慧代理理解的內容',
      nextStep: '下一步',
      connected: '最近和已連接的來源',
      emptyConnected: '與此工作連接的檔案和 URL 將顯示在這裡。',
      advanced: '進階匯入',
      advancedSummary: '直接選擇來源類型，或設定 Figma 存取、對象和標籤等詳細資訊。',
      requestUnderstanding: '建立新成果的需求',
      urlUnderstanding: '用作參考的網站',
      codeUnderstanding: '現有程式碼或文字檔案',
      figmaUnderstanding: '本機 Figma 設計檔案',
      documentUnderstanding: '要讀取並檢查結構的文件',
      nextDescribe: 'Studio 將整理需求並準備 Library 草稿。',
      nextUrl: 'Studio 將頁面連接為參考來源並準備 Library 草稿。',
      nextCode: 'Studio 將檔案作為畫面來源讀取並準備 Library 草稿。',
      nextFigma: 'Studio 將讀取設計，讓你檢查頁面和圖層。',
      nextDocument: 'Studio 將讀取文件，讓你檢查其結構。',
      currentRequest: '目前需求',
      selectedFile: '已選檔案',
      restoredDocument: '保存在此瀏覽器中的文件',
      connectedDesign: '已連接的 Figma 設計',
      urlHint: '請在同一個需求輸入框中貼上完整 URL。',
      unsupportedFile: '此輸入區域暫時無法讀取這種檔案格式。',
      activeDesignConflict: '目前連接的設計來源阻止了新的 URL 工作。請開啟該設計來源，或在新專案中從 URL 開始。',
      librarySaveMissing: '無法確認 Library 中已儲存的草稿。你的需求仍保留在此頁面，請重試。',
    },
    steps: {
      addSource: '加入來源',
      shape: '整理',
      save: '儲存',
      share: '分享',
    },
    form: {
      appName: '應用名稱',
      audience: '對象',
      audienceCreator: '創作者',
      audienceBusiness: '企業',
      audienceDeveloper: '開發者',
      sourceFieldSuffix: '來源',
      briefPlaceholder: '在此寫下你想要的應用、文件或報告。請一併說明對象、提供內容、頁面結構、表單、付款與期望語氣。',
    },
    upload: {
      title: '上傳檔案',
      body: '已經有應用包了嗎？直接上傳，資料庫中會保留以檔案為基礎的紀錄。',
      button: '上傳應用包',
    },
    groups: {
      fromIdea: '從想法開始',
      bringExisting: '匯入既有的內容',
    },
    otherWays: {
      title: '其他開始方式',
    },
    chatStart: {
      title: '從對話開始',
      body: '不想選擇來源？開啟對話並描述你想要的內容。建立之後使用相同的資料庫與發布流程。',
    },
    savedWith: {
      title: '一併儲存的內容',
      openLink: '可開啟的連結',
      savedFile: '已儲存的檔案',
      sourceSnapshot: '來源快照',
      launchCheck: '發布檢查',
    },
    after: {
      title: '加入之後',
      defaultNote: 'cogo 會保存來源，因此你可以開啟、分享、優化或發布，而不會遺失它的出處。',
      figmaFileNote: 'cogo 將 .fig 證據與應用產生分開保存，以便先審閱來源涵蓋情況。',
      documentFileNote: '文件保留在此瀏覽器中，重新整理後仍然開啟，且不會被傳送到任何地方。',
      steps: {
        prepared: '工作已準備',
        designRead: '已讀取設計',
        documentRead: '已讀取文件',
        sourceSaved: '來源已儲存',
        keptInBrowser: '保留在此瀏覽器',
        mapperOpens: '開啟來源對應器',
        structureOpens: '在下方展開結構',
        libraryOpens: '開啟資料庫並顯示後續動作',
      },
    },
    panel: {
      tags: '標籤',
      librarySuffix: 'cogo 會附上來源備註與後續動作，儲存為資料庫卡片。',
      figmaFileNote: '在產生任何內容之前，我們會讀取你的 Figma 檔案並保留原件。',
      documentFileNote: '我們在此瀏覽器內讀取文件並顯示其結構。不會上傳，也尚未據此產生任何內容。',
      figmaTokenLabel: 'Figma 權杖',
      figmaTokenPasteHint: '請貼上 Figma 權杖並確認。',
      forgetToken: '移除',
      readDesign: '讀取設計',
      readDocument: '讀取文件',
      localFigmaFile: '本機 Figma 檔案',
      localDocument: '本機文件',
    },
    help: {
      assistant_prompt: {
        title: '在此寫下你的需求。',
        detail: '請一併寫明對象、提供內容、頁面結構、表單、付款與期望語氣。',
        example: '例如：為美妝創作者做一個預約頁面，包含服務清單、使用者評價、Instagram 連結，以及可直接付款的行動按鈕。',
      },
      url_import: {
        title: '貼上一個公開 URL。',
        detail: 'cogo 會作為視覺與內容參考，並將來源 URL 保留在資料庫中。',
        example: '例如：https://example.com/product-page',
      },
      ide_file: {
        title: '貼上一個元件、路由或畫面檔案。',
        detail: '當你已有畫面並想接續時最合適。',
        example: '例如：src/features/checkout/checkout-page.tsx',
      },
      github_pr: {
        title: '貼上儲存庫與提取請求編號。',
        detail: 'cogo 會保留變更與審閱備註，方便與團隊共享。',
        example: '例如：acme/storefront#128',
      },
      figma_frame: {
        title: '貼上 Figma 畫框 URL 或檔案/節點 ID。',
        detail: 'cogo 會保留設計來源，方便日後審閱轉換品質。',
        example: '例如：https://www.figma.com/design/abc123/Checkout?node-id=12-345',
      },
      figma_file: {
        title: '選擇本機 .fig 檔案。',
        detail: 'cogo 會讀取檔案、保留原件，並在生成前顯示設計內容。',
        example: '例如：checkout-flow.fig',
      },
      document_file: {
        title: '選擇 .docx 或 .hwpx 檔案。',
        detail: 'cogo 會讀取文件、保留原件，並在生成前顯示其結構。',
        example: '例如：account-opening-spec.docx',
      },
    },
    refLabels: {
      assistant_prompt: '摘要',
      url_import: 'URL',
      ide_file: '檔案',
      github_pr: 'GitHub',
      figma_frame: 'Figma',
      figma_file: '.fig',
      document_file: '文件',
    },
    cards: {
      assistant_prompt: {
        label: '用文字描述',
        description: '寫下你想要的應用、文件或報告。',
        bestFor: '從一個想法開始',
        output: '資料庫中的草稿',
      },
      url_import: {
        label: '使用網站',
        description: '將頁面 URL 作為參考引入。',
        bestFor: '既有網站與參考資料',
        output: '以參考為基礎的草稿',
      },
      ide_file: {
        label: '貼上程式碼',
        description: '引入元件、路由或畫面檔案。',
        bestFor: '已有實作時',
        output: '畫面草稿',
      },
      github_pr: {
        label: '使用提取請求',
        description: '附上需要審閱的儲存庫變更。',
        bestFor: '團隊變更',
        output: '審閱卡片',
      },
      figma_frame: {
        label: 'Figma 畫框',
        description: '引入設計畫框以進行轉換審閱。',
        bestFor: '設計交接',
        output: '以設計為基礎的草稿',
      },
      figma_file: {
        label: 'Figma .fig',
        description: '讀取本機 Figma 檔案並保留原件。',
        bestFor: '先確認設計時',
        output: '設計詳情',
      },
      document_file: {
        label: 'Word / HWPX',
        description: '讀取 .docx 或 .hwpx 並保留原件。',
        bestFor: '規格、指南、手冊',
        output: '文件詳情',
      },
    },
  },
  chrome: {
    breadcrumb: '目前位置',
    search: '搜尋應用、文件、遊戲與現況...',
    openNavigation: '開啟導覽',
    openChat: '開啟聊天',
    closeChat: '關閉聊天',
    notifications: '通知',
    lightMode: '切換到淺色模式',
    darkMode: '切換到深色模式',
    publish: '發佈',
    publishCurrent: '發佈目前專案',
    openProjectToPublish: '請開啟要發佈的伺服器專案',
    publishBlocked: '發佈已阻擋',
    conflict: '衝突',
    saveFailed: '儲存失敗',
    saving: '正在儲存…',
    unsaved: '未儲存',
    live: '已上線',
    saved: '已儲存',
    draft: '草稿',
    reloadConflict: '另一個工作階段修改了此區塊 — 請重新載入最新狀態',
    reload: '重新載入',
    reloading: '正在重新載入…',
    exportSource: '匯出應用原始檔',
    openProjectToExport: '請開啟要匯出的專案',
    exportSuccess: '應用原始檔已匯出',
    toggleIntentLoop: '聊天',
    moreControls: '更多控制',
    history: '歷史',
  },
  proposal: {
    change: "變更建議",
    info: "資訊",
    warning: "注意",
    critical: "重要",
    before: "變更前",
    after: "變更後",
    unchanged: "無變更",
    removed: "已移除",
    details: "詳細資訊",
    close: "關閉",
    whatWillCheck: "coogo 將檢查的項目",
    reviewChecks: "檢查項目",
    reviewBeforeApply: "套用前檢查",
    attachedProof: "附加依據",
    check: "依據",
    notAttached: "未附加",
    readyToUse: "可以套用",
    reviewFirst: "需要檢查",
    anotherEditPending: "另一個編輯正在等待。",
    compareConflict: "比較衝突變更的詳細資訊",
    keep: "保留",
    revise: "修改",
    reviseAria: "修改建議的變更",
    use: "套用",
    reviewNeeded: "需要檢查",
    keepCurrent: "保留目前版本",
    keepCurrentAria: "保留目前版本並結束此建議",
  },
  home: {
    ...zh.home,
    title: "今天想建立或修改什麼？",
    subtitle: "連接來源或選擇工作類型，coogo 會協助整理第一個請求。",
    startWithChat: "從聊天開始",
    workTypes: "工作類型",
    workTypeHint: "選擇工作類型後，輸入框會填入起始語句。",
    recentWork: "最近工作",
    reviewNeeded: "需要確認",
    viewAll: "查看全部",
    review: "確認",
    noWork: "還沒有工作。",
    noWorkHint: "請從上方輸入框開始第一個工作。",
    firstProjectAction: "描述第一個專案",
    continueWorking: "繼續工作",
    openWork: "開啟工作",
    continue: "繼續",
    latestProjectEmpty: "真實專案和驗證狀態會顯示在這裡。",
    readiness: "coogo 就緒狀態",
    totalWork: "全部工作",
    productionVerified: "正式環境驗證",
    settings: "管理設定",
    countSuffix: "個",
    readinessSummary:
      "可分享狀態：就緒 {ready} 個、待確認 {review} 個、受阻 {blocked} 個。",
    featuredOutcome: {
      eyebrow: "旗艦工作流 · 預覽",
      title: "電子報訂閱 → 會員服務",
      outcomes: {
        newsletterMembership: { title: "電子報訂閱 → 會員服務" },
        reservationOperations: { title: "預約營運" },
      },
      description: "以訂閱者和內容為基礎，啟動受控的會員服務預覽。",
      boundary:
        "僅建立預覽候選項。驗證、發佈、部署和正式環境即時回讀皆為獨立關卡。",
      action: "選擇此工作流程",
      clearAction: "清除所選工作流程",
      inProgress: "此工作流程要求已在進行中。",
      prompt:
        "將我的電子報訂閱者和現有內容轉換為包含會員導引、受保護內容和營運洞察的付費會員服務。",
    },
    lanes: {
      app: { title: "應用", prompt: "我想建立一個工作應用。" },
      document: { title: "文件", prompt: "我想根據已連接的來源建立文件。" },
      game: { title: "遊戲", prompt: "我想建立一個規則與互動清楚的遊戲。" },
      existing: { title: "修改現有工作", prompt: "我想選擇並更新現有工作。" },
      starter: {
        title: "從 Starter 開始",
        prompt: "我想選擇 Starter 並開始。",
      },
    },
  },
  chat: {
    ...zh.chat,
    // These five inherited Simplified Chinese from `{ ...zh.chat }` and
    // rendered it in a Traditional session. See the `{ ...zh }` class guard.
    greeting: (name) => `${name}，今天要建立什麼？`,
    cancel: "取消請求",
    checkingSession: "正在檢查 Studio 工作階段",
    cancelled: "計畫已取消",
    ontologySelectionRequired:
      "產生前請先選擇本專案要使用的應用程式套件。",
    startingNotice:
      "從你的請求開始。coogo 會將製作內容、下一步變更和對話保持在一起。",
    placeholder: "想建立或修改什麼？",
    attach: "檔案 · 來源",
    attachLabel: "附加檔案或連接來源",
    send: "傳送請求",
    appGenerationStarting: "正在建立專案並準備畫面、互動和資料...",
    webMcpVerified: (target) =>
      target
        ? `已透過 WebMCP 確認：${target}。`
        : "已透過 WebMCP 確認目前的 Studio 工作內容。",
    readOnlyCompletionFallback:
      "無法從回應中確認你詢問的狀態。未執行任何變更，也未建立待核准項目。",
    suggestions: [
      "建立預約應用",
      "將文件轉換為可審閱的報告",
      "更新現有工作",
    ],
    templateProposal: {
      heading: "現有範本可能適合",
      startFaster: "可協助你更快開始。",
      accept: "使用範本",
      accepting: "正在建立...",
      decline: "從頭建立",
      accepted: "已使用範本建立專案。",
      declined: "正在從頭建立並準備畫面、互動和資料。",
      unavailable: "此範本建議已無法使用。",
      failure: "無法使用該範本。你仍可從頭建立。",
    },
    planningFailure: {
      ontologyBlocked:
        "coogo 目前還無法建立此內容 — 所需的知識尚未收錄在資料庫中。請嘗試其他產品創意，或請團隊新增此領域的知識。",
      planNotActionable:
        "計畫中沒有包含這個應用程式所需的資料連線，因此沒有建立任何內容。請補充它需要讀取與寫入的資訊——例如成員是誰、向他們收取什麼——然後重新傳送。",
      requirementCoverage:
        "規劃沒有涵蓋你請求中的一部分內容，因此未建立任何內容。請把請求拆成更短的句子後重新傳送。",
      generic: "規劃過程中發生問題，請重試。",
    },
    updateExistingWork: {
      noExistingWork:
        "此工作區還沒有已儲存的作品。告訴我你想做什麼，我來開始。",
      opening: "正在開啟「{name}」。告訴我你想修改什麼。",
      chooseOne: "你有多個已儲存的專案 — {choices}。要開啟哪一個？",
    },
    toolFailure: {
      invalidInput:
        "請求中缺少此變更所需的內容，因此未做任何變更。請用一句具體的話說明你想修改什麼。",
      notPermitted: "此變更暫時無法從聊天中執行，因此未做任何變更。",
      unknownAction: "coogo 目前還沒有該操作，因此未做任何變更。",
      generic: "無法完成此變更，因此未做任何變更。",
    },
  },
  templateContent: {
    aiFill: "AI 填入",
    briefPlaceholder: "例如：充滿活力的社區麵包店，溫暖的色調",
    filling: "正在填入...",
    aiUnavailable: "AI 服務尚未設定。",
    fillFailed: "AI 填入失敗。",
    fillBatchProgress: "正在填入批次 {current}/{total}...",
    fillPartialNotice: "AI 未能填入 {count} 個欄位；其餘欄位已填入。",
    fillAllFailedNotice: "AI 未能填入全部 {count} 個欄位，沒有任何內容被填入。",
    judgeChecking: "正在審閱...",
    judgeFailed: "無法取得審閱結果。",
    pass: "通過",
    caution: "注意",
    reasons: "原因",
    hide: "隱藏",
    show: "顯示",
    general: "一般",
    changed: "已變更",
    records: "{count} 筆記錄",
    readOnly: "由於資料檔案無法使用，此版本為唯讀。",
    invalidJson: "請輸入有效的 JSON。",
    persistError: "部分變更無法儲存，請重試。",
    applySuccess: "內容已套用。",
    applyFailed: "套用失敗，請檢查下方錯誤。",
    applyFailureSummary: "{count} 項套用失敗。",
    applying: "正在套用...",
    apply: "套用 ({count})",
    designRebindTitle: "設計發佈",
    designRebindPresetLoadError: "無法載入預設",
    designRebindPresetPlaceholder: "選擇預設",
    designRebindApply: "套用設計",
    designRebindApplying: "正在套用...",
    designRebindApplySuccess: "已套用設計",
    designRebindApplyFailed: "設計套用失敗",
  },
  candidateVerification: {
    title: "候選項驗證",
    description: "此生成候選項需要四項獨立依據和一項完整性膠囊。",
    authorityNote:
      "只有經過身分驗證的持久日誌回讀才能將這些檢查標記為已驗證。",
    promotionBoundary:
      "候選項依據驗證不證明產品品質、發佈、部署或正式環境即時回讀。",
    authorityLabel: "簽發方：{authority}",
    verifiedStatus: "候選項依據已驗證",
    publicationGateStatus: "發佈是獨立關卡",
    listLabel: "候選項驗證依據角色",
    refresh: "重新整理檢查",
    refreshing: "正在重新整理檢查",
    askCoogo: "詢問 Coogo",
    liveChecking: "正在重新整理候選項驗證檢查。",
    liveResult: "候選項驗證狀態：{status}。",
    statuses: {
      pending: "待處理",
      checking: "檢查中",
      ready: "已就緒，未驗證",
      verified: "已驗證",
      blocked: "已阻擋",
      unavailable: "無法使用",
    },
    roles: {
      "sufficiency-assessment": "來源充分性評估",
      "validator-safety-pass": "Validator 安全檢查通過",
      "second-model-judge": "獨立模型審查",
      "render-execution-receipt": "渲染執行收據",
      "verification-capsule": "驗證膠囊",
    },
    authorities: {
      "source-assessment-authority": "來源評估服務",
      "validator-runtime": "安全驗證服務",
      "independent-model-judge": "獨立模型評審服務",
      "renderer-runtime": "渲染驗證服務",
      "publishing-kernel": "發佈完整性服務",
    },
    causes: {
      "journal-checking": "正在讀取持久驗證日誌。",
      "journal-unavailable": "持久日誌回讀目前無法使用。",
      "journal-scope-invalid": "持久日誌與此候選項不相符。",
      "journal-contract-invalid": "持久日誌未通過契約驗證。",
      "verification-pending": "尚未記錄已驗證的持久事件。",
      "producer-receipt-pending":
        "正在等待指定伺服器生成方的收據；此候選項尚未驗證。",
      "verification-incomplete": "驗證事件缺少必要的獨立依據。",
      "source-assessment-current": "目前評估已就緒，但不代表已驗證。",
      "source-assessment-insufficient": "目前評估不符合來源充分性要求。",
      "source-assessment-missing": "需要目前且不可變的來源評估。",
      "source-assessment-stale": "候選項的評估版本已不是最新版本。",
      "durable-journal-verified": "持久日誌已記錄四項獨立依據及其完整性膠囊。",
    },
  },
  report: {
    ...zh.report,
    title: '現況',
    ontology: '本體',
    ontologySubtitle: '本體涵蓋率 · 目前證據與目標範圍',
    unavailableTitle: '尚無可顯示的現況',
    unavailableBody:
      "產品連接經過驗證的設計來源後，品質現況將顯示在這裡。coogo 不會顯示編造的分數。",
    unavailableBodyDeveloperDetail:
      "請選擇具有與目前發佈版本關聯的最新驗證結果的專案。coogo 不會產生佔位分數。",
    connectSource: "開啟設計來源",
    axesLabel: "本體涵蓋率軸",
    axesSummary: "6 個軸 · 必要維度最低分",
    axisMethod: "軸值取必要維度中的最低分。它用於呈現證據，不授予權限。",
    coverage: "本體涵蓋率",
    coverageSubtitle: "目前證據與目標範圍",
    lowestRequiredAxis: "必要軸最低值",
    loadingChart: "正在載入本體涵蓋率圖表",
    currentEvidence: "目前證據",
    targetEnvelope: "目標範圍",
    gap: "差距",
    axisDetails: "軸詳細資料",
    gaps: "差距",
    priority: "優先順序",
    blocks: "阻擋項目",
    noTrack: "無阻擋",
    askCoogo: "詢問 coogo",
    noOpenRequirements: "此發佈版本綁定的 assessment 中沒有未解決的本體需求。",
    allDimensions: "全部 {count} 個 application ontology 維度",
    dimension: "維度",
    authority: "權限",
    resolution: "解決方式",
    current: "目前",
    evidence: "證據",
    none: "無",
    status: {
      ready: "已就緒",
      warn: "警告",
      blocked: "已阻擋",
      missing: "尚無",
      missingTooltip: "尚無經過驗證的證據",
    },
    conversionProvenance: {
      title: "與原始畫面對比",
      sourceLabel: "轉換自",
      capturedLabel: "讀取時間",
      blocksLabel: "已讀取的畫面區塊",
      decisionsLabel: "已記錄的轉換決定",
      realizationLabel: "已落入應用的宣告",
      realizationAbsent:
        "已記錄的決定中沒有可結構化核對的項目，因此沒有落實率。coogo 不會編造數值。",
      sourceKindLegacyScreen: "貼上的舊有畫面",
    },
  },
  guide: {
    ...zh.guide,
    title: "指南工作區",
    documents: "指南",
    noPageSelected: "尚未選擇指南頁面。",
    page: "頁面",
    pageActivity: "頁面活動",
    proposals: "建議",
    applied: "已套用",
    exports: "匯出",
    pages: "頁",
    revision: "修訂",
    addPage: "新增頁面",
    pageTitle: "頁面標題",
    openingEditor: "正在開啟指南編輯器...",
    pageActions: "頁面操作",
    reviewPage: "檢查頁面",
    proposeEdit: "建議修改",
    editContent: "編輯內容",
    move: "移動",
    delete: "刪除",
    draft: "草稿",
    export: "匯出",
    readback: "驗證結果",
    output: "輸出",
    notRequested: "尚未請求",
    noExportRequested: "目前指南沒有匯出請求。",
  },
  connectedDocument: {
    notConnected: "未連接",
    connectedToTemplate: "已連接到{provider}",
    documentReference: "文件連結或名稱",
    loadDocument: "載入文件",
    loadErrorNotConnected: "此工作區尚未連接。請聯繫團隊設定文件連接。",
    loadErrorGeneric: "無法載入文件，請重試。",
    panelTitle: "已連接的文件",
    provider: "提供者",
    activeSourceSnapshot: "目前來源快照",
    connectionReference: "連接參照",
    documentIdLabel: "文件 ID",
    pageIdLabel: "頁面 ID",
    fetchVerifiedSnapshot: "取得已驗證快照",
    saveProjectFirst: "連接提供者文件前，請先儲存此專案。",
    unsavedDraftBlocker:
      "取得已連接文件快照前，請先完成或提交未儲存的 WordGard 草稿。",
    readbackSummary: (blocks, unsupported, accessMode) =>
      `${blocks} 個區塊 · ${unsupported} 個不支援 · ${accessMode}`,
    accessMode: { "read-only": "唯讀", "read-write": "讀寫" },
    openSource: "開啟來源",
    newPageParentId: "新頁面的父層 ID（選填）",
    publishApproval:
      "我同意在提供者端建立一份新副本。原始文件不會被更新。",
    publishAction: "建立並驗證新副本",
    publishBlockedUnsupported: "在解決不支援的提供者元素之前，發布將被阻擋。",
    connectionReadOnly:
      "此連接為唯讀。請使用具有寫入權限的方式重新連接後再發布副本。",
    proofLabel: "新副本證據:",
    proofStatus: { pass: "通過", fail: "失敗", blocked: "已阻擋" },
    structure: "結構",
    structureReadBack: "已回讀",
    structureMissing: "缺少",
    exportsReadBack: (count) => ` · 已回讀 ${count} 項匯出`,
    openNewCopy: "開啟新副本",
    publishVerified: (provider, versionRef) =>
      `已在 ${versionRef} 驗證新的 ${provider} 副本。`,
    publishBlockedBeforeVerified: "在驗證提供者副本之前，發布已被阻擋。",
    publishProofFailed: "新副本可能已建立，但其回讀證據未通過。",
    publishGenericError: "無法發布已連接文件的副本。",
  },
  behavior: {
    ...zh.behavior,
    navigation: "ActionFlow 導覽",
    title: "動作流程",
    selectFlow: "選擇事件或流程",
    unsaved: "未儲存",
    target: "行為目標",
    noElement: "未選擇元素",
    selectInPreview: "在預覽中選擇",
    section: "區段",
    status: "狀態",
    bindings: "綁定",
    flows: "流程",
    empty: "空",
    steps: "步驟",
    verified: "已驗證",
    failed: "失敗",
    notVerified: "未驗證",
    noFlows: "此專案未宣告流程。",
    eventBindings: "事件綁定",
    connected: "已連接",
    orphan: "孤立",
    inspectElement: "請選擇要檢查行為的應用元素。",
    noBindings: "此專案未宣告事件綁定。",
    chatActions: "聊天操作",
    reviewFlow: "審核流程",
    addStep: "新增步驟",
    connectEvent: "連接事件",
    testFlow: "測試流程",
    editor: "行為編輯器",
    unsavedFlow: "未儲存流程",
    noBlocks: "尚無區塊，無法儲存流程",
    testRun: "執行測試",
    closeFlowForm: "關閉流程表單",
    newFlow: "新增流程",
    addBlockFirst: "建立流程前請先新增頁面區塊",
    nodeTypes: "支援的 ActionFlow 節點類型",
    nodes: "節點",
    executionModel: "執行模型",
    binding: "綁定",
    direct: "直接",
    directAtomic: "直接 atomic",
    namedFlow: "命名流程",
    lanes: "領域",
    actionLane: "Action",
    uiLane: "UI",
    motionLane: "Motion",
    order: "順序",
    completionBarrier: "完成屏障",
    stage: "Stage",
    saga: "Saga",
    sagaCompensation: "Saga 依反向順序補償 · 非 ACID",
    palette: {
      ifElse: "條件分支",
      loop: "迴圈",
      switch: "分支",
      parallel: "平行",
      tryCatch: "例外處理",
    },
    flowCreateFailed: (name) =>
      `Flow「${name}」未建立 — 伺服器拒絕了該請求，本機變更已復原`,
    flowCreated: (name) => `已建立 Flow "${name}"`,
    flowCreatedLocalOnly: (name) =>
      `Flow "${name}" 僅在本次工作階段中建立 — 尚未儲存到伺服器`,
  },
  approval: {
    categoryFlows: "流程",
    categoryDatasources: "資料來源",
    categoryVariables: "變數",
    categoryEvents: "事件繫結",
    compositeActionFallback: "組合操作",
    operationCount: (count) => `${count} 項操作`,
    applyAll: "全部套用",
    applySelected: (selected, total) => `套用 (${selected}/${total})`,
    dismiss: "取消",
    applied: "已套用",
    pendingValidation: (reason) => `等待驗證${reason ? `：${reason}` : ""}`,
    dismissed: "已取消",
    edit: "編輯",
  },
  insertPanel: {
    noActivePage: "沒有開啟的頁面",
    searchPlaceholder: "搜尋 UI",
    maxChildrenReached: (current, max) =>
      `已達到子元素上限 (${current}/${max})`,
    fromLibrary: "來自元件庫",
    createManually: "手動建立",
    detailCreateOrOpenPage: "新增 UI 之前，請先建立或開啟一個頁面。",
    detailNeedsBlockTarget: "新元素需要一個區塊作為目標。",
    detailAppendsToBlock: "在此區塊的結尾新增元素。",
    detailInside: (target) => `在 ${target} 內`,
    detailAfterSelectionIn: (target) => `在 ${target} 中所選元素之後`,
    targetLabel: "目標",
    newBlock: "新增區塊",
    noLibraryBlocks: "尚無可重複使用的區塊",
    moreBlocks: (count) => `再顯示 ${count} 個區塊`,
    noElementsFound: "找不到元素",
  },
  dataExplorer: {
    datasourceCreated: (name) => `已建立資料來源「${name}」`,
    datasourceNameTaken: (name, blockName) =>
      `區塊「${blockName}」中已存在名為「${name}」的資料來源。未做任何變更 — 請換一個名稱，或編輯現有的資料來源。`,
    datasourceCreateFailed: (name, blockName) =>
      `無法建立「${name}」 — 找不到區塊「${blockName}」（是否已刪除或重新命名？）。未做任何變更。`,
  },
  routeHeadings: {
    map: "應用地圖",
    admin: "管理員",
    billing: "帳單",
    themeEditor: "主題編輯器",
    newProject: "我們要建立什麼？",
  },
  dataInspector: {
    header: {
      title: "項目詳細資料",
      reviewEntity: "檢查項目",
    },
    fields: {
      name: "名稱",
      block: "區塊",
      scope: "範圍",
      type: "類型",
      defaultValue: "預設值",
      expression: "運算式",
      path: "路徑",
      importSource: "匯入來源",
      call: "呼叫",
      method: "方法",
      kind: "種類",
      endpoint: "端點",
      resultTarget: "結果目標",
      headers: "標頭",
      body: "內容",
      source: "來源",
      eventKey: "事件鍵",
      component: "元件",
      event: "事件",
      flow: "流程",
      trigger: "觸發條件",
      steps: "步驟",
      usedBy: "參照",
    },
    placeholders: {
      noDefault: "沒有預設值",
      expression: "例如 #{otherVar} + 1",
      resultTarget: "此資料來源寫入的狀態變數",
      body: "請求內容範本",
    },
    emptyStates: {
      selectEntity: "請選取要檢視的項目",
      entityNotFound: "找不到項目",
      variableNotFound: "找不到變數",
      datasourceNotFound: "找不到資料來源",
      eventNotFound: "找不到事件",
      flowNotFound: "找不到流程",
    },
    actions: {
      testInExpressionLab: "在運算式實驗室中測試",
      writeExpressionInLab: "在運算式實驗室中撰寫運算式",
      openInFlowEditor: "在流程編輯器中開啟",
      deleteVariable: "刪除變數",
      deleteDatasource: "刪除資料來源",
      deleteEvent: "刪除事件",
    },
    appManifest: {
      scopeValue: "應用程式資訊清單",
      note: "應用程式資訊清單的資料來源是整個專案共用的輸入。區塊層級的繫結請在區塊資料來源或程式碼畫面中編輯。",
    },
    schema: {
      request: "請求結構",
      response: "回應結構",
    },
    flowUnbound: "-- 未繫結 --",
    stepCount: (count) => `${count} 個步驟`,
    validationSummary: (errors, warnings) =>
      `${errors} 個錯誤，${warnings} 個警告`,
    referencedWarning:
      "此變數在其他地方被參照。刪除後可能會使運算式失效。",
    toasts: {
      variableDeleted: (name) => `已刪除變數「${name}」`,
      datasourceDeleted: (name) => `已刪除資料來源「${name}」`,
      eventRemoved: (name) => `已移除事件「${name}」`,
      bindingsUpdated: (count, oldPath, newPath) =>
        `已更新 ${count} 個繫結：${oldPath} -> ${newPath}`,
      bindingsUpdateFailed:
        "更新繫結失敗。部分參照舊欄位名稱的位置可能需要手動修正。",
    },
  },
  dataValidation: {
    confirm: {
      deletePrompt: (name) => `確定要刪除「${name}」嗎？`,
      typeChangePrompt: (name) => `確定要變更「${name}」的類型嗎？`,
      deleteAction: "刪除",
      typeChangeAction: "變更類型",
      cancel: "取消",
      unnamedField: "未命名欄位",
      nestedFieldWarning: (count) =>
        `這會一併刪除此欄位底下的 ${count} 個巢狀欄位及其中的所有內容。`,
    },
    messages: {
      unmatchedBrackets: "#{} 括號不對稱",
      emptyExpression: "運算式 #{} 是空的",
      duplicateVariableName: (name, blockName) =>
        `區塊「${blockName}」中的變數名稱「${name}」重複`,
      variableUnreferenced: (name) =>
        `變數「${name}」未被任何流程或運算式參照`,
      flowMissing: (flowName) => `流程「${flowName}」不存在`,
      restDatasourceNoEndpoint: "REST 資料來源沒有端點 URL",
    },
  },
  proofHealth: {
    verified: "已驗證",
    needsReview: "需要覆核",
    needsLiveCheck: "需要線上檢查",
    blocked: "已阻擋",
    none: "無憑證",
    checkedLive: (date) => `${date} 線上已檢查`,
    dateUnrecorded: "已記錄",
    liveCheckBlocked: "線上檢查已被阻擋。",
    liveCheckNeedsReview: "線上檢查需要覆核。",
    recordBlocked: "憑證記錄已被阻擋。",
    recordNeedsReview: "憑證記錄需要覆核。",
    openDeployToRun: "開啟發布頁執行線上檢查。",
    reviewEvidence: "查看憑證",
    reviewShare: "查看分享",
  },

  // ── lane/kodash · map · projects · apps-live · import ────────────────────
  // Four screens whose readable surface was English on 2026-08-20, harvested
  // by `__tests__/readable-surface-localized.test.tsx` (33 + 21 + 16 + 14
  // word-entries). Appended at the END of every table on purpose: two sibling
  // lanes were editing the sections above at the same time, and the tail is
  // the one place four concurrent additions cannot collide.
  //
  // EVERY key below is spelled out in all five tables. `ko`, `ja` and `zh`
  // open with `...en` and `zhTW` with `...zh`, so a key added to `en` alone
  // is INHERITED rather than missing — and because the residue walk derives
  // its keep-Latin allowlist from the translated tables, inherited English
  // allowlists its own words and the walk goes silent. Measured, not assumed:
  // mutation M5 in the register test deletes one `ko` override here and the
  // residue walk stays green. The inheritance gate is what turns it red.
  appMap: {
    description: "檢視 cogo 應用內部的頁面、區塊、動作、狀態與資料連線。",
    emptyTitle: "開啟應用即可檢視其結構地圖",
    emptyBody:
      "載入專案、匯入的來源或產生的資料庫項目後，應用地圖才會派上用場。它會在發佈前顯示頁面、資料與動作流程步驟之間的連線。",
    createApp: "建立應用",
    importSource: "匯入來源",
    openProjects: "開啟工作",
    noAppLoaded: "未載入應用",
    nodesUnit: "個節點",
    edgesUnit: "條連線",
    nodeTypes: {
      app: "應用",
      page: "頁面",
      block: "區塊",
      atom: "設計元素",
      flow: "流程",
      stateVar: "狀態變數",
      datasource: "資料來源",
    },
    stats: { title: "統計", edges: "連線" },
    legend: {
      title: "圖例",
      filterLabel: "依節點類型篩選",
      clearFilter: "清除篩選",
    },
    nodeDetail: {
      title: "節點詳細資料",
      empty: "點選節點以檢視詳細資料",
      incomingUnit: "入邊",
      outgoingUnit: "出邊",
    },
    sessions: {
      title: "工作階段",
      body: "腦力激盪觀察時間軸將於第 3 階段推出。此面板將重播目前開啟應用的採用、否決與設計系統調整記錄。",
    },
    canvas: {
      controls: "檢視控制",
      zoomIn: "放大",
      zoomOut: "縮小",
      fitView: "符合視窗",
      interactive: "切換互動鎖定",
      miniMap: "縮圖",
      handle: "連接點",
      nodeDescription:
        "按 Enter 或空白鍵選取節點，接著可用方向鍵移動它。按 delete 刪除，按 esc 取消。",
      nodeKeyboardDisabled:
        "按 Enter 或空白鍵選取節點。用方向鍵移動它。按 delete 刪除，按 esc 取消。",
      nodeMoved: (direction, x, y) => `已將選取節點往 ${direction} 移動。新位置 x：${x}，y：${y}`,
      edgeDescription:
        "按 Enter 或空白鍵選取連接線，接著按 delete 刪除，按 esc 取消。",
    },
  },
  projectCard: {
    status: {
      published: "已上線",
      draft: "草稿",
      archived: "已封存",
      active: "進行中",
    },
    share: "分享",
    shareNamed: (name) => `分享 ${name}`,
    openActions: (name) => `開啟 ${name} 的操作選單`,
    rename: "重新命名",
    duplicate: "複製",
    settings: "設定",
    delete: "刪除",
  },
  outputsSection: {
    heading: "成果",
    breakdown: (shared, drafts) => `已分享 ${shared} 個 · 草稿 ${drafts} 個`,
    loading: "正在載入成果…",
    emptyTitle: "尚無成果",
    emptyBody:
      "建立或匯入應用、文件或報告後，就會顯示在這裡——先是草稿，分享後成為分享的成果。",
    showMore: (count) => `再顯示 ${count} 個成果`,
  },
  launchLoop: {
    addSource: "想法、檔案、URL、程式碼儲存庫或設計稿",
    shape: "cogo 會準備好這份工作",
    save: "把來源與後續步驟儲存在一起",
    share: "開啟、分享或發佈",
  },
  // ─── lane/kobiz: the nine business screens ───────────────────────────
  businessScreens: {
    review: {
      tryAgain: '重試',
      openSurface: (surface) => `開啟${surface}`,
      viewSurface: (surface) => `檢視${surface}`,
      tableEmpty: '沒有可顯示的資料',
      periodAll: '全部',
      observation: {
        measuredSummary: '已測量的審閱資料。',
        emptySummary: '審閱觀測詳情',
        observedAt: (stamp) => `觀測於 ${stamp}`,
        evidence: '證據：',
      },
      pagination: {
        first: '第一頁',
        previous: '上一頁',
        next: '下一頁',
        last: '最後一頁',
      },
      status: {
        active: '啟用',
        draft: '草稿',
        archived: '已封存',
        pending: '待處理',
        processing: '處理中',
        completed: '已完成',
        refunded: '已退款',
        shipping: '配送中',
      },
    },
    data: {
      description: '應用、文件與報告的來源資料包、擷取的欄位、就緒狀態與來歷。',
      addSource: '新增來源',
      loadFailed: '目前無法載入你的來源。',
      loading: '正在載入來源審閱資料…',
      empty: '未在此工作區觀測到已受理的來源資料包。',
      scope: '來源資料包審閱',
      summaryLabel: '來源摘要',
      tiles: { sources: '來源', ready: '就緒', drafts: '草稿' },
      columns: {
        name: '來源',
        artifact: '產物',
        confidence: '信心度',
        rows: '列數',
        status: '狀態',
      },
      readyStatus: '就緒',
      filters: {
        artifact: '產物',
        allArtifacts: '全部產物',
        status: '狀態',
        all: '全部',
        ready: '就緒',
        draft: '草稿',
        archived: '已封存',
      },
      unavailable: {
        'producer-not-configured': {
          summary: '來源審閱資料產出方的連線尚未設定。',
          title: '設定來源審閱連線',
          detail: '需由工作區管理員設定已受理的來源審閱資料產出方。在此期間仍可繼續新增來源資料包。',
        },
        'metric-not-supported': {
          summary: '目前的審閱約定不會測量此來源指標。',
          title: '使用支援的來源審閱欄位',
          detail: '請繼續使用已受理的來源資料包欄位。目前的審閱約定無法回報此指標。',
        },
        'authority-not-admitted': {
          summary: '目前的來源與權利授權不允許測量此來源資料。',
          title: '檢視來源授權',
          detail: '在申請測量之前，請先檢視來源權利與受理證據。',
        },
      },
    },
    activity: {
      description: '追蹤產物分享後請求的變更、審閱狀態與團隊操作。',
      exportLabel: '匯出',
      exportAria: (count) => `將 ${count} 筆活動匯出為 CSV`,
      exportBlockedAria: (reason) => `匯出 — ${reason}`,
      blocked: {
        notLoaded: '無法載入活動',
        none: '尚無可匯出的活動',
        loading: '正在載入活動',
        unavailable: '活動測量無法使用',
      },
      exported: (count, filename) => `已將 ${count} 筆匯出至 ${filename}`,
      loadFailed: '目前無法載入你的活動。',
      loading: '正在載入活動審閱資料…',
      empty: '未在此工作區觀測到已記錄的活動。',
      scope: '已發布成果的活動審閱',
      summaryLabel: '活動摘要',
      tiles: { pending: '待處理', applied: '已採納', reviewed: '已審閱' },
      tabs: {
        all: '全部',
        pending: '待處理',
        applied: '已採納',
        reviewed: '已審閱',
        rejected: '已退回',
      },
      columns: {
        actor: '操作者',
        artifact: '產物',
        changes: '變更',
        state: '狀態',
        updated: '更新',
      },
      changeCount: (count) => `${count} 項變更`,
      unavailable: {
        'producer-not-configured': {
          summary: '活動審閱資料產出方的連線尚未設定。',
          title: '設定活動審閱連線',
          detail: '需由工作區管理員設定已受理的活動審閱資料產出方。設定期間仍可繼續分享工作。',
        },
        'metric-not-supported': {
          summary: '目前的審閱約定不會測量此活動指標。',
          title: '使用支援的活動觀測',
          detail: '請繼續使用支援的審閱事件。此指標不在目前活動審閱約定的範圍內。',
        },
        'authority-not-admitted': {
          summary: '目前的來源與權利授權不允許測量此活動。',
          title: '檢視活動授權',
          detail: '在申請測量之前，請先檢視此活動的來源權利與受理證據。',
        },
      },
      drawer: {
        title: (id) => `活動 #${id}`,
        close: '關閉',
        process: '處理',
        refund: '退回',
        customer: '操作者：',
        email: '電子郵件：',
        app: '應用：',
        date: '時間：',
        items: '項目',
        noItems: '此記錄沒有項目。',
        subtotal: '小計',
        shipping: '運費',
        discount: '折扣',
        total: '合計',
      },
    },
    impact: {
      description: '已分享 cogo 產物的瀏覽量、審閱轉換、已採納的變更與成效訊號。',
      loadFailed: '目前無法載入成效審閱資料。',
      loading: '正在載入成效審閱資料…',
      empty: '在此期間內，權威成效審閱沒有任何觀測。',
      scope: '所選成效審閱期間',
      columns: {
        artifact: '產物',
        views: '瀏覽量',
        reviews: '審閱',
        cvr: '轉換率',
      },
      seriesViews: '瀏覽量',
      trendTitle: '成效趨勢',
      byArtifact: '各產物的成效',
      unavailable: {
        'producer-not-configured': {
          summary: '成效審閱資料產出方的連線尚未設定。',
          title: '設定成效審閱連線',
          detail: '需由工作區管理員設定已受理的成效審閱資料產出方。設定期間仍可繼續分享產物。',
        },
        'metric-not-supported': {
          summary: '目前的審閱約定不會測量此成效指標。',
          title: '使用支援的成效觀測',
          detail: '請繼續使用支援的成效訊號。此指標不在目前成效審閱約定的範圍內。',
        },
        'authority-not-admitted': {
          summary: '目前的來源與權利授權不允許測量此成效。',
          title: '檢視成效授權',
          detail: '在申請測量之前，請先檢視成效訊號背後的來源權利與受理證據。',
        },
      },
    },
    audience: {
      description: '檢視已發布應用的成員成長、流量來源與回訪者。',
      loadFailed: '目前無法載入受眾審閱資料。',
      loading: '正在載入受眾審閱資料…',
      empty: '對於此工作區，權威受眾審閱沒有任何觀測。',
      scope: '受眾審閱',
      kpis: { reviewers: '審閱者', new7d: '新增（7 天）', returnRate: '回訪率' },
      rolesTitle: '受眾角色',
      recentReviewers: '最近的審閱者',
      columns: {
        name: '姓名',
        email: '電子郵件',
        role: '角色',
        joined: '加入時間',
      },
      unavailable: {
        'producer-not-configured': {
          summary: '受眾審閱資料產出方的連線尚未設定。',
          title: '設定受眾審閱連線',
          detail: '需由工作區管理員設定已受理的受眾審閱資料產出方。設定期間仍可繼續分享產物。',
        },
        'metric-not-supported': {
          summary: '目前的審閱約定不會測量此受眾指標。',
          title: '使用支援的受眾觀測',
          detail: '請繼續使用支援的審閱參與訊號。此指標不在目前受眾審閱約定的範圍內。',
        },
        'authority-not-admitted': {
          summary: '目前的來源與權利授權不允許測量此受眾。',
          title: '檢視受眾授權',
          detail: '在申請測量之前，請先檢視受眾觀測的來源權利與受理證據。',
        },
      },
    },
    analytics: { eyebrow: '應用遙測', body: '開啟已分享的成果，檢視流量、轉換與成效訊號。遙測與已審閱的成果綁定，因此不會跳到無關的首頁。' },
    requestFlow: {
      prepared: { title: '請求已準備', body: 'cogo 正在準備應用、文件或報告的變更。' },
      attached: { title: '審閱證據隨行', body: '來源、畫面與分享狀態都會隨請求一起保留。' },
      approval: { title: '由你核准分享', body: '發布與上線更新會等到你完成審閱。' },
    },
    themeEditor: {
      badge: '設計系統',
      description: '調整可直接用於 Studio 的主題維度，用真實的 cogo 元件區塊核對結果，再儲存到資源庫。',
      livePreviewNote: '即時預覽使用與傳送給 cogo 轉譯器完全相同的酬載。',
    },
    billing: {
      currentPlan: '目前方案',
      currentPlanNote: '你正在使用的訂閱詳情',
      cancelsAtPeriodEnd: '將在本期結束時取消',
      cycle: '計費週期',
      amount: '金額',
      started: '開始日期',
      nextBilling: '下次計費',
      changePlan: '變更方案',
      cancelSubscription: '取消訂閱',
      cancelling: '正在取消…',
      viewPlans: '檢視方案',
      paymentHistory: '付款紀錄',
      paymentHistoryNote: '你的所有付款紀錄',
      paymentsSignedOut: '登入後可檢視你的付款紀錄',
      paymentsFailed: '無法載入你的付款紀錄',
      paymentsEmpty: '沒有付款紀錄',
      columns: {
        date: '日期',
        amount: '金額',
        status: '狀態',
        provider: '付款方式',
        reference: '參考號',
      },
      status: {
        active: '生效中',
        paid: '已付款',
        pending: '待處理',
        cancelled: '已取消',
        failed: '失敗',
      },
        statusUnknown: (value) => `未知狀態（${value}）`,
      cancelDialog: {
        title: '要取消訂閱嗎？',
        body: (periodEnd) => `訂閱會保留到本計費週期結束的 ${periodEnd}。之後不再計費。`,
        keep: '保留訂閱',
        confirm: '是，取消',
      },
    },
    admin: { accessDeniedTitle: '拒絕存取', accessDeniedBody: '你需要管理員權限才能存取此頁面。' },
  },
  // ─── end lane/kobiz ─────────────────────────────────────────────────
};

export const STUDIO_DICTIONARIES: Record<LocaleCode, StudioDictionary> = {
  en,
  ko,
  ja,
  zh,
  "zh-TW": zhTW,
};

// E2E-only exposure (same VITE_E2E_MOCK gate + window.__COGO_*__ convention
// as studio-store.ts's `__COGO_STUDIO__` / editor-store.ts's `__COGO_EDITOR__`)
// — lets Playwright mutate a locale's strings IN PLACE, in the already-running
// app, to prove a probe would actually detect a real copy change without
// touching this file on disk. First consumer: studio-locale-visual-census.spec.ts's
// sentinel probe (`CENSUS_SENTINEL_MUTATION=1`), which flips `ko.work.title`
// then asserts the captured heading changed. Gated OFF in production builds.
if (typeof window !== "undefined" && import.meta.env.VITE_E2E_MOCK === "true") {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).__COGO_STUDIO_DICTIONARIES__ = STUDIO_DICTIONARIES;
}
